// pages/OwnerDashboardPage.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRightLeft as ArrowRightLeftIcon,
  Ban,
  Banknote,
  BarChart3,
  Bell,
  Calendar,
  Check,
  CheckCircle2,
  ChefHat,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleDot,
  ClipboardList,
  Coins,
  Copy,
  CreditCard,
  HandCoins,
  ImagePlus,
  Lock,
  LogOut,
  Mail,
  Minus,
  Package,
  Pencil,
  Plus,
  QrCode,
  Radio,
  Receipt,
  RefreshCw,
  Search,
  Settings,
  Smartphone,
  Sparkles,
  Store,
  Ticket,
  Trash2,
  Truck,
  Undo2,
  Users,
  UtensilsCrossed,
  Warehouse,
  Wine,
  X,
} from "lucide-react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { buildSheetFromRows, SpreadsheetsWorkspace } from "@/components/SpreadsheetsWorkspace";
import { AiAssistant } from "@/components/AiAssistant";
import { AiSettingsPanel } from "@/components/AiSettingsPanel";
import { ExpensesProfitPanel } from "@/components/ExpensesProfitPanel";
import { BankReconciliationPanel } from "@/components/BankReconciliationPanel";
import { StationHealthBanner } from "@/components/StationHealthBanner";
import { IngredientsPanel, MoveStockDialog } from "@/components/IngredientsPanel";
import { VariancePanel } from "@/components/inventory/VariancePanel";
import { WastePanel } from "@/components/inventory/WastePanel";
import { ReceivingPanel } from "@/components/inventory/ReceivingPanel";
import { CostCoveragePanel } from "@/components/inventory/CostCoveragePanel";
import { MenuEngineeringPanel } from "@/components/inventory/MenuEngineeringPanel";
import { StockLocationsPanel } from "@/components/inventory/StockLocationsPanel";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { realtime, useRealtimeConnection } from "@/lib/realtime";
import { hasLocalSnapshot, loadDashboardBundle } from "@/lib/localDb";
import { LOCAL_EVENT_APPLIED } from "@/lib/eventEngine";
import { EventType } from "@/lib/events";
import { EVENT_SYNC_FLUSHED } from "@/lib/eventSync";
import { performStockAction } from "@/lib/stockSync";
import { cachedGet } from "@/lib/refCache";
import { RecipesPanel } from "@/components/inventory/RecipesPanel";
import { ApprovalsPanel, PrepSheetsPanel } from "@/components/prep/PrepSheetsPanel";
import { FallbackOverridesPanel } from "@/components/inventory/FallbackOverridesPanel";
import { OwnerNotifications } from "@/components/prep/OwnerNotifications";
import { StaffSalesPanel } from "@/components/StaffSalesPanel";
import { MeshStatusPill } from "@/components/MeshStatusPill";
import { DeviceSyncPill } from "@/components/DeviceSyncPill";
import {
  BACKEND_URL,
  clearSession,
  confirmSession,
  confirmSessionDeadThen,
  DEFAULT_COVER,
  formatPrice,
  getOrderTimeLabel,
  getSession,
  http,
  shouldKickOutOn401,
  useAutoInstall,
  useOfflineSessionUpgrade,
} from "@/app/core";
import {
  DashboardLoadingState,
  EmptyState,
  groupTipRowsBySplitTable,
  NfcWriteDialog,
  resizeImageFile,
  SectionHeader,
  ShareableLinkCard,
  SplitGuestBreakdown,
  StatusBadge,
  TagQrPreview,
} from "@/app/fragments";
import { useLoginChimeOnMount } from "@/app/chimes";
import { beginLogoutSuppression, exitFullscreenIfActive, FullscreenToggleButton, ThemeToggleButton } from "@/app/fullscreen";
import { StaffDashboardPage } from "@/pages/StaffDashboardPage";



/**
 * Session 36 — who served an order. ONE rule for every list, receipt and
 * report: the waiter stamped on the order at creation (PIN user, else the
 * table's assigned waiter), then legacy `user`, then the table's waiter
 * from the drill-down payload, and only as a last resort the tip
 * recipient. Previously several screens showed the tip recipient only,
 * so any order without a tip displayed "—" for the waiter.
 */
const orderWaiterLabel = (o) => {
  if (!o) return "";
  return String(
    o.waiter || o.waiter_name || o.user || o.server_name || o.table_waiter || o.tip_recipient || o.sold_by || "",
  ).trim();
};

const buildSimulatedNfcCode = () => {
  const uid = Array.from({ length: 12 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  return `nfc-${uid}`;
};

// Reusable schedule editor — same UX as the Specials schedule. Used inside
// the Item Category and S&E Category dialogs so owners can pick weekly
// recurring days OR a date range, with an optional time-of-day window.
//
// Props:
//   value: { type: "weekly"|"date_range", weekdays?: number[], start_date?, end_date?, start_time?, end_time? }
//   onChange: (next) => void
//   testidPrefix: string ("category-schedule", "sides-category-schedule", etc.)
function ScheduleEditor({ value, onChange, testidPrefix = "schedule" }) {
  const v = value || { type: "weekly", weekdays: [0, 1, 2, 3, 4, 5, 6] };
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="rounded-2xl border border-border bg-secondary/40 p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium">Schedule</p>
        <Select value={v.type || "weekly"} onValueChange={(value) => set({ type: value })}>
          <SelectTrigger className="h-9 w-[180px] rounded-xl" data-testid={`${testidPrefix}-type-select`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="weekly">Recurring weekly</SelectItem>
            <SelectItem value="date_range">Date range</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {(v.type || "weekly") === "weekly" ? (
        <div className="mt-3">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Days of the week</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label, idx) => {
              const checked = (v.weekdays || []).includes(idx);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    const days = new Set(v.weekdays || []);
                    if (days.has(idx)) days.delete(idx); else days.add(idx);
                    set({ weekdays: Array.from(days) });
                  }}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    checked
                      ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                      : "border border-border bg-card text-foreground hover:bg-secondary"
                  }`}
                  data-testid={`${testidPrefix}-weekday-${idx}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={`${testidPrefix}-start-date`}>Start date</Label>
            <Input id={`${testidPrefix}-start-date`} type="date" value={v.start_date || ""} onChange={(event) => set({ start_date: event.target.value })} className="h-11 rounded-xl" data-testid={`${testidPrefix}-start-date-input`} />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${testidPrefix}-end-date`}>End date</Label>
            <Input id={`${testidPrefix}-end-date`} type="date" value={v.end_date || ""} onChange={(event) => set({ end_date: event.target.value })} className="h-11 rounded-xl" data-testid={`${testidPrefix}-end-date-input`} />
          </div>
        </div>
      )}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${testidPrefix}-start-time`}>Start time (optional)</Label>
          <Input id={`${testidPrefix}-start-time`} type="time" value={v.start_time || ""} onChange={(event) => set({ start_time: event.target.value })} className="h-11 rounded-xl" data-testid={`${testidPrefix}-start-time-input`} />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${testidPrefix}-end-time`}>End time (optional)</Label>
          <Input id={`${testidPrefix}-end-time`} type="time" value={v.end_time || ""} onChange={(event) => set({ end_time: event.target.value })} className="h-11 rounded-xl" data-testid={`${testidPrefix}-end-time-input`} />
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Leave times blank for an all-day window. Times are interpreted in the restaurant's local timezone. Overnight ranges (e.g. 22:00 → 02:00) are supported.</p>
    </div>
  );
}

/**
/**
 * Session 27.9 — Consolidated session receipt helper.
 *
 * Merges every order that shares a `table_session_id` into a single
 * synthetic "session order" object that the existing receipt modal
 * can render as one unified receipt. Used by the Receipts search
 * when the owner clicks a session card — they get the whole dining
 * experience on one document instead of chasing individual add-item
 * orders.
 *
 * Fields aggregated:
 *   • items[] — concatenated in chronological order across the session
 *   • subtotal / vat_total / total — summed
 *   • tip_amount / cash_paid / card_paid / refund_amount — summed
 *   • payment_method — the single method if uniform, otherwise "split"
 *   • tip_recipient / waiter — comma-joined unique staff who touched it
 *   • order_number — synthetic "SESSION-{first}..{last}"
 *   • created_at — timestamp of the FIRST order in the session
 *   • __isSessionMerge / __sourceOrders — flags used by the modal to
 *     hide edit/refund buttons that only make sense on a single order.
 */
function mergeSessionOrders(orders) {
  const list = (orders || []).slice().sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  if (list.length === 0) return null;
  const sum = (key) => list.reduce((s, o) => s + Number(o[key] || 0), 0);
  const maxOf = (key) => list.reduce((m, o) => Math.max(m, Number(o[key] || 0)), 0);
  const items = [];
  list.forEach((o) => {
    (o.items || []).forEach((it) => items.push({ ...it, __from_order: o.order_number || o.id }));
  });
  const methods = [...new Set(list.map((o) => (o.payment_method || "").toLowerCase()).filter(Boolean))];
  const paymentMethod = methods.length === 1 ? methods[0] : methods.length > 1 ? "split" : "";
  const waiters = [...new Set(list.map((o) => orderWaiterLabel(o)).filter(Boolean))];
  // Session 47 — every round on a table now shares ONE order number, so
  // de-dupe before deciding whether to show a single number or a range.
  const orderNumbers = [...new Set(list.map((o) => o.order_number || o.id).filter(Boolean))];
  const first = list[0];
  const last = list[list.length - 1];
  const subtotal = Math.round(sum("subtotal") * 100) / 100;
  const vatTotal = Math.round(sum("vat_total") * 100) / 100;
  const total = Math.round(list.reduce((s, o) => s + Number(o.total ?? (Number(o.subtotal || 0) + Number(o.vat_total || 0))), 0) * 100) / 100;
  // Aggregate status for the whole receipt: any round still in the
  // kitchen keeps the table "pending" / "confirmed"; only when every
  // round is completed does the receipt read "completed".
  const allStatuses = list.map((o) => String(o.status || "").toLowerCase());
  const statuses = allStatuses.filter((s) => s !== "voided");
  const status = !statuses.length ? "voided"
    : statuses.includes("pending") ? "pending"
    : statuses.includes("confirmed") ? "confirmed"
    : statuses.length && statuses.every((s) => s === "completed") ? "completed"
    : (statuses.find(Boolean) || "confirmed");
  return {
    // Existing receipt-modal fields (must match schema).
    id: `session:${first.table_session_id || first.id}`,
    order_number: orderNumbers.length > 1
      ? `Session · ${orderNumbers[0]} – ${orderNumbers[orderNumbers.length - 1]}`
      : (orderNumbers[0] || "Session"),
    table_number: first.table_number || null,
    created_at: first.created_at,
    completed_at: last.completed_at || last.created_at || null,
    confirmed_at: first.confirmed_at || null,
    items,
    subtotal,
    vat_total: vatTotal,
    total,
    total_items: list.reduce((s, o) => s + Number(o.total_items || 0), 0),
    status,
    payment_method: paymentMethod,
    tip_amount: Math.round(sum("tip_amount") * 100) / 100,
    tip_cash_amount: Math.round(sum("tip_cash_amount") * 100) / 100,
    tip_card_amount: Math.round(sum("tip_card_amount") * 100) / 100,
    // Session 46 — cash_paid / card_paid / eft_paid are the TABLE totals
    // mirrored onto EVERY order, so summing them across the session
    // multiplied the payment by the number of orders (R324 × 3 = R972).
    // The mirror is identical on each order → take the max, not the sum.
    cash_paid: Math.round(maxOf("cash_paid") * 100) / 100,
    card_paid: Math.round(maxOf("card_paid") * 100) / 100,
    eft_paid: Math.round(maxOf("eft_paid") * 100) / 100,
    service_fee_amount: Math.round(sum("service_fee_amount") * 100) / 100,
    service_fee_pct: list.map((o) => o.service_fee_pct).find((v) => v != null) ?? null,
    extra_tip_amount: Math.round(sum("extra_tip_amount") * 100) / 100,
    refund_amount: Math.round(sum("refund_amount") * 100) / 100,
    tip_recipient: waiters.join(", "),
    waiter_name: waiters.join(", "),
    department_routing: null,
    customer_name: first.customer_name || first.session_customer_name || null,
    session_customer_name: first.session_customer_name || null,
    // Merge flags — the modal uses these to render the correct title
    // and to hide per-order actions that don't apply to a whole
    // session (refund, void, edit, reopen — those still work from
    // the individual-order path).
    __isSessionMerge: true,
    __sourceOrders: list.map((o) => ({ id: o.id, order_number: o.order_number, total: o.total, payment_method: o.payment_method, created_at: o.created_at })),
    table_session_id: first.table_session_id || null,
    // Session 65 — the table's bill truth (items + fee − voucher = final
    // total, payments, outstanding) rides along so the receipt and the
    // Operations row show the SAME final bill total.
    session_money: list.map((o) => o.session_money).find(Boolean) || null,
    split_guests: list.map((o) => o.split_guests).find(Boolean) || null,
  };
}

/**
 * Receipts panel for Owner Dashboard.
 * - Date-range filtering (Today, 7d, 30d, custom)
 * - Daily groupings with totals
 * - Email full CSV export to an auditor
 */
function ReceiptsPanel({ authHeaders, onOpenReceipt }) {
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [preset, setPreset] = useState("7");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(todayIso);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [auditorEmail, setAuditorEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  // Search across all loaded receipts — matches receipt number,
  // table number, or waiter name. Empty string shows the normal
  // day-grouped view.
  const [searchQuery, setSearchQuery] = useState("");

  const computedRange = useMemo(() => {
    const end = endDate || todayIso;
    if (preset === "custom") {
      return { start: startDate || end, end };
    }
    const days = preset === "today" ? 1 : Number(preset || 7);
    const start = new Date(Date.now() - (days - 1) * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    return { start, end };
  }, [preset, startDate, endDate, todayIso]);

  const fetchReceipts = useCallback(async () => {
    if (!authHeaders) return;
    setLoading(true);
    try {
      const response = await http.get("/owner/receipts", {
        headers: authHeaders,
        params: { start_date: computedRange.start, end_date: computedRange.end },
      });
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load receipts");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, computedRange.start, computedRange.end]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const handleSendAuditor = async () => {
    const email = auditorEmail.trim();
    if (!email) {
      toast.error("Enter the auditor's or bookkeeper's email");
      return;
    }
    setSending(true);
    try {
      const response = await http.post(
        "/owner/receipts/email-auditor",
        {
          email,
          start_date: computedRange.start,
          end_date: computedRange.end,
          range_label: `${computedRange.start} to ${computedRange.end}`,
        },
        { headers: authHeaders }
      );
      toast.success(`Sent ${response.data.orders} orders to ${email}`);
      setAuditorEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send to auditor. Check email settings.");
    } finally {
      setSending(false);
    }
  };

  const presets = [
    { value: "today", label: "Today" },
    { value: "7", label: "Last 7 days" },
    { value: "30", label: "Last 30 days" },
    { value: "90", label: "Last 90 days" },
    { value: "custom", label: "Custom range" },
  ];

  return (
    <div className="space-y-4" data-testid="owner-receipts-panel">
      <SectionHeader
        eyebrow="Audit"
        title="Receipts"
        description="Review receipts by day and email a full export to your auditor."
      />

      <Card className="rounded-[24px] border-border bg-card">
        <CardContent className="space-y-4 p-5">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Range</Label>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="receipts-range-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {presets.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {preset === "custom" ? (
              <>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Start</Label>
                  <Input
                    type="date"
                    className="h-11 rounded-xl"
                    value={startDate}
                    max={endDate || todayIso}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="receipts-start-date"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">End</Label>
                  <Input
                    type="date"
                    className="h-11 rounded-xl"
                    value={endDate}
                    max={todayIso}
                    onChange={(e) => setEndDate(e.target.value)}
                    data-testid="receipts-end-date"
                  />
                </div>
              </>
            ) : null}
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={fetchReceipts}
              disabled={loading}
              data-testid="receipts-refresh-button"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          <div className="grid gap-3 rounded-2xl bg-secondary/60 p-4 sm:grid-cols-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Range</p>
              <p className="font-medium">{computedRange.start} → {computedRange.end}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Orders</p>
              <p className="font-semibold" data-testid="receipts-total-orders">{data?.totals?.orders ?? 0}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Grand total</p>
              <p className="font-semibold" data-testid="receipts-grand-total">{formatPrice(data?.totals?.grand_total ?? 0)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[24px] border-border bg-card">
        <CardContent className="space-y-4 p-5">
          <SectionHeader
            eyebrow="Email"
            title="Send to auditor / bookkeeper"
            description="Export the selected range as a CSV and email it for audits or bookkeeping."
          />
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <div className="space-y-1">
              <Label htmlFor="auditor-email">Auditor / bookkeeper email</Label>
              <Input
                id="auditor-email"
                type="email"
                placeholder="auditor@example.com"
                className="h-11 rounded-xl"
                value={auditorEmail}
                onChange={(e) => setAuditorEmail(e.target.value)}
                data-testid="auditor-email-input"
              />
            </div>
            <Button
              className="h-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={handleSendAuditor}
              disabled={sending || !auditorEmail || !data?.totals?.orders}
              data-testid="send-to-auditor-button"
            >
              <Mail className="h-4 w-4" />
              {sending ? "Sending…" : "Email CSV export"}
            </Button>
          </div>
          {!data?.totals?.orders ? (
            <p className="text-xs text-muted-foreground">No orders in this range — pick a wider date range to enable export.</p>
          ) : null}
        </CardContent>
      </Card>

      <div className="space-y-3">
        {/* Receipt search — quick lookup across the loaded date range
            by receipt number, table number, or waiter name. */}
        <Card className="rounded-[24px] border-border bg-card">
          <CardContent className="p-4">
            <Label htmlFor="receipt-search" className="text-xs uppercase tracking-wide text-muted-foreground">Search</Label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="receipt-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Receipt number, table number, waiter, or customer name…"
                className="h-11 rounded-xl pl-9"
                data-testid="receipts-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {loading && !data ? (
          <Card className="rounded-[24px] border-border bg-card"><CardContent className="p-6"><Skeleton className="h-24 w-full" /></CardContent></Card>
        ) : null}
        {data && data.groups.length === 0 ? (
          <EmptyState icon={Receipt} title="No receipts in this range" description="Try expanding the date range to see more orders." />
        ) : null}

        {/* Search mode — flat list of matches across all loaded days.
            Session 27.9 — matches roll up into ONE row per dining
            session (grouped by `table_session_id`) so an owner
            looking for a specific customer's slip sees the full
            paid receipt, not the individual add-item orders
            scattered underneath it. */}
        {searchQuery.trim() && data ? (() => {
          const q = searchQuery.trim().toLowerCase();
          // 1) filter every order across every day
          const matchedOrders = [];
          (data.groups || []).forEach((g) => {
            (g.orders || []).forEach((o) => {
              const recNum = String(o.order_number || o.id || "").toLowerCase();
              const tbl = String(o.table_number || "").toLowerCase();
              const waiter = orderWaiterLabel(o).toLowerCase();
              const cust = String(o.customer_name || o.session_customer_name || "").toLowerCase();
              if (
                recNum.includes(q) ||
                tbl.includes(q) ||
                waiter.includes(q) ||
                (cust && cust.includes(q))
              ) {
                matchedOrders.push({ ...o, __date: g.date });
              }
            });
          });
          // 2) group the matches by session so a search for "table 5"
          //    or "John" returns ONE row per dining session even if
          //    the party ordered 4 rounds of drinks.
          const sessionMap = new Map();
          matchedOrders.forEach((o) => {
            const sid = String(o.table_session_id || `__loose:${o.id}`);
            if (!sessionMap.has(sid)) {
              sessionMap.set(sid, {
                session_id: sid,
                loose: sid.startsWith("__loose:"),
                table_number: o.table_number || null,
                date: o.__date,
                orders: [],
              });
            }
            sessionMap.get(sid).orders.push(o);
          });
          const sessionGroups = Array.from(sessionMap.values()).map((sg) => {
            sg.orders.sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
            const first = sg.orders[0];
            const total = sg.orders.reduce((s, o) => s + (Number(o.total) || (Number(o.subtotal || 0) + Number(o.vat_total || 0))), 0);
            const payMethods = new Set(sg.orders.map((o) => (o.payment_method || "").toLowerCase()).filter(Boolean));
            const primaryMethod = payMethods.size === 1 ? [...payMethods][0] : (payMethods.size > 1 ? "split" : "");
            const waiter = orderWaiterLabel(first) || orderWaiterLabel(sg.orders.find((o) => orderWaiterLabel(o))) || "";
            const customer = first?.customer_name || first?.session_customer_name || "";
            return { ...sg, first_order: first, total: Math.round(total * 100) / 100, primary_method: primaryMethod, waiter, customer };
          });
          sessionGroups.sort((a, b) => String(b.first_order?.created_at || "").localeCompare(String(a.first_order?.created_at || "")));
          return (
            <Card className="rounded-[24px] border-border bg-card" data-testid="receipts-search-results">
              <CardContent className="p-3">
                {sessionGroups.length ? (
                  <div className="space-y-2">
                    <p className="px-2 pt-2 text-xs text-muted-foreground">
                      {sessionGroups.length} {sessionGroups.length === 1 ? "session" : "sessions"} · {matchedOrders.length} matching {matchedOrders.length === 1 ? "order" : "orders"}
                    </p>
                    {sessionGroups.slice(0, 50).map((sg) => {
                      const m = sg.primary_method;
                      const tone = m === "cash"
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                        : m === "card"
                          ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30"
                          : m === "split"
                            ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30"
                            : "bg-secondary text-muted-foreground border-border";
                      return (
                        <button
                          key={sg.session_id}
                          type="button"
                          onClick={() => {
                            // Session 27.9 — clicking a session card
                            // opens ONE consolidated receipt covering
                            // every order under that dining session.
                            const merged = mergeSessionOrders(sg.orders);
                            if (merged && onOpenReceipt) onOpenReceipt(merged);
                          }}
                          className="flex w-full flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-left transition-colors hover:bg-secondary/50"
                          data-testid={`receipts-search-session-${sg.session_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold">
                              Table {sg.table_number || "—"}
                              {sg.customer ? <span className="ml-2 text-muted-foreground">· {sg.customer}</span> : null}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {sg.orders.length} order{sg.orders.length === 1 ? "" : "s"}
                              {sg.waiter ? ` · ${sg.waiter}` : ""}
                              {" · "}
                              {sg.date}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {m ? <Badge className={`capitalize ${tone}`}>{m}</Badge> : null}
                            <p className="font-semibold tabular-nums">{formatPrice(sg.total)}</p>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </button>
                      );
                    })}
                    {sessionGroups.length > 50 ? (
                      <p className="px-2 pb-2 text-xs text-muted-foreground">Showing 50 of {sessionGroups.length}. Refine your search to narrow down.</p>
                    ) : null}
                  </div>
                ) : (
                  <div className="p-4">
                    <EmptyState icon={Search} title="No matches" description="Try a different receipt number, table, waiter, or customer name." />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })() : null}

        {!searchQuery.trim() && (data?.groups || []).map((group) => {
          const expanded = !!expandedDays[group.date];
          return (
            <Card key={group.date} className="rounded-[24px] border-border bg-card">
              <CardContent className="space-y-3 p-5">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 text-left"
                  onClick={() => setExpandedDays((prev) => ({ ...prev, [group.date]: !prev[group.date] }))}
                  data-testid={`receipts-day-toggle-${group.date}`}
                >
                  <div>
                    <p className="text-sm text-muted-foreground">{group.date}</p>
                    <p className="font-['Space_Grotesk'] text-xl font-semibold">{group.count} {group.count === 1 ? "order" : "orders"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Total</p>
                    <p className="font-semibold">{formatPrice(group.total)}</p>
                  </div>
                </button>
                {expanded ? (
                  <div className="space-y-2 rounded-xl border border-border bg-secondary/50 p-3">
                    {group.orders.map((order) => (
                      <button
                        key={order.id}
                        type="button"
                        onClick={() => onOpenReceipt && onOpenReceipt(order)}
                        className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg bg-card px-3 py-2 text-left text-sm transition-colors hover:bg-secondary/60"
                        data-testid={`receipts-order-row-${order.id}`}
                      >
                        <div>
                          <p className="font-semibold">{order.order_number || order.id}</p>
                          <p className="text-xs text-muted-foreground">Table {order.table_number || "—"} · {order.status}</p>
                        </div>
                        <div className="flex items-center gap-2 text-right">
                          <div>
                            <p className="font-semibold">{formatPrice(order.total ?? order.subtotal ?? 0)}</p>
                            <p className="text-xs text-muted-foreground">{getOrderTimeLabel(order.created_at)}</p>
                          </div>
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                      </button>
                    ))}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── Void / Refund reason catalogs ───────────────────────────────────────
// Shared between the void picker and the refund dialog so labels stay
// consistent in the Audit Log and stored on each order.
const VOID_REASON_LABELS = {
  wrong_item: "Wrong Item",
  customer_changed_mind: "Customer Changed Mind",
  kitchen_error: "Kitchen Error",
  duplicate_order: "Duplicate Order",
  training: "Training",
  other: "Other",
};
const REFUND_REASON_LABELS = {
  wrong_item: "Wrong Item",
  food_quality: "Food Quality",
  customer_changed_mind: "Customer Changed Mind",
  duplicate_charge: "Duplicate Charge",
  service_issue: "Service Issue",
  other: "Other",
};

export function OwnerDashboardPage() {  const navigate = useNavigate();
  useLoginChimeOnMount();
  const [session, setSessionState] = useState(() => getSession("owner"));
  useOfflineSessionUpgrade("owner", session, setSessionState); // Session 46
  useEffect(() => { try { confirmSession("owner"); } catch (_) { /* noop */ } }, []);
  useAutoInstall(session);
  const [context, setContext] = useState(null);
  const [overview, setOverview] = useState(null);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [priceFilters, setPriceFilters] = useState([
    { label: "Under R10", min: 0, max: 10 },
    { label: "R10 to R20", min: 10, max: 20 },
    { label: "Over R20", min: 20, max: 9999 }
  ]);
  const [menuSettingsOpen, setMenuSettingsOpen] = useState(false);

  // Session 63.E — Operations → Orders sub-tab is controlled so a
  // notification can deep-link to Voided / Active, highlighting the order.
  const [ordersSubtab, setOrdersSubtab] = useState("active");
  const [fallbackBannerDismissedKey, setFallbackBannerDismissedKey] = useState(null);
  const [highlightOrderId, setHighlightOrderId] = useState(null);
  // Session 63.E — "menu" (VAT / fee / alerts / OOS default) vs "digital"
  // (QR-menu-only settings). Same dialog + save handler, different sections.
  const [menuSettingsMode, setMenuSettingsMode] = useState("menu");

  const [categories, setCategories] = useState([]);
  // Sides & Extras categories — independent grouping list managed under
  // Manage Menu → S&E Categories. Same shape as `categories` so the UI can
  // be a 1:1 clone of the Item Categories tab.
  const [sidesCategories, setSidesCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [orders, setOrders] = useState([]);
  // Voided orders live in their own tab under Orders so the main list stays
  // focused on active service. Lazy-loaded when the owner opens the tab.
  const [voidedOrders, setVoidedOrders] = useState([]);
  const [voidedOrdersLoaded, setVoidedOrdersLoaded] = useState(false);
  // Tracks the on-demand "Refresh" button on the Orders subtab. Used to
  // disable the button + spin its icon while the fetch is in flight so
  // owners can poll for new tickets without reloading the whole page.
  const [ordersRefreshing, setOrdersRefreshing] = useState(false);
  // Same UX as the Orders refresh — sits on the Audit Log subtab.
  const [auditRefreshing, setAuditRefreshing] = useState(false);
  // Pending tips (un-paid-out) aggregated by recipient. Loaded lazily when
  // the Tips subtab is first opened.
  const [tipsRows, setTipsRows] = useState([]);
  const [tipsLoaded, setTipsLoaded] = useState(false);
  // Session 37 — objective gratuity exceptions for manager review.
  const [tipExceptions, setTipExceptions] = useState([]);
  const [tipExceptionsOpen, setTipExceptionsOpen] = useState(false);
  const [tipsBusy, setTipsBusy] = useState("");
  // Tips drill-down — when a manager clicks a waiter row, we open this
  // dialog with the underlying orders that contributed to their tips.
  const [tipDetail, setTipDetail] = useState({ open: false, recipient: "", loading: false, data: null });
  // Reports drill-down — daily totals row click reveals that day's orders.
  const [dailyDetail, setDailyDetail] = useState({ open: false, date: "", loading: false, orders: [] });
  // Receipts search query — searches across the loaded receipts groups.
  const [receiptsSearchQuery, setReceiptsSearchQuery] = useState("");
  // ── Cash Management ───────────────────────────────────────────────────
  // Lazy-loaded when the Cash Management tab is first opened. Date range
  // is owner-controlled via presets (today / yesterday / 7d / custom).
  const [cashLoaded, setCashLoaded] = useState(false);
  const [cashData, setCashData] = useState({
    summary: { opening_float: 0, cash_sales: 0, card_sales: 0, cash_payouts: 0, supplier_payments_cash: 0, supplier_payments_total: 0, expected_cash: 0 },
    ledger: [],
    cash_ups: [],
    start_date: "",
    end_date: "",
  });
  const [cashHistory, setCashHistory] = useState([]);
  const [cashRange, setCashRange] = useState("today");
  const [cashCustom, setCashCustom] = useState({ start: "", end: "" });
  const [floatDialog, setFloatDialog] = useState({ open: false, mode: "create", id: "", values: { amount: "", user: "", notes: "", register_id: "", denominations: {}, use_total_only: false } });
  const [payoutDialog, setPayoutDialog] = useState({ open: false, values: { amount: "", reason: "", category: "stock_purchase", user: "", notes: "" } });
  const [supplierDialog, setSupplierDialog] = useState({ open: false, values: { supplier_name: "", amount: "", payment_method: "cash", reference: "", user: "", notes: "" } });
  const [cashUpDialog, setCashUpDialog] = useState({ open: false, values: { actual_cash: "", user: "", notes: "", denominations: {} } });
  const [cashUpDetail, setCashUpDetail] = useState({ open: false, record: null });
  // ── Multi-register support — list of registers + currently-filtered
  // register on the Cash Management dashboard. The selector defaults to
  // "all" (combine every register's totals). Each dialog now carries an
  // explicit `register_id` so floats/payouts/supplier/cash-ups can be
  // tied to a specific till. The dialogs default to Main Register.
  const [registers, setRegisters] = useState([]);
  const [registersLoaded, setRegistersLoaded] = useState(false);
  const [selectedRegisterId, setSelectedRegisterId] = useState("all");
  const [registerDialog, setRegisterDialog] = useState({ open: false, mode: "create", id: "", values: { name: "", description: "" } });
  const defaultRegisterId = useMemo(() => {
    const d = (registers || []).find((r) => r.is_default);
    return d ? d.id : (registers[0]?.id || "");
  }, [registers]);

  // ── Report scheduling — owners can schedule recurring email reports.
  const [reportSchedules, setReportSchedules] = useState([]);
  const [reportSchedulesLoaded, setReportSchedulesLoaded] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState({ open: false, mode: "create", id: "", values: { name: "", frequency: "daily", report_types: ["business_summary", "sales_mix", "category_mix"], recipients: "", enabled: true } });
  const [testReportEmail, setTestReportEmail] = useState("");
  const [testReportSending, setTestReportSending] = useState(false);

  // ── Inventory Alerts Scheduling ──────────────────────────────────────
  const [inventoryAlertSchedules, setInventoryAlertSchedules] = useState([]);
  const [inventoryAlertSchedulesLoaded, setInventoryAlertSchedulesLoaded] = useState(false);
  const [inventoryAlertDialog, setInventoryAlertDialog] = useState({
    open: false, mode: "create", id: "",
    values: { name: "", frequency: "daily", alert_types: ["low_stock", "out_of_stock"], channel: "email", recipients: "", enabled: true }
  });
  const [testAlertSending, setTestAlertSending] = useState(false);

  // ── YOCO Payment Gateway state ─────────────────────────────────────
  const [yocoGateway, setYocoGateway] = useState({
    yoco_mode: "test", yoco_payments_enabled: false, yoco_auto_confirm_paid: false,
    yoco_secret_key_test_hint: "", yoco_secret_key_live_hint: "",
    yoco_webhook_secret_test_hint: "", yoco_webhook_secret_live_hint: "",
    yoco_secret_key_test_set: false, yoco_secret_key_live_set: false,
    yoco_webhook_secret_test_set: false, yoco_webhook_secret_live_set: false,
  });
  const [yocoGatewayLoaded, setYocoGatewayLoaded] = useState(false);
  const [yocoSaving, setYocoSaving] = useState(false);
  const [yocoForm, setYocoForm] = useState({
    yoco_mode: "test", yoco_payments_enabled: false, yoco_auto_confirm_paid: false,
    yoco_secret_key_test: "", yoco_secret_key_live: "",
    yoco_webhook_secret_test: "", yoco_webhook_secret_live: "",
  });
  // Session 30 — Additional South African gateways (PayFast / Ozow /
  // SnapScan / Zapper). Config storage: enable toggles + merchant
  // credentials, saved via the same /owner/payment-gateway endpoint.
  const [saForm, setSaForm] = useState({
    payfast_enabled: false, payfast_mode: "sandbox", payfast_merchant_id: "", payfast_merchant_key: "", payfast_passphrase: "",
    ozow_enabled: false, ozow_mode: "sandbox", ozow_site_code: "", ozow_private_key: "",
    snapscan_enabled: false, snapscan_mode: "sandbox", snapscan_merchant_id: "", snapscan_api_key: "",
    zapper_enabled: false, zapper_mode: "sandbox", zapper_merchant_id: "", zapper_api_key: "",
  });
  const [saSaving, setSaSaving] = useState(false);
  // Session 30.2 — which gateway's config panel is open in the unified
  // Payment Gateways section (Yoco | PayFast | Ozow | SnapScan | Zapper).
  const [selectedGateway, setSelectedGateway] = useState("yoco");

  // ── Inventory Usage Analytics (Phase 5) — per-ingredient usage stats.
  const [inventoryUsage, setInventoryUsage] = useState(null);
  const [inventoryUsageLoaded, setInventoryUsageLoaded] = useState(false);
  const [inventoryUsageLoading, setInventoryUsageLoading] = useState(false);
  const [inventoryUsageRange, setInventoryUsageRange] = useState("7d");

  // ── Business Summary custom date-range picker.
  // Owners can pick presets (Today / Yesterday / 7d / 30d / This Month /
  // Last Month / This Year / All time) or punch in custom start+end dates.
  // The current selection is mirrored into the URL params sent to
  // /owner/reports/summary so the backend reflects the right window.
  const _today_iso = () => new Date().toISOString().slice(0, 10);
  const [bsRange, setBsRange] = useState({ preset: "today", start: _today_iso(), end: _today_iso() });

  // ── Stock audit trail (Stock History + Stock Take History) ─────────────
  const [stockHistoryLoaded, setStockHistoryLoaded] = useState(false);
  const [stockHistoryData, setStockHistoryData] = useState([]);
  const [stockHistoryRange, setStockHistoryRange] = useState("7d");
  const [stockHistoryCustom, setStockHistoryCustom] = useState({ start: "", end: "" });
  const [stockHistoryQuery, setStockHistoryQuery] = useState("");
  const [stockTakesLoaded, setStockTakesLoaded] = useState(false);
  const [stockTakesList, setStockTakesList] = useState([]);
  const [stockTakeDetail, setStockTakeDetail] = useState({ open: false, record: null, loading: false });
  // Quick item history — opened from the row-level "History" action.
  const [itemHistoryDialog, setItemHistoryDialog] = useState({ open: false, item_id: "", item_name: "", loading: false, rows: [] });
  // Per-adjustment "Details" dialog — opened from "View details" action.
  const [stockEntryDetail, setStockEntryDetail] = useState({ open: false, row: null });
  // Stock-take wizard — pre-filled with current expected stock per row.
  // `scope` lets the user filter the wizard to All / Items / Ingredients
  // so they can count one category at a time without scrolling past the
  // other. Saving still pushes both types in a single snapshot — the
  // backend already supports that.
  // Session 39.5 — `location` is a stock-location ID (the legacy keys
  // "floor" / "back_of_house" are resolved to IDs on open); `locations` is
  // the business's ACTIVE stock locations; each line carries `expected` /
  // `actual` maps keyed by location id so switching tabs never reloads.
  const [stockTakeWizard, setStockTakeWizard] = useState({ open: false, user: "", notes: "", search: "", scope: "all", location: "floor", locations: [], lines: [], saving: false });
  const [stockTakeCustomLocations, setStockTakeCustomLocations] = useState([]);
  // Session-level "Recorded by" attribution applied to every inline stock
  // edit / ingredient save so the Stock History attributes the change to
  // a real person instead of "Manager". Persisted to localStorage.
  const [stockRecorder, setStockRecorder] = useState(() => {
    try { return localStorage.getItem("stockRecorder") || ""; } catch { return ""; }
  });
  useEffect(() => {
    try { localStorage.setItem("stockRecorder", stockRecorder || ""); } catch { /* ignore */ }
  }, [stockRecorder]);
  // Low Stock banner click takes the manager straight to the Stock subtab
  // with the low-only filter applied. We use a tick to force focus.
  const [stockLowOnly, setStockLowOnly] = useState(false);
  // ── Global Audit Log ──────────────────────────────────────────────────
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditCatalog, setAuditCatalog] = useState([]);
  const [auditRange, setAuditRange] = useState("7d");
  const [auditCustom, setAuditCustom] = useState({ start: "", end: "" });
  const [auditUserFilter, setAuditUserFilter] = useState("");
  const [auditActionFilter, setAuditActionFilter] = useState("all");
  const [auditQuery, setAuditQuery] = useState("");
  const [auditDetail, setAuditDetail] = useState({ open: false, row: null });
  // ── Refund (owner-only, opened from receipt drill-down) ───────────────
  const [refundDialog, setRefundDialog] = useState({ open: false, order: null, editing: false, values: { amount: "", reason: "wrong_item", reason_other: "", pin: "", user: "", notes: "" }, saving: false });
  // ── Void with reason (manager PIN + reason) ───────────────────────────
  const [voidDialog, setVoidDialog] = useState({ open: false, order_id: "", order_label: "", values: { pin: "", reason: "wrong_item" }, saving: false });
  // Client-side payment-method filter for the Active orders table.
  // Values: "all" | "cash" | "card" | "split" | "unpaid".
  const [paymentFilter, setPaymentFilter] = useState("all");
  // Client-side date filter for the Active orders table.
  // Presets: "today" | "yesterday" | "7d" | "all" | "custom" (uses customDate).
  const [dateFilter, setDateFilter] = useState("today");
  const [customDate, setCustomDate] = useState("");
  const [departments, setDepartments] = useState([]);
  // Session 66 — departments that OWN stock (routed to a kitchen or a bar).
  // Drives the department tabs on Stock Take, Stock History and Move Stock.
  const stockDepartments = useMemo(() => (departments || []).filter((d) => d && d.active !== false && (d.route_to_kitchen || d.route_to_bar)), [departments]);
  const [stockHistoryDept, setStockHistoryDept] = useState("all");
  const [departmentDialog, setDepartmentDialog] = useState({
    open: false,
    mode: "create",
    values: { id: "", name: "", route_to_kitchen: false, route_to_bar: false, route_to_staff: false, active: true },
  });
  const [deptCategoryIds, setDeptCategoryIds] = useState([]);
  // Sides library — owner-managed extras (e.g. Chips R25). Items reference
  // these via menuItem.sides_config.
  const [sides, setSides] = useState([]);
  const [sideDialog, setSideDialog] = useState({
    open: false,
    mode: "create",
    values: { id: "", name: "", description: "", price: "", active: true, sides_category_id: "", department: "", image: "", notes_mode: "off" },
  });
  // Specials — time-scheduled promos. Owner sets kind (discount/standalone/
  // menu_takeover), schedule (weekly weekdays + time window OR date range),
  // and mode (additive vs exclusive takeover).
  const [specials, setSpecials] = useState([]);
  const [specialNowLocal, setSpecialNowLocal] = useState("");
  // Voucher codes — flat-discount codes staff can apply to any receipt.
  const [vouchers, setVouchers] = useState([]);
  const [voucherDialog, setVoucherDialog] = useState({
    open: false,
    saving: false,
    values: { code: "", discount_amount: "", usage_type: "single", max_uses: "1", expires_at: "", active: true },
  });
  const [specialDialog, setSpecialDialog] = useState({
    open: false,
    mode: "create",
    values: {
      id: "",
      name: "",
      description: "",
      kind: "discount",
      linked_item_id: "",
      special_price: "",
      price: "",
      image: "",
      department: "",
      takeover_category_ids: [],
      takeover_item_ids: [],
      schedule: { type: "weekly", weekdays: [], start_date: "", end_date: "", start_time: "", end_time: "" },
      mode: "additive",
      banner_color: "",
      active: true,
    },
  });
  const [orderReceipt, setOrderReceipt] = useState({ open: false, order: null });
  const [analytics, setAnalytics] = useState({ summary: {}, daily: [] });
  const [analyticsRange, setAnalyticsRange] = useState(30);
  // Combo-chart layer toggles. Bars = order count, Line = revenue.
  // Manager can turn either off to declutter.
  const [chartShowBars, setChartShowBars] = useState(true);
  const [chartShowLine, setChartShowLine] = useState(true);
  // Service-time + growth + stock data — all owner-only, lazy-loaded when
  // the matching Analytics sub-tab is opened.
  const [serviceTimes, setServiceTimes] = useState(null);
  const [longWaitReport, setLongWaitReport] = useState(null); // Session 30.22 — daily wait report
  // How to sort the per-order service-time drill-down table on the
  // Business Summary. Defaults to "slowest first" because the most
  // common manager workflow is to look for tickets that ran long.
  const [serviceTimeSort, setServiceTimeSort] = useState("slowest");
  // Detail dialog for a single slow order — clicking a row opens it.
  const [serviceTimeDetail, setServiceTimeDetail] = useState({ open: false, order: null, loading: false });
  const [growthStats, setGrowthStats] = useState(null);
  const [businessSummary, setBusinessSummary] = useState(null);
  const [businessProfit, setBusinessProfit] = useState(null); // Session 31.14 — expenses/profit for the same range
  const [paymentCorrection, setPaymentCorrection] = useState({ open: false, order: null, values: { new_method: "cash", reason: "", manager_pin: "", cash_paid: "", card_paid: "" }, saving: false });
  // Reopen Table — manager-PIN-gated correction that flips a paid/closed
  // table session back to "open" so staff can add a corrective order or
  // fix an accidental close. Audited.
  const [reopenTable, setReopenTable] = useState({ open: false, order: null, values: { manager_pin: "", reason: "" }, saving: false });
  const [businessSummaryLoading, setBusinessSummaryLoading] = useState(false);
  const [businessSummarySort, setBusinessSummarySort] = useState("revenue_desc");
  const [businessSummaryCatSort, setBusinessSummaryCatSort] = useState("revenue_desc");
  // Session 47 — the service-fee toggles kept "turning themselves off":
  // a stale local snapshot / background refresh could replace the
  // in-progress settings with an older copy where the fee was OFF, and
  // the next Save wrote that back. We now (a) only SEND the fee fields the
  // owner actually touched and (b) never let a refresh overwrite a
  // touched field before it is saved.
  const feeTouchedRef = useRef(new Set());
  const editFeeSetting = (field, value) => {
    feeTouchedRef.current.add(field);
    setBusinessSettings((prev) => ({ ...(prev || {}), [field]: value }));
  };
  const mergeIncomingSettings = (incoming) => {
    setBusinessSettings((prev) => {
      if (!incoming) return prev;
      const keep = {};
      feeTouchedRef.current.forEach((k) => { if (prev && k in prev) keep[k] = prev[k]; });
      return { ...incoming, ...keep };
    });
  };
  // Session 61 — the manual 'Recover missing service fees' control was
  // removed: fees are now self-healing (server sweeper + read-time repair).
  const [stockRows, setStockRows] = useState({ items: [], low_stock: [] });
  const [stockLoaded, setStockLoaded] = useState(false);
  const [stockEdits, setStockEdits] = useState({});
  const [stockFilter, setStockFilter] = useState({ sort: "stock_asc", scope: "all", query: "" });
  // Inventory: ingredients + recipes. Both are owner-only and lazy-loaded.
  // `lowStock` powers the persistent banner across all owner tabs; the
  // banner endpoint is light enough that we refresh it on overview loads.
  const [ingredients, setIngredients] = useState([]);
  const [ingredientsLoaded, setIngredientsLoaded] = useState(false);
  const [recipeMenuItemId, setRecipeMenuItemId] = useState("");
  const [recipeLines, setRecipeLines] = useState([]); // [{ingredient_id, quantity, unit}]
  const [recipeSaving, setRecipeSaving] = useState(false);
  const [recipeItemSearch, setRecipeItemSearch] = useState("");
  const [recipesSummary, setRecipesSummary] = useState([]);
  const [recipesSummaryLoaded, setRecipesSummaryLoaded] = useState(false);
  const [orderTimes, setOrderTimes] = useState(null);
  const [orderTimesLoaded, setOrderTimesLoaded] = useState(false);
  const [orderTimesSort, setOrderTimesSort] = useState({ key: "total_minutes", dir: "desc" });
  const [lowStock, setLowStock] = useState({ items: [], ingredients: [], total: 0 });
  // Session 39 — unacknowledged fallback / shortfall events for the dashboard banner.
  const [stockEvents, setStockEvents] = useState({ counts: null, events: [] });
  // Session 63.E — signature of the events currently shown in the stock
  // fallback banner; "Noted" hides the banner until this changes.
  const fallbackBannerKey = useMemo(
    () => (stockEvents.events || []).filter((e) => e.type === "shortfall" || e.first_in_episode).map((e) => e.id || `${e.ingredient_id || e.ingredient_name}:${e.at || e.created_at || ""}`).join("|"),
    [stockEvents.events],
  );
  // Open Accounts (running tabs) — list, detail, and inline payment form.
  const [accountsList, setAccountsList] = useState({ accounts: [], summary: { open_count: 0, total_outstanding: 0 } });
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [accountsFilter, setAccountsFilter] = useState("open"); // open | settled | all
  const [accountDetail, setAccountDetail] = useState(null);
  const [accountPaymentDraft, setAccountPaymentDraft] = useState({ amount: "", method: "cash", notes: "" });
  const [accountPaymentSaving, setAccountPaymentSaving] = useState(false);
  // Staff roster — list of waiter names exposed via the View Receipt
  // dialog so the till user can tap a name instead of typing.
  const [staffRoster, setStaffRoster] = useState([]);
  const [staffRosterLoaded, setStaffRosterLoaded] = useState(false);
  const [newStaffName, setNewStaffName] = useState("");
  const [staffBusy, setStaffBusy] = useState(false);
  // ── User Accounts ───────────────────────────────────────────────────
  // Per-business named users (Owner / Manager / Supervisor / Waiter /
  // Kitchen / Bar) with PIN-based login. The currently logged-in user
  // is available on `session.user` and drives audit attribution.
  const [userAccounts, setUserAccounts] = useState([]);
  const [userAccountsLoaded, setUserAccountsLoaded] = useState(false);
  // Staff-list filter (owner Staff & Access tab). "all" = show everyone.
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userRolesList, setUserRolesList] = useState(["owner", "manager", "supervisor", "waiter", "kitchen", "bar"]);
  const [userTab, setUserTab] = useState("roster");
  const [userDialog, setUserDialog] = useState({ open: false, mode: "create", id: "", values: { first_name: "", last_name: "", username: "", pin: "", role: "waiter", active: true, allowed_sections: null }, saving: false });
  const [userPinDialog, setUserPinDialog] = useState({ open: false, user: null, pin: "", saving: false });
  // Hard-delete-user dialog. Requires Void Manager PIN; supports ALL
  // roles (Owner, Manager, Supervisor, Shift Leader, Waiter). Self-
  // delete is blocked server-side. "Disable" remains a separate, softer
  // action via toggleUserActive.
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, user: null, pin: "", saving: false });
  const [userActivityDialog, setUserActivityDialog] = useState({ open: false, user: null, entries: [], loading: false });
  const sessionUser = session?.user || null;
  const sessionRole = sessionUser?.role || (session?.role === "owner" ? "owner" : null);
  // Lightweight permission check — keeps UI in sync with the backend
  // matrix in `ROLE_PERMISSIONS`. When no user account is attached
  // (legacy password login) we treat the owner scope as full-access.
  const userCan = useCallback((perm) => {
    if (!sessionUser) return session?.role === "owner";
    const role = sessionUser.role || "";
    const map = {
      owner: ["*"],
      manager: ["refund", "void", "cash_up", "stock_take", "stock_adjust", "supplier_payment", "cash_payout", "cash_float", "analytics", "move_tables", "merge_tables", "settle_account", "view_orders", "open_table", "add_order", "process_payment", "manage_specials"],
      shift_leader: ["refund", "void", "cash_up", "stock_take", "stock_adjust", "supplier_payment", "cash_payout", "cash_float", "analytics", "move_tables", "merge_tables", "settle_account", "view_orders", "open_table", "add_order", "process_payment", "manage_specials"],
      supervisor: ["move_tables", "merge_tables", "settle_account", "view_orders", "open_table", "add_order", "process_payment"],
      waiter: ["open_table", "add_order", "process_payment", "view_own_tips"],
      kitchen: ["view_kitchen_orders", "mark_ready"],
      bar: ["view_bar_orders", "mark_ready"],
    };
    const allowed = map[role] || [];
    return allowed.includes("*") || allowed.includes(perm);
  }, [sessionUser, session]);
  // Reports tab visibility is now controlled by role permissions only —
  // the old analytics-password gate has been removed (per the refinement
  // pass). We keep the state variables to avoid touching every call site,
  // but Reports is treated as permanently "unlocked" for owners/managers/
  // shift-leaders who hold the `analytics` permission, and the prompt
  // dialog never fires.
  const analyticsUnlocked = true;
  const setAnalyticsUnlocked = () => {};
  const [analyticsPromptOpen, setAnalyticsPromptOpen] = useState(false);
  const [analyticsPasswordInput, setAnalyticsPasswordInput] = useState("");
  const [analyticsVerifying, setAnalyticsVerifying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  // Owner dashboard navigation is grouped into 7 top-level sections
  // (Overview / Operations / Menu / Finance / Reports / Inventory /
  // Settings). `activeGroup` controls the top row of pills, while
  // `activeTab` continues to drive the existing TabsContent values so
  // none of the screens or workflows had to change. `analyticsSubtab` +
  // `stockSubtab` let the Reports + Inventory group buttons deep-link
  // into the right inner subtab.
  const [activeGroup, setActiveGroup] = useState("overview");
  const [analyticsSubtab, setAnalyticsSubtab] = useState("reports");
  const [stockSubtab, setStockSubtab] = useState("items");
  // Keep `activeGroup` in sync when other code paths (e.g. the low-stock
  // banner shortcut or the outstanding-accounts widget on Overview) call
  // `setActiveTab(...)` directly. Mapping is the inverse of NAV_GROUPS.
  useEffect(() => {
    const TAB_TO_GROUP = {
      overview: "overview",
      orders: "operations",
      staff: "operations",
      accounts: "operations",
      "manage-menu": "menu",
      cash: "finance",
      // Session 63.E — Report Schedules is its own top-level group.
      analytics: analyticsSubtab === "schedules" ? "schedules" : ["stock", "alerts"].includes(analyticsSubtab) ? "inventory" : "finance",
      access: "settings",
      links: "settings",
      tags: "settings",
      branding: "settings",
      payment_gateways: "settings",
    };
    const next = TAB_TO_GROUP[activeTab];
    if (next && next !== activeGroup) setActiveGroup(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, analyticsSubtab]);
  const [manageMenuTab, setManageMenuTab] = useState("items");
  // Items tab filters — let the owner narrow down the (potentially long)
  // items list by category and by a free-text name/description search.
  const [itemFilterCategory, setItemFilterCategory] = useState("all");
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  // Session 27.108 — Alphabetical (A-Z) sort toggle for the owner
  // menu-items list. Persists per-device via localStorage.
  const [ownerMenuSortAZ, setOwnerMenuSortAZFn] = useState(() => {
    try { return localStorage.getItem("nfcs:owner:menuSortAZ") === "1"; } catch (_) { return false; }
  });
  const setOwnerMenuSortAZ = useCallback((val) => {
    setOwnerMenuSortAZFn(Boolean(val));
    try { localStorage.setItem("nfcs:owner:menuSortAZ", val ? "1" : "0"); } catch (_) { /* noop */ }
  }, []);
  const [categoryDialog, setCategoryDialog] = useState({
    open: false,
    mode: "create",
    values: { id: "", name: "", description: "", active: true, schedule: { type: "weekly", weekdays: [0, 1, 2, 3, 4, 5, 6], start_time: "", end_time: "" } },
  });
  // Sides & Extras category dialog — same shape as categoryDialog so we can
  // reuse the same field structure (group / available_from / available_to /
  // visibility flags) in the dedicated S&E Categories CRUD tab.
  const [sidesCategoryDialog, setSidesCategoryDialog] = useState({
    open: false,
    mode: "create",
    values: { id: "", name: "", description: "", active: true, visible_customer: true, visible_staff: true, group: "other", available_from: "", available_to: "", schedule: { type: "weekly", weekdays: [0, 1, 2, 3, 4, 5, 6], start_time: "", end_time: "" } },
  });
  const [itemDialog, setItemDialog] = useState({
    open: false,
    mode: "create",
    values: {
      id: "",
      category_id: "",
      name: "",
      description: "",
      price: "",
      image: "",
      vat_percent: 15,
      department: "",
      active: true,
      featured: false,
      // Sides & extras configuration. selection_mode "none" disables the
      // sides flow entirely. allowed_side_ids is the subset of the global
      // sides library this item offers; included_side_ids are pre-selected
      // for the customer (still subject to free_count for charge calcs).
      sides_config: {
        selection_mode: "none",
        free_count: 0,
        max_select: 1,
        allowed_side_ids: [],
        included_side_ids: [],
      },
    },
  });
  const [tagDialog, setTagDialog] = useState({
    open: false,
    mode: "create",
    values: { id: "", label: "", type: "menu", table_number: "", active: true, code: "" },
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [accessForm, setAccessForm] = useState({ owner_password: "", staff_password: "", kitchen_password: "", bar_password: "", void_password: "", runner_password: "", retail_password: "" });
  const [coverImageDraft, setCoverImageDraft] = useState("");
  const [coverImageFit, setCoverImageFit] = useState("cover");
  // Session 30.30 — square logo used as the home-screen icon for the saved digital menu.
  const [logoImageDraft, setLogoImageDraft] = useState("");
  const [isScanningNfc, setIsScanningNfc] = useState(false);
  const [allowManualTagCode, setAllowManualTagCode] = useState(false);
  const scanTimeoutRef = useRef(null);

  const handleUnauthorized = useCallback(() => {
    // Session 27.110 — Only kick out on repeated 401s so a single
    // transient response doesn't tear down the owner dashboard. Also
    // confirm with /auth/me before actually clearing.
    if (!shouldKickOutOn401("owner")) return;
    const token = session?.token;
    confirmSessionDeadThen("owner", token, () => {
      clearSession("owner", "server_401_confirmed");
      setSessionState(null);
      navigate("/owner", { replace: true });
    });
  }, [navigate, session?.token]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${session?.token}` }), [session?.token]);
  // Session 73 — "Fix paid tables showing a balance". Owner-triggered
  // run of the server reconciliation (preview first, then apply) so a
  // table that was paid but still shows "R x due" can be closed.
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcilePreview, setReconcilePreview] = useState(null);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const runReconcilePaidTables = useCallback(async (dryRun) => {
    setReconcileBusy(true);
    try {
      const r = await http.post("/owner/reconcile-paid-tables", { dry_run: !!dryRun, min_age_minutes: 0 }, { headers: authHeaders });
      const data = r?.data || {};
      if (dryRun) {
        setReconcilePreview(data);
        setReconcileOpen(true);
      } else {
        setReconcilePreview(null);
        setReconcileOpen(false);
        const n = Number(data.reconciled || 0);
        toast.success(n > 0 ? `${n} table${n === 1 ? "" : "s"} marked Paid in full` : "No tables needed fixing", {
          description: n > 0 ? `R ${Number(data.total_adjustment || 0).toFixed(2)} written off as a reconciliation adjustment. Staff can now close them.` : "Every open table with a payment already balances.",
        });
      }
    } catch (e) {
      toast.error("Could not reconcile tables", { description: e?.response?.data?.detail || e?.message || "Please try again." });
    } finally {
      setReconcileBusy(false);
    }
  }, [authHeaders]);
  // Session 41 — Owner/Manager joins the realtime bus. Cloud WS while the
  // internet is up; LAN-mesh envelopes are injected into the same bus by
  // meshBridge, so prep / production / approval events reach this screen
  // either way (see OwnerNotifications).
  useRealtimeConnection({ baseUrl: BACKEND_URL, token: session?.token });

  // ── Session 31 — Voice-first AI assistant ─────────────────────────────
  // `aiContext` = { enabled (business flag), allowed (this user has the
  // ai_assistant section), settings }. `aiNavRef` exposes the grouped-nav
  // goToChild() so engine `navigate` actions drive the same code path as
  // clicking the sidebar.
  const [aiContext, setAiContext] = useState({ enabled: false, allowed: false, settings: null, usage: null, voices: [], model: null, loaded: false });
  const aiNavRef = useRef(null);
  const loadAiContext = useCallback(async () => {
    if (!session?.token) return;
    try {
      const r = await http.get("/ai/context", { headers: authHeaders });
      setAiContext({ enabled: !!r.data?.enabled, allowed: !!r.data?.allowed, settings: r.data?.settings || null, usage: r.data?.usage || null, voices: r.data?.voices || [], model: r.data?.model || null, loaded: true });
    } catch (_) {
      setAiContext((c) => ({ ...c, loaded: true }));
    }
  }, [session?.token, authHeaders]);
  useEffect(() => { loadAiContext(); }, [loadAiContext]);
  const aiHighlight = useCallback((ids) => {
    (ids || []).forEach((id, i) => {
      const el = document.querySelector(`[data-testid="${id}"]`);
      if (!el) return;
      if (i === 0) { try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) { /* noop */ } }
      el.classList.add("ai-highlight");
      setTimeout(() => el.classList.remove("ai-highlight"), 2600);
    });
  }, []);
  // Session 30.36 — slices already populated from the cloud (see applyResult).
  const cloudAppliedKeysRef = useRef(new Set());
  // Session 30.39 — one shared date window for the Orders tab (Active +
  // Voided lists and the totals card all use it).
  const ordersDateWindow = useMemo(() => {
    const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
    const today = startOfDay(new Date());
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const last7 = new Date(today); last7.setDate(today.getDate() - 6);
    let from = null, to = null;
    if (dateFilter === "today") { from = today; to = new Date(today); to.setDate(today.getDate() + 1); }
    else if (dateFilter === "yesterday") { from = yesterday; to = today; }
    else if (dateFilter === "7d") { from = last7; to = new Date(today); to.setDate(today.getDate() + 1); }
    else if (dateFilter === "custom" && customDate) {
      from = startOfDay(customDate); to = new Date(from); to.setDate(from.getDate() + 1);
    }
    return { from, to };
  }, [dateFilter, customDate]);
  const inOrdersDateWindow = useCallback((ts) => {
    const { from, to } = ordersDateWindow;
    if (!from || !to) return true;
    const d = ts ? new Date(ts) : null;
    return !!d && d >= from && d < to;
  }, [ordersDateWindow]);
  // Session 30.41 — Every date window is fetched EXACTLY from the server
  // with no cap: "All" = every order the business has ever rung up;
  // Today / Yesterday / 7 days / custom send since+until. The initial
  // 500-order paint stays for a fast first render; this effect refines it
  // as soon as the Orders tab / a filter is used. Cached per window key so
  // flipping between chips doesn't refetch.
  const fetchedOrderWindowsRef = useRef(new Set());
  // Session 73 — money RECEIVED in the window, from the server's one shared
  // money function (ledger entries by payment date) — identical to the
  // emailed reports.
  // Session 74 — the whole totals card (sales, receipts, fee, outstanding
  // AND money received) is computed by the server over every order in the
  // window. The on-screen list is never the source of the headline numbers:
  // it starts as "latest 500" and widens asynchronously, so summing it made
  // "Last 7 days" smaller than a single Friday on busy venues.
  const [moneySummary, setMoneySummary] = useState(null);
  const moneyKeyRef = useRef("");
  const [moneySummaryError, setMoneySummaryError] = useState(false);
  const [ordersWindowError, setOrdersWindowError] = useState(false);
  const [ordersWindowRetry, setOrdersWindowRetry] = useState(0);
  useEffect(() => {
    if (!session?.token) return;
    if (activeTab !== "operations" && activeTab !== "orders") return;
    if (dateFilter === "custom" && !customDate) return;
    const { from, to } = ordersDateWindow;
    const params = {};
    if (from && to && dateFilter !== "all") { params.start_date = from.toISOString(); params.end_date = to.toISOString(); }
    else if (dateFilter === "all") { params.start_date = "2000-01-01"; params.end_date = new Date(Date.now() + 86400000).toISOString(); }
    if (paymentFilter && paymentFilter !== "all") params.payment_method = paymentFilter;
    let cancelled = false;
    // Only blank the card when the WINDOW changes — a background refresh of
    // the same window keeps the current figures on screen until new ones land.
    const key = `${params.start_date || ""}|${params.end_date || ""}|${params.payment_method || "all"}`;
    if (moneyKeyRef.current !== key) { moneyKeyRef.current = key; setMoneySummary(null); }
    setMoneySummaryError(false);
    http.get("/owner/orders/totals", { headers: authHeaders, params, timeout: 90000 })
      .then((r) => { if (!cancelled) setMoneySummary(r.data || null); })
      .catch(() => { if (!cancelled) { setMoneySummary(null); setMoneySummaryError(true); } });
    return () => { cancelled = true; };
  }, [session?.token, activeTab, dateFilter, customDate, paymentFilter, ordersDateWindow, authHeaders, ordersRefreshing, ordersWindowRetry]);
  useEffect(() => {
    if (!session?.token) return;
    if (activeTab !== "operations" && activeTab !== "orders") return;
    const { from, to } = ordersDateWindow;
    if (dateFilter === "custom" && !customDate) return;
    const key = dateFilter === "all" ? "all" : `${from?.toISOString?.() || ""}|${to?.toISOString?.() || ""}`;
    if (fetchedOrderWindowsRef.current.has(key)) return;
    fetchedOrderWindowsRef.current.add(key);
    let cancelled = false;
    setOrdersRefreshing(true);
    setOrdersWindowError(false);
    const params = { limit: 0 };
    if (from && to && dateFilter !== "all") { params.since = from.toISOString(); params.until = to.toISOString(); }
    // Session 74 — a 7-day list on a busy venue is thousands of orders; the
    // default 20 s ceiling is for small calls. Give the list time, and if it
    // still fails say so instead of silently showing a partial list.
    http.get("/owner/orders", { headers: authHeaders, params, timeout: 120000 })
      .then((r) => {
        if (cancelled) return;
        try { cloudAppliedKeysRef.current.add("orders"); } catch (_) { /* noop */ }
        const incoming = r.data?.orders || [];
        if (dateFilter === "all") { setOrders(incoming); return; }
        // Merge the exact window into what we already hold (dedupe by id).
        setOrders((prev) => {
          const map = new Map((prev || []).map((o) => [o.id, o]));
          incoming.forEach((o) => map.set(o.id, o));
          return Array.from(map.values()).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        });
      })
      .catch(() => {
        fetchedOrderWindowsRef.current.delete(key);
        if (!cancelled) {
          setOrdersWindowError(true);
          toast.error("Couldn't load every order for this window. The totals above come straight from the server; the list below may be incomplete — tap Retry.");
        }
      })
      .finally(() => { if (!cancelled) setOrdersRefreshing(false); });
    return () => { cancelled = true; };
  }, [dateFilter, customDate, ordersDateWindow, session?.token, authHeaders, activeTab, ordersWindowRetry]);
  // Session 30.39 — bulk "Clear all voids" (void password gated).
  const [clearVoidsDialog, setClearVoidsDialog] = useState({ open: false, password: "", submitting: false });
  // Session 30.40 — deep-link into a spreadsheet created from a report.
  const [spreadsheetOpenId, setSpreadsheetOpenId] = useState(null);
  const sendRowsToSpreadsheet = useCallback(async (name, rows, source = "report") => {
    try {
      const sheet = buildSheetFromRows("Sheet1", rows);
      const r = await http.post("/owner/spreadsheets", { name, sheets: [sheet], source, period_date: new Date().toISOString().slice(0, 10) }, { headers: authHeaders });
      setSpreadsheetOpenId(r.data.id);
      setActiveGroup("finance");
      setAnalyticsSubtab("spreadsheets");
      setActiveTab("analytics");
      toast.success(`Opened "${name}" in Spreadsheets`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the spreadsheet.");
    }
  }, [authHeaders]);

  const refreshDashboard = useCallback(async (showLoading = true) => {
    if (!session?.token) return;
    if (showLoading) setLoading(true);
    // Session 27.52 — PROGRESSIVE rendering.
    //
    // Previously (Session 27.49) we fired all 12 endpoints in parallel
    // via `Promise.allSettled` but still waited for EVERY single one
    // to settle before flipping `loading=false`. On production the
    // owner's business has ~1000+ historical orders and a large
    // analytics dataset — `/owner/analytics` and `/owner/orders` can
    // take several seconds even on a healthy connection, so the whole
    // dashboard sat at "Loading owner dashboard…" for ~5-10s waiting
    // for the two heaviest endpoints, even though the tiny critical
    // ones (auth/me, business-settings, categories, menu-items,
    // tags) had all finished in <200ms.
    //
    // Now we split into CRITICAL (small, needed for first paint) and
    // BACKGROUND (heavy — orders history + analytics + specials).
    // Critical endpoints resolve, we `setLoading(false)` immediately
    // and paint the dashboard. Background endpoints fill in their
    // sections as they land — the KPI cards / analytics charts get
    // their own tiny inline skeletons via the `analytics` / `orders`
    // state being null-ish, and switch to real data on arrival.
    // First-paint time on a large business drops from ~5-10s to
    // ~200-400ms.
    const applyResult = (key, r) => {
      if (r.status !== "fulfilled") {
        if (r.reason?.response?.status === 401) return "unauthorized";
        return null;
      }
      const data = r.value?.data;
      // Session 30.36 — remember which slices the CLOUD has delivered so
      // the slower local-snapshot prime (below) can never overwrite them
      // with stale/partial data (the snapshot only carries 24h of orders,
      // which made the app's Operations "All" filter show today only).
      try { cloudAppliedKeysRef.current.add(key); } catch (_) { /* noop */ }
      if (key === "me")          setContext(data);
      else if (key === "overview")    setOverview(data);
      else if (key === "settings") {
        mergeIncomingSettings(data?.business);
        setCoverImageDraft(data?.business?.cover_image || "");
        setCoverImageFit(data?.business?.cover_image_fit || "cover");
        setLogoImageDraft(data?.business?.logo_image || "");
      }
      else if (key === "categories")  setCategories(data?.categories || []);
      else if (key === "items")       setMenuItems(data?.menu_items || []);
      else if (key === "tags")        setTags(data?.tags || []);
      else if (key === "orders") {
        // Session 30.41 — the poll only carries the latest 500; merge so a
        // deeper window ("All") loaded by the Orders tab is never shrunk.
        const incoming = data?.orders || [];
        setOrders((prev) => {
          if (!prev || prev.length <= incoming.length) return incoming;
          const map = new Map(prev.map((o) => [o.id, o]));
          incoming.forEach((o) => map.set(o.id, o));
          return Array.from(map.values()).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        });
      }
      else if (key === "analytics")   setAnalytics(data || { summary: {}, daily: [] });
      else if (key === "departments") setDepartments(data?.departments || []);
      else if (key === "sides")       setSides(data?.sides || []);
      else if (key === "specials") {
        setSpecials(data?.specials || []);
        setSpecialNowLocal(data?.now_local || "");
      }
      else if (key === "sidesCats")   setSidesCategories(data?.categories || []);
      else if (key === "vouchers")    setVouchers(data?.vouchers || []);
      return null;
    };
    // Critical set — needed for the header, KPI strip, menu editor,
    // tag manager. All lightweight (<1KB each on production).
    const criticalEndpoints = [
      ["me",           "/auth/me"],
      ["overview",     "/owner/dashboard"],
      ["settings",     "/owner/business-settings"],
      ["categories",   "/owner/categories"],
      ["items",        "/owner/menu-items"],
      ["tags",         "/owner/tags"],
      ["departments",  "/owner/departments"],
      ["sides",        "/owner/sides"],
      ["sidesCats",    "/owner/sides-categories"],
    ];
    // Background set — heavier payloads (orders history, analytics)
    // and rarely-touched routes (specials, vouchers). These populate
    // their sections as they arrive; the dashboard is already
    // interactive by then.
    const backgroundEndpoints = [
      ["orders",       "/owner/orders"],
      ["analytics",    "/owner/analytics"],
      ["specials",     "/owner/specials"],
      ["vouchers",     "/owner/vouchers"],
    ];
    let sawUnauthorized = false;
    try {
      const criticalResults = await Promise.allSettled(
        criticalEndpoints.map(([, path]) => http.get(path, { headers: authHeaders }))
      );
      criticalResults.forEach((r, i) => {
        const [key] = criticalEndpoints[i];
        if (applyResult(key, r) === "unauthorized") sawUnauthorized = true;
      });
      if (sawUnauthorized) {
        handleUnauthorized();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error(error.response?.data?.detail || "Unable to load owner dashboard.");
      }
    } finally {
      // Paint the dashboard immediately — critical data is in.
      setLoading(false);
    }
    // Fire background endpoints in parallel — no awaits blocking the
    // UI. Each success updates its own state slice as soon as it
    // lands. Silent on 401 for these (the critical batch above
    // already handled auth). Silent on other failures too — a heavy
    // endpoint failing shouldn't spam a toast; the next refresh
    // will retry.
    backgroundEndpoints.forEach(([key, path]) => {
      http.get(path, { headers: authHeaders })
        .then((res) => applyResult(key, { status: "fulfilled", value: res }))
        .catch((err) => {
          if (err?.response?.status === 401) handleUnauthorized();
          /* other errors are silently swallowed — polling will retry */
        });
    });
  }, [authHeaders, handleUnauthorized, session?.token]);

  const loadAnalytics = useCallback(async (days) => {
    if (!session?.token) return;
    try {
      setAnalyticsRange(days);
      const response = await http.get("/owner/analytics", { headers: authHeaders, params: { days } });
      setAnalytics(response.data || { summary: {}, daily: [] });
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error("Unable to load analytics.");
      }
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  // Service times — avg / median / fastest / slowest + daily-avg series.
  const loadServiceTimes = useCallback(async (days = analyticsRange) => {
    if (!session?.token) return;
    try {
      const r = await http.get(`/owner/analytics/service-times?days=${days}`, { headers: authHeaders });
      setServiceTimes(r.data);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
    // Session 30.22 — daily long-wait report rides along with service times.
    try {
      const lw = await http.get(`/owner/analytics/long-waits?days=${days}`, { headers: authHeaders });
      setLongWaitReport(lw.data);
    } catch (_) { /* best-effort */ }
  }, [authHeaders, handleUnauthorized, session?.token, analyticsRange]);

  // Growth percentages — day/week/month/year vs the prior matching window.
  const loadBusinessSummary = useCallback(async (range) => {
    if (!session?.token) return;
    setBusinessSummaryLoading(true);
    try {
      const [r, pr] = await Promise.all([
        http.get("/owner/reports/summary", {
          headers: authHeaders,
          params: { start_date: range?.start, end_date: range?.end },
        }),
        // Session 31.14 — expenses, cost of goods and break-even for the
        // exact same date range, so the summary tells the whole story.
        http.get("/owner/reports/profit", {
          headers: authHeaders,
          params: { period: "custom", start: range?.start, end: range?.end },
        }).catch(() => null),
      ]);
      setBusinessSummary(r.data);
      setBusinessProfit(pr?.data || null);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
      toast.error(error.response?.data?.detail || "Unable to load Business Summary.");
    } finally {
      setBusinessSummaryLoading(false);
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  // ── End-of-day / Business-Summary export helpers ───────────────────
  // Build CSV from current businessSummary state.
  const exportBusinessSummaryCSV = useCallback(() => {
    if (!businessSummary) { toast.error("Load the report first."); return; }
    const lines = [];
    lines.push("Business Summary");
    lines.push(`Period,${businessSummary.range?.start || ""} → ${businessSummary.range?.end || ""}`);
    lines.push("");
    lines.push("Metric,Value");
    const summary = businessSummary.summary || {};
    const labels = [
      ["Total orders", summary.total_orders],
      ["Total revenue", summary.total_revenue],
      ["Cash revenue", summary.cash_revenue],
      ["Card revenue", summary.card_revenue],
      ["Split revenue", summary.split_revenue],
      ["Total tips", summary.total_tips],
      ["Refund total", summary.refund_total],
      ["Void total", summary.void_total],
      ["Outstanding tabs", summary.open_accounts_total],
      ["Cash variance", summary.cash_variance_total],
    ];
    for (const [k, v] of labels) lines.push(`"${k}",${v ?? 0}`);
    if (summary.top_selling_item) {
      lines.push("");
      lines.push("Top selling item,Quantity,Revenue");
      lines.push(`"${summary.top_selling_item.name}",${summary.top_selling_item.quantity},${summary.top_selling_item.revenue}`);
    }
    lines.push("");
    lines.push("Top items");
    lines.push("Item,Qty,Revenue,% rev");
    for (const r of (businessSummary.items || [])) {
      lines.push(`"${(r.name || "").replace(/"/g, '""')}",${r.quantity},${r.revenue},${r.percentage}`);
    }
    lines.push("");
    lines.push("Category mix");
    lines.push("Category,Qty,Revenue,% rev");
    for (const c of (businessSummary.categories || [])) {
      lines.push(`"${(c.category || "").replace(/"/g, '""')}",${c.quantity},${c.revenue},${c.percentage}`);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const dt = (businessSummary.range?.start || "").slice(0, 10);
    a.download = `business-summary-${dt}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded.");
  }, [businessSummary]);

  // Email the current Business Summary using the test-report endpoint.
  // Session 50 — sends the range that is ON SCREEN (Today / Yesterday /
  // Last 7 days / custom…), not just today.
  const emailBusinessSummary = useCallback(async () => {
    const presetLabels = { today: "Today", yesterday: "Yesterday", "7d": "Last 7 days", "30d": "Last 30 days", this_month: "This month", last_month: "Last month", this_year: "This year", last_year: "Last year", all: "All time", custom: "Custom range" };
    const rangeLabel = presetLabels[bsRange?.preset] || "Custom range";
    const recipient = window.prompt(`Email this Business Summary (${rangeLabel} · ${bsRange?.start || "?"} → ${bsRange?.end || "?"}) to:`, testReportEmail || "");
    if (!recipient || !recipient.includes("@")) return;
    try {
      await http.post("/owner/report-schedules/test", {
        report_types: ["business_summary", "sales_mix", "category_mix", "cash_up", "low_stock", "register"],
        recipient,
        start_date: bsRange?.start || undefined,
        end_date: bsRange?.end || undefined,
        range_label: rangeLabel,
      }, { headers: authHeaders });
      toast.success(`${rangeLabel} report emailed to ${recipient}.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to send report.");
    }
  }, [authHeaders, testReportEmail, bsRange]);

  // Session 73 — Email a focused report (void detail / payments by method)
  // for the date window currently selected on the Orders / Voids tabs.
  const ordersWindowDates = useCallback(() => {
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    const today = new Date();
    const day = (n) => new Date(today.getFullYear(), today.getMonth(), today.getDate() + n);
    if (dateFilter === "yesterday") return { start: iso(day(-1)), end: iso(day(-1)), label: "Yesterday" };
    if (dateFilter === "7d") return { start: iso(day(-6)), end: iso(today), label: "Last 7 days" };
    if (dateFilter === "custom" && customDate) return { start: customDate, end: customDate, label: customDate };
    if (dateFilter === "all") return { start: iso(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())), end: iso(today), label: "Last 12 months" };
    return { start: iso(today), end: iso(today), label: "Today" };
  }, [dateFilter, customDate]);
  const emailFocusedReport = useCallback(async (reportTypes, what) => {
    const win = ordersWindowDates();
    const recipient = window.prompt(`Email the ${what} report (${win.label} · ${win.start} → ${win.end}) to:`, testReportEmail || "");
    if (!recipient || !recipient.includes("@")) return;
    try {
      await http.post("/owner/report-schedules/test", {
        report_types: reportTypes,
        recipient,
        start_date: win.start,
        end_date: win.end,
        range_label: `${what} · ${win.label}`,
      }, { headers: authHeaders });
      toast.success(`${what} report (${win.label}) emailed to ${recipient}.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to send report.");
    }
  }, [authHeaders, testReportEmail, ordersWindowDates]);

  // Session 73 — duplicate rounds (glitch recovery): preview, tick, void.
  const [dupDialog, setDupDialog] = useState({ open: false, loading: false, data: null, selected: new Set(), pin: "", submitting: false });
  const findDuplicateOrders = useCallback(async () => {
    const win = ordersWindowDates();
    setDupDialog({ open: true, loading: true, data: null, selected: new Set(), pin: "", submitting: false });
    try {
      const r = await http.get("/owner/orders/duplicates", { headers: authHeaders, params: { start_date: win.start, end_date: win.end, window_minutes: 20 } });
      const data = r.data || { groups: [] };
      // Pre-tick every suspected duplicate copy (never the kept original).
      const sel = new Set((data.groups || []).flatMap((g) => (g.duplicates || []).map((d) => d.order_id)));
      setDupDialog({ open: true, loading: false, data: { ...data, label: win.label }, selected: sel, pin: "", submitting: false });
    } catch (e) {
      setDupDialog((d) => ({ ...d, loading: false }));
      toast.error(e?.response?.data?.detail || "Could not scan for duplicate orders.");
    }
  }, [authHeaders, ordersWindowDates]);
  const voidSelectedDuplicates = useCallback(async () => {
    const ids = Array.from(dupDialog.selected || []);
    if (!ids.length) { toast.error("Tick at least one duplicate to void."); return; }
    if (!dupDialog.pin || dupDialog.pin.length < 4) { toast.error("Enter the manager / void PIN."); return; }
    setDupDialog((d) => ({ ...d, submitting: true }));
    try {
      const r = await http.post("/owner/orders/duplicates/void", { order_ids: ids, void_password: dupDialog.pin, reason: "Duplicate order (glitch recovery)" }, { headers: authHeaders });
      const n = (r.data?.voided || []).length; const sk = r.data?.skipped || [];
      toast.success(`${n} duplicate order${n === 1 ? "" : "s"} voided`, { description: sk.length ? `${sk.length} skipped: ${sk.map((s) => s.reason).slice(0, 3).join("; ")}` : "Sales, bills and reports now exclude them. Logged in the audit trail." });
      setDupDialog({ open: false, loading: false, data: null, selected: new Set(), pin: "", submitting: false });
      try { fetchedOrderWindowsRef.current.clear(); setOrdersRefreshing((v) => !v); } catch (_) { /* noop */ }
    } catch (e) {
      setDupDialog((d) => ({ ...d, submitting: false }));
      toast.error(e?.response?.data?.detail || "Could not void the selected duplicates.");
    }
  }, [authHeaders, dupDialog.selected, dupDialog.pin]);


  const loadGrowth = useCallback(async () => {
    if (!session?.token) return;
    try {
      const r = await http.get("/owner/analytics/growth", { headers: authHeaders });
      setGrowthStats(r.data);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  // Stock-take list. Pings the low-stock toast when entering Analytics
  // (caller decides; loader is plain).
  const loadStock = useCallback(async () => {
    if (!session?.token) return;
    try {
      const r = await http.get("/owner/analytics/stock?threshold=10", { headers: authHeaders });
      setStockRows({ items: r.data.items || [], low_stock: r.data.low_stock || [] });
      setStockLoaded(true);
      // Session 31.14 — menu-item counters are no longer part of the
      // Inventory screens, so we no longer toast about them here (the
      // ingredient banner covers low stock).
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  // saveStock handles BOTH stock and cost edits. `stockEdits[id]` can be:
  //   - a string (legacy path: stock-only)
  //   - an object { stock?: string, cost?: string } (new path)
  // Empty / undefined fields are sent as `clear_stock` / `clear_cost` so the
  // backend knows to NULL them out rather than ignore them.
  const saveStock = async (id) => {
    const raw = stockEdits[id];
    if (raw === undefined) return;
    const edits = typeof raw === "object" && raw !== null ? raw : { stock: raw };
    const payload = {};
    if (edits.stock !== undefined) {
      if (edits.stock === "") payload.clear_stock = true;
      else payload.stock = Math.max(0, Math.floor(Number(edits.stock)));
    }
    if (edits.cost !== undefined) {
      if (edits.cost === "") payload.clear_cost = true;
      else payload.cost = Math.max(0, Number(edits.cost));
    }
    // Session 41.6 — per-item low-stock warning level. Blank = no warning.
    if (edits.warn !== undefined) {
      if (edits.warn === "") payload.clear_low_stock_threshold = true;
      else payload.low_stock_threshold = Math.max(0, Math.floor(Number(edits.warn)));
    }
    if (Object.keys(payload).length === 0) return;
    if (stockRecorder.trim()) payload.user = stockRecorder.trim();
    try {
      await http.patch(`/owner/analytics/stock/${id}`, payload, { headers: authHeaders });
      toast.success("Saved.");
      setStockEdits((s) => { const n = { ...s }; delete n[id]; return n; });
      await loadStock();
      await loadLowStock();
      if (stockHistoryLoaded) loadStockHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed.");
    }
  };

  // Low-stock banner — unified items + ingredients. Polls on dashboard load
  // and after any stock-changing action so the banner stays accurate.
  const loadLowStock = useCallback(async () => {
    if (!session?.token) return;
    try {
      const r = await http.get("/owner/analytics/low-stock?threshold=10", { headers: authHeaders });
      setLowStock({
        items: r.data.items || [],
        ingredients: r.data.ingredients || [],
        total: r.data.total || 0,
      });
      // Session 39 — best-effort; the banner simply stays hidden on failure.
      try {
        const ev = await http.get("/owner/inventory/stock-events", { headers: authHeaders, params: { days: 2, unacknowledged_only: true, limit: 20 } });
        setStockEvents({ counts: ev.data?.counts || null, events: ev.data?.events || [] });
      } catch (_e) { /* ignore */ }
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  // Session 41.7 — the fallback / shortfall banner opens Stock Locations →
  // Fallback activity, where the events are listed and acknowledged (that
  // is what clears the banner on every device).
  // Which sub-view Stock Locations opens on (banner click → "fallback");
  // the nonce remounts the panel so a repeat click re-applies it.
  const [stockLocationsInitialView, setStockLocationsInitialView] = useState("overview");
  const [stockLocationsPanelNonce, setStockLocationsPanelNonce] = useState(0);
  // Session 63.C — merged panels: Fallback History (approvals | service-stock
  // overrides) and Variance & Waste; plus "Move stock" now lives under Stock Take.
  const [fallbackHistoryView, setFallbackHistoryView] = useState("approvals");
  const [varianceWasteView, setVarianceWasteView] = useState("variance");
  const [stockTakeMoveOpen, setStockTakeMoveOpen] = useState(false);
  const [stockTakeMoveDirection, setStockTakeMoveDirection] = useState("to_floor");
  // Legacy sub-tab ids (old links, saved nav state, notifications) → merged screens.
  useEffect(() => {
    if (stockSubtab === "approvals" || stockSubtab === "fallback-overrides") { setFallbackHistoryView(stockSubtab === "approvals" ? "approvals" : "overrides"); setStockSubtab("fallback-history"); }
    else if (stockSubtab === "variance" || stockSubtab === "waste") { setVarianceWasteView(stockSubtab); setStockSubtab("variance-waste"); }
  }, [stockSubtab]);
  // The low-stock banner has no "event" to acknowledge — we hide the exact
  // set that was showing; as soon as a NEW item/ingredient goes low (or a
  // count changes) the signature differs and the banner returns.
  const LOW_STOCK_DISMISS_KEY = "nfcs.lowStockBannerDismissed";
  const lowStockSignature = useMemo(() => [
    ...(lowStock.items || []).map((x) => `i:${x.id}:${x.stock}`),
    ...(lowStock.ingredients || []).map((x) => `g:${x.id}:${x.stock_on_hand ?? x.stock ?? ""}`),
  ].sort().join("|"), [lowStock.items, lowStock.ingredients]);
  const [lowStockDismissedSig, setLowStockDismissedSig] = useState(() => { try { return window.localStorage.getItem("nfcs.lowStockBannerDismissed") || ""; } catch (_) { return ""; } });
  const lowStockBannerHidden = lowStock.total > 0 && lowStockDismissedSig === lowStockSignature;
  const dismissLowStockBanner = useCallback((ev) => {
    if (ev) { ev.preventDefault(); ev.stopPropagation(); }
    setLowStockDismissedSig(lowStockSignature);
    try { window.localStorage.setItem(LOW_STOCK_DISMISS_KEY, lowStockSignature); } catch (_) { /* noop */ }
  }, [lowStockSignature]);

  // Open Accounts — settles-later customer tabs.
  const loadAccounts = useCallback(async (statusFilter = "open") => {
    if (!session?.token) return;
    try {
      const r = await http.get(`/owner/accounts?status=${statusFilter}`, { headers: authHeaders });
      setAccountsList({
        accounts: r.data.accounts || [],
        summary: r.data.summary || { open_count: 0, total_outstanding: 0 },
      });
      setAccountsLoaded(true);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  // Reservations (owner view) — read-only list with the same status
  // actions as Staff; reuses /api/staff/reservations (owner role accepted).
  const [ownerReservations, setOwnerReservations] = useState([]);
  const [ownerReservationsFilter, setOwnerReservationsFilter] = useState("upcoming");
  const [ownerReservationsLoaded, setOwnerReservationsLoaded] = useState(false);
  const loadOwnerReservations = useCallback(async (statusFilter = "upcoming") => {
    if (!session?.token) return;
    try {
      const qs = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const r = await http.get(`/staff/reservations${qs}`, { headers: authHeaders });
      setOwnerReservations(r.data.reservations || []);
      setOwnerReservationsLoaded(true);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
      else toast.error(error.response?.data?.detail || "Could not load reservations.");
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  const setOwnerReservationStatus = useCallback(async (id, status) => {
    try {
      await http.patch(`/staff/reservations/${id}`, { status }, { headers: authHeaders });
      toast.success(status === "arrived" ? "Marked arrived." : status === "cancelled" ? "Cancelled." : status === "completed" ? "Completed." : "Updated.");
      loadOwnerReservations(ownerReservationsFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update.");
    }
  }, [authHeaders, loadOwnerReservations, ownerReservationsFilter]);

  // Owner-side "New reservation" — mirrors the Staff dashboard create
  // flow. Uses the same /staff/reservations POST endpoint which already
  // accepts the owner role on the backend.
  const [ownerReservationDialog, setOwnerReservationDialog] = useState({
    open: false,
    saving: false,
    form: { customer_name: "", phone: "", party_size: "2", table_label: "", reserved_date: "", reserved_time: "", notes: "" },
  });
  const submitOwnerReservation = useCallback(async () => {
    const v = ownerReservationDialog.form;
    if (!v.customer_name.trim()) { toast.error("Customer name required."); return; }
    if (!v.reserved_date || !v.reserved_time) { toast.error("Pick a date and time."); return; }
    const partySize = Math.max(1, Math.floor(Number(v.party_size) || 0));
    if (!partySize) { toast.error("Party size must be at least 1."); return; }
    const reservedAt = new Date(`${v.reserved_date}T${v.reserved_time}`).toISOString();
    setOwnerReservationDialog((d) => ({ ...d, saving: true }));
    try {
      await http.post("/staff/reservations", {
        customer_name: v.customer_name.trim(),
        phone: v.phone.trim(),
        party_size: partySize,
        table_label: v.table_label.trim(),
        reserved_at: reservedAt,
        notes: v.notes.trim(),
      }, { headers: authHeaders });
      toast.success("Reservation saved.");
      setOwnerReservationDialog({
        open: false,
        saving: false,
        form: { customer_name: "", phone: "", party_size: "2", table_label: "", reserved_date: "", reserved_time: "", notes: "" },
      });
      loadOwnerReservations(ownerReservationsFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save reservation.");
      setOwnerReservationDialog((d) => ({ ...d, saving: false }));
    }
  }, [authHeaders, loadOwnerReservations, ownerReservationDialog.form, ownerReservationsFilter]);

  // ── "Start fresh" destructive resets (void-password gated) ─────────
  // Single shared dialog used by BOTH the Finance tab and the Reports
  // tab. `scope` decides which endpoint we hit on confirm.
  const [clearHistoryDialog, setClearHistoryDialog] = useState({
    open: false,
    scope: null, // "finance" | "reports"
    void_password: "",
    saving: false,
  });
  const openClearHistoryDialog = useCallback((scope) => {
    setClearHistoryDialog({ open: true, scope, void_password: "", saving: false });
  }, []);
  const submitClearHistory = useCallback(async () => {
    const { scope, void_password } = clearHistoryDialog;
    if (!void_password) { toast.error("Void password required."); return; }
    if (!scope) return;
    setClearHistoryDialog((d) => ({ ...d, saving: true }));
    const endpoint = scope === "finance" ? "/owner/finance/clear-history" : "/owner/reports/clear-history";
    try {
      const r = await http.post(endpoint, { void_password }, { headers: authHeaders });
      if (scope === "finance") {
        toast.success(`Finance history cleared (${r.data?.deleted_count ?? 0} entries).`);
        // Cash summary refreshes via next poll cycle
      } else {
        toast.success(`Reports history cleared (${r.data?.orders_deleted ?? 0} orders, ${r.data?.sessions_deleted ?? 0} sessions).`);
        if (typeof loadBusinessSummary === "function") loadBusinessSummary(bsRange);
      }
      setClearHistoryDialog({ open: false, scope: null, void_password: "", saving: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not clear history.");
      setClearHistoryDialog((d) => ({ ...d, saving: false }));
    }
  // loadBusinessSummary referenced via closure once
  // defined further down in this component — runtime existence is
  // guaranteed by the time the dialog can be triggered.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders, clearHistoryDialog]);

  const openAccountDetail = useCallback(async (accountId) => {    try {
      const r = await http.get(`/owner/accounts/${accountId}`, { headers: authHeaders });
      setAccountDetail(r.data.account);
      setAccountPaymentDraft({ amount: "", method: "cash", notes: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load account.");
    }
  }, [authHeaders]);

  const recordAccountPayment = async () => {
    if (!accountDetail) return;
    const amount = Number(accountPaymentDraft.amount || 0);
    if (!amount || amount <= 0) {
      toast.error("Enter a payment amount.");
      return;
    }
    setAccountPaymentSaving(true);
    try {
      const r = await http.post(
        `/owner/accounts/${accountDetail.id}/payments`,
        {
          amount,
          method: accountPaymentDraft.method || "cash",
          notes: (accountPaymentDraft.notes || "").trim() || undefined,
        },
        { headers: authHeaders },
      );
      setAccountDetail(r.data.account);
      setAccountPaymentDraft({ amount: "", method: "cash", notes: "" });
      toast.success(r.data.account.status === "settled" ? "Account settled in full." : "Payment recorded.");
      await loadAccounts(accountsFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed.");
    } finally {
      setAccountPaymentSaving(false);
    }
  };

  // Ingredients CRUD
  const loadIngredients = useCallback(async () => {
    if (!session?.token) return;
    try {
      // Session 40.1 — falls back to the last known list when offline so the
      // waste / receiving / stock take forms stay usable.
      const r = await cachedGet(http, "/owner/ingredients", { headers: authHeaders });
      setIngredients(r.data.ingredients || []);
      setIngredientsLoaded(true);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  // Recipe management — pick a menu item and edit its ingredient list.
  const loadRecipe = useCallback(async (itemId) => {
    if (!itemId || !session?.token) {
      setRecipeLines([]);
      return;
    }
    try {
      const r = await http.get(`/owner/menu-items/${itemId}/recipe`, { headers: authHeaders });
      setRecipeLines((r.data.lines || []).map((l) => ({
        ingredient_id: l.ingredient_id,
        quantity: l.quantity,
        unit: l.unit || l.ingredient_unit || "pcs",
      })));
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
      setRecipeLines([]);
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  const saveRecipe = async () => {
    if (!recipeMenuItemId) return;
    const cleaned = recipeLines
      .filter((l) => l.ingredient_id && Number(l.quantity) > 0)
      .map((l) => ({
        ingredient_id: l.ingredient_id,
        quantity: Number(l.quantity),
        unit: l.unit || undefined,
      }));
    if (cleaned.length !== recipeLines.length) {
      // Missing ingredient picks or zero qty: bail with a friendly nudge.
      const missing = recipeLines.find((l) => !l.ingredient_id || Number(l.quantity) <= 0);
      if (missing) {
        toast.error("Pick an ingredient and a quantity > 0 on every row.");
        return;
      }
    }
    setRecipeSaving(true);
    try {
      await http.put(`/owner/menu-items/${recipeMenuItemId}/recipe`, { lines: cleaned }, { headers: authHeaders });
      toast.success("Recipe saved.");
      await loadRecipesSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Save failed.");
    } finally {
      setRecipeSaving(false);
    }
  };

  const loadRecipesSummary = useCallback(async () => {
    if (!session?.token) return;
    try {
      const r = await http.get("/owner/recipes", { headers: authHeaders });
      setRecipesSummary(r.data.recipes || []);
      setRecipesSummaryLoaded(true);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  // Per-order service times list.
  const loadOrderTimes = useCallback(async (days = 30) => {
    if (!session?.token) return;
    try {
      const r = await http.get(`/owner/analytics/order-times?days=${days}&limit=200`, { headers: authHeaders });
      setOrderTimes(r.data);
      setOrderTimesLoaded(true);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  // the tab for this session and switch the active tab to analytics so the
  // owner doesn't have to click twice.
  const verifyAnalyticsPassword = async () => {
    if (!analyticsPasswordInput.trim()) return;
    setAnalyticsVerifying(true);
    try {
      await http.post(
        "/owner/verify-analytics-password",
        { password: analyticsPasswordInput },
        { headers: authHeaders },
      );
      setAnalyticsUnlocked(true);
      setAnalyticsPromptOpen(false);
      setAnalyticsPasswordInput("");
      setActiveTab("analytics");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Incorrect password.");
    } finally {
      setAnalyticsVerifying(false);
    }
  };

  const loadVoidedOrders = useCallback(async () => {
    if (!session?.token) return;
    try {
      const response = await http.get("/owner/voided-orders", { headers: authHeaders });
      setVoidedOrders(response.data.orders || []);
      setVoidedOrdersLoaded(true);
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error(error.response?.data?.detail || "Unable to load voided orders.");
      }
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  const loadStaffRoster = useCallback(async () => {
    if (!session?.token) return;
    try {
      const response = await http.get("/owner/staff-members", { headers: authHeaders });
      setStaffRoster(response.data.staff || []);
      setStaffRosterLoaded(true);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  const addStaffMember = async () => {
    const name = newStaffName.trim();
    if (!name) return;
    setStaffBusy(true);
    try {
      await http.post("/owner/staff-members", { name }, { headers: authHeaders });
      setNewStaffName("");
      await loadStaffRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to add staff member.");
    } finally {
      setStaffBusy(false);
    }
  };

  const removeStaffMember = async (id) => {
    try {
      await http.delete(`/owner/staff-members/${id}`, { headers: authHeaders });
      await loadStaffRoster();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to remove.");
    }
  };

  // ── User account handlers ────────────────────────────────────────────
  const loadUserAccounts = useCallback(async () => {
    if (!session?.token) return;
    try {
      const r = await http.get("/owner/users", { headers: authHeaders });
      setUserAccounts(r.data?.users || []);
      if (Array.isArray(r.data?.roles) && r.data.roles.length) setUserRolesList(r.data.roles);
      setUserAccountsLoaded(true);
    } catch (error) {
      if (error.response?.status === 401) handleUnauthorized();
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  const openCreateUser = () => setUserDialog({ open: true, mode: "create", id: "", values: { first_name: "", last_name: "", username: "", pin: "", role: "waiter", active: true }, saving: false });
  const openEditUser = (u) => setUserDialog({ open: true, mode: "edit", id: u.id, values: { first_name: u.first_name || "", last_name: u.last_name || "", username: u.username || "", pin: "", role: u.role || "waiter", active: !!u.active, allowed_sections: Array.isArray(u.allowed_sections) ? u.allowed_sections : null }, saving: false });

  const submitUserDialog = async () => {
    const v = userDialog.values;
    if (!v.first_name.trim()) { toast.error("First name is required."); return; }
    if (!v.username.trim()) { toast.error("Username is required."); return; }
    if (userDialog.mode === "create" && (v.pin || "").length < 4) { toast.error("PIN must be 4–10 characters."); return; }
    setUserDialog((d) => ({ ...d, saving: true }));
    try {
      // Owner is implicitly all-access. For waiter we don't store any
      // section overrides (their dashboard is separate). For
      // manager/shift_leader/supervisor we send the explicit list so
      // owners can hand-pick which sections a specific user sees.
      // Session 46 — owner-ROLE accounts can be restricted too. Sending an
      // empty list clears a previous restriction (backend stores None).
      const sectionsPayload = ["owner","manager","shift_leader","supervisor"].includes(v.role)
        ? (Array.isArray(v.allowed_sections) ? v.allowed_sections : [])
        : [];
      if (userDialog.mode === "create") {
        await http.post("/owner/users", {
          first_name: v.first_name.trim(),
          last_name: v.last_name.trim(),
          username: v.username.trim(),
          pin: v.pin,
          role: v.role,
          active: !!v.active,
          allowed_sections: sectionsPayload,
        }, { headers: authHeaders });
        toast.success("User created.");
      } else {
        await http.patch(`/owner/users/${userDialog.id}`, {
          first_name: v.first_name.trim(),
          last_name: v.last_name.trim(),
          username: v.username.trim(),
          role: v.role,
          active: !!v.active,
          allowed_sections: sectionsPayload,
        }, { headers: authHeaders });
        toast.success("User updated.");
      }
      setUserDialog((d) => ({ ...d, open: false, saving: false }));
      await loadUserAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save user.");
      setUserDialog((d) => ({ ...d, saving: false }));
    }
  };

  const toggleUserActive = async (u) => {
    try {
      await http.patch(`/owner/users/${u.id}`, { active: !u.active }, { headers: authHeaders });
      toast.success(u.active ? "User disabled." : "User enabled.");
      await loadUserAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update user.");
    }
  };

  const submitResetPin = async () => {
    const v = userPinDialog;
    if ((v.pin || "").length < 4) { toast.error("PIN must be 4–10 characters."); return; }
    setUserPinDialog((d) => ({ ...d, saving: true }));
    try {
      await http.post(`/owner/users/${v.user.id}/reset-pin`, { pin: v.pin }, { headers: authHeaders });
      toast.success("PIN reset.");
      setUserPinDialog({ open: false, user: null, pin: "", saving: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reset PIN.");
      setUserPinDialog((d) => ({ ...d, saving: false }));
    }
  };

  const submitDeleteUser = async () => {
    const v = deleteUserDialog;
    if (!v.user) return;
    if (!v.pin || v.pin.length < 4) { toast.error("Enter the Void Manager Password."); return; }
    setDeleteUserDialog((d) => ({ ...d, saving: true }));
    try {
      await http.delete(`/owner/users/${v.user.id}`, { headers: authHeaders, data: { manager_pin: v.pin } });
      toast.success(`${v.user.full_name || "User"} deleted.`);
      setDeleteUserDialog({ open: false, user: null, pin: "", saving: false });
      await loadUserAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete user.");
      setDeleteUserDialog((d) => ({ ...d, saving: false }));
    }
  };

  const openUserActivity = async (u) => {
    setUserActivityDialog({ open: true, user: u, entries: [], loading: true });
    try {
      const r = await http.get(`/owner/users/${u.id}/activity`, { headers: authHeaders, params: { limit: 200 } });
      setUserActivityDialog({ open: true, user: u, entries: r.data?.entries || [], loading: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load activity.");
      setUserActivityDialog({ open: false, user: null, entries: [], loading: false });
    }
  };

  const loadTips = useCallback(async () => {
    if (!session?.token) return;
    try {
      const response = await http.get("/owner/tips", { headers: authHeaders });
      setTipsRows(response.data.tips || []);
      setTipsLoaded(true);
      http.get("/owner/tips/exceptions", { headers: authHeaders, params: { limit: 100 } })
        .then((r) => setTipExceptions(r?.data?.exceptions || []))
        .catch(() => { /* non-blocking */ });
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized();
      } else {
        toast.error(error.response?.data?.detail || "Unable to load tips.");
      }
    }
  }, [authHeaders, handleUnauthorized, session?.token]);

  const cashUpTip = async (recipient) => {
    setTipsBusy(recipient || "__unassigned__");
    try {
      await http.post("/owner/tips/cash-up", { recipient: recipient || "" }, { headers: authHeaders });
      toast.success(recipient ? `Cashed up ${recipient}.` : "Cashed up unassigned tips.");
      await loadTips();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to cash up.");
    } finally {
      setTipsBusy("");
    }
  };

  // Drill into a single waiter's tips — fetches the orders that built up
  // their pending balance so the manager can audit how each tip was made.
  const openTipDetail = useCallback(async (recipient) => {
    const name = (recipient || "").trim();
    setTipDetail({ open: true, recipient: name, loading: true, data: null });
    try {
      const response = await http.get("/owner/tips/orders", {
        headers: authHeaders,
        params: { recipient: name },
      });
      setTipDetail({ open: true, recipient: name, loading: false, data: response.data });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load tip detail.");
      setTipDetail({ open: false, recipient: "", loading: false, data: null });
    }
  }, [authHeaders]);

  // Drill into a single day's orders from Analytics → Reports → Daily totals.
  const openDailyDetail = useCallback(async (date) => {
    if (!date) return;
    setDailyDetail({ open: true, date, loading: true, orders: [] });
    try {
      const response = await http.get("/owner/receipts", {
        headers: authHeaders,
        params: { start_date: date, end_date: date },
      });
      const group = (response.data?.groups || []).find((g) => g.date === date);
      setDailyDetail({ open: true, date, loading: false, orders: group?.orders || [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load orders for that day.");
      setDailyDetail({ open: false, date: "", loading: false, orders: [] });
    }
  }, [authHeaders]);

  // Open the full receipt dialog given just an order id. Used by Service
  // Times rows where the loaded payload is intentionally slim.
  const openReceiptById = useCallback(async (orderId) => {
    if (!orderId) return;
    setOrderReceipt({ open: true, order: { id: orderId, order_number: "", table_number: "", items: [] } });
    try {
      const response = await http.get(`/owner/orders/${orderId}`, { headers: authHeaders });
      setOrderReceipt({ open: true, order: response.data.order });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load receipt.");
      setOrderReceipt({ open: false, order: null });
    }
  }, [authHeaders]);

  // Session 36 — open a receipt from a list row INSTANTLY with the row we
  // already have, then enrich it in the background with the table's
  // accountability trail (assigned waiter, who took payment, who closed).
  const openOrderReceipt = useCallback((order) => {
    if (!order) return;
    setOrderReceipt({ open: true, order });
    if (!order.id) return;
    if (order.__isSessionMerge) {
      // Session 70 — a merged "table receipt" still needs the table-level
      // detail (split bill: who paid what). Fetch it via any round's id.
      const srcId = (Array.isArray(order.__sourceOrders) && order.__sourceOrders[0]?.id) || String(order.id).split("#")[0];
      if (!srcId || String(srcId).startsWith("local-")) return;
      http.get(`/owner/orders/${srcId}`, { headers: authHeaders, timeout: 8000 })
        .then((r) => {
          const full = r?.data?.order;
          if (!full) return;
          const extra = { split_guests: full.split_guests || null, payment_taken_by: full.payment_taken_by, table_closed_at: full.table_closed_at, table_waiter: full.table_waiter };
          setOrderReceipt((cur) => (cur.open && cur.order?.id === order.id ? { open: true, order: { ...cur.order, ...extra } } : cur));
        })
        .catch(() => { /* keep the merged receipt we already had */ });
      return;
    }
    http.get(`/owner/orders/${order.id}`, { headers: authHeaders, timeout: 8000 })
      .then((r) => {
        const full = r?.data?.order;
        if (!full) return;
        setOrderReceipt((cur) => (cur.open && cur.order?.id === order.id ? { open: true, order: { ...order, ...full } } : cur));
      })
      .catch(() => { /* keep the row we already had */ });
  }, [authHeaders]);

  // Session 46 — from a single order's receipt, open the WHOLE table's
  // receipt (every order on the session merged into one document) so the
  // owner can see exactly what the R324 on the card paid for.
  const openTableReceiptFromSummary = useCallback(async (ss) => {
    if (!ss || !Array.isArray(ss.orders) || ss.orders.length === 0) return;
    try {
      const rows = await Promise.all(ss.orders.map((so) =>
        http.get(`/owner/orders/${so.id}`, { headers: authHeaders, timeout: 8000 })
          .then((r) => r?.data?.order || null)
          .catch(() => null),
      ));
      const list = rows.filter(Boolean);
      if (!list.length) { toast.error("Could not load the table's orders."); return; }
      const merged = mergeSessionOrders(list);
      if (!merged) return;
      setOrderReceipt({ open: true, order: { ...merged, session_summary: ss } });
    } catch (_) {
      toast.error("Could not load the table receipt.");
    }
  }, [authHeaders]);

  // ── Cash Management loaders ───────────────────────────────────────────
  // Computes the start/end ISO dates from the active range preset. "today"
  // and "yesterday" map to single-day windows; "7d" → last 7 days.
  const cashDateRange = useMemo(() => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (cashRange === "today") {
      return { start: iso(today), end: iso(today) };
    }
    if (cashRange === "yesterday") {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { start: iso(y), end: iso(y) };
    }
    if (cashRange === "7d") {
      const s = new Date(today); s.setDate(s.getDate() - 6);
      return { start: iso(s), end: iso(today) };
    }
    return { start: cashCustom.start || iso(today), end: cashCustom.end || iso(today) };
  }, [cashRange, cashCustom]);

  const loadCash = useCallback(async () => {
    try {
      const [summary, history, regs] = await Promise.all([
        http.get("/owner/cash/summary", { headers: authHeaders, params: { start_date: cashDateRange.start, end_date: cashDateRange.end, ...(selectedRegisterId && selectedRegisterId !== "all" ? { register_id: selectedRegisterId } : {}) } }),
        http.get("/owner/cash/cash-ups", { headers: authHeaders, params: { limit: 50 } }),
        registersLoaded ? Promise.resolve({ data: { registers } }) : http.get("/owner/registers", { headers: authHeaders }),
      ]);
      setCashData(summary.data);
      setCashHistory(history.data.cash_ups || []);
      if (!registersLoaded) {
        setRegisters(regs.data.registers || []);
        setRegistersLoaded(true);
      }
      setCashLoaded(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load cash data.");
    }
  }, [authHeaders, cashDateRange.start, cashDateRange.end, selectedRegisterId, registersLoaded, registers]);

  // Register CRUD helpers — used by the Registers card on Cash Management.
  const reloadRegisters = useCallback(async () => {
    try {
      const r = await http.get("/owner/registers", { headers: authHeaders });
      setRegisters(r.data.registers || []);
      setRegistersLoaded(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load registers.");
    }
  }, [authHeaders]);

  const submitRegister = async () => {
    const v = registerDialog.values;
    if (!v.name.trim()) { toast.error("Enter a register name."); return; }
    try {
      if (registerDialog.mode === "edit" && registerDialog.id) {
        await http.patch(`/owner/registers/${registerDialog.id}`, { name: v.name.trim(), description: v.description || null }, { headers: authHeaders });
        toast.success("Register updated.");
      } else {
        await http.post("/owner/registers", { name: v.name.trim(), description: v.description || null }, { headers: authHeaders });
        toast.success("Register created.");
      }
      setRegisterDialog({ open: false, mode: "create", id: "", values: { name: "", description: "" } });
      await reloadRegisters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save register.");
    }
  };

  const toggleRegisterActive = async (reg) => {
    try {
      await http.patch(`/owner/registers/${reg.id}`, { active: !reg.active }, { headers: authHeaders });
      toast.success(`Register ${!reg.active ? "enabled" : "disabled"}.`);
      await reloadRegisters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update register.");
    }
  };

  // Report schedule helpers.
  const loadReportSchedules = useCallback(async () => {
    try {
      const r = await http.get("/owner/report-schedules", { headers: authHeaders });
      setReportSchedules(r.data.schedules || []);
      setReportSchedulesLoaded(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load schedules.");
    }
  }, [authHeaders]);

  const submitSchedule = async () => {
    const v = scheduleDialog.values;
    if (!v.name.trim()) { toast.error("Enter a schedule name."); return; }
    if (!v.report_types?.length) { toast.error("Pick at least one report type."); return; }
    const recipients = (v.recipients || "").split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    try {
      const body = {
        name: v.name.trim(),
        frequency: v.frequency,
        report_types: v.report_types,
        recipients,
        enabled: !!v.enabled,
      };
      if (scheduleDialog.mode === "edit" && scheduleDialog.id) {
        await http.patch(`/owner/report-schedules/${scheduleDialog.id}`, body, { headers: authHeaders });
        toast.success("Schedule updated.");
      } else {
        await http.post("/owner/report-schedules", body, { headers: authHeaders });
        toast.success("Schedule created.");
      }
      setScheduleDialog({ open: false, mode: "create", id: "", values: { name: "", frequency: "daily", report_types: ["business_summary", "sales_mix", "category_mix"], recipients: "", enabled: true } });
      await loadReportSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save schedule.");
    }
  };

  const deleteSchedule = async (s) => {
    if (!window.confirm(`Delete schedule "${s.name}"?`)) return;
    try {
      await http.delete(`/owner/report-schedules/${s.id}`, { headers: authHeaders });
      toast.success("Schedule deleted.");
      await loadReportSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete schedule.");
    }
  };

  const toggleScheduleEnabled = async (s) => {
    try {
      await http.patch(`/owner/report-schedules/${s.id}`, { enabled: !s.enabled }, { headers: authHeaders });
      await loadReportSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update schedule.");
    }
  };

  // Session 51 — "Send a report now": pick a date range, tick the sections,
  // enter one or more recipients. Replaces the old fixed "test report".
  const REPORT_SECTION_OPTIONS = [
    { v: "business_summary", l: "Business Summary", d: "Revenue, profit, payments, tips & service fee, voids, stock take, received, waste" },
    { v: "sales_mix", l: "Sales Mix", d: "Every item sold — qty, revenue, share, profit" },
    { v: "category_mix", l: "Category Mix", d: "Sales by menu category" },
    { v: "cash_up", l: "Cash Drawer", d: "Float, cash sales, payouts, expected cash" },
    { v: "low_stock", l: "Low Stock", d: "Items & ingredients under threshold" },
    { v: "register", l: "Registers", d: "Register list and status" },
    { v: "voids_detail", l: "Void Report", d: "Every void — when, order, table, waiter, voided by, reason, items, value" },
    { v: "payments_cash", l: "Cash Payments", d: "Every cash bill — table, who took it, tip, total" },
    { v: "payments_card", l: "Card Payments", d: "Every card bill — table, who took it, tip, total" },
    { v: "payments_eft", l: "EFT Payments", d: "Every EFT bill — table, who took it, tip, total" },
    { v: "payments_split", l: "Split Payments", d: "Bills paid with more than one method, with each portion" },
  ];
  const [sendNow, setSendNow] = useState(() => {
    const d = new Date();
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    return { preset: "yesterday", start: iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1)), end: iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1)), sections: ["business_summary", "sales_mix", "category_mix", "cash_up", "low_stock", "register"] };
  });
  const sendNowPresets = useMemo(() => {
    const today = new Date();
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    const shift = (n) => iso(new Date(today.getFullYear(), today.getMonth(), today.getDate() + n));
    return [
      { v: "today", label: "Today", start: iso(today), end: iso(today) },
      { v: "yesterday", label: "Yesterday", start: shift(-1), end: shift(-1) },
      { v: "7d", label: "Last 7 days", start: shift(-6), end: iso(today) },
      { v: "30d", label: "Last 30 days", start: shift(-29), end: iso(today) },
      { v: "last_month", label: "Last month", start: iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)), end: iso(new Date(today.getFullYear(), today.getMonth(), 0)) },
      { v: "custom", label: "Custom range" },
    ];
  }, []);
  const sendTestReport = async () => {
    const recipients = testReportEmail.split(/[,;\s]+/).map((r) => r.trim()).filter(Boolean);
    if (!recipients.length || recipients.some((r) => !r.includes("@"))) { toast.error("Enter at least one valid recipient email (separate several with commas)."); return; }
    if (!sendNow.sections.length) { toast.error("Tick at least one report section."); return; }
    if (!sendNow.start || !sendNow.end) { toast.error("Pick both From and To dates."); return; }
    if (sendNow.start > sendNow.end) { toast.error("'From' must be before 'To'."); return; }
    const presetLabel = (sendNowPresets.find((p) => p.v === sendNow.preset) || {}).label || "Custom range";
    setTestReportSending(true);
    try {
      const r = await http.post("/owner/report-schedules/test", {
        report_types: sendNow.sections,
        recipients,
        start_date: sendNow.start,
        end_date: sendNow.end,
        range_label: presetLabel,
      }, { headers: authHeaders });
      const sentTo = r.data?.sent_to_all || recipients;
      toast.success(`${presetLabel} report sent to ${sentTo.join(", ")}.`);
      if (r.data?.failed?.length) toast.warning(`Could not deliver to: ${r.data.failed.join("; ")}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to send report.");
    } finally {
      setTestReportSending(false);
    }
  };

  // ── Inventory Alerts Scheduling functions ────────────────────────────
  const loadInventoryAlertSchedules = useCallback(async () => {
    try {
      const r = await http.get("/owner/inventory-alert-schedules", { headers: authHeaders });
      setInventoryAlertSchedules(r.data.schedules || []);
      setInventoryAlertSchedulesLoaded(true);
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to load inventory alert schedules."); }
  }, [authHeaders]);

  const submitInventoryAlertSchedule = async () => {
    const v = inventoryAlertDialog.values;
    if (!v.name.trim()) { toast.error("Enter a schedule name."); return; }
    if (!v.alert_types.length) { toast.error("Pick at least one alert type."); return; }
    const recipientsList = v.recipients.split(",").map((r) => r.trim()).filter(Boolean);
    const body = { name: v.name.trim(), frequency: v.frequency, alert_types: v.alert_types, channel: v.channel, recipients: recipientsList, enabled: v.enabled };
    try {
      if (inventoryAlertDialog.mode === "edit" && inventoryAlertDialog.id) {
        await http.patch(`/owner/inventory-alert-schedules/${inventoryAlertDialog.id}`, body, { headers: authHeaders });
      } else {
        await http.post("/owner/inventory-alert-schedules", body, { headers: authHeaders });
      }
      toast.success("Inventory alert schedule saved.");
      setInventoryAlertDialog({ open: false, mode: "create", id: "", values: { name: "", frequency: "daily", alert_types: ["low_stock", "out_of_stock"], channel: "email", recipients: "", enabled: true } });
      await loadInventoryAlertSchedules();
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to save alert schedule."); }
  };

  const deleteInventoryAlertSchedule = async (s) => {
    if (!window.confirm(`Delete alert schedule "${s.name}"?`)) return;
    try {
      await http.delete(`/owner/inventory-alert-schedules/${s.id}`, { headers: authHeaders });
      toast.success("Alert schedule deleted.");
      await loadInventoryAlertSchedules();
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to delete schedule."); }
  };

  const toggleInventoryAlertEnabled = async (s) => {
    try {
      await http.patch(`/owner/inventory-alert-schedules/${s.id}`, { enabled: !s.enabled }, { headers: authHeaders });
      await loadInventoryAlertSchedules();
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to update alert schedule."); }
  };

  const sendTestInventoryAlert = async () => {
    setTestAlertSending(true);
    try {
      const r = await http.post("/owner/inventory-alert-schedules/test", {
        alert_types: ["low_stock", "out_of_stock", "negative_stock"],
        channel: "email",
      }, { headers: authHeaders });
      if (r.data.ok) {
        toast.success(r.data.detail || `Test alert sent to ${r.data.sent_to || "default recipient"}.`);
      } else {
        toast.error(r.data.detail || "Test alert could not be sent.");
      }
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to send test alert."); }
    finally { setTestAlertSending(false); }
  };

  // ── YOCO Payment Gateway functions ────────────────────────────────
  const loadYocoGateway = useCallback(async () => {
    try {
      const r = await http.get("/owner/payment-gateway", { headers: authHeaders });
      setYocoGateway(r.data);
      // Pre-fill form with current values (keys stay blank unless user re-enters).
      setYocoForm((prev) => ({
        ...prev,
        yoco_mode: r.data.yoco_mode || "test",
        yoco_payments_enabled: r.data.yoco_payments_enabled || false,
        yoco_auto_confirm_paid: r.data.yoco_auto_confirm_paid || false,
      }));
      // Session 30 — seed SA gateway toggles + public ids (secrets stay
      // blank; hints/badges show they're configured).
      setSaForm((prev) => ({
        ...prev,
        payfast_enabled: !!r.data.payfast_enabled,
        payfast_mode: r.data.payfast_mode || "sandbox",
        payfast_merchant_id: r.data.payfast_merchant_id || "",
        ozow_enabled: !!r.data.ozow_enabled,
        ozow_mode: r.data.ozow_mode || "sandbox",
        ozow_site_code: r.data.ozow_site_code || "",
        snapscan_enabled: !!r.data.snapscan_enabled,
        snapscan_mode: r.data.snapscan_mode || "sandbox",
        snapscan_merchant_id: r.data.snapscan_merchant_id || "",
        zapper_enabled: !!r.data.zapper_enabled,
        zapper_mode: r.data.zapper_mode || "sandbox",
        zapper_merchant_id: r.data.zapper_merchant_id || "",
      }));
      setYocoGatewayLoaded(true);
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to load payment gateway config."); }
  }, [authHeaders]);

  const saveYocoGateway = async () => {
    setYocoSaving(true);
    try {
      const body = {};
      if (yocoForm.yoco_mode) body.yoco_mode = yocoForm.yoco_mode;
      body.yoco_payments_enabled = !!yocoForm.yoco_payments_enabled;
      body.yoco_auto_confirm_paid = !!yocoForm.yoco_auto_confirm_paid;
      if (yocoForm.yoco_secret_key_test) body.yoco_secret_key_test = yocoForm.yoco_secret_key_test;
      if (yocoForm.yoco_secret_key_live) body.yoco_secret_key_live = yocoForm.yoco_secret_key_live;
      if (yocoForm.yoco_webhook_secret_test) body.yoco_webhook_secret_test = yocoForm.yoco_webhook_secret_test;
      if (yocoForm.yoco_webhook_secret_live) body.yoco_webhook_secret_live = yocoForm.yoco_webhook_secret_live;
      const r = await http.patch("/owner/payment-gateway", body, { headers: authHeaders });
      setYocoGateway(r.data);
      // Clear entered secrets from form (they're now saved).
      setYocoForm((prev) => ({ ...prev, yoco_secret_key_test: "", yoco_secret_key_live: "", yoco_webhook_secret_test: "", yoco_webhook_secret_live: "" }));
      toast.success("Payment gateway settings saved.");
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to save payment gateway settings."); }
    finally { setYocoSaving(false); }
  };

  // Session 30 — Save PayFast / Ozow / SnapScan / Zapper configuration.
  const saveSaGateways = async () => {
    setSaSaving(true);
    try {
      const body = {
        payfast_enabled: !!saForm.payfast_enabled,
        ozow_enabled: !!saForm.ozow_enabled,
        snapscan_enabled: !!saForm.snapscan_enabled,
        zapper_enabled: !!saForm.zapper_enabled,
        // Session 30.2 — sandbox/live mode per gateway.
        payfast_mode: saForm.payfast_mode || "sandbox",
        ozow_mode: saForm.ozow_mode || "sandbox",
        snapscan_mode: saForm.snapscan_mode || "sandbox",
        zapper_mode: saForm.zapper_mode || "sandbox",
      };
      if (saForm.payfast_merchant_id) body.payfast_merchant_id = saForm.payfast_merchant_id;
      if (saForm.payfast_merchant_key) body.payfast_merchant_key = saForm.payfast_merchant_key;
      if (saForm.payfast_passphrase) body.payfast_passphrase = saForm.payfast_passphrase;
      if (saForm.ozow_site_code) body.ozow_site_code = saForm.ozow_site_code;
      if (saForm.ozow_private_key) body.ozow_private_key = saForm.ozow_private_key;
      if (saForm.snapscan_merchant_id) body.snapscan_merchant_id = saForm.snapscan_merchant_id;
      if (saForm.snapscan_api_key) body.snapscan_api_key = saForm.snapscan_api_key;
      if (saForm.zapper_merchant_id) body.zapper_merchant_id = saForm.zapper_merchant_id;
      if (saForm.zapper_api_key) body.zapper_api_key = saForm.zapper_api_key;
      const r = await http.patch("/owner/payment-gateway", body, { headers: authHeaders });
      setYocoGateway(r.data);
      // Clear entered secrets — they're saved (hints/badges confirm).
      setSaForm((prev) => ({ ...prev, payfast_merchant_key: "", payfast_passphrase: "", ozow_private_key: "", snapscan_api_key: "", zapper_api_key: "" }));
      toast.success("Gateway settings saved.");
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to save gateway settings."); }
    finally { setSaSaving(false); }
  };

  // Inventory usage loader.
  const loadInventoryUsage = useCallback(async (rangeKey, explicitDates) => {
    setInventoryUsageLoading(true);
    try {
      let start;
      let end;
      if (explicitDates?.start && explicitDates?.end) {
        // Session 50 — Inventory Usage now lives INSIDE Stock History and
        // follows its date filter exactly.
        start = explicitDates.start;
        end = explicitDates.end;
      } else {
        const key = rangeKey || inventoryUsageRange;
        const today = new Date();
        const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        end = iso(today);
        if (key === "1d") start = end;
        else if (key === "7d") { const d = new Date(today); d.setDate(d.getDate() - 6); start = iso(d); }
        else if (key === "30d") { const d = new Date(today); d.setDate(d.getDate() - 29); start = iso(d); }
        else start = end;
      }
      const r = await http.get("/owner/inventory/usage", { headers: authHeaders, params: { start_date: start, end_date: end } });
      setInventoryUsage(r.data);
      setInventoryUsageLoaded(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load inventory usage.");
    } finally {
      setInventoryUsageLoading(false);
    }
  }, [authHeaders, inventoryUsageRange]);

  // Session 50 — "All items sold" (same list as Business Summary) inside
  // Stock History, for the Stock History date range.
  const [stockItemsSold, setStockItemsSold] = useState(null);
  const [stockItemsSoldLoading, setStockItemsSoldLoading] = useState(false);
  const [stockItemsSoldQuery, setStockItemsSoldQuery] = useState("");
  const loadStockItemsSold = useCallback(async (dates) => {
    if (!dates?.start || !dates?.end) return;
    setStockItemsSoldLoading(true);
    try {
      const r = await http.get("/owner/reports/summary", { headers: authHeaders, params: { start_date: dates.start, end_date: dates.end } });
      setStockItemsSold({ items: r.data?.items || [], range: r.data?.range || dates, total_items: (r.data?.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0) });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load items sold.");
    } finally {
      setStockItemsSoldLoading(false);
    }
  }, [authHeaders]);

  const submitFloat = async () => {
    const v = floatDialog.values;
    if (!v.amount || Number(v.amount) < 0) { toast.error("Enter an opening float amount."); return; }
    try {
      if (floatDialog.mode === "edit" && floatDialog.id) {
        await http.patch(`/owner/cash/floats/${floatDialog.id}`, {
          amount: Number(v.amount), user: v.user || null, notes: v.notes || null,
        }, { headers: authHeaders });
        toast.success("Opening float updated.");
      } else {
        // If the manager filled in denominations, send the breakdown.
        // Otherwise (use_total_only mode) we just send the typed amount.
        const denoms = v.use_total_only ? null : (Object.keys(v.denominations || {}).filter((k) => Number(v.denominations[k] || 0) > 0).length ? v.denominations : null);
        await http.post("/owner/cash/floats", {
          amount: Number(v.amount), user: v.user || null, notes: v.notes || null,
          register_id: v.register_id || defaultRegisterId || null,
          denominations: denoms,
        }, { headers: authHeaders });
        toast.success("Opening float recorded.");
      }
      setFloatDialog({ open: false, mode: "create", id: "", values: { amount: "", user: "", notes: "", register_id: "", denominations: {}, use_total_only: false } });
      await loadCash();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save float.");
    }
  };

  const submitPayout = async () => {
    const v = payoutDialog.values;
    if (!v.amount || Number(v.amount) <= 0) { toast.error("Enter a payout amount."); return; }
    if (!v.reason.trim()) { toast.error("Enter a reason for the payout."); return; }
    try {
      await http.post("/owner/cash/payouts", {
        amount: Number(v.amount), reason: v.reason.trim(), category: v.category,
        user: v.user || null, notes: v.notes || null,
        register_id: v.register_id || defaultRegisterId || null,
      }, { headers: authHeaders });
      toast.success("Cash payout recorded.");
      setPayoutDialog({ open: false, values: { amount: "", reason: "", category: "stock_purchase", user: "", notes: "", register_id: "" } });
      await loadCash();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save payout.");
    }
  };

  const submitSupplier = async () => {
    const v = supplierDialog.values;
    if (!v.supplier_name.trim()) { toast.error("Enter the supplier name."); return; }
    if (!v.amount || Number(v.amount) <= 0) { toast.error("Enter an amount."); return; }
    try {
      await http.post("/owner/cash/supplier-payments", {
        supplier_name: v.supplier_name.trim(),
        amount: Number(v.amount),
        payment_method: v.payment_method,
        reference: v.reference || null,
        user: v.user || null,
        notes: v.notes || null,
        register_id: v.register_id || defaultRegisterId || null,
      }, { headers: authHeaders });
      toast.success("Supplier payment recorded.");
      setSupplierDialog({ open: false, values: { supplier_name: "", amount: "", payment_method: "cash", reference: "", user: "", notes: "", register_id: "" } });
      await loadCash();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save supplier payment.");
    }
  };

  const submitCashUp = async () => {
    const v = cashUpDialog.values;
    if (v.actual_cash === "" || Number(v.actual_cash) < 0) { toast.error("Enter the actual cash counted."); return; }
    try {
      await http.post("/owner/cash/cash-ups", {
        actual_cash: Number(v.actual_cash),
        user: v.user || null,
        notes: v.notes || null,
        start_date: cashDateRange.start,
        end_date: cashDateRange.end,
        denominations: Object.keys(v.denominations || {}).length ? v.denominations : null,
        register_id: v.register_id || defaultRegisterId || null,
      }, { headers: authHeaders });
      toast.success("Cash-up recorded.");
      setCashUpDialog({ open: false, values: { actual_cash: "", user: "", notes: "", denominations: {}, register_id: "" } });
      await loadCash();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save cash-up.");
    }
  };

  // ── Stock audit trail loaders ──────────────────────────────────────────
  const stockHistoryDateRange = useMemo(() => {
    const today = new Date();
    // Session 50 — LOCAL calendar date (toISOString() is UTC, which made
    // "Today" roll over at 02:00 SAST and show the wrong day's movements).
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (stockHistoryRange === "today") return { start: iso(today), end: iso(today) };
    if (stockHistoryRange === "yesterday") {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { start: iso(y), end: iso(y) };
    }
    if (stockHistoryRange === "7d") {
      const s = new Date(today); s.setDate(s.getDate() - 6);
      return { start: iso(s), end: iso(today) };
    }
    if (stockHistoryRange === "30d") {
      const s = new Date(today); s.setDate(s.getDate() - 29);
      return { start: iso(s), end: iso(today) };
    }
    return { start: stockHistoryCustom.start || iso(today), end: stockHistoryCustom.end || iso(today) };
  }, [stockHistoryRange, stockHistoryCustom]);

  const loadStockHistory = useCallback(async () => {
    try {
      const params = {
        start_date: stockHistoryDateRange.start,
        end_date: stockHistoryDateRange.end,
        limit: 500,
      };
      if (stockHistoryQuery.trim()) params.query = stockHistoryQuery.trim();
      // Session 66 — one department at a time (Beach Bar vs Main Bar …).
      if (stockHistoryDept && stockHistoryDept !== "all") params.department_id = stockHistoryDept;
      const r = await http.get("/owner/stock/history", { headers: authHeaders, params });
      setStockHistoryData(r.data.adjustments || []);
      setStockHistoryLoaded(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load stock history.");
    }
  }, [authHeaders, stockHistoryDateRange.start, stockHistoryDateRange.end, stockHistoryQuery, stockHistoryDept]);

  const loadStockTakes = useCallback(async () => {
    try {
      const r = await cachedGet(http, "/owner/stock-takes", { headers: authHeaders, params: { limit: 50 } });
      setStockTakesList(r.data.stock_takes || []);
      setStockTakesLoaded(true);
      // Session 39.5 — custom stock locations get their own quick-start
      // buttons (best-effort; the two legacy buttons never depend on it).
      try {
        const l = await cachedGet(http, "/owner/stock-locations", { headers: authHeaders });
        setStockTakeCustomLocations((l.data?.locations || []).filter((x) => x.active !== false && !x.key));
      } catch (_e) { /* ignore */ }
    } catch (error) {
      if (navigator.onLine === false) { setStockTakesLoaded(true); return; } // offline, no cache yet — the Run button still works from cached balances
      toast.error(error.response?.data?.detail || "Unable to load stock takes.");
    }
  }, [authHeaders]);

  // Open per-item history dialog. Fetches the recent adjustments for one
  // item/ingredient so managers can audit a single line at a glance.
  const openItemHistory = useCallback(async (itemId, itemName) => {
    if (!itemId) return;
    setItemHistoryDialog({ open: true, item_id: itemId, item_name: itemName, loading: true, rows: [] });
    try {
      const r = await http.get("/owner/stock/history", { headers: authHeaders, params: { item_id: itemId, limit: 100 } });
      setItemHistoryDialog({ open: true, item_id: itemId, item_name: itemName, loading: false, rows: r.data.adjustments || [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load item history.");
      setItemHistoryDialog({ open: false, item_id: "", item_name: "", loading: false, rows: [] });
    }
  }, [authHeaders]);

  // Open the Run Stock Take wizard pre-filled with every tracked item +
  // ingredient and its current expected value. Manager edits "actual"
  // counts; save calls the snapshot endpoint.
  const openStockTakeWizard = useCallback(async (preset = {}) => {
    try {
      // Session 40.1 — a stock take can BEGIN offline: reference data and the
      // "expected" balances come from the last successful load. The server
      // still checks each expected value at sync time, so a stale expected
      // becomes a sync exception rather than overwriting a newer balance.
      const [ingResp, stockResp, locResp] = await Promise.all([
        cachedGet(http, "/owner/ingredients", { headers: authHeaders }),
        cachedGet(http, "/owner/analytics/stock?threshold=10", { headers: authHeaders }),
        cachedGet(http, "/owner/stock-locations", { headers: authHeaders }),
      ]);
      const _fromCache = ingResp.fromCache || stockResp.fromCache || locResp.fromCache;
      if (_fromCache) {
        toast.info("Offline — counting against the last known balances.", { description: "Your count is saved on this device and applied when the connection returns. Any line whose balance changed in the meantime becomes a sync exception instead of overwriting newer stock." });
      }
      // Session 39.5 — the wizard counts ONE stock location at a time, and
      // any of the business's active locations can be picked (not just the
      // two legacy ones). Every line carries an expected + actual value per
      // location so the manager can flip tabs without reloading; only the
      // selected location is saved and only that location's balance moves.
      // Tracked menu items ("Items", e.g. bottled drinks) only live on the
      // floor location.
      const allLocs = (locResp.data?.locations || []).filter((l) => l.active !== false);
      const floorLoc = allLocs.find((l) => l.key === "floor") || allLocs[0] || null;
      const locations = allLocs.length ? allLocs : [{ id: "floor", key: "floor", name: "Floor / Kitchen" }, { id: "back_of_house", key: "back_of_house", name: "Back of house" }];
      const resolveLoc = (v) => {
        if (!v) return null;
        const hit = locations.find((l) => l.id === v || l.key === v);
        return hit ? hit.id : null;
      };
      const preferredId = resolveLoc((locResp.data?.default_priority || [])[0]) || (floorLoc ? floorLoc.id : locations[0].id);
      const location = resolveLoc(preset.location) || preferredId;
      const itemLines = (stockResp.data.items || [])
        .filter((it) => it.stock !== null && it.stock !== undefined)
        .map((it) => ({
          item_type: "item",
          item_id: it.id,
          name: it.name,
          unit: "pcs",
          category: "items",
          expected: Object.fromEntries(locations.map((l) => [l.id, l.id === (floorLoc ? floorLoc.id : locations[0].id) ? Number(it.stock || 0) : 0])),
          // A physical count can't be negative — a shortfall balance pre-fills as 0 (expected keeps the real figure).
          actual: Object.fromEntries(locations.map((l) => [l.id, l.id === (floorLoc ? floorLoc.id : locations[0].id) ? String(Math.max(0, Number(it.stock || 0))) : "0"])),
        }));
      const balanceAt = (ing, l) => {
        const sbl = ing.stock_by_location;
        if (sbl && Object.prototype.hasOwnProperty.call(sbl, l.id)) return Number(sbl[l.id] || 0);
        if (l.key === "floor") return Number(ing.stock_on_hand || 0);
        if (l.key === "back_of_house") return Number(ing.back_of_house_stock || 0);
        return 0;
      };
      const ingLines = (ingResp.data.ingredients || []).map((ing) => ({
        item_type: "ingredient",
        item_id: ing.id,
        name: ing.name,
        unit: ing.unit || "",
        // Session 66 — stock belongs to a DEPARTMENT: count one department
        // at a time (Kitchen / Bar / Beach Bar …). Legacy ingredients without
        // a department fall back to their kitchen/bar station bucket.
        category: ing.department_id ? `dept:${ing.department_id}` : (ing.category === "bar" ? "bar" : "kitchen"),
        dept_name: ing.department_name || (ing.category === "bar" ? "Bar" : "Kitchen"),
        expected: Object.fromEntries(locations.map((l) => [l.id, balanceAt(ing, l)])),
        actual: Object.fromEntries(locations.map((l) => [l.id, String(Math.max(0, balanceAt(ing, l)))])),
      }));
      // Session 66 — a quick start may name a department (`dept:<id>`) or a
      // legacy station ("kitchen"/"bar"). Legacy names resolve to the first
      // department routed to that station when every ingredient has one.
      const allLines = [...ingLines, ...itemLines];
      let scope = preset.category || "all";
      if ((scope === "kitchen" || scope === "bar") && !allLines.some((ln) => ln.category === scope)) {
        const hit = (departments || []).find((d) => d && d.active !== false && (scope === "bar" ? (d.route_to_bar && !d.route_to_kitchen) : d.route_to_kitchen));
        scope = hit ? `dept:${hit.id}` : "all";
      }
      if (scope !== "all" && scope !== "items" && !allLines.some((ln) => ln.category === scope)) scope = "all";
      setStockTakeWizard({ open: true, user: stockRecorder.trim() || "", notes: "", search: "", scope, location, locations, lines: allLines, saving: false, context: null });
      // Session 63 — the story behind each line (last count, received,
      // moved, used today, should-be) loads in the background so the
      // wizard opens instantly and still works offline without it.
      http.get("/owner/stock-takes/context", { headers: authHeaders, timeout: 20000 })
        .then((r) => setStockTakeWizard((s) => (s.open ? { ...s, context: r.data || null } : s)))
        .catch(() => { /* context is optional */ });
    } catch (error) {
      toast.error(error.response?.data?.detail || (navigator.onLine === false ? "Offline and no cached stock data yet — open Inventory once while online, then stock takes work offline." : "Unable to start stock take."));
    }
  }, [authHeaders, stockRecorder, departments]);

  // Which wizard location is being counted, and whether menu items can be
  // counted there (items only live on the floor location).
  const stockTakeLoc = (w) => w.locations.find((l) => l.id === w.location) || w.locations[0] || null;
  const stockTakeItemsAllowed = (w) => { const l = stockTakeLoc(w); return !!l && (l.key === "floor" || (!w.locations.some((x) => x.key === "floor") && w.locations[0]?.id === l.id)); };
  // Session 66 — the scope tabs are the business's DEPARTMENTS (one tab per
  // department that owns stock: Kitchen, Bar, Beach Bar …), plus legacy
  // station buckets for ingredients that pre-date departments and "Items"
  // (tracked menu items, floor only). Default is "all" — the manager picks
  // a single department when they only want to count that one.
  const stockTakeScopes = (w) => {
    const itemsAllowed = stockTakeItemsAllowed(w);
    const seen = new Map();
    (w.lines || []).forEach((ln) => {
      if (ln.item_type === "item") return;
      const key = ln.category || "kitchen";
      if (!seen.has(key)) seen.set(key, { id: key, label: key.startsWith("dept:") ? (ln.dept_name || "Department") : (key === "bar" ? "Bar (unassigned)" : "Kitchen (unassigned)"), count: 0, dept: key.startsWith("dept:") });
      seen.get(key).count += 1;
    });
    const deptTabs = [...seen.values()].sort((a, b) => (a.dept === b.dept ? a.label.localeCompare(b.label) : a.dept ? -1 : 1));
    // Hide the "(unassigned)" suffix when there are no real departments at all.
    if (!deptTabs.some((t) => t.dept)) deptTabs.forEach((t) => { t.label = t.label.replace(" (unassigned)", ""); });
    const itemCount = (w.lines || []).filter((ln) => ln.item_type === "item").length;
    return itemsAllowed ? [...deptTabs, { id: "items", label: "Items", count: itemCount, dept: false }] : deptTabs;
  };

  const submitStockTake = async () => {
    const w = stockTakeWizard;
    const loc = stockTakeLoc(w);
    if (!loc) { toast.error("Pick the location you are counting."); return; }
    const itemsAllowed = stockTakeItemsAllowed(w);
    // Session 40 — every line carries `expected` (the balance this device
    // saw when the count began). Online the count is applied as before
    // (`force: true`, the server re-bases if a sale landed mid-count). If
    // the connection is down the SAME lines are queued with `force: false`:
    // at replay the server applies a line only if its balance still equals
    // `expected`, otherwise it parks a sync exception with the physical
    // count preserved for a manager to resolve.
    const lines = w.lines
      .filter((ln) => w.scope === "all" || ln.category === w.scope)
      .filter((ln) => !(!itemsAllowed && ln.item_type === "item")) // items only live on the floor
      .filter((ln) => (ln.actual?.[loc.id] ?? "") !== "")
      .map((ln) => ({ item_type: ln.item_type, item_id: ln.item_id, actual: Number(ln.actual[loc.id] || 0), expected: Number(ln.expected?.[loc.id] ?? 0), location: loc.id }));
    if (!lines.length) { toast.error("There is nothing to count in this view."); return; }
    setStockTakeWizard((s) => ({ ...s, saving: true }));
    try {
      // Session 66 — scope is a DEPARTMENT (dept:<id>), a legacy station
      // bucket (kitchen/bar), "items" or "all". The department id travels
      // with the count so the server can refuse lines from elsewhere.
      const scopeDef = stockTakeScopes(w).find((t) => t.id === w.scope);
      const scopeName = scopeDef ? scopeDef.label : (w.scope === "all" ? (itemsAllowed ? "All departments + Items" : "All departments") : "All");
      const scopeLabel = `${loc.name} · ${scopeName}`;
      const deptId = String(w.scope || "").startsWith("dept:") ? w.scope.slice(5) : null;
      const base = { user: w.user || null, notes: w.notes || null, apply: true, scope: scopeLabel, lines, department_id: deptId, department_name: deptId ? scopeName : null };
      const res = await performStockAction({
        http, authHeaders, path: "/owner/stock-takes", body: { ...base, force: true },
        eventType: EventType.STOCK_TAKE_APPLIED, eventPayload: { ...base, force: false }, session,
      });
      if (res.status === "queued") {
        toast.info(`Stock take saved on this device — ${scopeLabel} (${lines.length} lines) will sync when the connection returns.`, { description: "Lines whose balance changed in the meantime become sync exceptions instead of overwriting newer stock." });
      } else {
        toast.success(`Stock take recorded — ${scopeLabel}.`);
      }
      setStockTakeWizard({ open: false, user: "", notes: "", search: "", scope: "all", location: "floor", locations: [], lines: [], saving: false });
      if (res.status !== "queued") await Promise.all([loadStockTakes(), loadStockHistory(), loadIngredients(), loadLowStock()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save stock take.");
      setStockTakeWizard((s) => ({ ...s, saving: false }));
    }
  };

  // ── Audit log ──────────────────────────────────────────────────────────
  const auditDateRange = useMemo(() => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (auditRange === "today") return { start: iso(today), end: iso(today) };
    if (auditRange === "yesterday") { const y = new Date(today); y.setDate(y.getDate() - 1); return { start: iso(y), end: iso(y) }; }
    if (auditRange === "7d") { const s = new Date(today); s.setDate(s.getDate() - 6); return { start: iso(s), end: iso(today) }; }
    if (auditRange === "30d") { const s = new Date(today); s.setDate(s.getDate() - 29); return { start: iso(s), end: iso(today) }; }
    // Session 73 — "All time": no date bounds, so an action filter
    // (e.g. "Voids cleared") searches every entry ever recorded.
    if (auditRange === "all") return { start: undefined, end: undefined };
    return { start: auditCustom.start || iso(today), end: auditCustom.end || iso(today) };
  }, [auditRange, auditCustom]);

  // Session 31.6 — After the AI assistant applies a confirmed change (stock
  // adjustment, price change) refresh the data the owner is looking at.
  // The assistant said "Done" but every list on screen kept the OLD value
  // until a manual reload, which read as "it did nothing".
  useEffect(() => {
    const onAiExecuted = (ev) => {
      const kind = ev?.detail?.data_affected?.kind || ev?.detail?.action || "";
      try {
        refreshDashboard(false);
        if (kind === "ingredient" || kind === "menu_item" || kind === "adjust_stock") {
          loadStock();
          loadLowStock();
          loadIngredients();
          loadStockHistory();
        }
      } catch (_) { /* best-effort refresh */ }
    };
    window.addEventListener("nfcs:ai-action-executed", onAiExecuted);
    return () => window.removeEventListener("nfcs:ai-action-executed", onAiExecuted);
  }, [refreshDashboard, loadStock, loadLowStock, loadIngredients, loadStockHistory]);

  // Session 40.1 — when queued stock events land on the server (this device
  // came back online), refresh the stock lists so the next absolute edit /
  // count starts from the balance the server now holds — not a stale one
  // that would only turn into a `balance_moved` exception.
  useEffect(() => {
    const onFlushed = (ev) => {
      const results = (ev?.detail?.results || []);
      if (!results.some((r) => r && (r.status === "accepted" || r.status === "duplicate" || r.status === "rejected"))) return;
      try {
        if (ingredientsLoaded) loadIngredients();
        loadLowStock();
        if (typeof loadStockHistory === "function") loadStockHistory();
      } catch (_) { /* best-effort refresh */ }
    };
    window.addEventListener(EVENT_SYNC_FLUSHED, onFlushed);
    return () => window.removeEventListener(EVENT_SYNC_FLUSHED, onFlushed);
  }, [ingredientsLoaded, loadIngredients, loadLowStock, loadStockHistory]);


  // Session 73 — "who has ever cleared voids" quick-search (thief hunt).
  const [voidClearers, setVoidClearers] = useState([]);
  const loadVoidClearers = useCallback(async () => {
    try {
      const r = await http.get("/owner/audit-log/void-clearers", { headers: authHeaders });
      setVoidClearers(r.data?.clearers || []);
    } catch (_) { /* best effort */ }
  }, [authHeaders]);
  const jumpToVoidClears = useCallback((clearer) => {
    setAuditRange("all");
    setAuditActionFilter("voided_orders_cleared");
    setAuditUserFilter(clearer?.user || "");
    setAuditQuery("");
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const params = { limit: auditRange === "all" ? 1000 : 500 };
      if (auditDateRange.start) params.start_date = auditDateRange.start;
      if (auditDateRange.end) params.end_date = auditDateRange.end;
      if (auditUserFilter.trim()) params.user = auditUserFilter.trim();
      if (auditActionFilter && auditActionFilter !== "all") params.action = auditActionFilter;
      if (auditQuery.trim()) params.query = auditQuery.trim();
      const r = await http.get("/owner/audit-log", { headers: authHeaders, params });
      setAuditEntries(r.data.entries || []);
      // Catalog only when fetched first time so filter dropdown isn't
      // overwritten with whatever the current filtered window contains.
      if (!auditCatalog.length) setAuditCatalog(r.data.action_catalog || []);
      setAuditLoaded(true);
      loadVoidClearers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load audit log.");
    }
  }, [authHeaders, auditRange, auditDateRange.start, auditDateRange.end, auditUserFilter, auditActionFilter, auditQuery, auditCatalog.length, loadVoidClearers]);

  // ── Refund ─────────────────────────────────────────────────────────────
  const submitPaymentCorrection = async () => {
    const v = paymentCorrection.values;
    const ord = paymentCorrection.order;
    if (!ord) return;
    if (!v.manager_pin) { toast.error("Enter the manager PIN."); return; }
    const m = v.new_method;
    if (!["cash", "card", "split"].includes(m)) { toast.error("Pick a payment method."); return; }
    const cashNum = Number(v.cash_paid || 0);
    const cardNum = Number(v.card_paid || 0);
    if (m === "cash" && cashNum <= 0) { toast.error("Cash amount must be greater than zero."); return; }
    if (m === "card" && cardNum <= 0) { toast.error("Card amount must be greater than zero."); return; }
    if (m === "split" && (cashNum <= 0 || cardNum <= 0)) { toast.error("Split needs both cash AND card amounts."); return; }
    setPaymentCorrection((d) => ({ ...d, saving: true }));
    try {
      // Use the full-fidelity endpoint (method + amount editing). The
      // legacy /payment-method endpoint only swapped methods at the same
      // total, which is too restrictive when staff need to fix the
      // recorded total too.
      await http.post(
        `/owner/orders/${ord.id}/correct-payment`,
        {
          manager_pin: v.manager_pin,
          new_method: m,
          cash_paid: (m === "cash" || m === "split") ? cashNum : 0,
          card_paid: (m === "card" || m === "split") ? cardNum : 0,
          reason: (v.reason || "").trim() || "Payment correction",
        },
        { headers: authHeaders },
      );
      toast.success("Payment corrected.");
      setPaymentCorrection({ open: false, order: null, values: { new_method: "cash", reason: "", manager_pin: "", cash_paid: "", card_paid: "" }, saving: false });
      if (typeof loadAudit === "function") loadAudit();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to correct payment.");
      setPaymentCorrection((d) => ({ ...d, saving: false }));
    }
  };

  const submitReopenTable = async () => {
    const v = reopenTable.values;
    const ord = reopenTable.order;
    if (!ord) return;
    if (!v.manager_pin || v.manager_pin.length < 4) { toast.error("Enter the manager PIN."); return; }
    setReopenTable((d) => ({ ...d, saving: true }));
    try {
      await http.post(
        `/owner/orders/${ord.id}/reopen-table`,
        { manager_pin: v.manager_pin, reason: (v.reason || "").trim() },
        { headers: authHeaders }
      );
      toast.success("Table reopened. Staff can now add corrective orders.");
      setReopenTable({ open: false, order: null, values: { manager_pin: "", reason: "" }, saving: false });
      setOrderReceipt({ open: false, order: null });
      if (typeof loadAudit === "function") loadAudit();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reopen table.");
      setReopenTable((d) => ({ ...d, saving: false }));
    }
  };

  const submitRefund = async () => {
    const v = refundDialog.values;
    const ord = refundDialog.order;
    if (!ord) return;
    if (!v.pin || v.pin.length < 4) { toast.error("Manager PIN required."); return; }
    if (!v.reason) { toast.error("Pick a refund reason."); return; }
    if (v.reason === "other" && !(v.reason_other || "").trim()) { toast.error("Please type a reason for ‘Other’."); return; }
    const amt = Number(v.amount || 0);
    if (!amt || amt <= 0) { toast.error("Enter a refund amount."); return; }
    const reasonOut = v.reason === "other" ? v.reason_other.trim() : (REFUND_REASON_LABELS[v.reason] || v.reason);
    setRefundDialog((d) => ({ ...d, saving: true }));
    try {
      const body = {
        manager_pin: v.pin,
        amount: amt,
        reason: reasonOut,
        user: v.user || null,
        notes: v.notes || null,
      };
      const r = refundDialog.editing
        ? await http.patch(`/owner/orders/${ord.id}/refund`, body, { headers: authHeaders })
        : await http.post(`/owner/orders/${ord.id}/refund`, body, { headers: authHeaders });
      toast.success(refundDialog.editing ? "Refund updated." : "Refund issued.");
      setRefundDialog({ open: false, order: null, editing: false, values: { amount: "", reason: "wrong_item", reason_other: "", pin: "", user: "", notes: "" }, saving: false });
      // Refresh the receipt dialog with the latest order payload.
      try {
        const fresh = await http.get(`/owner/orders/${ord.id}`, { headers: authHeaders });
        setOrderReceipt({ open: true, order: fresh.data.order });
      } catch (_) { /* ignore */ }
      if (auditLoaded) loadAudit();
    } catch (error) {
      toast.error(error.response?.data?.detail || (refundDialog.editing ? "Unable to update refund." : "Unable to issue refund."));
      setRefundDialog((d) => ({ ...d, saving: false }));
    }
  };

  // ── Void with reason ──────────────────────────────────────────────────
  const submitVoid = async () => {
    const v = voidDialog.values;
    if (!v.pin || v.pin.length < 4) { toast.error("Manager PIN required."); return; }
    if (!v.reason) { toast.error("Pick a reason."); return; }
    setVoidDialog((d) => ({ ...d, saving: true }));
    try {
      // Session 40 — offline-safe void (HTTP with change_id; queued when the
      // connection is down; the server applies each change_id once).
      const known = (orders || []).find((o) => o && String(o.id) === String(voidDialog.order_id));
      const body = { void_password: v.pin, reason: VOID_REASON_LABELS[v.reason] || v.reason };
      const res = await performStockAction({
        http, authHeaders, path: `/staff/orders/${voidDialog.order_id}/void`, body,
        eventType: EventType.ORDER_VOIDED, eventPayload: { order_id: String(voidDialog.order_id), order_change_id: known?._change_id || null, ...body },
        session, parentChangeId: known?._change_id || null,
      });
      if (res.status === "queued") toast.info("Void saved on this device — it will sync when the connection returns.");
      else toast.success("Order voided.");
      setVoidDialog({ open: false, order_id: "", order_label: "", values: { pin: "", reason: "wrong_item" }, saving: false });
      if (typeof fetchDashboard === "function") fetchDashboard(false); // eslint-disable-line no-undef
      if (auditLoaded) loadAudit();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to void.");
      setVoidDialog((d) => ({ ...d, saving: false }));
    }
  };

  // Session 30.39 — bulk wipe of the fully voided orders in view.
  const clearAllVoids = async (orderIds) => {
    if (!orderIds?.length) { setClearVoidsDialog({ open: false, password: "", submitting: false }); return; }
    setClearVoidsDialog((d) => ({ ...d, submitting: true }));
    try {
      const r = await http.post("/owner/voided-orders/clear", { void_password: clearVoidsDialog.password, order_ids: orderIds }, { headers: authHeaders });
      const n = r.data?.deleted ?? 0;
      const gone = new Set(orderIds);
      setVoidedOrders((current) => current.filter((o) => !gone.has(o.id)));
      setOrders((current) => current.filter((o) => !gone.has(o.id)));
      setClearVoidsDialog({ open: false, password: "", submitting: false });
      toast.success(`${n} voided order${n === 1 ? "" : "s"} permanently deleted.`);
    } catch (error) {
      setClearVoidsDialog((d) => ({ ...d, submitting: false }));
      toast.error(error.response?.data?.detail || "Unable to clear voided orders.");
    }
  };

  const deleteVoidedOrder = async (orderId) => {
    try {
      await http.delete(`/owner/voided-orders/${orderId}`, { headers: authHeaders });
      setVoidedOrders((current) => current.filter((o) => o.id !== orderId));
      toast.success("Voided order removed from history.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete.");
    }
  };

  // "Hard delete" — wipes an order (active or voided) permanently. Gated by
  // the void password because it disappears from the bookkeeper's view.
  // Stock is NOT auto-restored — owners can adjust manually if needed.
  const [hardDeleteDialog, setHardDeleteDialog] = useState({ open: false, order: null, password: "", reason: "", submitting: false });

  const openHardDeleteDialog = (order) => {
    setHardDeleteDialog({ open: true, order, password: "", reason: "", submitting: false });
  };

  // Reload cash data when the manager changes the date range filter.
  useEffect(() => {
    if (!cashLoaded) return;
    loadCash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashDateRange.start, cashDateRange.end]);

  // Reload stock history when its range / search changes.
  useEffect(() => {
    if (!stockHistoryLoaded) return;
    loadStockHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockHistoryDateRange.start, stockHistoryDateRange.end, stockHistoryQuery, stockHistoryDept]);

  // Session 50 — the collapsible "Inventory usage" / "All items sold"
  // sections inside Stock History follow the same date filter.
  const [stockHistoryUsageOpen, setStockHistoryUsageOpen] = useState(false);
  const [stockHistorySoldOpen, setStockHistorySoldOpen] = useState(false);
  useEffect(() => {
    if (stockHistoryUsageOpen) loadInventoryUsage(null, stockHistoryDateRange);
    if (stockHistorySoldOpen) loadStockItemsSold(stockHistoryDateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockHistoryDateRange.start, stockHistoryDateRange.end, stockHistoryUsageOpen, stockHistorySoldOpen]);

  // Session 31.14 — the low-stock banner hands `stockLowOnly` to the
  // IngredientsPanel, which switches its own filter to "Low on the floor"
  // and then calls back to reset the flag.

  // Reload audit log when its filters change.
  useEffect(() => {
    if (!auditLoaded) return;
    loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditDateRange.start, auditDateRange.end, auditUserFilter, auditActionFilter, auditQuery]);

  const submitHardDelete = async () => {
    const { order, password, reason } = hardDeleteDialog;
    if (!order) return;
    if (!password || password.length < 4) {
      toast.error("Enter the void password (min 4 chars).");
      return;
    }
    setHardDeleteDialog((s) => ({ ...s, submitting: true }));
    try {
      // Session 47 — a grouped receipt row (every round on a table) deletes
      // ALL of its underlying orders; a plain row deletes just the one.
      const targets = order.__isSessionMerge && Array.isArray(order.__sourceOrders) && order.__sourceOrders.length
        ? order.__sourceOrders.map((so) => so.id).filter(Boolean)
        : [order.id];
      for (const oid of targets) {
        await http.post(
          `/owner/orders/${oid}/hard-delete`,
          { void_password: password, reason: reason || null },
          { headers: authHeaders },
        );
      }
      const gone = new Set(targets);
      // Pull it out of both the receipts feed and the voided feed (might be in either).
      setOrders((current) => current.filter((o) => !gone.has(o.id)));
      setVoidedOrders((current) => current.filter((o) => !gone.has(o.id)));
      toast.success(targets.length > 1
        ? `Receipt #${order.order_number} deleted (${targets.length} rounds).`
        : `Order #${order.order_number || order.id.slice(-6)} deleted.`);
      setHardDeleteDialog({ open: false, order: null, password: "", reason: "", submitting: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete order.");
      setHardDeleteDialog((s) => ({ ...s, submitting: false }));
    }
  };


  // Session 27.2 — LOCAL-FIRST OWNER DASHBOARD.
  //
  // Before the 12 parallel cloud fetches even start, paint the whole
  // dashboard from the local IndexedDB snapshot. On slow / dropped
  // wifi the dashboard renders instantly with the last-known state
  // instead of hanging on a spinner. The cloud fetch still runs in
  // the background to reconcile anything that changed while this
  // device was offline.
  //
  // This effect runs synchronously with mount, before the refresh
  // useEffect below. `primedRef` propagates the "we've painted
  // already, skip the skeleton" signal into refreshDashboard.
  const primedFromLocalRef = useRef(false);
  useEffect(() => {
    if (!session?.token) return;
    let cancelled = false;
    (async () => {
      try {
        const primed = await hasLocalSnapshot();
        if (!primed || cancelled) return;
        const bundle = await loadDashboardBundle();
        if (!bundle || cancelled) return;
        // Session 30.36 — only prime slices the cloud hasn't already
        // delivered. Local IDB reads on a phone can take longer than the
        // network round-trip, and the snapshot is a PARTIAL view (24h of
        // orders / sessions), so letting it land last silently replaced
        // the full cloud dataset. Web and app must show identical data.
        const cloudHas = (k) => { try { return cloudAppliedKeysRef.current.has(k); } catch (_) { return false; } };
        // Menu — categories with items attached, matching the
        // /owner/categories + /owner/menu-items network shape.
        const cats = (bundle.collections?.categories || [])
          .slice()
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        const items = bundle.collections?.menu_items || [];
        if (!cloudHas("categories")) setCategories(cats);
        if (!cloudHas("items")) setMenuItems(items);
        if (!cloudHas("tags")) setTags(bundle.collections?.tags || []);
        if (!cloudHas("sides")) setSides(bundle.collections?.sides || []);
        if (!cloudHas("specials")) setSpecials(bundle.collections?.specials || []);
        if (!cloudHas("sidesCats")) setSidesCategories(bundle.collections?.sides_categories || []);
        if (!cloudHas("departments")) setDepartments(bundle.collections?.departments || []);
        if (!cloudHas("orders")) setOrders(bundle.collections?.orders || []);
        if (!cloudHas("vouchers")) setVouchers(bundle.collections?.vouchers || []);
        // Business settings — what the "Manage business" tab reads.
        if (bundle.business && !cloudHas("settings")) {
          mergeIncomingSettings(bundle.business);
          setCoverImageDraft(bundle.business.cover_image || "");
          setCoverImageFit(bundle.business.cover_image_fit || "cover");
          setLogoImageDraft(bundle.business.logo_image || "");
        }
        // Overview / analytics / context are server-computed and
        // therefore NOT primeable from the snapshot. The background
        // refresh below fills them in — leaving them null just means
        // those specific widgets show a skeleton until the network
        // returns, but the main menu / orders / tables UI is already
        // fully interactive.
        primedFromLocalRef.current = true;
        setLoading(false); // suppress the skeleton — data is on-screen.
      } catch (_) { /* local-first is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [session?.token]);

  useEffect(() => {
    if (!session?.token) {
      navigate("/owner", { replace: true });
      return;
    }
    // If local primed the dashboard already, refresh in the background
    // (no skeleton flash). Otherwise this IS the first paint.
    refreshDashboard(!primedFromLocalRef.current);
    loadLowStock();
    loadAccounts("open");
  }, [navigate, refreshDashboard, session?.token, loadLowStock, loadAccounts]);

  // Re-poll low-stock every 60s while dashboard is mounted so the banner
  // stays fresh after staff have placed orders.
  useEffect(() => {
    if (!session?.token) return;
    const id = window.setInterval(() => loadLowStock(), 60000);
    return () => window.clearInterval(id);
  }, [loadLowStock, session?.token]);

  // Session 41.8 — always re-pull the ingredient list when Prep Sheets is
  // opened so an ingredient you JUST flagged "Prepared in-house" shows up
  // in "Add prep item or task" without a page reload.
  useEffect(() => {
    if (!session?.token) return;
    if (activeTab === "analytics" && analyticsSubtab === "stock" && (stockSubtab === "prep" || stockSubtab === "fallback-overrides")) loadIngredients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockSubtab, analyticsSubtab, activeTab, session?.token]);

  // Session 41.5 — FAST inventory refresh after sales. Every order / payment
  // / stock event that reaches this device's realtime bus (cloud WS or LAN
  // mesh) re-pulls the low-stock banner and — when they're on screen — the
  // Items / Ingredients lists, debounced to 1.2 s so a burst of tickets
  // costs one round-trip instead of waiting for the 60 s poll.
  useEffect(() => {
    if (!session?.token) return undefined;
    let timer = null;
    const kick = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          loadLowStock();
          if (ingredientsLoaded) loadIngredients();
          if (stockLoaded) loadStock();
        } catch (_) { /* best-effort */ }
      }, 1200);
    };
    const RELEVANT = /^(order_created|order_confirmed_by_staff|order_status_changed|order_updated|payment_recorded|table_session_closed|stock_event|production_confirmed|stock_adjusted|stock_transfer|stock_received|stock_waste_recorded|ingredient_policy_changed|items_moved)$/;
    const unsub = realtime.subscribe((msg) => {
      if (msg && RELEVANT.test(String(msg.type || ""))) kick();
    });
    window.addEventListener(LOCAL_EVENT_APPLIED, kick);
    return () => { unsub(); window.removeEventListener(LOCAL_EVENT_APPLIED, kick); if (timer) clearTimeout(timer); };
  }, [session?.token, loadLowStock, loadIngredients, loadStock, ingredientsLoaded, stockLoaded]);

  // Session 35 — Live KPI strip. The Overview cards (open tables, pending
  // orders, live revenue) must behave like a live till, so while the
  // Overview tab is on screen we re-poll the light /owner/dashboard
  // endpoint every 15s (in addition to the event-driven refresh below,
  // which only fires for activity that reaches THIS device's event bus).
  useEffect(() => {
    if (!session?.token || activeTab !== "overview") return;
    let cancelled = false;
    const tick = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const r = await http.get("/owner/dashboard", { headers: authHeaders, timeout: 8000 });
        if (!cancelled && r?.data) setOverview(r.data);
      } catch (_) { /* keep the last known numbers */ }
    };
    const id = window.setInterval(tick, 15000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [session?.token, activeTab, authHeaders]);

  // ── Live updates: listen for LAN mesh / cloud events and debounce
  // a background refresh so the owner dashboard KPIs, overview, and
  // operations metrics stay in sync with floor activity (orders,
  // table closes, payments) without a manual reload.
  useEffect(() => {
    if (!session?.token) return;
    let debounceTimer = null;
    const debouncedRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        refreshDashboard(false); // false = no loading spinner
      }, 2000); // 2s debounce — prevents rapid event storms
    };
    const handler = () => debouncedRefresh();
    window.addEventListener(LOCAL_EVENT_APPLIED, handler);
    window.addEventListener("nfcs:mesh-event", handler);
    return () => {
      window.removeEventListener(LOCAL_EVENT_APPLIED, handler);
      window.removeEventListener("nfcs:mesh-event", handler);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [session?.token, refreshDashboard]);

  useEffect(() => () => {
    if (scanTimeoutRef.current) {
      window.clearTimeout(scanTimeoutRef.current);
    }
  }, []);

  const categoryNameMap = useMemo(() => Object.fromEntries(categories.map((category) => [category.id, category.name])), [categories]);

  const handleLogout = async () => {
    beginLogoutSuppression();
    await exitFullscreenIfActive();
    const tokenSnapshot = session?.token;
    clearSession("owner", "user_logout");
    setSessionState(null);
    navigate("/owner", { replace: true });
    try {
      if (tokenSnapshot) {
        await http.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${tokenSnapshot}` } });
      }
    } catch {
      // no-op — local session already cleared.
    }
  };

  const resetCategoryDialog = () => setCategoryDialog({ open: false, mode: "create", values: { id: "", name: "", description: "", active: true, visible_customer: true, visible_staff: true, group: "other", available_from: "", available_to: "", schedule: { type: "weekly", weekdays: [0, 1, 2, 3, 4, 5, 6], start_time: "", end_time: "" } } });
  const resetItemDialog = () => setItemDialog({ open: false, mode: "create", values: { id: "", category_id: categories[0]?.id || "", name: "", description: "", price: "", cost: "", stock: "", image: "", department: departments[0]?.name || "", active: true, featured: false, notes_mode: "off", sides_config: { selection_mode: "none", free_count: 0, max_select: 1, allowed_side_ids: [], included_side_ids: [] }, prep_prompts: [] } });
  const resetTagDialog = () => {
    setTagDialog({ open: false, mode: "create", values: { id: "", label: "", type: "menu", table_number: "", active: true, code: "" } });
    setAllowManualTagCode(false);
    setIsScanningNfc(false);
  };

  const handleItemImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const optimized = await resizeImageFile(file);
      setItemDialog((current) => ({ ...current, values: { ...current.values, image: optimized } }));
      toast.success("Image ready.");
    } catch (error) {
      toast.error(error.message || "Unable to process image.");
    } finally {
      event.target.value = "";
    }
  };

  const handleCoverImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const optimized = await resizeImageFile(file);
      setCoverImageDraft(optimized);
      toast.success("Cover image ready.");
    } catch (error) {
      toast.error(error.message || "Unable to process image.");
    } finally {
      event.target.value = "";
    }
  };

  const handleLogoImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const optimized = await resizeImageFile(file);
      setLogoImageDraft(optimized);
      toast.success("Menu icon ready.");
    } catch (error) {
      toast.error(error.message || "Unable to process image.");
    } finally {
      event.target.value = "";
    }
  };

  const saveCoverImage = async () => {
    setSaving(true);
    try {
      const response = await http.patch("/owner/business-settings", { cover_image: coverImageDraft || "", cover_image_fit: coverImageFit || "cover", logo_image: logoImageDraft || "" }, { headers: authHeaders });
      setBusinessSettings(response.data.business);
      toast.success("Branding updated.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save cover image.");
    } finally {
      setSaving(false);
    }
  };

  const submitCategory = async () => {
    setSaving(true);
    try {
      const payload = {
        name: categoryDialog.values.name,
        description: categoryDialog.values.description,
        active: true,
        visible_customer: categoryDialog.values.visible_customer !== false,
        visible_staff: categoryDialog.values.visible_staff !== false,
        group: categoryDialog.values.group || "other",
        available_from: categoryDialog.values.available_from || null,
        available_to: categoryDialog.values.available_to || null,
        schedule: categoryDialog.values.schedule || null,
      };
      if (categoryDialog.mode === "create") {
        await http.post("/owner/categories", payload, { headers: authHeaders });
        toast.success("Category created.");
      } else {
        await http.patch(`/owner/categories/${categoryDialog.values.id}`, payload, { headers: authHeaders });
        toast.success("Category updated.");
      }
      resetCategoryDialog();
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save category.");
    } finally {
      setSaving(false);
    }
  };

  // Move a category one slot up or down. Persists the full ordered ID list
  // to the backend in a single round-trip; optimistic-updates the local
  // state so the UI feels instant. Refreshes from server on success to
  // pick up any reconciliation.
  const moveOwnerCategory = async (index, direction) => {
    const next = [...categories];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setCategories(next);
    try {
      await http.patch(
        "/owner/categories/reorder",
        { ids: next.map((c) => c.id) },
        { headers: authHeaders },
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reorder.");
      refreshDashboard();
    }
  };

  // Sides & Extras category helpers — mirror the Item Categories actions
  // 1:1 so the UX stays identical. Backed by the dedicated
  // /owner/sides-categories endpoints introduced alongside this feature.
  const resetSidesCategoryDialog = () => setSidesCategoryDialog({
    open: false,
    mode: "create",
    values: { id: "", name: "", description: "", active: true, visible_customer: true, visible_staff: true, group: "other", available_from: "", available_to: "", schedule: { type: "weekly", weekdays: [0, 1, 2, 3, 4, 5, 6], start_time: "", end_time: "" } },
  });

  const submitSidesCategory = async () => {
    setSaving(true);
    try {
      const payload = {
        name: sidesCategoryDialog.values.name,
        description: sidesCategoryDialog.values.description,
        active: true,
        visible_customer: sidesCategoryDialog.values.visible_customer !== false,
        visible_staff: sidesCategoryDialog.values.visible_staff !== false,
        group: sidesCategoryDialog.values.group || "other",
        available_from: sidesCategoryDialog.values.available_from || null,
        available_to: sidesCategoryDialog.values.available_to || null,
        schedule: sidesCategoryDialog.values.schedule || null,
      };
      if (sidesCategoryDialog.mode === "create") {
        await http.post("/owner/sides-categories", payload, { headers: authHeaders });
        toast.success("Category created.");
      } else {
        await http.patch(`/owner/sides-categories/${sidesCategoryDialog.values.id}`, payload, { headers: authHeaders });
        toast.success("Category updated.");
      }
      resetSidesCategoryDialog();
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save category.");
    } finally {
      setSaving(false);
    }
  };

  const moveOwnerSidesCategory = async (index, direction) => {
    const next = [...sidesCategories];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setSidesCategories(next);
    try {
      await http.patch(
        "/owner/sides-categories/reorder",
        { ids: next.map((c) => c.id) },
        { headers: authHeaders },
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reorder.");
      refreshDashboard();
    }
  };

  const submitItem = async () => {
    setSaving(true);
    try {
      // Normalise sides_config — when the owner has chosen "none" we still
      // send a clean object so the backend overwrites any stale config from
      // a previous edit.
      const sc = itemDialog.values.sides_config || {};
      const allowedIds = Array.isArray(sc.allowed_side_ids) ? [...new Set(sc.allowed_side_ids.map(String))] : [];
      const allowedSet = new Set(allowedIds);
      const includedIds = Array.isArray(sc.included_side_ids)
        ? [...new Set(sc.included_side_ids.map(String).filter((id) => allowedSet.has(id)))]
        : [];
      const sidesConfigPayload = sc.selection_mode && sc.selection_mode !== "none"
        ? {
            selection_mode: sc.selection_mode,
            free_count: Math.max(0, Number(sc.free_count) || 0),
            max_select: Math.max(1, Number(sc.max_select) || 1),
            allowed_side_ids: allowedIds,
            included_side_ids: includedIds,
          }
        : {
            selection_mode: "none",
            free_count: 0,
            max_select: 1,
            allowed_side_ids: [],
            included_side_ids: [],
          };
      // Clean prep prompts: drop empty options, trim labels, guarantee each
      // prompt has at least one option or it's dropped entirely.
      const rawPrompts = Array.isArray(itemDialog.values.prep_prompts) ? itemDialog.values.prep_prompts : [];
      const prepPromptsPayload = rawPrompts
        .map((p) => {
          const options = Array.isArray(p.options)
            ? [...new Set(p.options.map((o) => String(o || "").trim()).filter(Boolean))]
            : [];
          const label = String(p.label || "").trim();
          if (!label || options.length === 0) return null;
          return {
            id: p.id ? String(p.id) : undefined,
            label,
            required: p.required !== false,
            options,
          };
        })
        .filter(Boolean);

      const payload = {
        category_id: itemDialog.values.category_id,
        name: itemDialog.values.name,
        description: itemDialog.values.description,
        price: Number(itemDialog.values.price),
        // Cost is optional — only send when the owner typed a value. Sending
        // `null` explicitly clears a previously-saved cost on edit.
        cost:
          itemDialog.values.cost === "" || itemDialog.values.cost === null || itemDialog.values.cost === undefined
            ? null
            : Number(itemDialog.values.cost),
        // Stock is optional — sending null explicitly clears tracking on
        // edit, sending a number enables tracking. Blank = not tracked.
        stock:
          itemDialog.values.stock === "" || itemDialog.values.stock === null || itemDialog.values.stock === undefined
            ? null
            : Math.max(0, Math.floor(Number(itemDialog.values.stock))),
        image: itemDialog.values.image || null,
        vat_percent: Number(itemDialog.values.vat_percent || 0),
        department: itemDialog.values.department || "kitchen",
        active: Boolean(itemDialog.values.active),
        featured: Boolean(itemDialog.values.featured),
        // Two-toggle visibility. Default true on either omission so the item
        // remains visible everywhere unless the owner explicitly hides it.
        visible_customer: itemDialog.values.visible_customer === false ? false : true,
        visible_staff: itemDialog.values.visible_staff === false ? false : true,
        visible_retail: itemDialog.values.visible_retail === false ? false : true,
        hide_when_out_of_stock: false,
        // Session 47 — out-of-stock items are never hidden; this decides
        // whether they may still be SOLD past zero.
        sell_when_out_of_stock: Boolean(itemDialog.values.sell_when_out_of_stock ?? (itemDialog.values.hide_when_out_of_stock === false)),
        sides_config: sidesConfigPayload,
        prep_prompts: prepPromptsPayload,
        notes_mode: ["off", "staff", "all"].includes(itemDialog.values.notes_mode) ? itemDialog.values.notes_mode : "off",
      };
      if (itemDialog.mode === "create") {
        await http.post("/owner/menu-items", payload, { headers: authHeaders });
        toast.success("Menu item created.");
      } else {
        await http.patch(`/owner/menu-items/${itemDialog.values.id}`, payload, { headers: authHeaders });
        toast.success("Menu item updated.");
      }
      resetItemDialog();
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save menu item.");
    } finally {
      setSaving(false);
    }
  };

  const simulateTagScan = () => {
    if (scanTimeoutRef.current) {
      window.clearTimeout(scanTimeoutRef.current);
    }
    setIsScanningNfc(true);
    scanTimeoutRef.current = window.setTimeout(() => {
      const generatedCode = buildSimulatedNfcCode();
      setTagDialog((current) => ({ ...current, values: { ...current.values, code: generatedCode } }));
      setIsScanningNfc(false);
      toast.success(`Tag captured: ${generatedCode}`);
    }, 1200);
  };

  const [nfcWriteTag, setNfcWriteTag] = useState(null);

  const handleWriteNfc = (tag) => {
    if (!tag || !tag.path) {
      toast.error("This tag is missing a URL — try refreshing.");
      return;
    }
    setNfcWriteTag(tag);
  };

  const submitDepartment = async () => {
    const v = departmentDialog.values;
    const name = (v.name || "").trim();
    if (!name) {
      toast.error("Department name is required.");
      return;
    }
    try {
      let savedId = v.id;
      if (departmentDialog.mode === "create") {
        const created = await http.post(
          "/owner/departments",
          { name, route_to_kitchen: !!v.route_to_kitchen, route_to_bar: !!v.route_to_bar, route_to_staff: !!v.route_to_staff, active: v.active !== false },
          { headers: authHeaders },
        );
        savedId = created.data.department?.id;
        toast.success("Department created");
      } else {
        await http.patch(
          `/owner/departments/${v.id}`,
          { name, route_to_kitchen: !!v.route_to_kitchen, route_to_bar: !!v.route_to_bar, route_to_staff: !!v.route_to_staff, active: v.active !== false },
          { headers: authHeaders },
        );
        toast.success("Department updated");
      }
      // Bulk-assign categories if the user selected any.
      if (savedId && deptCategoryIds.length > 0) {
        try {
          const res = await http.post(
            `/owner/departments/${savedId}/assign-categories`,
            { category_ids: deptCategoryIds },
            { headers: authHeaders },
          );
          if (res.data?.updated) {
            toast.success(`Reassigned ${res.data.updated} item(s) to "${name}".`);
          }
        } catch (err) {
          toast.error(err.response?.data?.detail || "Could not bulk-assign categories.");
        }
      }
      setDepartmentDialog({ open: false, mode: "create", values: { id: "", name: "", route_to_kitchen: false, route_to_bar: false, route_to_staff: false, active: true } });
      setDeptCategoryIds([]);
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save department.");
    }
  };

  const deleteDepartment = async (dept) => {
    if (!window.confirm(`Delete department "${dept.name}"? Menu items must be reassigned first.`)) return;
    try {
      await http.delete(`/owner/departments/${dept.id}`, { headers: authHeaders });
      toast.success("Department deleted");
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete department.");
    }
  };

  // ---- Sides handlers --------------------------------------------------------
  const submitSide = async () => {
    const v = sideDialog.values;
    const name = (v.name || "").trim();
    const priceNum = Number(v.price);
    if (!name) {
      toast.error("Side name is required.");
      return;
    }
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      toast.error("Side price must be 0 or higher.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        description: (v.description || "").trim(),
        price: priceNum,
        active: v.active !== false,
        sides_category_id: (v.sides_category_id || "").trim() || "",
        department: (v.department || "").trim() || "",
        image: v.image || "",
        notes_mode: v.notes_mode || "off",
      };
      if (sideDialog.mode === "create") {
        await http.post("/owner/sides", payload, { headers: authHeaders });
        toast.success("Side added");
      } else {
        await http.patch(`/owner/sides/${v.id}`, payload, { headers: authHeaders });
        toast.success("Side updated");
      }
      setSideDialog({ open: false, mode: "create", values: { id: "", name: "", description: "", price: "", active: true, sides_category_id: "", department: "", image: "", notes_mode: "off" } });
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save side.");
    } finally {
      setSaving(false);
    }
  };

  const deleteSide = async (side) => {
    if (!window.confirm(`Delete side "${side.name}"? It will be removed from any items that offer it.`)) return;
    try {
      await http.delete(`/owner/sides/${side.id}`, { headers: authHeaders });
      toast.success("Side deleted");
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete side.");
    }
  };

  // ---- Specials handlers -----------------------------------------------------
  const resetSpecialDialog = () => setSpecialDialog({
    open: false,
    mode: "create",
    values: {
      id: "",
      name: "",
      description: "",
      kind: "discount",
      linked_item_id: menuItems[0]?.id || "",
      special_price: "",
      price: "",
      image: "",
      department: "Kitchen",
      takeover_category_ids: [],
      takeover_item_ids: [],
      schedule: { type: "weekly", weekdays: [], start_date: "", end_date: "", start_time: "", end_time: "" },
      mode: "additive",
      banner_color: "",
      active: true,
    },
  });

  const submitSpecial = async () => {
    const v = specialDialog.values;
    const name = (v.name || "").trim();
    if (!name) { toast.error("Name is required."); return; }
    if (v.kind === "discount" && (!v.linked_item_id || !v.special_price)) {
      toast.error("Discount specials need a linked item and a special price.");
      return;
    }
    if (v.kind === "standalone" && !v.price) {
      toast.error("Standalone specials need a price.");
      return;
    }
    if (v.kind === "menu_takeover" && (!v.takeover_category_ids?.length && !v.takeover_item_ids?.length)) {
      toast.error("Menu takeover specials need at least one category or item.");
      return;
    }
    if (v.schedule.type === "weekly" && !(v.schedule.weekdays || []).length) {
      toast.error("Pick at least one weekday.");
      return;
    }
    if (v.schedule.type === "date_range" && !v.schedule.start_date && !v.schedule.end_date) {
      toast.error("Provide a start or end date.");
      return;
    }
    setSaving(true);
    try {
      const sched = { type: v.schedule.type };
      if (v.schedule.start_time) sched.start_time = v.schedule.start_time;
      if (v.schedule.end_time) sched.end_time = v.schedule.end_time;
      if (v.schedule.type === "weekly") {
        sched.weekdays = (v.schedule.weekdays || []).map(Number).sort((a, b) => a - b);
      } else {
        if (v.schedule.start_date) sched.start_date = v.schedule.start_date;
        if (v.schedule.end_date) sched.end_date = v.schedule.end_date;
      }
      const payload = {
        name,
        description: (v.description || "").trim(),
        kind: v.kind,
        mode: v.mode,
        active: !!v.active,
        schedule: sched,
        banner_color: v.banner_color || null,
        image: v.image || null,
        department: v.department || null,
      };
      if (v.kind === "discount") {
        payload.linked_item_id = v.linked_item_id;
        payload.special_price = Number(v.special_price);
      } else if (v.kind === "standalone") {
        payload.price = Number(v.price);
      } else if (v.kind === "menu_takeover") {
        payload.takeover_category_ids = v.takeover_category_ids || [];
        payload.takeover_item_ids = v.takeover_item_ids || [];
      }
      if (specialDialog.mode === "create") {
        await http.post("/owner/specials", payload, { headers: authHeaders });
        toast.success("Special created.");
      } else {
        await http.patch(`/owner/specials/${v.id}`, payload, { headers: authHeaders });
        toast.success("Special updated.");
      }
      resetSpecialDialog();
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save special.");
    } finally {
      setSaving(false);
    }
  };

  const deleteSpecial = async (special) => {
    if (!window.confirm(`Delete special "${special.name}"?`)) return;
    try {
      await http.delete(`/owner/specials/${special.id}`, { headers: authHeaders });
      toast.success("Special deleted");
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete special.");
    }
  };

  // ── Voucher codes CRUD ────────────────────────────────────────────
  const loadVouchers = useCallback(async () => {
    try {
      const res = await http.get("/owner/vouchers", { headers: authHeaders });
      setVouchers(res.data.vouchers || []);
    } catch (_e) { /* ignore */ }
  }, [authHeaders]);

  const openCreateVoucherDialog = () => {
    setVoucherDialog({
      open: true,
      saving: false,
      values: { code: "", discount_amount: "", usage_type: "single", max_uses: "1", expires_at: "", active: true },
    });
  };

  const submitVoucher = async () => {
    const v = voucherDialog.values;
    const code = (v.code || "").trim();
    const amt = parseFloat(v.discount_amount);
    if (!code) { toast.error("Enter a voucher code."); return; }
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a discount amount greater than 0."); return; }
    const usage_type = v.usage_type || "single";
    const payload = {
      code,
      discount_amount: amt,
      usage_type,
      active: Boolean(v.active),
    };
    if (usage_type === "multi") {
      const maxUses = parseInt(v.max_uses, 10);
      if (!Number.isFinite(maxUses) || maxUses < 1) { toast.error("Enter a valid maximum number of uses."); return; }
      payload.max_uses = maxUses;
    }
    if (v.expires_at) payload.expires_at = v.expires_at;
    setVoucherDialog((prev) => ({ ...prev, saving: true }));
    try {
      await http.post("/owner/vouchers", payload, { headers: authHeaders });
      toast.success("Voucher created");
      setVoucherDialog({ open: false, saving: false, values: { code: "", discount_amount: "", usage_type: "single", max_uses: "1", expires_at: "", active: true } });
      loadVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create voucher.");
      setVoucherDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const toggleVoucherActive = async (voucher) => {
    try {
      await http.patch(`/owner/vouchers/${voucher.id}`, { active: !voucher.active }, { headers: authHeaders });
      loadVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update voucher.");
    }
  };

  const deleteVoucher = async (voucher) => {
    if (!window.confirm(`Delete voucher "${voucher.code}"?`)) return;
    try {
      await http.delete(`/owner/vouchers/${voucher.id}`, { headers: authHeaders });
      toast.success("Voucher deleted");
      loadVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete voucher.");
    }
  };

  const submitTag = async () => {
    const tableName = tagDialog.values.label.trim();
    if (!tableName) {
      toast.error("Enter a table name or number for the tag.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: tableName,
        type: "table",
        table_number: tableName,
        active: Boolean(tagDialog.values.active),
      };
      if (tagDialog.mode === "create") {
        await http.post("/owner/tags", payload, { headers: authHeaders });
        toast.success("Tag created.");
      } else {
        await http.patch(`/owner/tags/${tagDialog.values.id}`, payload, { headers: authHeaders });
        toast.success("Tag updated.");
      }
      resetTagDialog();
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save tag.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await http.delete(deleteTarget.endpoint, { headers: authHeaders });
      setDeleteTarget(null);
      toast.success(`${deleteTarget.label} removed.`);
      refreshDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Unable to delete ${deleteTarget.label.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  const updateAccess = async () => {
    if (!accessForm.owner_password && !accessForm.staff_password && !accessForm.kitchen_password && !accessForm.bar_password && !accessForm.void_password && !accessForm.runner_password && !accessForm.retail_password) {
      toast.error("Enter at least one password to update.");
      return;
    }
    setSaving(true);
    try {
      const cleanedPayload = Object.fromEntries(
        Object.entries(accessForm).filter(([, v]) => v && String(v).trim().length > 0)
      );
      const response = await http.post("/owner/access", cleanedPayload, { headers: authHeaders });
      toast.success(response.data.message || "Passwords updated.");
      // IMPORTANT: do NOT log the caller out. Backend already does
      // scope-aware session invalidation (only affected legacy sessions
      // are cleared; the calling owner / manager stays signed in).
      setAccessForm({ owner_password: "", staff_password: "", kitchen_password: "", bar_password: "", void_password: "", runner_password: "", retail_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update access.");
    } finally {
      setSaving(false);
    }
  };

  const copyTagUrl = async (path) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      toast.success("Copied scan URL");
    } catch {
      toast.error("Unable to copy URL.");
    }
  };

  if (!session?.token) return null;

  return (
    <main className="min-h-screen bg-background px-4 py-6 sm:px-6 sm:py-8">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
        <header className="flex flex-col gap-4 rounded-[28px] border border-border bg-card p-5 shadow-[0_1px_0_rgba(0,0,0,0.04)] sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <Badge className="bg-[hsl(var(--primary))] text-primary-foreground">Owner / Manager dashboard</Badge>
            <h1 className="mt-3 font-['Space_Grotesk'] text-3xl font-semibold tracking-tight">{context?.business?.name || "Loading…"}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <MeshStatusPill />
            {/* Session 41.5 — this device's sync queue (replaces the Sync tab
                that used to sit inside Inventory → Stock Locations). */}
            <DeviceSyncPill
              http={http}
              authHeaders={authHeaders}
              canManage={!sessionUser?.role || ["owner", "manager"].includes(String(sessionUser?.role || "").toLowerCase())}
              onStockChanged={async () => {
                try {
                  await loadLowStock();
                  if (ingredientsLoaded) await loadIngredients();
                  if (stockHistoryLoaded) loadStockHistory();
                } catch (_) { /* best-effort */ }
              }}
            />
            {/* Session 33 — the AI copilot launcher portals itself in here so
                no floating button covers dashboard content. */}
            <div id="ai-copilot-slot" className="contents" data-testid="ai-copilot-slot" />
            <ThemeToggleButton />
            <FullscreenToggleButton />
            {/* Back button removed per user request — admin dashboard
                still has its own back button (it's a multi-page flow). */}
            <Button variant="outline" className="rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive" onClick={handleLogout} data-testid="owner-logout-button">
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>

        {/* Session 63.E — ONE sticky notification stack: stock fallback,
            low stock and the live notification cards. Every card is
            clickable (deep-link) and dismissable ("Noted"). */}
        <div className="sticky top-2 z-30 space-y-2" data-testid="owner-alert-stack">
        {stockEvents.events.filter((e) => e.type === "shortfall" || e.first_in_episode).length > 0 && fallbackBannerDismissedKey !== fallbackBannerKey ? (
          <div className="relative" data-testid="stock-fallback-banner-wrap">
          <button
            type="button"
            onClick={() => {
              // Session 41.7 — jump straight into Stock Locations → Fallback
              // activity (where these events are listed and acknowledged).
              setStockLocationsInitialView("fallback");
              setStockLocationsPanelNonce((n) => n + 1);
              setActiveTab("analytics");
              setAnalyticsSubtab("stock");
              setStockSubtab("locations");
              setActiveGroup("inventory");
              if (!ingredientsLoaded) loadIngredients();
            }}
            className={`group flex w-full items-start gap-3 rounded-[24px] border p-4 pr-14 text-left shadow-md backdrop-blur-md transition-colors ${stockEvents.events.some((e) => e.type === "shortfall") ? "border-destructive/40 bg-destructive/10 hover:bg-destructive/15" : "border-amber-300 bg-amber-50/95 hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950/80 dark:hover:bg-amber-950/90"}`}
            data-testid="stock-fallback-banner"
          >
            <div className={`mt-0.5 rounded-full p-2 ${stockEvents.events.some((e) => e.type === "shortfall") ? "bg-destructive/20 text-destructive" : "bg-amber-200 text-amber-900 dark:bg-amber-900/60 dark:text-amber-200"}`}>
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">
                {stockEvents.events.some((e) => e.type === "shortfall") ? "Stock shortfall — sales went through with no stock" : "Stock fallback — preferred location ran dry"}
              </p>
              <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                {stockEvents.events.filter((e) => e.type === "shortfall" || e.first_in_episode).slice(0, 3).map((e) => `${e.ingredient_name}: ${e.type === "shortfall" ? `${e.quantity_label || `${e.quantity} ${e.unit}`} short` : `${e.quantity_label || `${e.quantity} ${e.unit}`} drawn from ${(e.from_locations || []).map((f) => f.name).join(", ")}`}`).join(" · ")}
                {stockEvents.counts?.notify > 3 ? ` · +${stockEvents.counts.notify - 3} more` : ""}
              </p>
            </div>
            <span className="hidden text-xs font-medium underline-offset-2 group-hover:underline sm:inline">Open fallback activity →</span>
          </button>
          <button
            type="button"
            onClick={() => setFallbackBannerDismissedKey(fallbackBannerKey)}
            className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Noted — dismiss the stock fallback notice until something new happens"
            title="Noted"
            data-testid="stock-fallback-banner-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
          </div>
        ) : null}
        {lowStock.total > 0 && !lowStockBannerHidden ? (
          <div className="relative" data-testid="low-stock-banner-wrap">
          <button
            type="button"
            onClick={() => {
              // Session 63.E — owner asked for low stock to open Stock History.
              setActiveGroup("inventory");
              setActiveTab("analytics");
              setAnalyticsSubtab("stock");
              setStockSubtab("history");
              if (!stockHistoryLoaded) loadStockHistory();
              if (!ingredientsLoaded) loadIngredients();
              setStockLowOnly(true);
            }}
            className="group flex w-full items-start gap-3 rounded-[24px] border border-amber-300 bg-amber-50/95 p-4 pr-14 text-left shadow-md backdrop-blur-md transition-colors hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950/80 dark:hover:bg-amber-950/90"
            data-testid="low-stock-banner"
            aria-label="Low stock — pinned alert"
          >
            <div className="mt-0.5 rounded-full bg-amber-200 p-2 text-amber-900 dark:bg-amber-900/60 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                Low stock — {lowStock.total} {lowStock.total === 1 ? "line needs" : "lines need"} attention
              </p>
              <p className="mt-0.5 line-clamp-2 text-xs text-amber-800 dark:text-amber-200">
                {[
                  ...lowStock.ingredients.slice(0, 3).map((x) => `${x.name} (${x.stock} ${x.unit}${Number(x.back_of_house_stock) > 0 ? ` · ${x.back_of_house_stock} back of house` : ""})`),
                  ...lowStock.items.slice(0, 3).map((x) => `${x.name} (${x.stock} left)`),
                ].join(" · ")}
                {lowStock.total > 6 ? ` · +${lowStock.total - 6} more` : ""}
              </p>
            </div>
            <span className="hidden text-xs font-medium text-amber-900 underline-offset-2 group-hover:underline dark:text-amber-100 sm:inline">
              Open stock history →
            </span>
          </button>
          <button
            type="button"
            onClick={dismissLowStockBanner}
            className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-amber-900 transition-colors hover:bg-amber-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]/40 dark:text-amber-100 dark:hover:bg-amber-900/60"
            aria-label="Dismiss low-stock banner until something new goes low"
            title="Dismiss — comes back as soon as a new line goes low or a count changes"
            data-testid="low-stock-banner-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
          </div>
        ) : null}

        {/* Session 74 — tickets piling up on an unused Kitchen / Bar screen. */}
        {!sessionUser || ["owner", "manager"].includes(sessionUser.role) ? (
          <StationHealthBanner
            http={http}
            authHeaders={authHeaders}
            onOpenDepartments={() => { setActiveGroup("menu"); setActiveTab("manage-menu"); setManageMenuTab("departments"); }}
          />
        ) : null}

        {/* Session 63.E — notification stack (replaces the Local activity strip).
            Tap a card → jump to the right screen; ✕ → noted. */}
        {!sessionUser || ["owner", "manager", "shift_leader"].includes(sessionUser.role) ? (
          <OwnerNotifications
            http={http}
            authHeaders={authHeaders}
            businessId={session?.business?.id || session?.business_id || session?.business?.slug || null}
            canApprove={!sessionUser || ["owner", "manager", "shift_leader"].includes(sessionUser.role)}
            onNavigate={(target) => {
              const key = target?.key || "overview";
              const openStock = (sub) => { setActiveGroup("inventory"); setActiveTab("analytics"); setAnalyticsSubtab("stock"); setStockSubtab(sub); if (!ingredientsLoaded) loadIngredients(); };
              if (key === "approvals") { setFallbackHistoryView("approvals"); openStock("fallback-history"); return; }
              if (key === "prep") { openStock("prep"); return; }
              if (key === "stock-history") { openStock("history"); if (!stockHistoryLoaded) loadStockHistory(); return; }
              if (key === "fallback") {
                setStockLocationsInitialView("fallback");
                setStockLocationsPanelNonce((n) => n + 1);
                openStock("locations");
                return;
              }
              if (key === "order" || key === "live-orders") {
                setActiveGroup("operations");
                setActiveTab("orders");
                const sub = key === "order" ? "voided" : "active";
                setOrdersSubtab(sub);
                if (sub === "voided") loadVoidedOrders();
                setHighlightOrderId(target.order_id || target.order_number || null);
                setTimeout(() => setHighlightOrderId(null), 20000);
                return;
              }
              setActiveGroup("overview"); setActiveTab("overview");
            }}
          />
        ) : null}
        </div>

        <Tabs value={activeTab} onValueChange={(v) => {
          // Reports tab is no longer password-gated — controlled by role.
          if (v === "staff" && !staffRosterLoaded) loadStaffRoster();
          if (v === "staff" && !userAccountsLoaded) loadUserAccounts();
          if (v === "accounts" && !accountsLoaded) loadAccounts(accountsFilter);
          if (v === "reservations" && !ownerReservationsLoaded) loadOwnerReservations(ownerReservationsFilter);
          if (v === "cash" && !cashLoaded) loadCash();
          setActiveTab(v);
        }}>
          {(() => {
            // ── Grouped owner navigation ──────────────────────────────
            // 7 top-level groups, each holding its existing tabs as
            // children. Children drive `activeTab` + (for Reports /
            // Inventory) the inner analytics / stock subtab so we can
            // deep-link straight to e.g. Stock Take History without
            // touching the underlying screens or workflows.
            const ROLE_SECTIONS = {
              owner: ["overview","operations","staff_access","menu","finance","reports","inventory","settings"],
              manager: ["overview","operations","staff_access","menu","finance","reports","inventory","settings"],
              shift_leader: ["overview","operations","staff_access","reports","inventory"],
              supervisor: ["overview","operations"],
            };
            // Owner is always all-access. For everyone else we prefer the
            // user-specific override (`allowed_sections` set when the
            // owner customised the user) and fall back to the role's
            // defaults.
            // Session 46 — owner-ROLE user accounts honour a custom section
            // list too (the business-password login has no user → full).
            const visibleSections = (sessionUser && Array.isArray(sessionUser.allowed_sections) && sessionUser.allowed_sections.length)
              ? sessionUser.allowed_sections
              : (sessionUser ? (ROLE_SECTIONS[sessionUser.role] || ROLE_SECTIONS.owner) : ROLE_SECTIONS.owner);
            const NAV_GROUPS_ALL = [
              { id: "overview", label: "Overview", children: [
                { label: "Overview", tab: "overview" },
                { label: "Tabs", tab: "accounts" },
                { label: "Reservations", tab: "reservations" },
              ] },
              { id: "operations", label: "Operations", children: [
                { label: "Orders", tab: "orders", perm: "operations" },
                { label: "Staff & Access", tab: "staff", perm: "staff_access" },
              ]},
              // Session 31.19 — Finance now holds Cash Management AND every
              // report (one group fewer, in the order the owner asked for:
              // Overview · Operations · Finance · Menu · Inventory · Settings).
              // Children keep their own permission: `finance` for cash,
              // `reports` for the report screens, so a shift leader who may
              // see reports but not cash still gets the right subset.
              { id: "finance", label: "Finance", perms: ["finance", "reports"], children: [
                { label: "Cash Management", tab: "cash", perm: "finance" },
                { label: "Business Summary", tab: "analytics", analyticsSubtab: "business-summary", perm: "reports" },
                // Session 74 — what should be in the bank / counted as cash, day by day.
                { label: "Bank Reconciliation", tab: "analytics", analyticsSubtab: "bank-recon", perm: "reports" },
                // Session 31.5 / 31.14 — owner-defined running costs + break-even, right next to the summary.
                { label: "Expenses & Profit", tab: "analytics", analyticsSubtab: "profit", ownerManagerOnly: true, perm: "reports" },
                // Session 38 — Sales × Margin per item with transparent cut-offs.
                { label: "Menu Engineering", tab: "analytics", analyticsSubtab: "menu-engineering", ownerManagerOnly: true, perm: "reports" },
                { label: "Staff Sales", tab: "analytics", analyticsSubtab: "staff-sales", perm: "reports" },
                { label: "Receipts", tab: "analytics", analyticsSubtab: "receipts", perm: "reports" },
                // Session 41.5 — Report Schedules moved to Inventory (replaces the old Alerts tab).
                // Session 30.40 — Excel-style workspace (Owner + Manager only).
                { label: "Spreadsheets", tab: "analytics", analyticsSubtab: "spreadsheets", ownerManagerOnly: true, perm: "reports" },
                { label: "Audit Log", tab: "analytics", analyticsSubtab: "audit", perm: "reports" },
                // Session 31 — immutable log of everything the AI did.
              ]},
              { id: "menu", label: "Menu", children: [{ label: "Manage Menu", tab: "manage-menu" }] },
              { id: "inventory", label: "Inventory", children: [
                // Session 63.C — order: Items · Stock Take · Stock Locations ·
                // Ingredients · Recipes, then the analysis panels. "Move stock"
                // lives under Stock Take; Fallback approvals + "When service
                // stock runs out" are ONE "Fallback History" screen; Variance
                // and Waste are ONE screen.
                { label: "Items", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "items" },
                { label: "Stock Take", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "takes" },
                { label: "Stock Locations", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "locations" },
                { label: "Ingredients", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "ingredients" },
                { label: "Recipes", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "recipes" },
                { label: "Prep Sheets / Stock Reads", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "prep", ownerManagerOnly: true },
                { label: "Fallback History", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "fallback-history", ownerManagerOnly: true },
                { label: "Variance & Waste", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "variance-waste" },
                { label: "Receiving", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "receiving" },
                { label: "Stock History", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "history" },
                { label: "Cost Coverage", tab: "analytics", analyticsSubtab: "stock", stockSubtab: "coverage", ownerManagerOnly: true },
              ]},
              // Session 63.E — Report Schedules promoted to its own top-level
              // group (Overview · Operations · Finance · Menu · Inventory ·
              // Report Schedules · Settings).
              { id: "schedules", label: "Report Schedules", perms: ["reports"], children: [
                { label: "Report Schedules", tab: "analytics", analyticsSubtab: "schedules", perm: "reports" },
              ]},
              { id: "settings", label: "Settings", children: [
                { label: "Links", tab: "links" },
                { label: "Tags", tab: "tags" },
                { label: "Branding", tab: "branding" },
                { label: "Payment Gateways", tab: "payment_gateways" },
                // Session 31 — voice / text responses + what the AI learned.
                { label: "AI Assistant", tab: "ai-settings", aiOnly: true },
              ]},
            ];
            // Filter both top-level groups and the children inside
            // Operations (Orders is gated by "operations", Staff & Access
            // by the new "staff_access" permission so they can be
            // toggled independently).
            const isOwnerOrManager = !sessionUser || ["owner", "manager"].includes(sessionUser.role);
            const NAV_GROUPS = NAV_GROUPS_ALL
              .map((g) => {
                if (g.id === "operations") {
                  const kids = g.children.filter((c) => !c.perm || visibleSections.includes(c.perm));
                  return kids.length ? { ...g, children: kids } : null;
                }
                // Session 31.19 — a group may span several permission
                // sections (Finance = finance + reports); show it when the
                // user has any of them and filter its children per-child.
                const groupPerms = g.perms || [g.id];
                if (!groupPerms.some((perm) => visibleSections.includes(perm))) return null;
                const kids = g.children.filter((c) =>
                  (!c.perm || visibleSections.includes(c.perm))
                  && (!c.ownerManagerOnly || isOwnerOrManager)
                  && (!c.aiOnly || (aiContext.enabled && aiContext.allowed)));
                return kids.length ? { ...g, children: kids } : null;
              })
              .filter(Boolean);
            const currentGroup = NAV_GROUPS.find((g) => g.id === activeGroup) || NAV_GROUPS[0];
            const goToChild = (c) => {
              // Reports tab is no longer password-gated — visibility is
              // controlled entirely by role. The analytics-password prompt
              // has been removed.
              // Categories are loaded via the main data polling cycle
              if (c.tab === "staff" && !staffRosterLoaded) loadStaffRoster();
              if (c.tab === "staff" && !userAccountsLoaded) loadUserAccounts();
              if (c.tab === "accounts" && !accountsLoaded) loadAccounts(accountsFilter);
              if (c.tab === "reservations" && !ownerReservationsLoaded) loadOwnerReservations(ownerReservationsFilter);
              if (c.tab === "cash" && !cashLoaded) loadCash();
              if (c.tab === "analytics") {
                const sub = c.analyticsSubtab || "reports";
                setAnalyticsSubtab(sub);
                if (sub === "service-times" && !serviceTimes) loadServiceTimes(analyticsRange);
                if (sub === "service-times" && !orderTimesLoaded) loadOrderTimes(analyticsRange);
                if (sub === "stock" && !stockLoaded) loadStock();
                if (sub === "stock" && !ingredientsLoaded) loadIngredients();
                if (sub === "stock" && !recipesSummaryLoaded) loadRecipesSummary();
                if (sub === "reports" && !growthStats) loadGrowth();
                if (sub === "audit" && !auditLoaded) loadAudit();
                if (sub === "business-summary" && !businessSummary) loadBusinessSummary(bsRange);
                if (sub === "business-summary" && !serviceTimes) loadServiceTimes(analyticsRange);
                if (sub === "business-summary" && !orderTimesLoaded) loadOrderTimes(analyticsRange);
                if (sub === "schedules" && !reportSchedulesLoaded) loadReportSchedules();
                if (c.stockSubtab) {
                  setStockSubtab(c.stockSubtab);
                  if (c.stockSubtab === "history" && !stockHistoryLoaded) loadStockHistory();
                  if (c.stockSubtab === "takes" && !stockTakesLoaded) loadStockTakes();
                }
              }
              if (c.tab === "payment_gateways" && !yocoGatewayLoaded) loadYocoGateway();
              setActiveTab(c.tab);
            };
            // Session 31 — let the AI assistant navigate exactly like a click.
            aiNavRef.current = goToChild;
            const isChildActive = (c) => {
              if (c.tab !== activeTab) return false;
              if (c.tab === "analytics") {
                if ((c.analyticsSubtab || "reports") !== analyticsSubtab) return false;
                if (c.stockSubtab && c.stockSubtab !== stockSubtab) return false;
              }
              return true;
            };
            return (
              <div className="space-y-1.5" data-testid="owner-nav">
                <div className="flex flex-wrap gap-1 rounded-2xl bg-secondary p-1.5" data-testid="owner-nav-groups">
                  {NAV_GROUPS.map((g) => {
                    const active = g.id === activeGroup;
                    return (
                      <button
                        type="button"
                        key={g.id}
                        onClick={() => {
                          setActiveGroup(g.id);
                          // Auto-jump to the group's first child so the
                          // sub-row immediately reflects a valid state.
                          goToChild(g.children[0]);
                        }}
                        className={`min-h-11 rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors ${active ? "bg-[hsl(var(--primary))] text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-card/60 hover:text-foreground"}`}
                        data-testid={`owner-nav-group-${g.id}`}
                      >
                        {g.label}
                      </button>
                    );
                  })}
                </div>
                {currentGroup.children.length > 1 ? (
                  <div className="flex flex-wrap gap-1 rounded-2xl bg-secondary/60 p-1.5" data-testid={`owner-nav-children-${currentGroup.id}`}>
                    {currentGroup.children.map((c) => {
                      const active = isChildActive(c);
                      return (
                        <button
                          type="button"
                          key={`${c.tab}-${c.analyticsSubtab || ""}-${c.stockSubtab || ""}`}
                          onClick={() => goToChild(c)}
                          className={`min-h-10 rounded-xl px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm ${active ? "bg-card text-foreground shadow-sm ring-1 ring-border" : "text-muted-foreground hover:bg-card/60 hover:text-foreground"}`}
                          data-testid={`owner-nav-child-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })()}

          <TabsContent value="overview" className="space-y-4">
            {loading ? (
              <DashboardLoadingState />
            ) : (
              <>
                <SectionHeader eyebrow="Business snapshot" title="Quick view" description="What is on the floor right now — open tables, tickets still being prepared, and live revenue." />
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {[
                    { key: "open-tables", label: "Open tables", value: overview?.stats?.open_tables || 0, icon: CircleDot },
                    {
                      key: "pending-orders",
                      label: "Pending orders",
                      value: overview?.stats?.pending_orders || 0,
                      icon: ClipboardList,
                      // Session 35 — counts tickets still being prepared (kitchen/bar)
                      hint: (overview?.stats?.pending_items || 0) > 0 ? `${overview.stats.pending_items} item${overview.stats.pending_items === 1 ? "" : "s"} still in the kitchen / bar` : "Nothing waiting on the pass",
                    },
                    { key: "menu-items", label: "Menu items", value: overview?.stats?.menu_items || 0, icon: UtensilsCrossed },
                    {
                      key: "live-revenue",
                      label: "Live revenue",
                      value: formatPrice(overview?.stats?.live_revenue || 0),
                      icon: BarChart3,
                      // Session 35 — value of everything on the open tables right now;
                      // drops as tables are closed.
                      hint: (overview?.stats?.open_tables || 0) > 0
                        ? `On ${overview.stats.open_tables} open table${overview.stats.open_tables === 1 ? "" : "s"} · ${formatPrice(overview?.stats?.live_outstanding || 0)} still to collect`
                        : "No open tables right now",
                      live: true,
                    },
                  ].map((item) => (
                    <Card key={item.key} className="rounded-[24px] border-border bg-card" data-testid={`overview-kpi-${item.key}`}>
                      <CardContent className="flex items-center justify-between gap-4 p-5">
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 text-sm text-muted-foreground">
                            {item.label}
                            {item.live ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300" data-testid="overview-live-badge">
                                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                                Live
                              </span>
                            ) : null}
                          </p>
                          <p className="mt-1 text-3xl font-semibold" data-testid={`overview-kpi-${item.key}-value`}>{item.value}</p>
                          {item.hint ? <p className="mt-1 text-xs leading-snug text-muted-foreground" data-testid={`overview-kpi-${item.key}-hint`}>{item.hint}</p> : null}
                        </div>
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-[hsl(var(--primary))]">
                          <item.icon className="h-5 w-5" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {/* Outstanding customer accounts widget — clickable shortcut into the Accounts tab. */}
                {accountsList.summary.open_count > 0 ? (
                  <button
                    type="button"
                    onClick={() => { setActiveTab("accounts"); if (!accountsLoaded) loadAccounts("open"); }}
                    className="flex w-full items-center justify-between gap-4 rounded-[24px] border border-border bg-card p-5 text-left transition hover:border-[hsl(var(--accent))] hover:bg-secondary"
                    data-testid="overview-accounts-widget"
                  >
                    <div>
                      <p className="text-sm text-muted-foreground">Open tabs</p>
                      <p className="mt-1 text-2xl font-semibold">
                        {accountsList.summary.open_count} open · R {accountsList.summary.total_outstanding.toFixed(2)}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">Tap to view + record payments.</p>
                    </div>
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-[hsl(var(--primary))]">
                      <Receipt className="h-5 w-5" />
                    </div>
                  </button>
                ) : null}

                {/* Session 30.10 — Live table management for Owner/Manager.
                    Reuses the Staff dashboard (embedded mode): Active Tables
                    selector → receipt, pending orders with timers, Table
                    actions (split/merge/move/void/account/close), payments,
                    print/email. Opening NEW tables is intentionally hidden. */}
                <Card className="rounded-[24px] border-border bg-card" data-testid="owner-live-tables-card">
                  <CardHeader className="pb-2">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <CardTitle className="text-lg">Live tables</CardTitle>
                        <CardDescription>
                          Manage any open table exactly like staff — view the receipt, see what is pending and for how long, add items, split or merge, take payment, print or email the slip, and close.
                        </CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => runReconcilePaidTables(true)}
                        disabled={reconcileBusy}
                        data-testid="owner-reconcile-paid-tables-button"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        {reconcileBusy && !reconcileOpen ? "Checking…" : "Fix paid tables showing a balance"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {session?.token ? (
                      <StaffDashboardPage embedded embeddedSession={session} />
                    ) : null}
                  </CardContent>
                </Card>
                <Dialog open={dupDialog.open} onOpenChange={(o) => { if (!dupDialog.submitting) setDupDialog((d) => ({ ...d, open: o })); }}>
                  <DialogContent className="max-w-2xl rounded-[24px]" data-testid="owner-duplicates-dialog">
                    <DialogHeader>
                      <DialogTitle>Duplicate orders{dupDialog.data?.label ? ` · ${dupDialog.data.label}` : ""}</DialogTitle>
                      <DialogDescription>
                        Same table, identical items, placed within 20 minutes of each other. The earliest copy is kept; ticked copies are voided with the reason "Duplicate order (glitch recovery)" — exactly like a manual void, logged in the audit trail. Untick anything that was a genuine second round.
                      </DialogDescription>
                    </DialogHeader>
                    {dupDialog.loading ? (
                      <div className="space-y-2"><Skeleton className="h-12 w-full rounded-xl" /><Skeleton className="h-12 w-full rounded-xl" /></div>
                    ) : !dupDialog.data || (dupDialog.data.groups || []).length === 0 ? (
                      <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground" data-testid="owner-duplicates-empty">
                        No duplicate rounds found in this window.{(dupDialog.data?.voided_duplicates || []).length ? ` ${dupDialog.data.voided_duplicates.length} duplicate(s) were already voided by staff.` : ""}
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground" data-testid="owner-duplicates-summary">
                          {dupDialog.data.duplicate_count} suspected duplicate{dupDialog.data.duplicate_count === 1 ? "" : "s"} · {formatPrice(dupDialog.data.duplicate_value)} over-stated in sales{(dupDialog.data.voided_duplicates || []).length ? ` · ${dupDialog.data.voided_duplicates.length} already voided by staff` : ""}
                        </p>
                        <div className="max-h-80 space-y-3 overflow-y-auto pr-1" data-testid="owner-duplicates-list">
                          {dupDialog.data.groups.map((g) => (
                            <div key={g.keep.order_id} className="rounded-xl border border-border p-3 text-sm">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="font-semibold">Table {g.keep.table} · {g.keep.waiter || "—"}</p>
                                  <p className="truncate text-xs text-muted-foreground">{g.keep.items}</p>
                                </div>
                                <Badge variant="outline" className="shrink-0 rounded-lg">{g.span_seconds < 60 ? `${g.span_seconds}s apart` : `${Math.round(g.span_seconds / 60)} min apart`}</Badge>
                              </div>
                              <div className="mt-2 space-y-1.5">
                                <div className="flex items-center justify-between gap-2 rounded-lg bg-emerald-500/10 px-2 py-1.5 text-xs">
                                  <span>Keep · {g.keep.order_number || g.keep.order_id.slice(-6)} · {g.keep.at}</span>
                                  <span className="font-medium">{formatPrice(g.keep.value)}</span>
                                </div>
                                {g.duplicates.map((d) => (
                                  <label key={d.order_id} className="flex cursor-pointer items-center justify-between gap-2 rounded-lg bg-rose-500/10 px-2 py-1.5 text-xs" data-testid={`owner-duplicate-row-${d.order_id}`}>
                                    <span className="flex items-center gap-2">
                                      <Checkbox
                                        checked={dupDialog.selected.has(d.order_id)}
                                        onCheckedChange={(v) => setDupDialog((s) => { const n = new Set(s.selected); if (v) n.add(d.order_id); else n.delete(d.order_id); return { ...s, selected: n }; })}
                                        data-testid={`owner-duplicate-check-${d.order_id}`}
                                      />
                                      Void · {d.order_number || d.order_id.slice(-6)} · {d.at}{d.device && g.keep.device && d.device !== g.keep.device ? " · other device" : ""}
                                    </span>
                                    <span className="font-medium">{formatPrice(d.value)}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="grid gap-1.5">
                          <Label htmlFor="dup-void-pin" className="text-xs">Manager / void PIN</Label>
                          <Input id="dup-void-pin" type="password" value={dupDialog.pin} onChange={(e) => setDupDialog((d) => ({ ...d, pin: e.target.value }))} className="h-10 rounded-xl" data-testid="owner-duplicates-pin" />
                        </div>
                      </>
                    )}
                    <DialogFooter className="gap-2">
                      <Button variant="outline" className="rounded-xl" onClick={() => setDupDialog((d) => ({ ...d, open: false }))} disabled={dupDialog.submitting} data-testid="owner-duplicates-cancel">Close</Button>
                      {(dupDialog.data?.groups || []).length ? (
                        <Button className="rounded-xl" onClick={voidSelectedDuplicates} disabled={dupDialog.submitting || dupDialog.selected.size === 0} data-testid="owner-duplicates-void">
                          {dupDialog.submitting ? "Voiding…" : `Void ${dupDialog.selected.size} duplicate${dupDialog.selected.size === 1 ? "" : "s"}`}
                        </Button>
                      ) : null}
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={reconcileOpen} onOpenChange={(o) => { if (!reconcileBusy) setReconcileOpen(o); }}>
                  <DialogContent className="max-w-lg rounded-[24px]" data-testid="owner-reconcile-dialog">
                    <DialogHeader>
                      <DialogTitle>Fix paid tables showing a balance</DialogTitle>
                      <DialogDescription>
                        These open tables have a payment on their slip but still show money due. Applying marks each one Paid in full by writing off the difference as a labelled "Reconciliation adjustment" (not counted as cash, card or EFT) so staff can close them. Every change is logged in the audit trail.
                      </DialogDescription>
                    </DialogHeader>
                    {(reconcilePreview?.tables || []).length === 0 ? (
                      <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground" data-testid="owner-reconcile-empty">
                        No open tables need fixing — every table with a payment already balances.
                      </div>
                    ) : (
                      <div className="max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="owner-reconcile-list">
                        {(reconcilePreview?.tables || []).map((t) => (
                          <div key={t.table_session_id} className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2 text-sm" data-testid={`owner-reconcile-row-${t.table_session_id}`}>
                            <div className="min-w-0">
                              <p className="truncate font-semibold">Table {t.table_number}</p>
                              <p className="text-xs text-muted-foreground">Paid R {Number(t.paid || 0).toFixed(2)} · showed R {Number(t.owed_before || 0).toFixed(2)} owed</p>
                            </div>
                            <Badge variant="outline" className="shrink-0 rounded-lg">Write off R {Number(t.adjustment || 0).toFixed(2)}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                    <DialogFooter className="gap-2">
                      <Button variant="outline" className="rounded-xl" onClick={() => setReconcileOpen(false)} disabled={reconcileBusy} data-testid="owner-reconcile-cancel">Cancel</Button>
                      {(reconcilePreview?.tables || []).length > 0 ? (
                        <Button className="rounded-xl" onClick={() => runReconcilePaidTables(false)} disabled={reconcileBusy} data-testid="owner-reconcile-apply">
                          {reconcileBusy ? "Applying…" : `Mark ${reconcilePreview.tables.length} table${reconcilePreview.tables.length === 1 ? "" : "s"} Paid in full`}
                        </Button>
                      ) : null}
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </TabsContent>

          <TabsContent value="manage-menu" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SectionHeader eyebrow="Manage Menu" title="Categories and items" description="Keep the live menu clear and ready for service." />
              <div className="flex flex-wrap items-center gap-2">
                <Button 
                  variant="outline" 
                  className="rounded-xl" 
                  onClick={() => { setMenuSettingsMode("menu"); setMenuSettingsOpen(true); }}
                  data-testid="menu-settings-button"
                >
                  <Settings className="h-4 w-4" />
                  Menu Settings
                </Button>
                {/* Session 63.E — digital-menu-only settings live in their own dialog. */}
                <Button 
                  variant="outline" 
                  className="rounded-xl" 
                  onClick={() => { setMenuSettingsMode("digital"); setMenuSettingsOpen(true); }}
                  data-testid="digital-menu-settings-button"
                >
                  <Smartphone className="h-4 w-4" />
                  Digital Menu Settings
                </Button>
              </div>
            </div>
            <Tabs value={manageMenuTab} onValueChange={setManageMenuTab}>
              <TabsList className="h-auto w-full flex-wrap justify-start gap-2 rounded-2xl bg-secondary p-2">
                <TabsTrigger value="items" className="rounded-xl px-4 py-2">Items</TabsTrigger>
                <TabsTrigger value="categories" className="rounded-xl px-4 py-2">Item Categories</TabsTrigger>
                <TabsTrigger value="sides" className="rounded-xl px-4 py-2" data-testid="manage-menu-sides-tab">Sides & Extras</TabsTrigger>
                <TabsTrigger value="sides-categories" className="rounded-xl px-4 py-2" data-testid="manage-menu-sides-categories-tab">S&amp;E Categories</TabsTrigger>
                <TabsTrigger value="specials" className="rounded-xl px-4 py-2" data-testid="manage-menu-specials-tab">Specials</TabsTrigger>
                <TabsTrigger value="vouchers" className="rounded-xl px-4 py-2" data-testid="manage-menu-vouchers-tab">Vouchers</TabsTrigger>
                <TabsTrigger value="departments" className="rounded-xl px-4 py-2">Departments</TabsTrigger>
              </TabsList>

              <TabsContent value="items" className="space-y-4">
                <SectionHeader
                  title="Menu items"
                  action={
                    <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={() => setItemDialog({ open: true, mode: "create", values: { id: "", category_id: categories[0]?.id || "", name: "", description: "", price: "", cost: "", stock: "", image: "", department: departments[0]?.name || "", active: true, featured: false, notes_mode: "off", sides_config: { selection_mode: "none", free_count: 0, max_select: 1, allowed_side_ids: [], included_side_ids: [] }, prep_prompts: [] } })} data-testid="menu-items-create-button">
                      <Plus className="h-4 w-4" />
                      Add item
                    </Button>
                  }
                />
                {(() => {
                  const normalizedSearch = itemSearchTerm.trim().toLowerCase();
                  let filteredItems = menuItems.filter((item) => {
                    if (itemFilterCategory !== "all" && item.category_id !== itemFilterCategory) {
                      return false;
                    }
                    if (!normalizedSearch) return true;
                    const haystack = [
                      item.name || "",
                      item.description || "",
                      categoryNameMap[item.category_id] || "",
                      item.department || "",
                    ]
                      .join(" ")
                      .toLowerCase();
                    return haystack.includes(normalizedSearch);
                  });
                  // Session 27.108 — Apply the A-Z toggle after
                  // filtering; when OFF we keep the owner's chosen
                  // sort_order.
                  if (ownerMenuSortAZ) {
                    filteredItems = [...filteredItems].sort((a, b) =>
                      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, { sensitivity: "base" })
                    );
                  }
                  return (
                    <>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="relative flex-1">
                          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            value={itemSearchTerm}
                            onChange={(e) => setItemSearchTerm(e.target.value)}
                            placeholder="Search items by name, description or department…"
                            className="h-11 rounded-xl bg-background pl-10"
                            data-testid="menu-items-search-input"
                          />
                        </div>
                        <Select value={itemFilterCategory} onValueChange={setItemFilterCategory}>
                          <SelectTrigger className="h-11 w-full rounded-xl sm:w-64" data-testid="menu-items-category-filter">
                            <SelectValue placeholder="Filter by category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {/* Session 27.108 — Alphabetical (A-Z) sort toggle. */}
                        <label className="flex h-11 items-center gap-2 rounded-xl border border-border bg-card px-3 text-xs sm:w-auto" data-testid="owner-menu-sort-az-row">
                          <span className="font-medium whitespace-nowrap">A → Z</span>
                          <Switch
                            checked={ownerMenuSortAZ}
                            onCheckedChange={setOwnerMenuSortAZ}
                            data-testid="owner-menu-sort-az-switch"
                            aria-label="Sort menu items alphabetically"
                          />
                        </label>
                      </div>
                      <p className="text-xs text-muted-foreground" data-testid="menu-items-result-count">
                        Showing {filteredItems.length} of {menuItems.length} item{menuItems.length === 1 ? "" : "s"}
                        {itemFilterCategory !== "all" ? ` in ${categoryNameMap[itemFilterCategory] || "selected category"}` : ""}
                        {itemSearchTerm ? ` matching "${itemSearchTerm}"` : ""}.
                      </p>
                <Card className="rounded-[24px] border-border bg-card">
                  <CardContent className="p-0">
                    {filteredItems.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Price</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredItems.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  {item.image ? <img src={item.image} alt={item.name} className="h-12 w-12 rounded-2xl object-cover" /> : <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-[hsl(var(--primary))]"><UtensilsCrossed className="h-5 w-5" /></div>}
                                  <div>
                                    <p className="font-medium">{item.name}</p>
                                    <p className="max-w-md text-sm text-muted-foreground">{item.description}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>{categoryNameMap[item.category_id] || "—"}</TableCell>
                              <TableCell>{formatPrice(item.price)}</TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-2">
                                  <Badge variant={item.active ? "secondary" : "outline"}>{item.active ? "Show on menu" : "Hidden"}</Badge>
                                  {item.featured ? <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 dark:bg-emerald-500">Popular</Badge> : null}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex justify-end gap-2">
                                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setItemDialog({ open: true, mode: "edit", values: { ...item, price: item.price, cost: item.cost ?? "", stock: item.stock === null || item.stock === undefined ? "" : item.stock, image: item.image || "", department: item.department || (departments[0]?.name || ""), notes_mode: item.notes_mode || "off", sides_config: item.sides_config || { selection_mode: "none", free_count: 0, max_select: 1, allowed_side_ids: [], included_side_ids: [] }, prep_prompts: Array.isArray(item.prep_prompts) ? item.prep_prompts.map((p) => ({ id: p.id || "", label: p.label || "", required: p.required !== false, options: Array.isArray(p.options) ? [...p.options] : [] })) : [] } })}>
                                    <Pencil className="h-4 w-4" />
                                    Edit
                                  </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="outline" size="sm" className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteTarget({ label: "Menu item", endpoint: `/owner/menu-items/${item.id}` })}>
                                        <Trash2 className="h-4 w-4" />
                                        Delete
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete this menu item?</AlertDialogTitle>
                                        <AlertDialogDescription>It will be removed from the live menu immediately.</AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      menuItems.length === 0 ? (
                        <div className="flex flex-col items-center gap-4 py-10 px-6 text-center" data-testid="menu-items-empty-state">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary text-[hsl(var(--accent))]">
                            <UtensilsCrossed className="h-6 w-6" />
                          </div>
                          <div className="space-y-1">
                            <p className="font-['Space_Grotesk'] text-lg font-semibold">No items yet</p>
                            <p className="max-w-md text-sm text-muted-foreground">Start by adding your first menu item — customers will see it on the live menu.</p>
                          </div>
                          <Button
                            className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                            onClick={() => setItemDialog({ open: true, mode: "create", values: { id: "", category_id: categories[0]?.id || "", name: "", description: "", price: "", cost: "", stock: "", image: "", department: departments[0]?.name || "", active: true, featured: false, notes_mode: "off", sides_config: { selection_mode: "none", free_count: 0, max_select: 1, allowed_side_ids: [], included_side_ids: [] }, prep_prompts: [] } })}
                            data-testid="menu-items-empty-cta"
                          >
                            <Plus className="h-4 w-4" />
                            Add your first item
                          </Button>
                        </div>
                      ) : (
                        <EmptyState
                          icon={Search}
                          title="No matching items"
                          description={`No items found${itemFilterCategory !== "all" ? ` in ${categoryNameMap[itemFilterCategory] || "this category"}` : ""}${itemSearchTerm ? ` matching "${itemSearchTerm}"` : ""}. Try adjusting your filters.`}
                        />
                      )
                    )}
                  </CardContent>
                </Card>
                    </>
                  );
                })()}
              </TabsContent>

              <TabsContent value="categories" className="space-y-4">
                <SectionHeader
                  title="Categories"
                  action={
                    <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={() => setCategoryDialog({ open: true, mode: "create", values: { id: "", name: "", description: "", active: true, visible_customer: true, visible_staff: true, group: "other", available_from: "", available_to: "", schedule: { type: "weekly", weekdays: [0, 1, 2, 3, 4, 5, 6], start_time: "", end_time: "" } } })} data-testid="categories-create-button">
                      <Plus className="h-4 w-4" />
                      Add category
                    </Button>
                  }
                />
                <Card className="rounded-[24px] border-border bg-card">
                  <CardContent className="p-0">
                    {categories.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {categories.map((category, idx) => (
                            <TableRow key={category.id}>
                              <TableCell className="font-medium">{category.name}</TableCell>
                              <TableCell className="text-muted-foreground">{category.description}</TableCell>
                              <TableCell>
                                {(() => {
                                  const c = category.visible_customer !== false;
                                  const s = category.visible_staff !== false;
                                  if (c && s) return <Badge variant="secondary">Customer + Staff</Badge>;
                                  if (c) return <Badge variant="secondary">Customer only</Badge>;
                                  if (s) return <Badge variant="outline">Staff only</Badge>;
                                  return <Badge variant="outline">Hidden</Badge>;
                                })()}
                              </TableCell>
                              <TableCell>
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-9 w-9 rounded-xl"
                                    onClick={() => moveOwnerCategory(idx, -1)}
                                    disabled={idx === 0}
                                    aria-label={`Move ${category.name} up`}
                                    data-testid={`owner-category-up-${category.id}`}
                                  >
                                    <ChevronUp className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-9 w-9 rounded-xl"
                                    onClick={() => moveOwnerCategory(idx, 1)}
                                    disabled={idx === categories.length - 1}
                                    aria-label={`Move ${category.name} down`}
                                    data-testid={`owner-category-down-${category.id}`}
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </Button>
                                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setCategoryDialog({ open: true, mode: "edit", values: { ...category, group: category.group || "other", available_from: category.available_from || "", available_to: category.available_to || "", visible_customer: category.visible_customer !== false, visible_staff: category.visible_staff !== false, schedule: category.schedule || { type: "weekly", weekdays: [0, 1, 2, 3, 4, 5, 6], start_time: category.available_from || "", end_time: category.available_to || "" } } })}>
                                    <Pencil className="h-4 w-4" />
                                    Edit
                                  </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="outline" size="sm" className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteTarget({ label: "Category", endpoint: `/owner/categories/${category.id}` })}>
                                        <Trash2 className="h-4 w-4" />
                                        Delete
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete this category?</AlertDialogTitle>
                                        <AlertDialogDescription>Items in this category will also be removed.</AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <EmptyState icon={ClipboardList} title="No categories yet" description="Create the first category to structure the menu." />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="departments" className="space-y-4">
                <SectionHeader
                  eyebrow="Workflow"
                  title="Departments"
                  description="Departments decide which station each menu item is sent to. Items in a department flagged 'Routes to kitchen' will appear on the Kitchen Dashboard."
                  action={
                    <Button
                      className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={() => { setDeptCategoryIds([]); setDepartmentDialog({ open: true, mode: "create", values: { id: "", name: "", route_to_kitchen: false, route_to_bar: false, route_to_staff: false, active: true } }); }}
                      data-testid="departments-create-button"
                    >
                      <Plus className="h-4 w-4" />
                      Add department
                    </Button>
                  }
                />
                {departments.length === 0 ? (
                  <EmptyState
                    icon={ChefHat}
                    title="No departments yet"
                    description="Create your first department to start routing menu items to the kitchen or front of house."
                  />
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {departments.map((dept) => (
                      <Card key={dept.id} className="rounded-[24px] border-border bg-card">
                        <CardContent className="space-y-3 p-5">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-['Space_Grotesk'] text-lg font-semibold">{dept.name}</p>
                              <div className="mt-1 flex flex-wrap items-center gap-2">
                                {dept.route_to_kitchen ? (
                                  <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 dark:bg-emerald-500">
                                    <ChefHat className="h-3 w-3" />
                                    Routes to kitchen
                                  </Badge>
                                ) : null}
                                {dept.route_to_bar ? (
                                  <Badge className="bg-indigo-600 text-white hover:bg-indigo-600 dark:bg-indigo-500">
                                    <Wine className="h-3 w-3" />
                                    Routes to bar
                                  </Badge>
                                ) : null}
                                {dept.route_to_staff ? (
                                  <Badge className="bg-amber-500 text-white hover:bg-amber-500">
                                    <Bell className="h-3 w-3" />
                                    Notifies staff
                                  </Badge>
                                ) : null}
                                {!dept.route_to_kitchen && !dept.route_to_bar ? (
                                  <Badge variant="outline">Front of house</Badge>
                                ) : null}
                                {dept.active === false ? <Badge variant="outline" className="text-muted-foreground">Inactive</Badge> : null}
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {(menuItems || []).filter((it) => (it.department || "").toLowerCase() === (dept.name || "").toLowerCase()).length} menu item(s)
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-xl"
                              onClick={() => { setDeptCategoryIds([]); setDepartmentDialog({ open: true, mode: "edit", values: { id: dept.id, name: dept.name, route_to_kitchen: !!dept.route_to_kitchen, route_to_bar: !!dept.route_to_bar, route_to_staff: !!dept.route_to_staff, active: dept.active !== false } }); }}
                              data-testid={`department-edit-${dept.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => deleteDepartment(dept)}
                              data-testid={`department-delete-${dept.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Sides library — extras the customer can add (e.g. Chips R25). */}
              <TabsContent value="sides" className="space-y-4">
                <SectionHeader
                  title="Sides library"
                  description="Define extras (chips, salads, sauces) once here, then attach them to specific menu items."
                  action={
                    <Button
                      className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={() => setSideDialog({ open: true, mode: "create", values: { id: "", name: "", description: "", price: "", active: true, sides_category_id: "", department: departments[0]?.name || "", image: "", notes_mode: "off" } })}
                      data-testid="sides-create-button"
                    >
                      <Plus className="h-4 w-4" />
                      Add side
                    </Button>
                  }
                />
                <Card className="rounded-[24px] border-border bg-card">
                  <CardContent className="p-0">
                    {sides.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Side</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Price</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sides.map((side) => (
                            <TableRow key={side.id} data-testid={`side-row-${side.id}`}>
                              <TableCell className="font-medium">{side.name}</TableCell>
                              <TableCell className="max-w-md text-sm text-muted-foreground">{side.description || "—"}</TableCell>
                              <TableCell>{formatPrice(side.price)}</TableCell>
                              <TableCell>
                                <Badge variant={side.active ? "secondary" : "outline"}>
                                  {side.active ? "Active" : "Hidden"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-xl"
                                    onClick={() => setSideDialog({ open: true, mode: "edit", values: { id: side.id, name: side.name || "", description: side.description || "", price: side.price ?? 0, active: side.active !== false, sides_category_id: side.sides_category_id || "", department: side.department || "", image: side.image || "", notes_mode: side.notes_mode || "off" } })}
                                    data-testid={`side-edit-${side.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    onClick={() => deleteSide(side)}
                                    data-testid={`side-delete-${side.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="space-y-2 p-6 text-center text-sm text-muted-foreground">
                        <p>No sides yet.</p>
                        <p>
                          Add common extras like <span className="font-medium text-foreground">Chips</span>, <span className="font-medium text-foreground">Side salad</span>, or <span className="font-medium text-foreground">Sauces</span>. You'll then attach them to individual menu items.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ---- S&E Categories (stub) — full CRUD + reorder will
                   land in a follow-up. For now this tab explains the
                   intent and offers a shortcut to the live S&E tab so
                   the user can still add sides/extras directly. -- */}
              <TabsContent value="sides-categories" className="space-y-4" data-testid="owner-sides-categories-content">
                <SectionHeader
                  eyebrow="Sides & extras"
                  title="S&E Categories"
                  description="Group your sides & extras into reorderable sections (e.g. Drinks, Sauces, Sharers). Customers see them grouped on the menu just like Item Categories."
                  action={
                    <Button
                      className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={() => setSidesCategoryDialog({ open: true, mode: "create", values: { id: "", name: "", description: "", active: true, visible_customer: true, visible_staff: true, group: "other", available_from: "", available_to: "", schedule: { type: "weekly", weekdays: [0, 1, 2, 3, 4, 5, 6], start_time: "", end_time: "" } } })}
                      data-testid="sides-categories-create-button"
                    >
                      <Plus className="h-4 w-4" />
                      Add category
                    </Button>
                  }
                />
                <Card className="rounded-[24px] border-border bg-card">
                  <CardContent className="p-0">
                    {sidesCategories.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sidesCategories.map((category, idx) => (
                            <TableRow key={category.id}>
                              <TableCell className="font-medium">{category.name}</TableCell>
                              <TableCell className="text-muted-foreground">{category.description}</TableCell>
                              <TableCell>
                                {(() => {
                                  const c = category.visible_customer !== false;
                                  const s = category.visible_staff !== false;
                                  if (c && s) return <Badge variant="secondary">Customer + Staff</Badge>;
                                  if (c) return <Badge variant="secondary">Customer only</Badge>;
                                  if (s) return <Badge variant="outline">Staff only</Badge>;
                                  return <Badge variant="outline">Hidden</Badge>;
                                })()}
                              </TableCell>
                              <TableCell>
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-9 w-9 rounded-xl"
                                    onClick={() => moveOwnerSidesCategory(idx, -1)}
                                    disabled={idx === 0}
                                    aria-label={`Move ${category.name} up`}
                                    data-testid={`owner-sides-category-up-${category.id}`}
                                  >
                                    <ChevronUp className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-9 w-9 rounded-xl"
                                    onClick={() => moveOwnerSidesCategory(idx, 1)}
                                    disabled={idx === sidesCategories.length - 1}
                                    aria-label={`Move ${category.name} down`}
                                    data-testid={`owner-sides-category-down-${category.id}`}
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </Button>
                                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setSidesCategoryDialog({ open: true, mode: "edit", values: { ...category, group: category.group || "other", available_from: category.available_from || "", available_to: category.available_to || "", visible_customer: category.visible_customer !== false, visible_staff: category.visible_staff !== false, schedule: category.schedule || { type: "weekly", weekdays: [0, 1, 2, 3, 4, 5, 6], start_time: category.available_from || "", end_time: category.available_to || "" } } })} data-testid={`owner-sides-category-edit-${category.id}`}>
                                    <Pencil className="h-4 w-4" />
                                    Edit
                                  </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="outline" size="sm" className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteTarget({ label: "Category", endpoint: `/owner/sides-categories/${category.id}` })} data-testid={`owner-sides-category-delete-${category.id}`}>
                                        <Trash2 className="h-4 w-4" />
                                        Delete
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete this category?</AlertDialogTitle>
                                        <AlertDialogDescription>Sides & extras tagged to this category will be untagged but not removed.</AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <EmptyState icon={ClipboardList} title="No S&E categories yet" description="Create the first category to group your sides & extras." />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ---- Specials (moved under Manage Menu) ----------------- */}
              <TabsContent value="specials" className="space-y-4" data-testid="owner-specials-content">
                <SectionHeader
                  eyebrow="Promotions"
                  title="Specials & scheduling"
                  description={`Create discounts, standalone promo items, or whole-menu takeovers — and let them activate automatically on a schedule. ${specialNowLocal ? `Restaurant time: ${specialNowLocal.replace("T", " ").slice(0, 16)}` : ""}`}
              action={
                <Button
                  className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  onClick={() => setSpecialDialog((current) => ({ ...current, open: true, mode: "create", values: { ...current.values, id: "", name: "", description: "", kind: "discount", linked_item_id: menuItems[0]?.id || "", special_price: "", price: "", image: "", department: "Kitchen", takeover_category_ids: [], takeover_item_ids: [], schedule: { type: "weekly", weekdays: [], start_date: "", end_date: "", start_time: "", end_time: "" }, mode: "additive", banner_color: "", active: true } }))}
                  data-testid="specials-create-button"
                >
                  <Plus className="h-4 w-4" />
                  Add special
                </Button>
              }
            />
            {specials.length === 0 ? (
              <Card className="rounded-[24px] border-dashed border-border bg-card">
                <CardContent className="space-y-2 p-8 text-center text-sm text-muted-foreground">
                  <Sparkles className="mx-auto h-8 w-8 text-[hsl(var(--accent))]" />
                  <p className="font-semibold text-foreground">No specials yet</p>
                  <p>Add a Wednesday burger discount, a weekend pizza combo, or a one-day menu takeover for events. They'll activate on your schedule and disappear automatically.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {specials.map((special) => {
                  const linkedItem = special.kind === "discount" && special.linked_item_id ? menuItems.find((i) => i.id === special.linked_item_id) : null;
                  const sched = special.schedule || {};
                  const weekdayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                  const scheduleSummary = sched.type === "weekly"
                    ? (sched.weekdays || []).map((d) => weekdayNames[d]).join(", ") + (sched.start_time && sched.end_time ? ` · ${sched.start_time}–${sched.end_time}` : " · all day")
                    : `${sched.start_date || "—"} → ${sched.end_date || "—"}` + (sched.start_time && sched.end_time ? ` · ${sched.start_time}–${sched.end_time}` : "");
                  return (
                    <Card key={special.id} className="rounded-[24px] border-border bg-card" data-testid={`special-row-${special.id}`}>
                      <CardContent className="space-y-3 p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-['Space_Grotesk'] text-lg font-semibold leading-tight">{special.name}</h3>
                              {special.currently_active ? (
                                <Badge className="border-0 bg-emerald-600 text-white hover:bg-emerald-600 dark:bg-emerald-500" data-testid="special-on-now">On now</Badge>
                              ) : (
                                <Badge variant="outline" className="border-border">Scheduled</Badge>
                              )}
                              {!special.active ? <Badge variant="outline" className="border-border">Paused</Badge> : null}
                            </div>
                            {special.description ? <p className="mt-1 text-sm text-muted-foreground">{special.description}</p> : null}
                          </div>
                        </div>
                        <div className="grid gap-2 rounded-2xl bg-secondary/50 p-3 text-xs sm:grid-cols-2">
                          <div>
                            <p className="font-medium uppercase tracking-[0.16em] text-muted-foreground">Kind</p>
                            <p className="mt-0.5 capitalize">{(special.kind || "").replace(/_/g, " ")}{special.mode === "exclusive" ? " · exclusive takeover" : ""}</p>
                          </div>
                          <div>
                            <p className="font-medium uppercase tracking-[0.16em] text-muted-foreground">Schedule</p>
                            <p className="mt-0.5">{scheduleSummary}</p>
                          </div>
                          {special.kind === "discount" ? (
                            <div className="sm:col-span-2">
                              <p className="font-medium uppercase tracking-[0.16em] text-muted-foreground">Pricing</p>
                              <p className="mt-0.5">
                                {linkedItem ? `${linkedItem.name}: ` : ""}
                                {linkedItem ? <span className="line-through text-muted-foreground">{formatPrice(linkedItem.price)}</span> : null}
                                {" "}<span className="font-semibold">{formatPrice(special.special_price)}</span>
                              </p>
                            </div>
                          ) : special.kind === "standalone" ? (
                            <div className="sm:col-span-2">
                              <p className="font-medium uppercase tracking-[0.16em] text-muted-foreground">Pricing</p>
                              <p className="mt-0.5 font-semibold">{formatPrice(special.price)}</p>
                            </div>
                          ) : null}
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => setSpecialDialog({
                              open: true,
                              mode: "edit",
                              values: {
                                id: special.id,
                                name: special.name || "",
                                description: special.description || "",
                                kind: special.kind || "discount",
                                linked_item_id: special.linked_item_id || (menuItems[0]?.id || ""),
                                special_price: special.special_price ?? "",
                                price: special.price ?? "",
                                image: special.image || "",
                                department: special.department || "Kitchen",
                                takeover_category_ids: special.takeover_category_ids || [],
                                takeover_item_ids: special.takeover_item_ids || [],
                                schedule: {
                                  type: sched.type || "weekly",
                                  weekdays: sched.weekdays || [],
                                  start_date: sched.start_date || "",
                                  end_date: sched.end_date || "",
                                  start_time: sched.start_time || "",
                                  end_time: sched.end_time || "",
                                },
                                mode: special.mode || "additive",
                                banner_color: special.banner_color || "",
                                active: special.active !== false,
                              },
                            })}
                            data-testid={`special-edit-${special.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => deleteSpecial(special)}
                            data-testid={`special-delete-${special.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="vouchers" className="space-y-4" data-testid="owner-vouchers-content">
            <SectionHeader
              eyebrow="Discounts"
              title="Voucher codes"
              description="Create voucher codes with flat rand discounts. Staff apply them on the receipt before payment. Expired vouchers are removed automatically."
              action={
                <Button
                  className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  onClick={openCreateVoucherDialog}
                  data-testid="voucher-create-button"
                >
                  <Plus className="h-4 w-4" />
                  Add voucher
                </Button>
              }
            />
            {vouchers.length === 0 ? (
              <Card className="rounded-[24px] border-dashed border-border bg-card">
                <CardContent className="space-y-2 p-8 text-center text-sm text-muted-foreground" data-testid="voucher-empty-state">
                  <Ticket className="mx-auto h-8 w-8 text-[hsl(var(--accent))]" />
                  <p className="font-semibold text-foreground">No vouchers yet</p>
                  <p>Create a code — for example <span className="font-mono">WELCOME50</span> — and staff will be able to apply the discount to any table receipt.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {vouchers.map((voucher) => {
                  const expiresLabel = voucher.expires_at ? new Date(voucher.expires_at).toLocaleString() : "No expiry";
                  const usageLabel = voucher.usage_type === "unlimited"
                    ? `Reusable · used ${voucher.times_used || 0}×`
                    : voucher.usage_type === "single"
                      ? (voucher.times_used ? "Single-use · used" : "Single-use · unused")
                      : `Multi-use · ${voucher.times_used || 0}/${voucher.max_uses || 0} used`;
                  return (
                    <Card key={voucher.id} className="rounded-[24px] border-border bg-card" data-testid={`voucher-card-${voucher.code}`}>
                      <CardContent className="space-y-3 p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-mono text-lg font-semibold tracking-wider">{voucher.code}</p>
                            <p className="text-2xl font-bold text-[hsl(var(--accent))]" data-testid={`voucher-amount-${voucher.code}`}>
                              {formatPrice(voucher.discount_amount)}
                            </p>
                          </div>
                          <Badge variant={voucher.active ? "default" : "secondary"} className="rounded-lg" data-testid={`voucher-status-${voucher.code}`}>
                            {voucher.active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="space-y-1 text-xs text-muted-foreground">
                          <p>{usageLabel}</p>
                          <p>Expires: {expiresLabel}</p>
                          {voucher.created_by_name ? <p>Created by {voucher.created_by_name}</p> : null}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => toggleVoucherActive(voucher)}
                            data-testid={`voucher-toggle-${voucher.code}`}
                          >
                            {voucher.active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl text-destructive hover:bg-destructive/10"
                            onClick={() => deleteVoucher(voucher)}
                            data-testid={`voucher-delete-${voucher.code}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="tags" className="space-y-4">
            <SectionHeader
              eyebrow="Tag tools"
              title="NFC and QR tags"
              description="Create a tag and write its menu URL to a physical NFC sticker."
              action={
                <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={() => setTagDialog({ open: true, mode: "create", values: { id: "", label: "", type: "menu", table_number: "", active: true, code: "" } })} data-testid="tags-create-button">
                  <Plus className="h-4 w-4" />
                  Create tag
                </Button>
              }
            />
            <div className="grid gap-4 lg:grid-cols-2">
              {tags.filter((tag) => !tag.is_preview).map((tag) => (
                <Card key={tag.id} className="rounded-[24px] border-border bg-card">
                  <CardContent className="space-y-4 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <Ticket className="h-4 w-4 text-[hsl(var(--primary))]" />
                          <p className="font-semibold">{tag.label}</p>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{tag.type === "table" ? `Table tag · Table ${tag.table_number}` : "Menu tag"}</p>
                      </div>
                      <Badge variant={tag.active ? "secondary" : "outline"}>{tag.active ? "Active" : "Inactive"}</Badge>
                    </div>
                    <div className="rounded-2xl border border-border bg-secondary/60 p-4">
                      <p className="mb-2 text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">Scan URL</p>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="break-all font-mono text-sm text-foreground">{`${window.location.origin}${tag.path}`}</p>
                        <Button variant="outline" className="rounded-xl" onClick={() => copyTagUrl(tag.path)} data-testid="tags-copy-url-button">
                          <Copy className="h-4 w-4" />
                          Copy
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-start">
                      <TagQrPreview
                        url={`${window.location.origin}${tag.path}`}
                        filename={`qr-${tag.label || tag.code || tag.id}`}
                        size={160}
                        testIdPrefix={`tags-qr-${tag.id}`}
                      />
                      <div className="flex-1 rounded-2xl border border-dashed border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
                        <p className="font-semibold text-foreground">QR code ready to print</p>
                        <p className="mt-1">Each scan opens this tag's menu just like an NFC tap — using the same single-use token that expires after the table closes.</p>
                        <p className="mt-2">Click <span className="font-semibold">Save QR image</span> to download a 720&nbsp;×&nbsp;720&nbsp;px PNG you can drop into menu stickers or printables.</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" className="rounded-xl" onClick={() => setTagDialog({ open: true, mode: "edit", values: { ...tag, code: tag.code || "" } })}>
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => handleWriteNfc(tag)}
                        data-testid={`tags-write-nfc-${tag.id}`}
                      >
                        <Radio className="h-4 w-4" />
                        Write to NFC
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteTarget({ label: "Tag", endpoint: `/owner/tags/${tag.id}` })}>
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this tag?</AlertDialogTitle>
                            <AlertDialogDescription>Printed QR codes or programmed NFC tags using it will stop working.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="links" className="space-y-4">
            <SectionHeader
              eyebrow="Share access"
              title="Role login links"
              description="Send these links to your team. The business code will already be filled in for them."
            />
            {(() => {
              const slug = context?.business?.slug || "";
              const origin = typeof window !== "undefined" ? window.location.origin : "";
              const staffUrl = `${origin}/staff?business=${encodeURIComponent(slug)}`;
              const kitchenUrl = `${origin}/kitchen?business=${encodeURIComponent(slug)}`;
              const barUrl = `${origin}/bar?business=${encodeURIComponent(slug)}`;
              const runnerUrl = `${origin}/runner?business=${encodeURIComponent(slug)}`;
              const retailUrl = `${origin}/retail?business=${encodeURIComponent(slug)}`;
              const previewTag = (tags || []).find((t) => t.is_preview) || (tags || []).find((t) => t.type === "menu" && !t.table_number);
              const menuPreviewUrl = previewTag
                ? `${origin}/api/menu/scan?tag=${encodeURIComponent(previewTag.code)}`
                : "";
              return (
                <div className="grid gap-4 lg:grid-cols-2">
                  <ShareableLinkCard
                    icon={ClipboardList}
                    title="Staff login"
                    description="Front-of-house team — manage tables and confirm orders."
                    href={staffUrl}
                    badge="Staff"
                    testIdPrefix="owner-link-staff"
                  />
                  <ShareableLinkCard
                    icon={ChefHat}
                    title="Kitchen login"
                    description="Kitchen team — see incoming tickets and mark orders ready."
                    href={kitchenUrl}
                    badge="Kitchen"
                    testIdPrefix="owner-link-kitchen"
                  />
                  <ShareableLinkCard
                    icon={Wine}
                    title="Bar login"
                    description="Bar team — see incoming drink tickets and mark them ready."
                    href={barUrl}
                    badge="Bar"
                    testIdPrefix="owner-link-bar"
                  />
                  <ShareableLinkCard
                    icon={Truck}
                    title="Runner login"
                    description="Runners — see ready orders and mark them as delivered to tables."
                    href={runnerUrl}
                    badge="Runner"
                    testIdPrefix="owner-link-runner"
                  />
                  <ShareableLinkCard
                    icon={Store}
                    title="Retail login"
                    description="Retail counter — POS-style sales, cash & card payments, tickets to Kitchen/Bar."
                    href={retailUrl}
                    badge="Retail"
                    testIdPrefix="owner-link-retail"
                  />
                  {menuPreviewUrl ? (
                    <ShareableLinkCard
                      icon={QrCode}
                      title="Customer menu (preview)"
                      description={`Mints a fresh menu session via "${previewTag.label}". Use this to test the customer flow without rescanning a tag.`}
                      href={menuPreviewUrl}
                      badge="Customer"
                      testIdPrefix="owner-link-menu"
                    />
                  ) : null}
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="orders" className="space-y-4">
            <SectionHeader eyebrow="Service history" title="Orders" description="Click any order to view its full receipt." />
            <Tabs value={ordersSubtab} onValueChange={(v) => {
              if (v === "voided" && !voidedOrdersLoaded) loadVoidedOrders();
              if (v === "tips" && !tipsLoaded) loadTips();
              setOrdersSubtab(v);
            }}>
              <div className="flex flex-wrap items-center gap-2">
                <TabsList className="flex-1 justify-start rounded-xl bg-secondary/60 p-1">
                  <TabsTrigger value="active" className="rounded-lg data-[state=active]:bg-background" data-testid="orders-subtab-active">Active</TabsTrigger>
                  <TabsTrigger value="voided" className="rounded-lg data-[state=active]:bg-background" data-testid="orders-subtab-voided">
                    Voided
                    {voidedOrdersLoaded && voidedOrders.length ? (
                      <span className="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive">{voidedOrders.length}</span>
                    ) : null}
                  </TabsTrigger>
                  <TabsTrigger value="tips" className="rounded-lg data-[state=active]:bg-background" data-testid="orders-subtab-tips">
                    Tips
                    {tipsLoaded && tipsRows.length ? (
                      <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">{tipsRows.length}</span>
                    ) : null}
                  </TabsTrigger>
                </TabsList>
                {/* One-shot refresh for the Orders panel — pulls fresh
                    Active + Voided + Tips data so the owner can pick up
                    new tickets without reloading the entire dashboard. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={async () => {
                    setOrdersRefreshing(true);
                    try {
                      // Session 65 — FIRST re-check and correct every bill from
                      // the last 7 DAYS (today + 6, unthrottled, idempotent):
                      // items + fee − voucher = final bill total on every
                      // record, then verify each day adds up.
                      let healNote = "";
                      try {
                        const h = await http.post("/owner/operations/recheck-bills", null, { headers: authHeaders, params: { days: 7 } });
                        const d = h.data || {};
                        const bits = [];
                        if (d.bills_locked > 0) bits.push(`${d.bills_locked} bill${d.bills_locked === 1 ? "" : "s"} corrected to include the ${d.service_fee_pct}% fee`);
                        if (d.bills_corrected > 0) bits.push(`${d.bills_corrected} bill${d.bills_corrected === 1 ? "" : "s"} re-balanced`);
                        if (d.bills_not_fully_paid > 0) bits.push(`${d.bills_not_fully_paid} not fully paid (${formatPrice(d.bills_not_fully_paid_outstanding)} outstanding)`);
                        const v = d.verification || {};
                        const badDays = (v.days || []).filter((x) => !x.ok).map((x) => x.day);
                        bits.push(badDays.length ? `check failed for ${badDays.join(", ")}` : `all ${(v.days || []).length} day${(v.days || []).length === 1 ? "" : "s"} add up`);
                        healNote = ` ${bits.join(" · ")}.`;
                      } catch (he) {
                        healNote = "";
                      }
                      // Session 30.41 — refresh the CURRENT window uncapped
                      // (never shrink "All" back to the latest 500).
                      const { from, to } = ordersDateWindow;
                      const params = { limit: 0 };
                      if (from && to && dateFilter !== "all") { params.since = from.toISOString(); params.until = to.toISOString(); }
                      const r = await http.get("/owner/orders", { headers: authHeaders, params });
                      const incoming = r.data.orders || [];
                      if (dateFilter === "all") setOrders(incoming);
                      else setOrders((prev) => {
                        const map = new Map((prev || []).map((o) => [o.id, o]));
                        incoming.forEach((o) => map.set(o.id, o));
                        return Array.from(map.values()).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
                      });
                      // Refresh whichever side-lists have been opened
                      // before. We don't kick off voided/tips loads from
                      // scratch — that keeps the button cheap.
                      if (voidedOrdersLoaded) await loadVoidedOrders();
                      if (tipsLoaded) await loadTips();
                      toast.success(`Orders refreshed.${healNote}`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Could not refresh orders.");
                    } finally {
                      setOrdersRefreshing(false);
                    }
                  }}
                  disabled={ordersRefreshing}
                  data-testid="owner-orders-refresh-btn"
                  aria-label="Refresh orders"
                  title="Refresh orders"
                >
                  <RefreshCw className={`h-4 w-4 ${ordersRefreshing ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>

              <TabsContent value="active" className="mt-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</span>
                  {[
                    { value: "today", label: "Today" },
                    { value: "yesterday", label: "Yesterday" },
                    { value: "7d", label: "Last 7 days" },
                    { value: "all", label: "All" },
                  ].map((opt) => (
                    <Button
                      key={opt.value}
                      type="button"
                      variant={dateFilter === opt.value ? "default" : "outline"}
                      size="sm"
                      className="rounded-full"
                      onClick={() => setDateFilter(opt.value)}
                      data-testid={`owner-orders-date-filter-${opt.value}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                  <Input
                    type="date"
                    value={customDate}
                    onChange={(e) => { setCustomDate(e.target.value); if (e.target.value) setDateFilter("custom"); }}
                    className="h-8 w-auto rounded-full px-3 text-xs"
                    data-testid="owner-orders-custom-date"
                  />
                </div>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Payment</span>
                  {[
                    { value: "all", label: "All" },
                    { value: "cash", label: "Cash" },
                    { value: "card", label: "Card" },
                    { value: "eft", label: "EFT" },
                    { value: "split", label: "Split" },
                    { value: "unpaid", label: "Unpaid" },
                  ].map((opt) => (
                    <Button
                      key={opt.value}
                      type="button"
                      variant={paymentFilter === opt.value ? "default" : "outline"}
                      size="sm"
                      className="rounded-full"
                      onClick={() => setPaymentFilter(opt.value)}
                      data-testid={`owner-orders-payment-filter-${opt.value}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                  {(() => {
                    // Session 73 — email the operations report for the method
                    // on screen (All = cash + card + EFT + split sections).
                    const kinds = paymentFilter === "all" ? ["cash", "card", "eft", "split"] : ["cash", "card", "eft", "split"].includes(paymentFilter) ? [paymentFilter] : [];
                    if (!kinds.length) return null;
                    const what = paymentFilter === "all" ? "Payments" : `${paymentFilter === "eft" ? "EFT" : paymentFilter.charAt(0).toUpperCase() + paymentFilter.slice(1)} payments`;
                    return (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="ml-auto rounded-full"
                        onClick={() => emailFocusedReport(kinds.map((k) => `payments_${k}`), what)}
                        data-testid="owner-email-payments-report-button"
                      >
                        <Mail className="mr-1.5 h-4 w-4" />
                        Email {paymentFilter === "all" ? "payments" : what.replace("EFT", "EFT ").replace("  ", " ").toLowerCase().replace("eft", "EFT")} report
                      </Button>
                    );
                  })()}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full border-amber-500/40 text-amber-800 hover:bg-amber-500/10 dark:text-amber-200"
                    onClick={findDuplicateOrders}
                    data-testid="owner-find-duplicates-button"
                  >
                    <AlertTriangle className="mr-1.5 h-4 w-4" />
                    Find duplicate orders
                  </Button>
                </div>
                {(() => {
                  // Apply date + payment filters once and reuse for both the
                  // totals card and the table below — single source of truth.
                  const { from, to } = ordersDateWindow;
                  const dateMatched = from && to
                    ? orders.filter((o) => { const d = o.created_at ? new Date(o.created_at) : null; return d && d >= from && d < to; })
                    : orders;
                  // Session 61 — payment method comes from the TABLE ledger
                  // when we have it (an order's own mirror can lag or be
                  // missing on legacy / offline closes).
                  const orderMethod = (o) => String(o.session_money?.payment_method || o.payment_method || "unpaid").toLowerCase();
                  const filteredOrders = paymentFilter === "all"
                    ? dateMatched
                    : dateMatched.filter((o) => orderMethod(o) === paymentFilter);
                  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
                  const isVoidedOrder = (o) => Boolean(o.voided) || String(o.status || "").toLowerCase() === "voided";
                  // Customer-visible revenue of a live order: stored total,
                  // unless lines were voided and the total never recomputed.
                  const liveRevenue = (o) => {
                    const total = Number(o.total) || 0;
                    const items = Array.isArray(o.items) ? o.items : [];
                    if (items.some((it) => it && it.voided)) {
                      const liveSum = items.filter((it) => it && !it.voided).reduce((s, it) => s + (Number(it.line_total ?? ((it.price || it.unit_price || 0) * (it.quantity || 0))) || 0), 0);
                      if (total > liveSum + 0.009) return liveSum;
                    }
                    return total;
                  };
                  // ── Session 61 — OPERATIONS MATH, ONE SOURCE OF TRUTH ──
                  // Items sold are summed per LIVE order. Everything that is
                  // money on the BILL (cash / card / EFT / vouchers / service
                  // fee / tips / outstanding) is summed ONCE PER TABLE from the
                  // session ledger the server attaches as `session_money`
                  // (per-order mirrors are only a fallback for orders that
                  // never had a table — retail / takeaway).
                  const sales = filteredOrders.reduce((acc, o) => {
                    if (isVoidedOrder(o)) { acc.voided += Number(o.original_total || o.total) || 0; return acc; }
                    acc.gross += liveRevenue(o);
                    acc.refunds += Number(o.refund_amount) || 0;
                    return acc;
                  }, { gross: 0, refunds: 0, voided: 0 });
                  const moneyBySession = new Map();
                  const orphanMoney = { cash: 0, card: 0, eft: 0, tip: 0, fee: 0 };
                  // Session 65 — owner's rule: fee = pct × bill, on EVERY bill.
                  const liveFeePct = Number(context?.business?.service_fee_enabled ? context?.business?.service_fee_pct : 0) || 0;
                  for (const o of filteredOrders) {
                    const sm = o.session_money;
                    if (sm && sm.session_id) {
                      if (!moneyBySession.has(sm.session_id)) moneyBySession.set(sm.session_id, sm);
                    } else if (!o.table_session_id) {
                      // Table-less order (retail / takeaway): its own mirror IS the truth.
                      orphanMoney.cash += Number(o.cash_paid) || 0;
                      orphanMoney.card += Number(o.card_paid) || 0;
                      orphanMoney.eft += Number(o.eft_paid) || 0;
                      orphanMoney.tip += Number(o.tip_amount) || 0;
                      const mirrorFee = Number(o.service_fee_amount) || 0;
                      // No fee recorded yet (unpaid / legacy) → the live % still applies.
                      orphanMoney.fee += mirrorFee > 0 || isVoidedOrder(o) ? mirrorFee : r2(liveRevenue(o) * liveFeePct / 100);
                    }
                  }
                  // Legacy fallback: orders WITH a table but no session_money
                  // (server older than this build) — dedupe their mirrors.
                  const seenLegacy = new Set();
                  for (const o of filteredOrders) {
                    if (o.session_money || !o.table_session_id || seenLegacy.has(o.table_session_id)) continue;
                    seenLegacy.add(o.table_session_id);
                    orphanMoney.cash += Number(o.cash_paid) || 0;
                    orphanMoney.card += Number(o.card_paid) || 0;
                    orphanMoney.eft += Number(o.eft_paid) || 0;
                  }
                  if (seenLegacy.size) {
                    for (const o of filteredOrders) {
                      if (o.session_money || !o.table_session_id) continue;
                      orphanMoney.tip += Number(o.tip_amount) || 0;
                      orphanMoney.fee += Number(o.service_fee_amount) || 0;
                    }
                  }
                  let cashSum = orphanMoney.cash, cardSum = orphanMoney.card, eftSum = orphanMoney.eft;
                  // Session 65 — SERVICE FEE = pct × items, PART OF EVERY BILL:
                  //   items + fee − voucher = FINAL BILL TOTAL
                  //   final bill total − payments = OUTSTANDING
                  // The fee is never reduced to "what was collected" and never
                  // a separate balance. R13,039 of items at 10% → R1,303.90.
                  let feeSum = orphanMoney.fee;
                  let extraTipSum = orphanMoney.tip - orphanMoney.fee; // orphan tip mirrors include the fee
                  let voucherSum = 0, outstandingSum = 0, billedSum = 0;
                  for (const sm of moneyBySession.values()) {
                    cashSum += Number(sm.cash_paid) || 0;
                    cardSum += Number(sm.card_paid) || 0;
                    eftSum += Number(sm.eft_paid) || 0;
                    voucherSum += Number(sm.voucher_discount) || 0;
                    extraTipSum += Number(sm.tip_amount) || 0;
                    outstandingSum += Number(sm.outstanding) || 0;
                    billedSum += Number(sm.amount_owed) || 0;
                    feeSum += Number(sm.service_fee_amount) || 0;
                  }
                  cashSum = r2(cashSum); cardSum = r2(cardSum); eftSum = r2(eftSum);
                  feeSum = r2(feeSum); extraTipSum = r2(Math.max(0, extraTipSum)); voucherSum = r2(voucherSum);
                  outstandingSum = r2(outstandingSum);
                  // Session 73 — money RECEIVED comes from the server, by the
                  // moment each payment was recorded (what the bank sees), not
                  // "every table that had an order today" — a table open across
                  // midnight used to be counted on BOTH days here. Applies when
                  // the method filter is "all" (the server figure covers all methods).
                  // Session 74 — the server now returns the WHOLE card for any
                  // method filter (`sales` block), computed over every order in
                  // the window. Use it for every figure; the list below is for
                  // browsing, never for the totals.
                  const serverTotals = moneySummary && moneySummary.sales && typeof moneySummary.collected === "number" && String(moneySummary.payment_method || "all") === String(paymentFilter || "all") ? moneySummary : null;
                  const serverMoney = serverTotals && paymentFilter === "all" ? serverTotals : null;
                  const totalsPending = !serverTotals && !moneySummaryError;
                  let serverRounds = null, serverReceipts = null;
                  if (serverTotals) {
                    const sv = serverTotals.sales;
                    sales.gross = Number(sv.gross) || 0; sales.refunds = Number(sv.refunds) || 0; sales.voided = Number(sv.voided) || 0;
                    feeSum = r2(sv.service_fee); voucherSum = r2(sv.vouchers); outstandingSum = r2(sv.outstanding); billedSum = r2(sv.billed);
                    serverRounds = Number(sv.rounds) || 0; serverReceipts = Number(sv.receipts) || 0;
                    if (paymentFilter === "all") {
                      cashSum = r2(serverTotals.cash); cardSum = r2(serverTotals.card); eftSum = r2(serverTotals.eft);
                      extraTipSum = r2(Math.max(0, Number(serverTotals.tips) || 0));
                    } else {
                      cashSum = r2(sv.tables_cash); cardSum = r2(sv.tables_card); eftSum = r2(sv.tables_eft);
                      extraTipSum = r2(sv.extra_tips);
                    }
                  }
                  const collected = r2(cashSum + cardSum + eftSum);
                  const gratuitySum = r2(extraTipSum + feeSum);
                  // Session 46 — when the owner keeps the automatic service
                  // fee out of staff tips, Tips shows extra tips only and the
                  // fee gets its own card.
                  const feeSeparate = Boolean(context?.business?.service_fee_separate);
                  const tipSum = feeSeparate ? extraTipSum : gratuitySum;
                  const grossSales = r2(sales.gross);
                  // Reconciliation: what the bills asked for vs what came in.
                  //   billed = items + service fee − vouchers (per table)
                  //   collected + outstanding must equal billed exactly.
                  const expectedBilled = r2(grossSales - voucherSum + feeSum);
                  const reconGap = r2(expectedBilled - collected - outstandingSum);
                  const totals = { grand: grossSales };
                  // Session 47 — ONE ROW PER RECEIPT. Every round a table
                  // orders is its own order document (needed for kitchen
                  // tickets), but under Operations the owner wants the
                  // table's bill as a single line: all rounds merged, one
                  // order number, combined items / total. Orders without a
                  // table session (retail / takeaway) stay as-is. Groups
                  // with a single round keep the RAW order so refund / void
                  // / edit actions in the receipt dialog still work.
                  const receiptGroups = new Map();
                  const voidedRows = [];
                  for (const o of filteredOrders) {
                    // Voided rounds never blend into a live bill's total —
                    // they stay as their own (clearly badged) rows.
                    if (String(o.status || "").toLowerCase() === "voided") { voidedRows.push({ ...o, __rounds: 1 }); continue; }
                    const key = o.table_session_id || o.id;
                    if (!receiptGroups.has(key)) receiptGroups.set(key, []);
                    receiptGroups.get(key).push(o);
                  }
                  const receiptRows = Array.from(receiptGroups.values()).map((group) => {
                    if (group.length === 1) return { ...group[0], __rounds: 1 };
                    const merged = mergeSessionOrders(group);
                    const newest = group.reduce((m, o) => (new Date(o.created_at || 0) > new Date(m.created_at || 0) ? o : m), group[0]);
                    return { ...merged, __rounds: group.length, __lastRoundAt: newest.created_at };
                  }).concat(voidedRows).sort((a, b) => new Date(b.__lastRoundAt || b.created_at || 0) - new Date(a.__lastRoundAt || a.created_at || 0));
                  // ── Session 65 — WHY doesn't it add up? One line per bill
                  // that is still owed, paid without its fee, or whose table
                  // record could not be read. The owner can now see every
                  // rand of the gap instead of a bare "not accounted for".
                  const attention = [];
                  const groupInfo = (key) => {
                    const g = receiptGroups.get(key) || [];
                    const first = g[0] || {};
                    return { table: first.table_number || first.session_money?.table_number || "—", waiter: first.waiter || first.waiter_name || first.user || "—", orderNo: first.order_number || "" };
                  };
                  for (const sm of moneyBySession.values()) {
                    const info = groupInfo(sm.session_id);
                    const base = { key: sm.session_id, ...info, items: r2(sm.items_total), fee: r2(sm.service_fee_amount), bill: r2(sm.amount_owed), paid: r2(sm.paid_amount) };
                    if ((Number(sm.outstanding) || 0) > 0.009) {
                      // The bill is one number (items + fee − voucher); a shortfall
                      // is simply a bill that is not fully paid yet.
                      const paidSome = (Number(sm.paid_amount) || 0) > 0.009;
                      const st = String(sm.status || "").toLowerCase();
                      attention.push({ ...base, kind: "owed", amount: r2(sm.outstanding), label: paidSome ? `Not fully paid — ${formatPrice(sm.paid_amount)} of ${formatPrice(sm.amount_owed)} received` : st === "open" ? "Table still open — nothing paid yet" : sm.closed_unpaid ? "Closed without payment (write-off)" : "Unpaid" });
                    }
                    if (sm.source === "order_mirror") {
                      attention.push({ ...base, kind: "ledger", amount: r2(sm.outstanding), label: sm.source_reason === "table_record_missing" ? "Table record missing — figures taken from the orders" : "Table ledger unreadable — figures taken from the orders" });
                    }
                  }
                  for (const key of seenLegacy) {
                    const info = groupInfo(key);
                    const g = receiptGroups.get(key) || [];
                    attention.push({ key, ...info, kind: "ledger", items: r2(g.reduce((s, o) => s + (isVoidedOrder(o) ? 0 : liveRevenue(o)), 0)), paid: r2(Math.max(0, ...g.map((o) => (Number(o.cash_paid) || 0) + (Number(o.card_paid) || 0) + (Number(o.eft_paid) || 0)))), fee: r2(g.reduce((s, o) => s + (Number(o.service_fee_amount) || 0), 0)), amount: 0, label: "No table ledger returned by the server" });
                  }
                  attention.sort((a, b) => (b.amount || 0) - (a.amount || 0));
                  const attentionOwed = r2(attention.filter((a) => a.kind === "owed").reduce((s, a) => s + a.amount, 0));
                  // Session 74 — while the server totals are loading, show a
                  // placeholder instead of numbers summed from a partial list.
                  const money = (v) => (totalsPending ? "…" : formatPrice(v));
                  const receiptsShown = serverReceipts ?? receiptRows.length;
                  const roundsShown = serverRounds ?? filteredOrders.length;
                  const listIncomplete = serverRounds != null && filteredOrders.length < serverRounds;
                  return (
                    <>
                      {moneySummaryError ? (
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-300/60 bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200" data-testid="orders-totals-error">
                          <span>Could not load the totals for this window from the server. The figures below are summed from the orders on this screen and may be incomplete.</span>
                          <Button size="sm" variant="outline" className="h-7" data-testid="orders-totals-retry" onClick={() => setOrdersWindowRetry((n) => n + 1)}>Retry</Button>
                        </div>
                      ) : null}
                      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7" data-testid="orders-totals-grid" data-totals-source={serverTotals ? "server" : totalsPending ? "pending" : "list"}>
                        {paymentFilter !== "all" ? (
                          <div className="col-span-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-xs sm:col-span-3 lg:col-span-7" data-testid="orders-totals-filter-note">
                            <span>Showing <span className="font-semibold uppercase">{paymentFilter}</span> {paymentFilter === "unpaid" ? "tables" : "payments"} only — these totals are a slice of the day, not the full day.</span>
                            <Button type="button" size="sm" variant="outline" className="h-7 rounded-full" onClick={() => setPaymentFilter("all")} data-testid="orders-totals-show-all">Show the full day</Button>
                          </div>
                        ) : null}
                        <div className="rounded-2xl border border-border bg-card p-3">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Receipts</p>
                          <p className="mt-1 text-lg font-semibold" data-testid="orders-totals-count">{totalsPending ? "…" : receiptsShown}</p>
                          {!totalsPending && roundsShown !== receiptsShown ? (
                            <p className="text-[10px] text-muted-foreground" data-testid="orders-totals-rounds">{roundsShown} rounds ordered</p>
                          ) : null}
                        </div>
                        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/30">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Cash</p>
                          <p className="mt-1 text-lg font-semibold text-emerald-800 dark:text-emerald-200" data-testid="orders-totals-cash">{money(cashSum)}</p>
                        </div>
                        <div className="rounded-2xl border border-sky-200 bg-sky-50 p-3 dark:border-sky-900/50 dark:bg-sky-950/30">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">Card</p>
                          <p className="mt-1 text-lg font-semibold text-sky-800 dark:text-sky-200" data-testid="orders-totals-card">{money(cardSum)}</p>
                          {eftSum > 0 ? <p className="text-[10px] text-sky-700/80 dark:text-sky-300/80" data-testid="orders-totals-eft">+ EFT {formatPrice(eftSum)}</p> : null}
                        </div>
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/30">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Tips</p>
                          <p className="mt-1 text-lg font-semibold text-amber-800 dark:text-amber-200" data-testid="orders-totals-tips">{money(tipSum)}</p>
                          <p className="text-[10px] text-amber-700/80 dark:text-amber-300/80">{feeSeparate ? "Extra tips only" : feeSum > 0 ? `Incl. service fee ${formatPrice(feeSum)}` : "Not in revenue"}</p>
                        </div>
                        <div className="rounded-2xl border border-violet-200 bg-violet-50 p-3 dark:border-violet-900/50 dark:bg-violet-950/30" data-testid="orders-totals-service-fee-card">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">Service fee</p>
                          <p className="mt-1 text-lg font-semibold text-violet-800 dark:text-violet-200" data-testid="orders-totals-service-fee">{money(feeSum)}</p>
                          <p className="text-[10px] text-violet-700/80 dark:text-violet-300/80" data-testid="orders-totals-service-fee-note">{liveFeePct > 0 ? `${liveFeePct}% of items · part of every bill` : "Part of the bill"}{feeSeparate ? " · not paid to staff" : " · credited to waiters"}</p>
                        </div>
                        <div className="rounded-2xl border border-border bg-secondary/60 p-3">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Sales</p>
                          <p className="mt-1 text-lg font-semibold" data-testid="orders-totals-grand">{money(totals.grand)}</p>
                          <p className="text-[10px] text-muted-foreground" data-testid="orders-totals-grand-note">Items sold · excl. tips &amp; fee{voucherSum > 0 ? ` · vouchers −${formatPrice(voucherSum)}` : ""}{sales.refunds > 0 ? ` · refunds ${formatPrice(r2(sales.refunds))}` : ""}</p>
                        </div>
                        <div className={`rounded-2xl border p-3 ${outstandingSum > 0 ? "border-rose-200 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-950/30" : "border-border bg-card"}`}>
                          <p className={`text-[10px] font-semibold uppercase tracking-wide ${outstandingSum > 0 ? "text-rose-700 dark:text-rose-300" : "text-muted-foreground"}`}>Collected</p>
                          <p className={`mt-1 text-lg font-semibold ${outstandingSum > 0 ? "text-rose-800 dark:text-rose-200" : ""}`} data-testid="orders-totals-collected">{money(collected)}</p>
                          <p className={`text-[10px] ${outstandingSum > 0 ? "text-rose-700/80 dark:text-rose-300/80" : "text-muted-foreground"}`} data-testid="orders-totals-outstanding">{outstandingSum > 0 ? `${formatPrice(outstandingSum)} outstanding on bills` : "All bills paid in full"}</p>
                        </div>
                      </div>
                      {listIncomplete || ordersWindowError ? (
                        <div className="mb-3 -mt-1 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-400/50 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100" data-testid="orders-list-incomplete">
                          <span>{ordersRefreshing ? `Loading the full list — ${filteredOrders.length} of ${roundsShown} orders in this window so far. Totals above are already complete.` : `The list below shows ${filteredOrders.length} of ${roundsShown} orders in this window. Totals above are complete (computed by the server).`}</span>
                          {!ordersRefreshing ? <Button size="sm" variant="outline" className="h-7" data-testid="orders-list-retry" onClick={() => setOrdersWindowRetry((n) => n + 1)}>Retry</Button> : null}
                        </div>
                      ) : null}
                      {serverMoney ? (
                        <p className="mb-2 -mt-2 text-[11px] text-muted-foreground" data-testid="orders-totals-reconciliation">
                          Sales = items ordered in this window ({formatPrice(grossSales)}). Cash / Card / EFT / Collected = money <span className="font-medium text-foreground">received</span> in this window by payment date ({serverMoney.bills_settled} bill{serverMoney.bills_settled === 1 ? "" : "s"} settled{serverMoney.partial_payment_tables ? `, part-payments on ${serverMoney.partial_payment_tables} open table${serverMoney.partial_payment_tables === 1 ? "" : "s"}` : ""}) — the same figures as the emailed report and what the bank should show.
                          {outstandingSum > 0 ? <span className="ml-1">{formatPrice(outstandingSum)} is still outstanding on bills ordered in this window.</span> : null}
                        </p>
                      ) : (
                        <p className="mb-2 -mt-2 text-[11px] text-muted-foreground" data-testid="orders-totals-reconciliation">
                          Items {formatPrice(grossSales)} + service fee {formatPrice(feeSum)}{voucherSum > 0 ? ` − vouchers ${formatPrice(voucherSum)}` : ""} = billed {formatPrice(expectedBilled)} · collected {formatPrice(collected)}{outstandingSum > 0 ? ` + outstanding ${formatPrice(outstandingSum)}` : ""}
                          {Math.abs(reconGap) > 0.009 ? <span className="ml-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-medium text-amber-800 dark:text-amber-200" data-testid="orders-totals-recon-gap">{formatPrice(Math.abs(reconGap))} {reconGap > 0 ? "does not add up — see the bills below" : "collected above the bill (change recorded as payment?)"}</span> : <span className="ml-2 text-emerald-700 dark:text-emerald-300" data-testid="orders-totals-recon-ok">✓ adds up</span>}
                          {serverTotals ? <span className="ml-2 text-[10px] opacity-70" data-testid="orders-totals-version">· complete totals, computed on the server</span> : null}
                        </p>
                      )}
                      {attention.length ? (
                        <details className="mb-4 rounded-2xl border border-amber-500/40 bg-amber-500/5 open:bg-amber-500/10" data-testid="orders-attention-panel">
                          <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-sm font-medium text-amber-900 marker:hidden dark:text-amber-100 [&::-webkit-details-marker]:hidden">
                            <AlertTriangle className="h-4 w-4 shrink-0" />
                            <span data-testid="orders-attention-summary">{attention.length} bill{attention.length === 1 ? "" : "s"} not fully paid</span>
                            {attentionOwed > 0 ? <span className="rounded-md bg-rose-500/10 px-1.5 py-0.5 text-xs text-rose-800 dark:text-rose-200" data-testid="orders-attention-owed">{formatPrice(attentionOwed)} outstanding</span> : null}
                            <span className="ml-auto text-xs font-normal text-muted-foreground">Tap to {"show"} details</span>
                          </summary>
                          <div className="border-t border-amber-500/30 px-2 py-2 sm:px-4">
                            <div className="grid gap-1.5" data-testid="orders-attention-list">
                              {attention.slice(0, 40).map((a, i) => (
                                <div key={`${a.key}-${a.kind}-${i}`} className="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 rounded-xl bg-background/70 px-3 py-2 text-xs sm:grid-cols-[6rem_1fr_auto_auto_auto]" data-testid={`orders-attention-row-${i}`}>
                                  <span className="font-semibold">{a.table && a.table !== "—" ? `Table ${a.table}` : a.orderNo || "Bill"}</span>
                                  <span className="truncate text-muted-foreground">{a.label}{a.waiter && a.waiter !== "—" ? ` · ${a.waiter}` : ""}</span>
                                  <span className="hidden text-right text-muted-foreground sm:block">bill {formatPrice(a.bill ?? (a.items + a.fee))}</span>
                                  <span className="hidden text-right text-muted-foreground sm:block">paid {formatPrice(a.paid)}</span>
                                  <span className={`text-right font-semibold ${a.kind === "owed" ? "text-rose-700 dark:text-rose-300" : "text-amber-800 dark:text-amber-200"}`}>{a.amount > 0 ? `${formatPrice(a.amount)} outstanding` : "—"}</span>
                                </div>
                              ))}
                            </div>
                            {attention.length > 40 ? <p className="px-3 pt-2 text-[11px] text-muted-foreground">Showing the 40 largest of {attention.length}.</p> : null}
                            <p className="px-3 pt-2 text-[11px] text-muted-foreground">Every bill = items + {liveFeePct > 0 ? `${liveFeePct}% service fee` : "service fee"}{voucherSum > 0 ? " − vouchers" : ""}. Outstanding = bill total − payments received. Tap Refresh above to re-check and correct every bill from the last 7 days.</p>
                          </div>
                        </details>
                      ) : null}
                <Card className="rounded-[24px] border-border bg-card">
                  <CardContent className="p-0">
                    {receiptRows.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Order</TableHead>
                            <TableHead>Table</TableHead>
                            <TableHead>Waiter</TableHead>
                            <TableHead>Items</TableHead>
                            <TableHead>Bill total</TableHead>
                            <TableHead>Payment</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Time</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {receiptRows.map((order) => (
                            <TableRow
                              key={order.id}
                              onClick={() => openOrderReceipt(order)}
                              className="cursor-pointer transition hover:bg-secondary/60"
                              data-testid={`owner-order-row-${order.id}`}
                            >
                              <TableCell className="font-medium">
                                {order.order_number}
                                {order.__rounds > 1 ? (
                                  <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground" data-testid={`owner-order-rounds-${order.id}`}>{order.__rounds} rounds</span>
                                ) : null}
                              </TableCell>
                              <TableCell>{order.table_number}</TableCell>
                              <TableCell className="max-w-[160px] truncate" data-testid={`owner-order-waiter-${order.id}`}>{orderWaiterLabel(order) || <span className="text-muted-foreground">—</span>}</TableCell>
                              <TableCell>{order.total_items}</TableCell>
                              <TableCell>
                                {(() => {
                                  // Session 65 — the row shows the FINAL BILL TOTAL
                                  // (items + service fee − voucher), the same number
                                  // as the receipt. Items alone sit underneath.
                                  const sm = order.session_money;
                                  const itemsVal = Number(order.total) || 0;
                                  const feeVal = sm ? (Number(sm.service_fee_amount) || 0) : (Number(order.service_fee_amount) || 0) || (isVoidedOrder(order) ? 0 : r2(itemsVal * liveFeePct / 100));
                                  const voucherVal = sm ? (Number(sm.voucher_discount) || 0) : 0;
                                  const finalTotal = sm && order.__rounds > 1 ? (Number(sm.amount_owed) || 0) : r2(itemsVal + feeVal - voucherVal);
                                  const multiRoundShare = sm && order.__rounds === 1 && Math.abs((Number(sm.items_total) || 0) - itemsVal) > 0.011;
                                  return (
                                    <div className="leading-tight">
                                      <p className="font-semibold" data-testid={`owner-order-final-total-${order.id}`}>{formatPrice(multiRoundShare ? Number(sm.amount_owed) || 0 : finalTotal)}</p>
                                      {feeVal > 0 || voucherVal > 0 ? (
                                        <p className="text-[10px] text-muted-foreground" data-testid={`owner-order-total-breakdown-${order.id}`}>
                                          {multiRoundShare ? `table bill · this round ${formatPrice(itemsVal)}` : `items ${formatPrice(multiRoundShare ? Number(sm.items_total) || 0 : itemsVal)}${feeVal > 0 ? ` + fee ${formatPrice(feeVal)}` : ""}${voucherVal > 0 ? ` − voucher ${formatPrice(voucherVal)}` : ""}`}
                                        </p>
                                      ) : null}
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const sm = order.session_money;
                                  const outstanding = sm ? (Number(sm.outstanding) || 0) : 0;
                                  const paid = sm ? (Number(sm.paid_amount) || 0) : (Number(order.cash_paid) || 0) + (Number(order.card_paid) || 0) + (Number(order.eft_paid) || 0);
                                  const m = outstanding > 0.009 && paid <= 0.009 ? "unpaid" : (order.payment_method || sm?.payment_method || (paid > 0.009 ? "paid" : "unpaid"));
                                  const styles = {
                                    cash: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
                                    card: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
                                    split: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
                                    paid: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
                                    unpaid: "bg-muted text-muted-foreground",
                                  };
                                  return (
                                    <div className="flex flex-col items-start gap-1">
                                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${styles[m] || styles.unpaid}`}>
                                        {m}
                                      </span>
                                      {!isVoidedOrder(order) && outstanding > 0.009 && paid > 0.009 ? (
                                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-800 dark:bg-rose-900/40 dark:text-rose-200" data-testid={`owner-order-outstanding-${order.id}`}>
                                          Not fully paid · {formatPrice(outstanding)} outstanding
                                        </span>
                                      ) : null}
                                      {sm && Number(sm.split_guest_count) > 0 ? (
                                        /* Session 70 — open the receipt to see which guest paid what */
                                        <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800 dark:bg-violet-900/40 dark:text-violet-200" data-testid={`owner-order-split-badge-${order.id}`}>
                                          Split · {sm.split_guest_count} guests · {sm.split_paid_count || 0} paid
                                        </span>
                                      ) : null}
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              <TableCell><StatusBadge status={order.status} /></TableCell>
                              <TableCell className="text-muted-foreground">{getOrderTimeLabel(order.created_at)}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 rounded-lg text-destructive hover:bg-destructive/10"
                                  onClick={(e) => { e.stopPropagation(); openHardDeleteDialog(order); }}
                                  aria-label={`Delete order ${order.order_number}`}
                                  data-testid={`owner-order-delete-${order.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <EmptyState icon={ClipboardList} title="No orders yet" description="Orders will appear here after customers or staff add them." />
                    )}
                  </CardContent>
                </Card>
                    </>
                  );
                })()}
              </TabsContent>

              <TabsContent value="voided" className="mt-4">
                {(() => {
                  // Session 30.39 — the Voided list follows the same date
                  // window as Active, and "Clear all voids" only wipes the
                  // FULLY voided orders currently in view.
                  const voidedInView = voidedOrders.filter((o) => inOrdersDateWindow(o.voided_at || o.updated_at || o.created_at));
                  const clearable = voidedInView.filter((o) => o.status === "voided");
                  const windowLabel = dateFilter === "today" ? "today" : dateFilter === "yesterday" ? "yesterday" : dateFilter === "7d" ? "the last 7 days" : dateFilter === "custom" && customDate ? customDate : "all time";
                  return (
                <>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</span>
                    {[
                      { value: "today", label: "Today" },
                      { value: "yesterday", label: "Yesterday" },
                      { value: "7d", label: "Last 7 days" },
                      { value: "all", label: "All" },
                    ].map((opt) => (
                      <Button
                        key={opt.value}
                        type="button"
                        variant={dateFilter === opt.value ? "default" : "outline"}
                        size="sm"
                        className="rounded-full"
                        onClick={() => setDateFilter(opt.value)}
                        data-testid={`owner-voided-date-filter-${opt.value}`}
                      >
                        {opt.label}
                      </Button>
                    ))}
                    <p className="text-xs text-muted-foreground" data-testid="owner-voided-summary">
                      {voidedOrdersLoaded ? `${voidedInView.length} voided order${voidedInView.length === 1 ? "" : "s"} · ${windowLabel}` : "Loading…"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => emailFocusedReport(["voids_detail"], "Void")}
                      data-testid="owner-email-void-report-button"
                    >
                      <Mail className="mr-1.5 h-4 w-4" />
                      Email void report ({windowLabel})
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      disabled={!voidedOrdersLoaded || clearable.length === 0}
                      onClick={() => setClearVoidsDialog({ open: true, password: "", submitting: false })}
                      data-testid="owner-clear-all-voids-button"
                    >
                      <Trash2 className="mr-1.5 h-4 w-4" />
                      Clear all voids{clearable.length ? ` (${clearable.length})` : ""}
                    </Button>
                  </div>
                </div>
                <Dialog open={clearVoidsDialog.open} onOpenChange={(open) => setClearVoidsDialog((d) => ({ ...d, open }))}>
                  <DialogContent className="rounded-2xl sm:max-w-md" data-testid="clear-voids-dialog">
                    <DialogHeader>
                      <DialogTitle>Clear {clearable.length} voided order{clearable.length === 1 ? "" : "s"}?</DialogTitle>
                      <DialogDescription>
                        This permanently deletes every fully voided order for {windowLabel}. Partially voided orders are kept. This cannot be undone — enter the staff void password to confirm.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                      <Label htmlFor="clear-voids-password">Void password</Label>
                      <Input
                        id="clear-voids-password"
                        type="password"
                        inputMode="numeric"
                        autoComplete="off"
                        value={clearVoidsDialog.password}
                        onChange={(e) => setClearVoidsDialog((d) => ({ ...d, password: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter" && clearVoidsDialog.password) clearAllVoids(clearable.map((o) => o.id)); }}
                        className="h-11 rounded-xl"
                        data-testid="clear-voids-password-input"
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" className="rounded-xl" onClick={() => setClearVoidsDialog({ open: false, password: "", submitting: false })} data-testid="clear-voids-cancel">Cancel</Button>
                      <Button
                        type="button"
                        className="rounded-xl bg-destructive text-white hover:bg-destructive/90"
                        disabled={!clearVoidsDialog.password || clearVoidsDialog.submitting}
                        onClick={() => clearAllVoids(clearable.map((o) => o.id))}
                        data-testid="clear-voids-confirm"
                      >
                        {clearVoidsDialog.submitting ? "Deleting…" : `Delete ${clearable.length}`}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Card className="rounded-[24px] border-border bg-card">
                  <CardContent className="p-0">
                    {!voidedOrdersLoaded ? (
                      <div className="p-8"><Skeleton className="h-24 w-full" /></div>
                    ) : voidedInView.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Order</TableHead>
                            <TableHead>Table</TableHead>
                            <TableHead>Voided items</TableHead>
                            <TableHead>Reason</TableHead>
                            <TableHead>When</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {voidedInView.map((order) => {
                            const voidedCount = (order.items || []).filter((it) => it.voided).length;
                            const total = (order.items || []).length;
                            return (
                              <TableRow key={order.id} data-testid={`owner-voided-row-${order.id}`} className={highlightOrderId && (String(order.id) === String(highlightOrderId) || (order.order_number && String(order.order_number) === String(highlightOrderId))) ? "bg-rose-500/10 ring-2 ring-inset ring-rose-400/60" : undefined} data-highlighted={highlightOrderId && (String(order.id) === String(highlightOrderId) || (order.order_number && String(order.order_number) === String(highlightOrderId))) ? "true" : undefined}>
                                <TableCell
                                  className="cursor-pointer font-medium"
                                  onClick={() => setOrderReceipt({ open: true, order })}
                                >
                                  {order.order_number}
                                </TableCell>
                                <TableCell>{order.table_number}</TableCell>
                                <TableCell>{voidedCount} of {total}</TableCell>
                                <TableCell className="max-w-[280px] truncate text-muted-foreground">
                                  {order.void_reason || (order.items || []).find((it) => it.void_reason)?.void_reason || "—"}
                                </TableCell>
                                <TableCell className="text-muted-foreground">{getOrderTimeLabel(order.voided_at || order.updated_at || order.created_at)}</TableCell>
                                <TableCell className="text-right">
                                  {order.status === "voided" ? (
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                                          data-testid={`owner-voided-delete-${order.id}`}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                          Delete
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Permanently delete this voided order?</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Order {order.order_number} will be removed from history for good. You won't be able to reference it in reports afterwards.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction className="bg-destructive text-white hover:bg-destructive/90" onClick={() => deleteVoidedOrder(order.id)}>Delete</AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">Partial void</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      <EmptyState icon={Ban} title="Nothing voided" description={voidedOrders.length ? `No voided orders for ${windowLabel}. Change the date filter on the Active tab to see others.` : "Orders voided by staff appear here for audit."} />
                    )}
                  </CardContent>
                </Card>
                </>
                  );
                })()}
              </TabsContent>

              <TabsContent value="tips" className="mt-4 space-y-4">
                {/* Session 37 — Exceptions for review: facts, not accusations. */}
                {tipsLoaded ? (
                  <Card className={`rounded-[24px] ${tipExceptions.length ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-card"}`} data-testid="tip-exceptions-card">
                    <CardContent className="p-4">
                      <button
                        type="button"
                        onClick={() => setTipExceptionsOpen((v) => !v)}
                        className="flex w-full items-center justify-between gap-3 text-left"
                        data-testid="tip-exceptions-toggle"
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className={`h-4 w-4 ${tipExceptions.length ? "text-amber-600 dark:text-amber-300" : "text-muted-foreground"}`} />
                          <p className="text-sm font-semibold">Gratuity exceptions for review</p>
                          <Badge variant="outline" data-testid="tip-exceptions-count">{tipExceptions.length}</Badge>
                        </div>
                        <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${tipExceptionsOpen ? "rotate-90" : ""}`} />
                      </button>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Objective flags only — tables closed with the PIN while still owing (with tips recorded), tips recorded while a bill was short, repeated identical tips, tips on voided sales, and manager-authorised tip changes. Nothing here is an accusation; each row is something to check.
                      </p>
                      {tipExceptionsOpen ? (
                        tipExceptions.length ? (
                          <div className="mt-3 divide-y divide-border/60 rounded-2xl border border-border bg-background" data-testid="tip-exceptions-list">
                            {tipExceptions.slice(0, 50).map((e, i) => {
                              const labels = {
                                shortfall_with_tip: "Closed short · tip recorded",
                                closed_unpaid: "Closed unpaid (PIN)",
                                tip_while_outstanding: "Tip while bill short",
                                possible_duplicate_tip: "Possible duplicate tip",
                                tip_on_voided_sale: "Tip on voided sale",
                                tip_changed: "Tip changed (PIN)",
                                large_tip: "Large tip",
                                late_payment_rejected: "Late payment refused",
                                payment_edit_rejected: "Edit refused",
                              };
                              return (
                                <div key={`${e.kind}-${e.table_session_id}-${i}`} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2 text-xs" data-testid={`tip-exception-${e.kind}`}>
                                  <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Badge variant="outline" className="text-[10px]">{labels[e.kind] || e.kind}</Badge>
                                      <span className="font-medium">Table {e.table_number || "—"}</span>
                                      {e.waiter ? <span className="text-muted-foreground">waiter {e.waiter}</span> : null}
                                      {e.by || e.closed_by ? <span className="text-muted-foreground">by {e.by || e.closed_by}</span> : null}
                                    </div>
                                    <p className="mt-0.5 text-muted-foreground">{e.detail}</p>
                                  </div>
                                  <div className="text-right text-muted-foreground">
                                    {e.tip != null ? <p className="font-semibold text-foreground">Tip {formatPrice(e.tip)}</p> : null}
                                    {e.outstanding != null ? <p>Short {formatPrice(e.outstanding)}</p> : null}
                                    {e.previous_tip != null && e.new_tip != null ? <p className="font-semibold text-foreground">{formatPrice(e.previous_tip)} → {formatPrice(e.new_tip)}</p> : null}
                                    <p>{getOrderTimeLabel(e.at)}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="mt-3 text-xs text-muted-foreground" data-testid="tip-exceptions-empty">No exceptions in this period.</p>
                        )
                      ) : null}
                    </CardContent>
                  </Card>
                ) : null}
                <Card className="rounded-[24px] border-border bg-card">
                  <CardContent className="p-0">
                    {!tipsLoaded ? (
                      <div className="p-8"><Skeleton className="h-24 w-full" /></div>
                    ) : tipsRows.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Waiter</TableHead>
                            <TableHead>Orders</TableHead>
                            <TableHead>Last activity</TableHead>
                            <TableHead className="text-right">Tips owed</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tipsRows.map((row, idx) => {
                            const key = row.recipient || "__unassigned__";
                            const busy = tipsBusy === key;
                            return (
                              <TableRow
                                key={key + idx}
                                className="cursor-pointer transition-colors hover:bg-secondary/50"
                                onClick={() => openTipDetail(row.recipient || "")}
                                data-testid={`owner-tip-row-${key}`}
                              >
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    {row.recipient || <span className="italic text-muted-foreground">Unassigned</span>}
                                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                  </div>
                                </TableCell>
                                <TableCell>{row.count}</TableCell>
                                <TableCell className="text-muted-foreground">{getOrderTimeLabel(row.last_at)}</TableCell>
                                <TableCell className="text-right font-semibold">
                                  {formatPrice(row.total)}
                                  {Number(row.service_fee_total) > 0 ? (
                                    <p className="text-[10px] font-normal text-muted-foreground" data-testid={`owner-tip-breakdown-${key}`}>
                                      Service fee {formatPrice(row.service_fee_total)} + tips {formatPrice(row.extra_tip_total ?? Math.max(0, (row.total || 0) - row.service_fee_total))}
                                    </p>
                                  ) : null}
                                </TableCell>
                                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="rounded-xl"
                                        disabled={busy}
                                        data-testid={`owner-tip-cashup-${key}`}
                                      >
                                        {busy ? "Cashing up..." : "Cash up"}
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Cash up tips?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Mark {formatPrice(row.total)} of pending tips
                                          {row.recipient ? ` for ${row.recipient}` : " (unassigned)"} as paid out.
                                          This removes them from the pending list — orders are kept for audit.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                                          onClick={() => cashUpTip(row.recipient)}
                                        >
                                          Cash up
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow className="bg-secondary/40 font-semibold">
                            <TableCell colSpan={3}>Total pending</TableCell>
                            <TableCell className="text-right">
                              {formatPrice(tipsRows.reduce((s, r) => s + (r.total || 0), 0))}
                              {tipsRows.some((r) => Number(r.service_fee_total) > 0) ? (
                                <p className="text-[10px] font-normal text-muted-foreground" data-testid="owner-tips-total-breakdown">
                                  Service fee {formatPrice(tipsRows.reduce((s, r) => s + (Number(r.service_fee_total) || 0), 0))} + tips {formatPrice(tipsRows.reduce((s, r) => s + (Number(r.extra_tip_total ?? Math.max(0, (r.total || 0) - (r.service_fee_total || 0))) || 0), 0))}
                                </p>
                              ) : null}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <EmptyState icon={Receipt} title="No pending tips" description="Tips logged on receipts appear here for cash-up." />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Force-delete confirmation. Required password gates the action
                because the order disappears from receipts permanently. */}
            <Dialog
              open={hardDeleteDialog.open}
              onOpenChange={(open) => !open && !hardDeleteDialog.submitting && setHardDeleteDialog({ open: false, order: null, password: "", reason: "", submitting: false })}
            >
              <DialogContent className="max-w-md" data-testid="owner-hard-delete-dialog">
                <DialogHeader>
                  <DialogTitle>Delete order from receipts?</DialogTitle>
                  <DialogDescription>
                    Order <span className="font-mono font-semibold">#{hardDeleteDialog.order?.order_number}</span> for table <span className="font-semibold">{hardDeleteDialog.order?.table_number}</span>{hardDeleteDialog.order?.__rounds > 1 ? <> (all <span className="font-semibold">{hardDeleteDialog.order.__rounds} rounds</span> on this receipt)</> : null} will be permanently removed. It will no longer appear in receipts, voided history, exports, or the bookkeeper email.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                    <strong className="font-semibold">This cannot be undone.</strong> Stock that was deducted by this order will not be restored automatically — adjust stock manually if needed.
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hard-delete-password">Void password</Label>
                    <Input
                      id="hard-delete-password"
                      type="password"
                      value={hardDeleteDialog.password}
                      onChange={(e) => setHardDeleteDialog((s) => ({ ...s, password: e.target.value }))}
                      placeholder="Enter the void password"
                      autoFocus
                      data-testid="owner-hard-delete-password"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hard-delete-reason">Reason (optional, audit trail)</Label>
                    <Input
                      id="hard-delete-reason"
                      value={hardDeleteDialog.reason}
                      onChange={(e) => setHardDeleteDialog((s) => ({ ...s, reason: e.target.value }))}
                      placeholder="e.g. Test order — exclude from books"
                      maxLength={240}
                      data-testid="owner-hard-delete-reason"
                    />
                  </div>
                </div>
                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setHardDeleteDialog({ open: false, order: null, password: "", reason: "", submitting: false })}
                    disabled={hardDeleteDialog.submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-destructive text-white hover:bg-destructive/90"
                    onClick={submitHardDelete}
                    disabled={hardDeleteDialog.submitting || !hardDeleteDialog.password}
                    data-testid="owner-hard-delete-confirm"
                  >
                    <Trash2 className="h-4 w-4" />
                    {hardDeleteDialog.submitting ? "Deleting…" : "Delete permanently"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-4">
            <Tabs value={analyticsSubtab} onValueChange={(v) => {
              setAnalyticsSubtab(v);
              if (v === "service-times" && !serviceTimes) loadServiceTimes(analyticsRange);
              if (v === "service-times" && !orderTimesLoaded) loadOrderTimes(analyticsRange);
              if (v === "stock" && !stockLoaded) loadStock();
              if (v === "stock" && !ingredientsLoaded) loadIngredients();
              if (v === "stock" && !recipesSummaryLoaded) loadRecipesSummary();
              if (v === "reports" && !growthStats) loadGrowth();
              if (v === "audit" && !auditLoaded) loadAudit();
              if (v === "business-summary" && !businessSummary) loadBusinessSummary(bsRange);
              if (v === "business-summary" && !serviceTimes) loadServiceTimes(analyticsRange);
              if (v === "business-summary" && !orderTimesLoaded) loadOrderTimes(analyticsRange);
              if (v === "schedules" && !reportSchedulesLoaded) loadReportSchedules();
            }}>
              {/* Old inner subtab row is now driven by the top-level
                  Reports / Inventory group navigation. */}
              {/* Hidden — owner-nav top row drives subtab selection. */}
              <TabsList className="hidden">
                <TabsTrigger value="business-summary" data-testid="analytics-subtab-business-summary">Business Summary</TabsTrigger>
                <TabsTrigger value="staff-sales" data-testid="analytics-subtab-staff-sales">Staff Sales</TabsTrigger>
                <TabsTrigger value="stock" data-testid="analytics-subtab-stock">Stock</TabsTrigger>
                <TabsTrigger value="receipts" data-testid="analytics-subtab-receipts">Receipts</TabsTrigger>
                <TabsTrigger value="schedules" data-testid="analytics-subtab-schedules">Schedules</TabsTrigger>
                <TabsTrigger value="spreadsheets" data-testid="analytics-subtab-spreadsheets">Spreadsheets</TabsTrigger>
                <TabsTrigger value="audit" data-testid="analytics-subtab-audit">Audit log</TabsTrigger>
                <TabsTrigger value="profit" data-testid="analytics-subtab-profit">Expenses &amp; profit</TabsTrigger>
              </TabsList>

              <TabsContent value="spreadsheets" className="space-y-4 pt-4" data-testid="spreadsheets-tab">
                <SectionHeader eyebrow="Reports" title="Spreadsheets" description="Excel-style workbooks with formulas, formatting and multiple sheets — saved with a full version history so you can open any sheet exactly as it was on a past date." />
                <SpreadsheetsWorkspace
                  http={http}
                  authHeaders={authHeaders}
                  businessName={context?.business?.name}
                  initialOpenId={spreadsheetOpenId}
                  onConsumedInitialOpen={() => setSpreadsheetOpenId(null)}
                />
              </TabsContent>

              <TabsContent value="business-summary" className="space-y-4 pt-4" data-testid="business-summary-tab">
                <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
                  <SectionHeader eyebrow="Reports" title="Business Summary" description={businessSummary ? `${new Date(businessSummary.range.start).toLocaleDateString()} → ${new Date(businessSummary.range.end).toLocaleDateString()}` : "Loading…"} />
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" className="rounded-xl" onClick={() => loadBusinessSummary(bsRange)} disabled={businessSummaryLoading} data-testid="business-summary-refresh">{businessSummaryLoading ? "Refreshing…" : "Refresh"}</Button>
                    <Button variant="outline" size="sm" className="rounded-xl" onClick={() => window.print()} data-testid="business-summary-print">Print</Button>
                    <Button variant="outline" size="sm" className="rounded-xl" onClick={emailBusinessSummary} data-testid="business-summary-email">Email</Button>
                    <Button variant="outline" size="sm" className="rounded-xl" onClick={exportBusinessSummaryCSV} data-testid="business-summary-csv">Export CSV</Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      disabled={!businessSummary}
                      onClick={() => {
                        // Session 30.40 — open this report as an editable workbook.
                        const sm = businessSummary?.summary || {};
                        const start = (businessSummary?.range?.start || "").slice(0, 10);
                        const end = (businessSummary?.range?.end || "").slice(0, 10);
                        const rows = [
                          ["Business Summary", `${start} → ${end}`],
                          [],
                          ["Metric", "Value (R)"],
                          ["Total orders", Number(sm.total_orders || 0)],
                          ["Total revenue", Number(sm.total_revenue || 0)],
                          ["Cash revenue", Number(sm.cash_revenue || 0)],
                          ["Card revenue", Number(sm.card_revenue || 0)],
                          ["Split revenue", Number(sm.split_revenue || 0)],
                          ["Total tips", Number(sm.total_tips || 0)],
                          ["Refund total", Number(sm.refund_total || 0)],
                          ["Void total", Number(sm.void_total || 0)],
                          ["Outstanding tabs", Number(sm.open_accounts_total || 0)],
                          ["Cash variance", Number(sm.cash_variance_total || 0)],
                          [],
                          ["Top items", "Qty", "Revenue (R)", "% of revenue"],
                          ...((businessSummary?.items || []).map((r) => [r.name || "", Number(r.quantity || 0), Number(r.revenue || 0), Number(r.percentage || 0)])),
                          [],
                          ["Category mix", "Qty", "Revenue (R)", "% of revenue"],
                          ...((businessSummary?.categories || []).map((c) => [c.category || "", Number(c.quantity || 0), Number(c.revenue || 0), Number(c.percentage || 0)])),
                        ];
                        sendRowsToSpreadsheet(`Business summary ${start} → ${end}`, rows, "business_summary");
                      }}
                      data-testid="business-summary-to-spreadsheet"
                    >
                      Send to spreadsheet
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => openClearHistoryDialog("reports")} data-testid="business-summary-clear-history">
                      <Trash2 className="h-4 w-4" />
                      Clear history
                    </Button>
                  </div>
                </div>

                {/* Date range picker — owners can pick a preset or custom dates. */}
                <Card className="rounded-2xl border-border bg-card print:hidden" data-testid="business-summary-range-card">
                  <CardContent className="space-y-3 p-4">
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        const today = new Date();
                        const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                        const presets = [
                          { v: "today",      label: "Today",       start: isoLocal(today), end: isoLocal(today) },
                          { v: "yesterday",  label: "Yesterday",   start: (() => { const d = new Date(today); d.setDate(d.getDate()-1); return isoLocal(d); })(), end: (() => { const d = new Date(today); d.setDate(d.getDate()-1); return isoLocal(d); })() },
                          { v: "7d",         label: "Last 7 days", start: (() => { const d = new Date(today); d.setDate(d.getDate()-6); return isoLocal(d); })(), end: isoLocal(today) },
                          { v: "30d",        label: "Last 30 days",start: (() => { const d = new Date(today); d.setDate(d.getDate()-29); return isoLocal(d); })(), end: isoLocal(today) },
                          { v: "this_month", label: "This month",  start: isoLocal(new Date(today.getFullYear(), today.getMonth(), 1)), end: isoLocal(today) },
                          { v: "last_month", label: "Last month",  start: isoLocal(new Date(today.getFullYear(), today.getMonth()-1, 1)), end: isoLocal(new Date(today.getFullYear(), today.getMonth(), 0)) },
                          { v: "this_year",  label: "This year",   start: isoLocal(new Date(today.getFullYear(), 0, 1)), end: isoLocal(today) },
                          { v: "last_year",  label: "Last year",   start: isoLocal(new Date(today.getFullYear()-1, 0, 1)), end: isoLocal(new Date(today.getFullYear()-1, 11, 31)) },
                          { v: "all",        label: "All time",    start: "2020-01-01", end: isoLocal(today) },
                        ];
                        return presets.map((p) => (
                          <Button key={p.v} type="button" size="sm" variant={bsRange.preset === p.v ? "default" : "outline"} className="rounded-xl" data-testid={`bs-preset-${p.v}`} onClick={() => { const next = { preset: p.v, start: p.start, end: p.end }; setBsRange(next); loadBusinessSummary(next); }}>{p.label}</Button>
                        ));
                      })()}
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                      <div>
                        <Label htmlFor="bs-start" className="text-[11px]">From</Label>
                        <Input id="bs-start" type="date" value={bsRange.start} onChange={(e) => setBsRange((s) => ({ ...s, preset: "custom", start: e.target.value }))} className="h-10 rounded-xl" data-testid="bs-start" />
                      </div>
                      <div>
                        <Label htmlFor="bs-end" className="text-[11px]">To</Label>
                        <Input id="bs-end" type="date" value={bsRange.end} onChange={(e) => setBsRange((s) => ({ ...s, preset: "custom", end: e.target.value }))} className="h-10 rounded-xl" data-testid="bs-end" />
                      </div>
                      <Button type="button" size="sm" className="h-10 rounded-xl" data-testid="bs-apply" onClick={() => {
                        if (!bsRange.start || !bsRange.end) { toast.error("Pick both From and To dates."); return; }
                        if (bsRange.start > bsRange.end) { toast.error("'From' must be before 'To'."); return; }
                        loadBusinessSummary(bsRange);
                      }}>Apply custom range</Button>
                    </div>
                  </CardContent>
                </Card>
                {businessSummaryLoading ? (<Skeleton className="h-48 w-full rounded-2xl" />) : businessSummary ? (<>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="business-summary-cards">
                    {[{k:"total_orders",label:"Total orders",num:true},{k:"total_revenue",label:"Total revenue",tooltip:"Sum of Cash + Card + Split revenue across all non-voided orders in the selected period (incl. VAT). Tips are NOT included."},{k:"cash_revenue",label:"Cash revenue",tooltip:"Orders paid 100% by cash."},{k:"card_revenue",label:"Card revenue",tooltip:"Orders paid 100% by card."},{k:"split_revenue",label:"Split revenue",tooltip:"Orders paid with a mix of cash + card."},{k:"total_tips",label:businessSummary.summary.service_fee_separate ? "Total tips (extra tips)" : "Total tips",note:"Not included in total revenue",tooltip:businessSummary.summary.service_fee_separate ? "Extra tips left for staff. The automatic service fee is reported separately and is not paid to staff." : "Gratuities collected for staff. Tips are kept separate and never counted as revenue."},...(businessSummary.summary.service_fee_separate ? [{k:"service_fee_total",label:"Service fee",note:"Not included in total revenue",tooltip:"Automatic service fee collected on bills in this period. Kept separate from tips and from revenue."}] : []),{k:"refund_total",label:"Refund total"},{k:"void_total",label:"Void total"},{k:"open_accounts_total",label:"Outstanding tabs"},{k:"cash_variance_total",label:"Cash variance"}].map((c) => (
                      <div key={c.k} className="rounded-2xl border border-border bg-card p-3" data-testid={`business-summary-${c.k}`} title={c.tooltip || ""}>
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{c.label}{c.tooltip ? " ⓘ" : ""}</p>
                        <p className="mt-1 text-xl font-semibold tabular-nums">{c.num ? (businessSummary.summary[c.k] || 0) : formatPrice(businessSummary.summary[c.k] || 0)}</p>
                        {c.note ? <p className="mt-1 text-[10px] font-medium text-amber-700 dark:text-amber-300" data-testid={`business-summary-${c.k}-note`}>{c.note}</p> : null}
                      </div>
                    ))}
                  </div>

                  {/* ── Session 31.14 — Expenses & profit for the same range ── */}
                  {businessProfit ? (() => {
                    const bp = businessProfit;
                    const be = bp.break_even || {};
                    const pct = Math.max(0, Math.min(100, Number(be.pct || 0)));
                    const profit = Number(bp.profit || 0);
                    const netSales = Number(bp.sales?.net || 0);
                    const margin = netSales > 0 ? Math.round((profit / netSales) * 1000) / 10 : null;
                    const reached = !!be.reached_on || (Number(be.target || 0) > 0 && Number(be.remaining || 0) <= 0);
                    const cogsTotal = Number(bp.cost_of_goods?.total || 0);
                    const cogsBasis = bp.cost_of_goods?.basis;
                    return (
                      <Card className="rounded-2xl border-border bg-card" data-testid="business-summary-profit">
                        <CardContent className="space-y-4 p-4">
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <p className="text-sm font-semibold">Expenses & profit for this period</p>
                            <button type="button" className="text-[11px] font-medium text-muted-foreground underline-offset-2 hover:underline" onClick={() => setAnalyticsSubtab("profit")} data-testid="business-summary-open-profit">Open Expenses & Profit →</button>
                          </div>
                          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            <div className="rounded-2xl border border-border bg-background p-3" data-testid="business-summary-expenses">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Expenses</p>
                              <p className="mt-1 text-xl font-semibold tabular-nums">{formatPrice(bp.expenses?.total || 0)}</p>
                              <p className="mt-1 text-[10px] text-muted-foreground">Recurring costs, pro-rated to the range</p>
                            </div>
                            <div className="rounded-2xl border border-border bg-background p-3" data-testid="business-summary-cogs">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Cost of goods</p>
                              <p className="mt-1 text-xl font-semibold tabular-nums">{formatPrice(cogsTotal)}</p>
                              <p className="mt-1 text-[10px] text-muted-foreground">{cogsBasis === "purchases" ? "Ingredient purchases" : "Ingredient cost of what was sold"}</p>
                            </div>
                            <div className={`rounded-2xl border p-3 ${profit >= 0 ? "border-emerald-500/40 bg-emerald-500/5" : "border-destructive/40 bg-destructive/5"}`} data-testid="business-summary-net-profit">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Net profit</p>
                              <p className={`mt-1 text-xl font-semibold tabular-nums ${profit >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-destructive"}`}>{profit < 0 ? "−" : ""}{formatPrice(Math.abs(profit))}</p>
                              <p className="mt-1 text-[10px] text-muted-foreground">{margin === null || margin === undefined ? "No sales yet" : `${margin}% margin · excl. tips`}</p>
                            </div>
                            <div className="rounded-2xl border border-border bg-background p-3" data-testid="business-summary-break-even-remaining">
                              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{reached ? "Break-even reached" : "Still to break even"}</p>
                              <p className="mt-1 text-xl font-semibold tabular-nums">{reached ? (be.reached_on ? new Date(`${be.reached_on}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "Yes") : formatPrice(be.remaining || 0)}</p>
                              <p className="mt-1 text-[10px] text-muted-foreground">{reached ? "costs covered — everything since is profit" : "more gross margin needed to cover expenses"}</p>
                            </div>
                          </div>
                          <div data-testid="business-summary-break-even">
                            <div className="mb-1 flex items-center justify-between text-xs">
                              <span className="font-medium">Break-even progress</span>
                              <span className="tabular-nums text-muted-foreground">{formatPrice(be.covered || 0)} of {formatPrice(be.target || 0)} · {Math.round(pct)}%</span>
                            </div>
                            <div className="h-3 w-full overflow-hidden rounded-full bg-secondary" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
                              <div className={`h-full rounded-full transition-[width] duration-500 ${reached ? "bg-emerald-500" : "bg-[hsl(var(--accent))]"}`} style={{ width: `${pct}%` }} data-testid="business-summary-break-even-bar" />
                            </div>
                            <p className="mt-1 text-[11px] text-muted-foreground">Sales (excluding tips{bp.settings?.profit_basis === "excl_vat" ? " and VAT" : ""}{bp.settings?.paid_only ? ", paid orders only" : ""}) minus cost of goods, measured against your expenses for the same dates. Change these rules under Expenses & Profit.</p>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })() : null}

                  {/* ── Business Performance — growth comparisons ─────── */}
                  {businessSummary.growth ? (
                    <Card className="rounded-2xl border-border bg-card" data-testid="business-performance">
                      <CardContent className="p-4">
                        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                          <p className="text-sm font-semibold">Business Performance</p>
                          <p className="text-[11px] text-muted-foreground">Each card compares the active period to the immediately preceding equal-length period.</p>
                        </div>
                        {(() => {
                          const Delta = ({ delta, pct, money, testid }) => {
                            if (pct === null || pct === undefined) return <span className="text-xs text-muted-foreground" data-testid={testid}>—</span>;
                            const positive = pct >= 0;
                            const arrow = positive ? "▲" : "▼";
                            const sign = positive ? "+" : "";
                            return (
                              <span className={`text-xs font-semibold ${positive ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`} data-testid={testid}>
                                {arrow} {sign}{money ? formatPrice(Math.abs(delta || 0)) : Math.abs(delta || 0)} ({sign}{pct}%)
                              </span>
                            );
                          };
                          const labels = [
                            { k: "custom", title: "Selected range" },
                            { k: "day",   title: "Today vs Yesterday" },
                            { k: "week",  title: "This Week vs Last" },
                            { k: "month", title: "This Month vs Last" },
                            { k: "year",  title: "This Year vs Last" },
                          ];
                          return (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                              {labels.map(({ k, title }) => {
                                const b = businessSummary.growth[k] || {};
                                return (
                                  <div key={k} className="rounded-xl border border-border bg-secondary/30 p-3" data-testid={`perf-${k}`}>
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
                                    {b.current_label ? <p className="mt-0.5 text-[9px] text-muted-foreground">{b.current_label} vs {b.previous_label}</p> : null}
                                    <div className="mt-2 space-y-2 text-[11px]">
                                      <div>
                                        <p className="text-muted-foreground">Revenue</p>
                                        <p className="font-semibold">{formatPrice(b.current_revenue || 0)} <span className="text-muted-foreground">vs {formatPrice(b.previous_revenue || 0)}</span></p>
                                        <Delta delta={b.delta_revenue} pct={b.revenue_pct} money testid={`perf-${k}-revenue`} />
                                      </div>
                                      <div>
                                        <p className="text-muted-foreground">Orders</p>
                                        <p className="font-semibold">{b.current_orders || 0} <span className="text-muted-foreground">vs {b.previous_orders || 0}</span></p>
                                        <Delta delta={b.delta_orders} pct={b.orders_pct} testid={`perf-${k}-orders`} />
                                      </div>
                                      <div>
                                        <p className="text-muted-foreground">Avg order value</p>
                                        <p className="font-semibold">{formatPrice(b.current_aov || 0)} <span className="text-muted-foreground">vs {formatPrice(b.previous_aov || 0)}</span></p>
                                        <Delta delta={b.delta_aov} pct={b.aov_pct} money testid={`perf-${k}-aov`} />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  ) : null}

                  {/* ── Costed Item Profit ───────────────────────────── */}
                  {(() => {
                    const s = businessSummary.summary;
                    const tracked = !!s.costs_tracked;
                    const cr = Number(s.costed_revenue || 0);
                    const tr = Number(s.total_revenue || 0);
                    const cov = s.cost_coverage_pct ?? 0;
                    const dataUnavailable = !tracked || cr <= 0 || tr <= 0;
                    // Per the refinement-pass spec: when coverage is
                    // below 80% we hide profit numbers entirely and tell
                    // the manager that costing setup is incomplete.
                    const COVERAGE_THRESHOLD = 80;
                    const profitUnreliable = tracked && cr > 0 && cov < COVERAGE_THRESHOLD;
                    const src = s.cost_sources || {};
                    // Missing costed items = items in the period without
                    // any cost source. We surface them by name from the
                    // current sales mix.
                    const missing = (businessSummary.items || []).slice(0, 8); // top items so manager knows priorities
                    return (
                      <Card className={`rounded-2xl ${dataUnavailable || profitUnreliable ? "border-dashed border-border" : "border-emerald-500/40 bg-emerald-500/5"}`} data-testid="costed-profit-card">
                        <CardContent className="space-y-3 p-4">
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <p className="text-sm font-semibold">Costed Item Profit</p>
                            <p className="text-[11px] text-muted-foreground">Costs come from Menu Item → Recipe → Ingredients → Inventory.</p>
                          </div>
                          {dataUnavailable ? (
                            <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-4 text-center" data-testid="cost-data-unavailable">
                              <p className="text-sm font-semibold">Cost data unavailable</p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {!tracked
                                  ? "No items have cost data yet. Set ingredient costs in Inventory → Ingredients, then attach recipes to your menu items."
                                  : "No costed items were sold in the selected period. Try a wider date range or finish costing your remaining items."}
                              </p>
                            </div>
                          ) : profitUnreliable ? (
                            <div className="space-y-3" data-testid="profit-reporting-unavailable">
                              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">Profit reporting unavailable. Costing setup is incomplete.</p>
                                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Cost Coverage: <span className="font-semibold" data-testid="cost-coverage-pct">{cov}%</span> (needs ≥ {COVERAGE_THRESHOLD}%). We've hidden the profit values to avoid misleading you.</p>
                              </div>
                              <div className="rounded-xl border border-border bg-card p-3 text-xs">
                                <p className="mb-2 font-semibold">Top selling items in this range — review costing setup:</p>
                                <ul className="space-y-1">
                                  {missing.map((m) => (<li key={m.name} className="flex justify-between"><span>{m.name}</span><span className="text-muted-foreground">{m.quantity} sold · {formatPrice(m.revenue)}</span></li>))}
                                </ul>
                                <p className="mt-2 text-[10px] text-muted-foreground">Cost sources: {src.items_with_recipe_cost || 0} item(s) via recipe, {src.items_with_direct_cost || 0} item(s) via direct cost. Ingredients with cost set: {src.ingredients_with_cost || 0}/{src.total_ingredients || 0}.</p>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <div className="rounded-xl border border-border bg-card p-3" title="Revenue generated from items that have valid cost data.">
                                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Costed Revenue ⓘ</p>
                                  <p className="mt-1 text-lg font-semibold">{formatPrice(s.costed_revenue || 0)}</p>
                                </div>
                                <div className="rounded-xl border border-border bg-card p-3">
                                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Costed Costs</p>
                                  <p className="mt-1 text-lg font-semibold">{formatPrice(s.costed_cost || 0)}</p>
                                </div>
                                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3">
                                  <p className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Costed Item Profit</p>
                                  <p className="mt-1 text-lg font-semibold text-emerald-700 dark:text-emerald-300" data-testid="costed-item-profit">{formatPrice(s.costed_item_profit || 0)}</p>
                                </div>
                                <div className="rounded-xl border border-border bg-card p-3" title="Percentage of revenue that can be accurately costed using inventory and recipe data.">
                                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Cost Coverage % ⓘ</p>
                                  <p className="mt-1 text-lg font-semibold" data-testid="cost-coverage-pct">{cov}%</p>
                                </div>
                              </div>
                              <p className="text-[10px] text-muted-foreground">Cost sources: {src.items_with_recipe_cost || 0} via recipe, {src.items_with_direct_cost || 0} direct. Ingredients with cost set: {src.ingredients_with_cost || 0}/{src.total_ingredients || 0}.</p>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* ── Revenue + Orders Over Time chart ─────────────── */}
                  {businessSummary.daily?.length ? (
                    <Card className="rounded-2xl border-border bg-card" data-testid="business-summary-chart">
                      <CardHeader>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <CardTitle className="text-base">Revenue + Orders Over Time</CardTitle>
                            <CardDescription>Combo chart — bars show order count, line shows revenue (for the selected range).</CardDescription>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button type="button" variant={chartShowBars ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setChartShowBars((v) => !v)} data-testid="bs-chart-toggle-bars">{chartShowBars ? "Hide bars" : "Show bars"}</Button>
                            <Button type="button" variant={chartShowLine ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setChartShowLine((v) => !v)} data-testid="bs-chart-toggle-line">{chartShowLine ? "Hide line" : "Show line"}</Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={businessSummary.daily} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                              <XAxis dataKey="date" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} stroke="hsl(var(--border))" />
                              <YAxis yAxisId="revenue" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} tickFormatter={(v) => `R ${v}`} stroke="hsl(var(--border))" />
                              <YAxis yAxisId="orders" orientation="right" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} stroke="hsl(var(--border))" allowDecimals={false} />
                              <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '12px', padding: '12px' }} labelFormatter={(v) => new Date(v).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })} formatter={(v, name) => name === "Revenue" ? [`R ${Number(v).toFixed(2)}`, name] : [`${v}`, name]} />
                              <Legend />
                              {chartShowBars && (<Bar yAxisId="orders" dataKey="orders" name="Orders" fill="hsl(var(--accent))" opacity={0.65} radius={[6,6,0,0]} maxBarSize={32} />)}
                              {chartShowLine && (<Line yAxisId="revenue" type="monotone" dataKey="revenue" name="Revenue" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ fill: 'hsl(var(--primary))', r: 4 }} />)}
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                  {businessSummary.summary.top_selling_item || (businessSummary.categories || []).length ? (
                    <div className="grid gap-3 md:grid-cols-2">
                      {businessSummary.summary.top_selling_item ? (
                        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="business-summary-top-item">
                          <p className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">Top selling item</p>
                          <p className="mt-1 text-lg font-semibold">{businessSummary.summary.top_selling_item.name}</p>
                          <p className="text-sm text-muted-foreground">{businessSummary.summary.top_selling_item.quantity} sold · {formatPrice(businessSummary.summary.top_selling_item.revenue)}</p>
                        </div>
                      ) : null}
                      {(businessSummary.categories || []).length ? (() => {
                        const best = [...(businessSummary.categories || [])].sort((a, b) => b.revenue - a.revenue)[0];
                        return (
                          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="business-summary-best-category">
                            <p className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Best selling category</p>
                            <p className="mt-1 text-lg font-semibold">{best.category}</p>
                            <p className="text-sm text-muted-foreground">{best.quantity} items · {formatPrice(best.revenue)} · {best.percentage}%</p>
                          </div>
                        );
                      })() : null}
                    </div>
                  ) : null}
                  <div className="rounded-2xl border border-border bg-card">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
                      <p className="text-sm font-semibold">
                        {businessSummarySort === "all_items" ? `Sales Mix · All items sold (${(businessSummary.items || []).length})` : "Sales Mix · Top items"}
                      </p>
                      <Select value={businessSummarySort} onValueChange={setBusinessSummarySort}>
                        <SelectTrigger className="h-8 w-44 rounded-lg text-xs" data-testid="business-summary-sort"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all_items">All items sold · A–Z</SelectItem>
                          <SelectItem value="revenue_desc">Revenue · highest</SelectItem>
                          <SelectItem value="revenue_asc">Revenue · lowest</SelectItem>
                          <SelectItem value="quantity_desc">Quantity · highest</SelectItem>
                          <SelectItem value="quantity_asc">Quantity · lowest</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      <Table>
                        <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">% rev</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {[...(businessSummary.items || [])].sort((a, b) => { if (businessSummarySort === "all_items") return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" }); if (businessSummarySort === "revenue_asc") return a.revenue - b.revenue; if (businessSummarySort === "quantity_desc") return b.quantity - a.quantity; if (businessSummarySort === "quantity_asc") return a.quantity - b.quantity; return b.revenue - a.revenue; }).map((r) => (
                            <TableRow key={r.name}>
                              <TableCell className="font-medium">{r.name}</TableCell>
                              <TableCell className="text-right">{r.quantity}</TableCell>
                              <TableCell className="text-right">{formatPrice(r.revenue)}</TableCell>
                              <TableCell className="text-right">{r.percentage}%</TableCell>
                            </TableRow>
                          ))}
                          {!businessSummary.items?.length ? (<TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground">No item sales in this range.</TableCell></TableRow>) : null}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border bg-card">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
                      <p className="text-sm font-semibold">
                        {businessSummaryCatSort === "all_categories" ? `Category Sales Mix · All categories (${(businessSummary.categories || []).length})` : "Category Sales Mix"}
                      </p>
                      <Select value={businessSummaryCatSort} onValueChange={setBusinessSummaryCatSort}>
                        <SelectTrigger className="h-8 w-44 rounded-lg text-xs" data-testid="business-summary-category-sort"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all_categories">All categories · A–Z</SelectItem>
                          <SelectItem value="revenue_desc">Revenue · highest</SelectItem>
                          <SelectItem value="revenue_asc">Revenue · lowest</SelectItem>
                          <SelectItem value="quantity_desc">Quantity · highest</SelectItem>
                          <SelectItem value="quantity_asc">Quantity · lowest</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Table>
                      <TableHeader><TableRow><TableHead>Category</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">% rev</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {[...(businessSummary.categories || [])].sort((a, b) => { if (businessSummaryCatSort === "all_categories") return String(a.category || "").localeCompare(String(b.category || ""), undefined, { sensitivity: "base" }); if (businessSummaryCatSort === "revenue_asc") return a.revenue - b.revenue; if (businessSummaryCatSort === "quantity_desc") return b.quantity - a.quantity; if (businessSummaryCatSort === "quantity_asc") return a.quantity - b.quantity; return b.revenue - a.revenue; }).map((c) => (
                          <TableRow key={c.category}>
                            <TableCell className="font-medium">{c.category}</TableCell>
                            <TableCell className="text-right">{c.quantity}</TableCell>
                            <TableCell className="text-right">{formatPrice(c.revenue)}</TableCell>
                            <TableCell className="text-right">{c.percentage}%</TableCell>
                          </TableRow>
                        ))}
                        {!businessSummary.categories?.length ? (<TableRow><TableCell colSpan={4} className="text-center text-xs text-muted-foreground">No category data in this range.</TableCell></TableRow>) : null}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Best 10 / Worst 10 sellers — fed off the same items[] payload */}
                  {(businessSummary.items || []).length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2" data-testid="business-summary-best-worst">
                      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5">
                        <p className="border-b border-border p-3 text-sm font-semibold">🏆 Top 10 Best Sellers</p>
                        <Table>
                          <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">% rev</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {[...(businessSummary.items || [])].sort((a, b) => b.revenue - a.revenue).slice(0, 10).map((r) => (
                              <TableRow key={`best-${r.name}`} data-testid={`best-seller-${r.name}`}>
                                <TableCell className="font-medium">{r.name}</TableCell>
                                <TableCell className="text-right">{r.quantity}</TableCell>
                                <TableCell className="text-right">{formatPrice(r.revenue)}</TableCell>
                                <TableCell className="text-right">{r.percentage}%</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="rounded-2xl border border-destructive/30 bg-destructive/5">
                        <p className="border-b border-border p-3 text-sm font-semibold">⚠️ Bottom 10 Worst Sellers</p>
                        <Table>
                          <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">% rev</TableHead></TableRow></TableHeader>
                          <TableBody>
                            {[...(businessSummary.items || [])].sort((a, b) => a.revenue - b.revenue).slice(0, 10).map((r) => (
                              <TableRow key={`worst-${r.name}`} data-testid={`worst-seller-${r.name}`}>
                                <TableCell className="font-medium">{r.name}</TableCell>
                                <TableCell className="text-right">{r.quantity}</TableCell>
                                <TableCell className="text-right">{formatPrice(r.revenue)}</TableCell>
                                <TableCell className="text-right">{r.percentage}%</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ) : null}

                  {/* ── Operational Performance — service times ──────── */}
                  {serviceTimes ? (
                    <Card className="rounded-2xl border-border bg-card" data-testid="operational-performance">
                      <CardContent className="space-y-3 p-4">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <p className="text-sm font-semibold">Operational Performance · Service Times</p>
                          <p className="text-[11px] text-muted-foreground">Time from order placed to delivered. Based on {serviceTimes.summary.count} completed orders in last {serviceTimes.date_range_days} days.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                          <div className="rounded-xl border border-border bg-secondary/30 p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Average</p><p className="mt-1 text-2xl font-semibold">{serviceTimes.summary.avg_minutes} min</p></div>
                          <div className="rounded-xl border border-border bg-secondary/30 p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Median</p><p className="mt-1 text-2xl font-semibold">{serviceTimes.summary.median_minutes} min</p></div>
                          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Fastest</p><p className="mt-1 text-2xl font-semibold text-emerald-700 dark:text-emerald-300">{serviceTimes.summary.fastest_minutes} min</p></div>
                          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-destructive">Slowest</p><p className="mt-1 text-2xl font-semibold text-destructive">{serviceTimes.summary.slowest_minutes} min</p></div>
                        </div>
                        {serviceTimes.daily?.length ? (
                          <div className="h-[220px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={serviceTimes.daily} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                                <XAxis dataKey="date" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} stroke="hsl(var(--border))" tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} stroke="hsl(var(--border))" tickFormatter={(v) => `${v}m`} />
                                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '12px', padding: '12px' }} formatter={(v) => [`${v} min`, "Avg service time"]} />
                                <Line type="monotone" dataKey="avg_minutes" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ fill: 'hsl(var(--primary))', r: 4 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        ) : null}

                        {/* Order-level drill-down — restored from the old report */}
                        {serviceTimes.orders?.length ? (() => {
                          const sortKey = serviceTimeSort;
                          const sorted = [...serviceTimes.orders].sort((a, b) => {
                            if (sortKey === "fastest") return (a.total_minutes || 0) - (b.total_minutes || 0);
                            if (sortKey === "highest_revenue") return (b.revenue || 0) - (a.revenue || 0);
                            if (sortKey === "most_recent") return (b.placed_at || "").localeCompare(a.placed_at || "");
                            return (b.total_minutes || 0) - (a.total_minutes || 0); // slowest
                          }).slice(0, 50);
                          return (
                            <div className="space-y-2" data-testid="service-times-drilldown">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-xs font-semibold">Slow tickets · top 50</p>
                                <Select value={serviceTimeSort} onValueChange={setServiceTimeSort}>
                                  <SelectTrigger className="h-9 w-44 rounded-xl text-xs" data-testid="service-times-sort"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="slowest">Slowest first</SelectItem>
                                    <SelectItem value="fastest">Fastest first</SelectItem>
                                    <SelectItem value="highest_revenue">Highest revenue</SelectItem>
                                    <SelectItem value="most_recent">Most recent</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="overflow-x-auto rounded-2xl border border-border">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Table</TableHead>
                                      <TableHead>Order #</TableHead>
                                      <TableHead className="hidden sm:table-cell">Waiter</TableHead>
                                      <TableHead className="hidden md:table-cell">Placed</TableHead>
                                      <TableHead className="text-right hidden md:table-cell">Prep</TableHead>
                                      <TableHead className="text-right hidden md:table-cell">Delivery</TableHead>
                                      <TableHead className="text-right">Total</TableHead>
                                      <TableHead className="text-right">Revenue</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {sorted.map((o) => (
                                      <TableRow key={o.order_id} className="cursor-pointer hover:bg-secondary/50" onClick={async () => {
                                        setServiceTimeDetail({ open: true, order: o, loading: true });
                                        try {
                                          const r = await http.get(`/owner/orders/${o.order_id}`, { headers: authHeaders });
                                          setServiceTimeDetail({ open: true, order: { ...o, full: r.data?.order || r.data }, loading: false });
                                        } catch (err) {
                                          // Best-effort drill-down — we still show what we have from the list.
                                          setServiceTimeDetail({ open: true, order: o, loading: false });
                                        }
                                      }} data-testid={`service-time-row-${o.order_id}`}>
                                        <TableCell>{o.table}</TableCell>
                                        <TableCell className="font-medium">{o.order_number}</TableCell>
                                        <TableCell className="hidden sm:table-cell">{o.waiter}</TableCell>
                                        <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{new Date(o.placed_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</TableCell>
                                        <TableCell className="hidden text-right text-xs md:table-cell">{o.prep_minutes !== null ? `${o.prep_minutes}m` : "—"}</TableCell>
                                        <TableCell className="hidden text-right text-xs md:table-cell">{o.delivery_minutes !== null ? `${o.delivery_minutes}m` : "—"}</TableCell>
                                        <TableCell className={`text-right font-semibold ${o.total_minutes >= 30 ? "text-destructive" : ""}`}>{o.total_minutes}m</TableCell>
                                        <TableCell className="text-right text-xs">{formatPrice(o.revenue || 0)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          );
                        })() : null}
                      </CardContent>
                    </Card>
                  ) : null}

                </>) : (<EmptyState icon={Receipt} title="No report yet" description="Pick a date range or hit Refresh." />)}
              </TabsContent>

              <TabsContent value="reports" className="space-y-4 pt-4">
            <SectionHeader eyebrow="Analytics" title="Orders and revenue" description="Daily order volume and estimated revenue." />
            {growthStats ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { key: "day", label: "vs yesterday" },
                  { key: "week", label: "vs last week" },
                  { key: "month", label: "vs last month" },
                  { key: "year", label: "vs last year" },
                ].map(({ key, label }) => {
                  const b = growthStats[key];
                  if (!b) return null;
                  const p = b.revenue_pct;
                  const positive = p !== null && p >= 0;
                  return (
                    <div key={key} className="rounded-2xl border border-border bg-card p-3" data-testid={`growth-${key}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
                      <p className={`mt-1 text-lg font-semibold ${p === null ? "" : positive ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                        {p === null ? "—" : `${positive ? "+" : ""}${p}%`}
                      </p>
                      <p className="text-[11px] text-muted-foreground">R {b.current_revenue.toFixed(2)} vs R {b.previous_revenue.toFixed(2)}</p>
                    </div>
                  );
                })}
              </div>
            ) : null}
            
            {/* Time Range Filters */}
            <div className="flex flex-wrap gap-2">
              {[
                { label: "Last 7 days", days: 7 },
                { label: "Last 30 days", days: 30 },
                { label: "Last 90 days", days: 90 },
                { label: "Last year", days: 365 },
              ].map((filter) => (
                <Button
                  key={filter.days}
                  variant={analyticsRange === filter.days ? "default" : "outline"}
                  size="sm"
                  onClick={() => loadAnalytics(filter.days)}
                  className="rounded-xl"
                  data-testid={`analytics-filter-${filter.days}`}
                >
                  {filter.label}
                </Button>
              ))}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {[
                { label: "Total orders", value: analytics.summary?.total_orders || 0 },
                { label: "Estimated revenue", value: formatPrice(analytics.summary?.estimated_revenue || 0) },
                { label: "Average order amount", value: formatPrice(analytics.summary?.average_order_value || 0) },
              ].map((item) => (
                <Card key={item.label} className="rounded-[24px] border-border bg-card">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground">{item.label}</p>
                    <p className="mt-2 text-3xl font-semibold">{item.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Profit strip — only appears once the owner has started
                entering per-item costs. Stays hidden until then so nothing
                feels broken on a fresh business. */}
            {analytics.summary?.costs_tracked ? (
              <div className="grid gap-4 md:grid-cols-3">
                <Card className="rounded-[24px] border-border bg-card" data-testid="analytics-tracked-revenue">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground">Revenue (costed items)</p>
                    <p className="mt-2 text-3xl font-semibold">{formatPrice(analytics.summary?.tracked_revenue || 0)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">Sales of items with a cost set.</p>
                  </CardContent>
                </Card>
                <Card className="rounded-[24px] border-border bg-card" data-testid="analytics-tracked-cost">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground">Total cost</p>
                    <p className="mt-2 text-3xl font-semibold">{formatPrice(analytics.summary?.tracked_cost || 0)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">Cost × quantity for costed items.</p>
                  </CardContent>
                </Card>
                <Card className="rounded-[24px] border-emerald-500/60 bg-emerald-50/50 dark:bg-emerald-950/30" data-testid="analytics-estimated-profit">
                  <CardContent className="p-5">
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">Estimated profit</p>
                    <p className="mt-2 text-3xl font-semibold text-emerald-700 dark:text-emerald-300">
                      {formatPrice(analytics.summary?.estimated_profit || 0)}
                    </p>
                    <p className="mt-1 text-xs text-emerald-700/80 dark:text-emerald-300/80">Revenue − cost. Only counts items with a cost set.</p>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="rounded-[24px] border-dashed border-border bg-card" data-testid="analytics-costs-empty">
                <CardContent className="flex flex-col gap-2 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold">Track profit per item</p>
                    <p className="text-xs text-muted-foreground">Add a <span className="font-mono">cost</span> on any menu item (Manage Menu → Items → Edit) and this section will show estimated profit based on sales.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Revenue Line Chart */}
            {analytics.daily?.length > 0 && (
              <Card className="rounded-[24px] border-border bg-card">
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-lg">Revenue + Orders Over Time</CardTitle>
                      <CardDescription>Combo chart — bars show order count, line shows revenue.</CardDescription>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant={chartShowBars ? "default" : "outline"}
                        size="sm"
                        className="rounded-full"
                        onClick={() => setChartShowBars((v) => !v)}
                        data-testid="analytics-toggle-bars"
                      >
                        {chartShowBars ? "Hide bars" : "Show bars"}
                      </Button>
                      <Button
                        type="button"
                        variant={chartShowLine ? "default" : "outline"}
                        size="sm"
                        className="rounded-full"
                        onClick={() => setChartShowLine((v) => !v)}
                        data-testid="analytics-toggle-line"
                      >
                        {chartShowLine ? "Hide line" : "Show line"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={[...analytics.daily].reverse()} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          tickFormatter={(value) => {
                            const date = new Date(value);
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          }}
                          stroke="hsl(var(--border))"
                        />
                        {/* Left axis = revenue (line). Right axis = order count (bars).
                            Two axes so the two scales never fight each other. */}
                        <YAxis
                          yAxisId="revenue"
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          tickFormatter={(value) => `R ${value}`}
                          stroke="hsl(var(--border))"
                        />
                        <YAxis
                          yAxisId="orders"
                          orientation="right"
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          stroke="hsl(var(--border))"
                          allowDecimals={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '12px',
                            padding: '12px',
                          }}
                          labelFormatter={(value) => new Date(value).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                          formatter={(value, name) => {
                            if (name === "Revenue") return [`R ${Number(value).toFixed(2)}`, name];
                            return [`${value}`, name];
                          }}
                        />
                        <Legend />
                        {chartShowBars && (
                          <Bar
                            yAxisId="orders"
                            dataKey="orders"
                            name="Orders"
                            fill="hsl(var(--accent))"
                            opacity={0.65}
                            radius={[6, 6, 0, 0]}
                            maxBarSize={32}
                          />
                        )}
                        {chartShowLine && (
                          <Line
                            yAxisId="revenue"
                            type="monotone"
                            dataKey="estimated_revenue"
                            name="Revenue"
                            stroke="hsl(var(--primary))"
                            strokeWidth={3}
                            dot={{
                              fill: 'hsl(var(--primary))',
                              strokeWidth: 2,
                              stroke: 'hsl(var(--background))',
                              r: 4,
                            }}
                            activeDot={{
                              r: 6,
                              strokeWidth: 2,
                              stroke: 'hsl(var(--background))',
                            }}
                          />
                        )}
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Daily Totals Table */}
            <Card className="rounded-[24px] border-border bg-card">
              <CardHeader>
                <CardTitle className="text-lg">Daily totals</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {analytics.daily?.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Orders</TableHead>
                        <TableHead>Estimated revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.daily.map((row) => (
                        <TableRow
                          key={row.date}
                          className="cursor-pointer transition-colors hover:bg-secondary/50"
                          onClick={() => openDailyDetail(row.date)}
                          data-testid={`reports-daily-row-${row.date}`}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {row.date}
                              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                            </div>
                          </TableCell>
                          <TableCell>{row.orders}</TableCell>
                          <TableCell>{formatPrice(row.estimated_revenue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="p-5">
                    <EmptyState icon={BarChart3} title="No analytics yet" description="Orders will appear here after service begins." />
                  </div>
                )}
              </CardContent>
            </Card>
              </TabsContent>

              {/* Session 30.27 — Staff Sales (commission) report. */}
              <TabsContent value="staff-sales" className="space-y-4 pt-4" data-testid="analytics-staff-sales">
                <SectionHeader eyebrow="Team" title="Staff sales" description="What each waiter has rung up — daily, weekly, monthly, and a running figure you reset once commission is paid. Managers, supervisors and runners are not counted." />
                <StaffSalesPanel authHeaders={authHeaders} onSendToSpreadsheet={sendRowsToSpreadsheet} />
              </TabsContent>
              <TabsContent value="service-times" className="space-y-4 pt-4">
                <SectionHeader eyebrow="Speed" title="Order service times" description="Time from order placed to delivered (completed). Lower is better." />
                {serviceTimes ? (
                  <>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <div className="rounded-2xl border border-border bg-card p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Average</p><p className="mt-1 text-2xl font-semibold" data-testid="svc-times-avg">{serviceTimes.summary.avg_minutes} min</p></div>
                      <div className="rounded-2xl border border-border bg-card p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Median</p><p className="mt-1 text-2xl font-semibold">{serviceTimes.summary.median_minutes} min</p></div>
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/50 dark:bg-emerald-950/30"><p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Fastest</p><p className="mt-1 text-2xl font-semibold text-emerald-800 dark:text-emerald-200">{serviceTimes.summary.fastest_minutes} min</p></div>
                      <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-destructive">Slowest</p><p className="mt-1 text-2xl font-semibold text-destructive">{serviceTimes.summary.slowest_minutes} min</p></div>
                    </div>
                    <p className="text-xs text-muted-foreground">Based on {serviceTimes.summary.count} completed orders in the last {serviceTimes.date_range_days} days.</p>
                    {serviceTimes.daily.length ? (
                      <Card className="rounded-[24px] border-border bg-card">
                        <CardContent className="p-5">
                          <p className="mb-2 text-sm font-medium">Daily average</p>
                          <div className="h-[260px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={serviceTimes.daily} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                                <XAxis dataKey="date" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} stroke="hsl(var(--border))" tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }} stroke="hsl(var(--border))" tickFormatter={(v) => `${v}m`} />
                                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '12px', padding: '12px' }} formatter={(v) => [`${v} min`, "Avg service time"]} />
                                <Line type="monotone" dataKey="avg_minutes" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ fill: 'hsl(var(--primary))', r: 4 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    ) : null}

                    {/* Per-order timing list — placed → ready → delivered. */}
                    <Card className="rounded-[24px] border-border bg-card">
                      <CardContent className="p-5">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium">Order-by-order breakdown</p>
                            <p className="text-xs text-muted-foreground">Spot which tickets ran slow. Click a column to re-sort.</p>
                          </div>
                          <Select value={String(orderTimesSort.key) + ":" + orderTimesSort.dir} onValueChange={(v) => {
                            const [key, dir] = v.split(":");
                            setOrderTimesSort({ key, dir });
                          }}>
                            <SelectTrigger className="h-9 w-56 rounded-xl" data-testid="order-times-sort"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="total_minutes:desc">Slowest first (total)</SelectItem>
                              <SelectItem value="total_minutes:asc">Fastest first (total)</SelectItem>
                              <SelectItem value="prep_minutes:desc">Slowest prep first</SelectItem>
                              <SelectItem value="deliver_minutes:desc">Slowest delivery first</SelectItem>
                              <SelectItem value="placed_at:desc">Most recent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {orderTimesLoaded ? (
                          (orderTimes?.orders || []).length ? (
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Table</TableHead>
                                    <TableHead>Placed</TableHead>
                                    <TableHead className="text-right">Prep (placed→ready)</TableHead>
                                    <TableHead className="text-right">Delivery (ready→served)</TableHead>
                                    <TableHead className="text-right">Total</TableHead>
                                    <TableHead className="text-right">Items</TableHead>
                                    <TableHead className="text-right">Total R</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {[...(orderTimes?.orders || [])]
                                    .sort((a, b) => {
                                      const k = orderTimesSort.key;
                                      const av = a[k] ?? -Infinity;
                                      const bv = b[k] ?? -Infinity;
                                      if (k === "placed_at") {
                                        return orderTimesSort.dir === "desc"
                                          ? new Date(bv).getTime() - new Date(av).getTime()
                                          : new Date(av).getTime() - new Date(bv).getTime();
                                      }
                                      return orderTimesSort.dir === "desc" ? bv - av : av - bv;
                                    })
                                    .slice(0, 50)
                                    .map((o) => {
                                      const slow = (o.total_minutes ?? 0) >= 20;
                                      return (
                                        <TableRow
                                          key={o.id}
                                          className="cursor-pointer transition-colors hover:bg-secondary/50"
                                          onClick={() => openReceiptById(o.id)}
                                          data-testid={`order-time-row-${o.id}`}
                                        >
                                          <TableCell className="font-medium">
                                            <div className="flex items-center gap-2">
                                              {o.table_number || "—"}
                                              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                            </div>
                                          </TableCell>
                                          <TableCell className="text-xs text-muted-foreground">{o.placed_at ? new Date(o.placed_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                                          <TableCell className="text-right">{o.prep_minutes != null ? `${o.prep_minutes} min` : "—"}</TableCell>
                                          <TableCell className="text-right">{o.deliver_minutes != null ? `${o.deliver_minutes} min` : "—"}</TableCell>
                                          <TableCell className={`text-right font-semibold ${slow ? "text-destructive" : ""}`}>{o.total_minutes != null ? `${o.total_minutes} min` : "—"}</TableCell>
                                          <TableCell className="text-right">{o.items_count}</TableCell>
                                          <TableCell className="text-right">R {o.total.toFixed(2)}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                </TableBody>
                              </Table>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">No completed orders in this window yet.</p>
                          )
                        ) : (
                          <Skeleton className="h-32 w-full rounded-2xl" />
                        )}
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Skeleton className="h-32 w-full rounded-2xl" />
                )}

                {/* Session 30.22 — Daily wait report (long-wait threshold) */}
                <Card className="rounded-[24px] border-border bg-card" data-testid="analytics-long-wait-report">
                  <CardContent className="p-5">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold">Daily wait report</p>
                        <p className="text-xs text-muted-foreground">
                          Orders that waited longer than your long-wait threshold ({longWaitReport?.threshold_minutes ?? context?.business?.long_wait_minutes ?? 15} min) before the kitchen/bar marked them ready. Change the threshold under Menu Settings → Service alerts.
                        </p>
                      </div>
                      {longWaitReport ? (
                        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${longWaitReport.summary.over_threshold > 0 ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"}`} data-testid="analytics-long-wait-summary-badge">
                          {longWaitReport.summary.over_threshold} over · {longWaitReport.summary.over_pct}% of {longWaitReport.summary.orders}
                        </span>
                      ) : null}
                    </div>
                    {longWaitReport ? (
                      <>
                        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                          <div className="rounded-2xl border border-border bg-background p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Orders</p><p className="text-xl font-bold tabular-nums" data-testid="analytics-long-wait-orders">{longWaitReport.summary.orders}</p></div>
                          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 dark:border-rose-900/50 dark:bg-rose-950/30"><p className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">Over threshold</p><p className="text-xl font-bold tabular-nums text-rose-700 dark:text-rose-300" data-testid="analytics-long-wait-over">{longWaitReport.summary.over_threshold}</p></div>
                          <div className="rounded-2xl border border-border bg-background p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Avg wait to ready</p><p className="text-xl font-bold tabular-nums" data-testid="analytics-long-wait-avg">{longWaitReport.summary.avg_minutes} min</p></div>
                          <div className="rounded-2xl border border-border bg-background p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Worst wait</p><p className="text-xl font-bold tabular-nums" data-testid="analytics-long-wait-worst">{longWaitReport.summary.worst_minutes} min</p></div>
                        </div>
                        {longWaitReport.daily.length ? (
                          <div className="h-[240px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <ComposedChart data={longWaitReport.daily} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => new Date(v).toLocaleDateString("en-ZA", { day: "numeric", month: "short" })} />
                                <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
                                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="m" />
                                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "12px", padding: "12px" }} labelFormatter={(v) => new Date(v).toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "long" })} formatter={(v, name) => (name === "Avg wait" ? [`${v} min`, name] : [v, name])} />
                                <Legend />
                                <Bar yAxisId="left" dataKey="over_threshold" name="Orders over threshold" fill="hsl(350 80% 55%)" radius={[6, 6, 0, 0]} />
                                <Line yAxisId="right" type="monotone" dataKey="avg_minutes" name="Avg wait" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground" data-testid="analytics-long-wait-empty">No orders in this period yet.</p>
                        )}
                        {longWaitReport.daily.length ? (
                          <div className="mt-4 overflow-x-auto">
                            <table className="w-full text-sm" data-testid="analytics-long-wait-table">
                              <thead>
                                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                  <th className="py-2 pr-3 font-semibold">Day</th>
                                  <th className="py-2 pr-3 text-right font-semibold">Orders</th>
                                  <th className="py-2 pr-3 text-right font-semibold">Over threshold</th>
                                  <th className="py-2 pr-3 text-right font-semibold">Avg wait</th>
                                  <th className="py-2 text-right font-semibold">Worst</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[...longWaitReport.daily].reverse().map((d) => (
                                  <tr key={d.date} className="border-b border-border/60" data-testid={`analytics-long-wait-day-${d.date}`}>
                                    <td className="py-2 pr-3">{new Date(d.date).toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short" })}</td>
                                    <td className="py-2 pr-3 text-right tabular-nums">{d.orders}</td>
                                    <td className={`py-2 pr-3 text-right tabular-nums font-semibold ${d.over_threshold > 0 ? "text-rose-700 dark:text-rose-300" : "text-emerald-700 dark:text-emerald-300"}`}>{d.over_threshold} <span className="text-xs font-normal text-muted-foreground">({d.over_pct}%)</span>{d.still_waiting ? <span className="ml-1 text-xs font-normal text-amber-700 dark:text-amber-300">· {d.still_waiting} still waiting</span> : null}</td>
                                    <td className="py-2 pr-3 text-right tabular-nums">{d.avg_minutes} min</td>
                                    <td className="py-2 text-right tabular-nums">{d.max_minutes} min</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                        {longWaitReport.worst.length ? (
                          <details className="mt-4 rounded-2xl border border-border bg-background p-3" data-testid="analytics-long-wait-worst-list">
                            <summary className="cursor-pointer text-sm font-semibold">Longest waits ({longWaitReport.worst.length})</summary>
                            <ul className="mt-2 space-y-1.5 text-sm">
                              {longWaitReport.worst.map((w) => (
                                <li key={w.order_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-secondary/50 px-3 py-1.5">
                                  <span className="min-w-0 truncate"><span className="font-semibold">Table {w.table}</span>{w.order_number ? ` · ${w.order_number}` : ""} · {new Date(w.date).toLocaleDateString("en-ZA", { day: "numeric", month: "short" })}<span className="text-muted-foreground"> — {w.items.join(", ")}</span></span>
                                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${w.still_waiting ? "bg-amber-500 text-white" : "bg-rose-600 text-white"}`}>{w.wait_minutes} min{w.still_waiting ? " · still waiting" : ""}</span>
                                </li>
                              ))}
                            </ul>
                          </details>
                        ) : null}
                      </>
                    ) : (
                      <Skeleton className="h-40 w-full rounded-2xl" />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="stock" className="space-y-4 pt-4">
                <SectionHeader eyebrow="Inventory" title="Stock" description="Items sold as-is (drinks, retail) keep their own count under Items. Kitchen and bar ingredients live under Ingredients — on the floor and back of house — with recipes linking them to menu items." />
                {/* Session-level "Recorded by" — locked to the signed-in
                    user so Stock History always shows who. */}
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-secondary/40 p-3" data-testid="stock-recorder">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recorded by</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{sessionUser?.full_name || "Current user"}</span>
                    {sessionUser?.role ? <Badge variant="outline" className="rounded-full text-[10px] capitalize">{sessionUser.role}</Badge> : null}
                  </div>
                  <p className="ml-auto text-xs text-muted-foreground">Auto-attached to every stock change and stock take you do this session.</p>
                </div>
                <Tabs value={stockSubtab} className="space-y-4" onValueChange={(v) => {
                  // Session 63.C — legacy sub-tab ids still arrive from old
                  // links / notifications; map them onto the merged screens.
                  if (v === "approvals" || v === "fallback-overrides") { setFallbackHistoryView(v === "approvals" ? "approvals" : "overrides"); v = "fallback-history"; }
                  if (v === "variance" || v === "waste") { setVarianceWasteView(v); v = "variance-waste"; }
                  setStockSubtab(v);
                  if (v === "history" && !stockHistoryLoaded) loadStockHistory();
                  if (v === "takes" && !stockTakesLoaded) loadStockTakes();
                  // Session 50 — Inventory Usage merged into Stock History.
                  if (v === "usage") { setStockSubtab("history"); setStockHistoryUsageOpen(true); if (!stockHistoryLoaded) loadStockHistory(); }
                }}>
                  {/* Session 31.14 — hidden: the Inventory row in the top nav drives these sub-tabs (one row instead of two). */}
                  <TabsList className="hidden">
                    <TabsTrigger value="items" className="min-h-10 rounded-xl px-4 py-2" data-testid="stock-subtab-items">Items</TabsTrigger>
                    <TabsTrigger value="takes" className="min-h-10 rounded-xl px-4 py-2" data-testid="stock-subtab-takes">Stock Take</TabsTrigger>
                    <TabsTrigger value="locations" className="min-h-10 rounded-xl px-4 py-2" data-testid="stock-subtab-locations">Stock Locations</TabsTrigger>
                    <TabsTrigger value="ingredients" className="min-h-10 rounded-xl px-4 py-2" data-testid="stock-subtab-ingredients">Ingredients</TabsTrigger>
                    <TabsTrigger value="recipes" className="min-h-10 rounded-xl px-4 py-2" data-testid="stock-subtab-recipes">Recipes</TabsTrigger>
                    <TabsTrigger value="prep" className="min-h-10 rounded-xl px-4 py-2" data-testid="stock-subtab-prep">Prep Sheets / Stock Reads</TabsTrigger>
                    <TabsTrigger value="fallback-history" className="min-h-10 rounded-xl px-4 py-2" data-testid="stock-subtab-fallback-history">Fallback History</TabsTrigger>
                    <TabsTrigger value="variance-waste" className="min-h-10 rounded-xl px-4 py-2" data-testid="stock-subtab-variance-waste">Variance & Waste</TabsTrigger>
                    <TabsTrigger value="history" className="min-h-10 rounded-xl px-4 py-2" data-testid="stock-subtab-history">Stock History</TabsTrigger>
                  </TabsList>

                  <TabsContent value="items" className="space-y-3 pt-3">
                    <p className="text-sm text-muted-foreground">Set how many of each menu item are on hand AND what each one costs you to make (used in profit analytics). Leave stock blank to disable tracking. Set a <b>Warn at</b> level to get a low-stock warning when the count drops to that number or below — leave it blank and the item never raises a warning (even at 0).</p>
                    {stockLoaded ? (
                      stockRows.items.length ? (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <Input
                              type="search"
                              placeholder="Search items…"
                              value={stockFilter.query}
                              onChange={(e) => setStockFilter((f) => ({ ...f, query: e.target.value }))}
                              className="h-9 max-w-xs rounded-xl"
                              data-testid="stock-filter-query"
                            />
                            <Select value={stockFilter.scope} onValueChange={(v) => setStockFilter((f) => ({ ...f, scope: v }))}>
                              <SelectTrigger className="h-9 w-44 rounded-xl" data-testid="stock-filter-scope"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All items</SelectItem>
                                <SelectItem value="tracked">Tracked (has stock)</SelectItem>
                                <SelectItem value="untracked">Untracked</SelectItem>
                                <SelectItem value="low">Low stock only</SelectItem>
                                <SelectItem value="no_cost">Missing cost</SelectItem>
                              </SelectContent>
                            </Select>
                            <Select value={stockFilter.sort} onValueChange={(v) => setStockFilter((f) => ({ ...f, sort: v }))}>
                              <SelectTrigger className="h-9 w-52 rounded-xl" data-testid="stock-filter-sort"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="stock_asc">Sort: Low stock first</SelectItem>
                                <SelectItem value="stock_desc">Sort: High stock first</SelectItem>
                                <SelectItem value="name_asc">Sort: Name A→Z</SelectItem>
                                <SelectItem value="name_desc">Sort: Name Z→A</SelectItem>
                                <SelectItem value="cost_desc">Sort: Highest cost</SelectItem>
                                <SelectItem value="cost_asc">Sort: Lowest cost</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Card className="rounded-[24px] border-border bg-card">
                            <CardContent className="p-0">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Item</TableHead>
                                    <TableHead className="w-32">On hand</TableHead>
                                    <TableHead className="w-36">Stock</TableHead>
                                    <TableHead className="w-36">Cost (R)</TableHead>
                                    <TableHead className="w-36">Warn at</TableHead>
                                    <TableHead className="w-24" />
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(() => {
                                    const q = (stockFilter.query || "").trim().toLowerCase();
                                    let list = stockRows.items.filter((it) => {
                                      if (q && !(it.name || "").toLowerCase().includes(q)) return false;
                                      const tracked = it.stock !== null && it.stock !== undefined;
                                      if (stockFilter.scope === "tracked" && !tracked) return false;
                                      if (stockFilter.scope === "untracked" && tracked) return false;
                                      if (stockFilter.scope === "low" && !(tracked && it.low_stock_threshold != null && it.stock <= it.low_stock_threshold)) return false;
                                      if (stockFilter.scope === "no_cost" && it.cost != null) return false;
                                      return true;
                                    });
                                    const sorters = {
                                      stock_asc: (a, b) => (a.stock ?? Infinity) - (b.stock ?? Infinity) || a.name.localeCompare(b.name),
                                      stock_desc: (a, b) => (b.stock ?? -Infinity) - (a.stock ?? -Infinity) || a.name.localeCompare(b.name),
                                      name_asc: (a, b) => a.name.localeCompare(b.name),
                                      name_desc: (a, b) => b.name.localeCompare(a.name),
                                      cost_desc: (a, b) => (b.cost ?? -Infinity) - (a.cost ?? -Infinity) || a.name.localeCompare(b.name),
                                      cost_asc: (a, b) => (a.cost ?? Infinity) - (b.cost ?? Infinity) || a.name.localeCompare(b.name),
                                    };
                                    list = [...list].sort(sorters[stockFilter.sort] || sorters.stock_asc);
                                    if (list.length === 0) {
                                      return (
                                        <TableRow>
                                          <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No items match this filter.</TableCell>
                                        </TableRow>
                                      );
                                    }
                                    return list.map((it) => {
                                      const hasStock = it.stock !== null && it.stock !== undefined;
                                      const hasWarn = it.low_stock_threshold !== null && it.low_stock_threshold !== undefined;
                                      const isLow = hasStock && hasWarn && it.stock <= it.low_stock_threshold;
                                      const editRaw = stockEdits[it.id];
                                      const edit = typeof editRaw === "object" && editRaw !== null ? editRaw : (editRaw !== undefined ? { stock: editRaw } : {});
                                      const stockEditVal = edit.stock;
                                      const costEditVal = edit.cost;
                                      const warnEditVal = edit.warn;
                                      const hasAnyEdit = (stockEditVal !== undefined && stockEditVal !== `${it.stock ?? ""}`) || (costEditVal !== undefined && costEditVal !== `${it.cost ?? ""}`) || (warnEditVal !== undefined && warnEditVal !== `${it.low_stock_threshold ?? ""}`);
                                      return (
                                        <TableRow key={it.id} data-testid={`stock-row-${it.id}`}>
                                          <TableCell className="font-medium">{it.name}</TableCell>
                                          <TableCell>
                                            {hasStock ? (
                                              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${isLow ? "bg-destructive/10 text-destructive" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"}`}>
                                                {it.stock} {isLow ? "(low)" : ""}
                                              </span>
                                            ) : (
                                              <span className="text-xs text-muted-foreground">untracked</span>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            <Input
                                              type="number"
                                              min="0"
                                              step="1"
                                              placeholder={hasStock ? `${it.stock}` : "leave blank"}
                                              value={stockEditVal ?? ""}
                                              onChange={(e) => setStockEdits((s) => ({ ...s, [it.id]: { ...(typeof s[it.id] === "object" ? s[it.id] : {}), stock: e.target.value } }))}
                                              className="h-9 w-28 rounded-xl"
                                              data-testid={`stock-input-${it.id}`}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Input
                                              type="number"
                                              min="0"
                                              step="0.01"
                                              placeholder={it.cost != null ? `${it.cost}` : "—"}
                                              value={costEditVal ?? ""}
                                              onChange={(e) => setStockEdits((s) => ({ ...s, [it.id]: { ...(typeof s[it.id] === "object" ? s[it.id] : {}), cost: e.target.value } }))}
                                              className="h-9 w-28 rounded-xl"
                                              data-testid={`cost-input-${it.id}`}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Input
                                              type="number"
                                              min="0"
                                              step="1"
                                              placeholder={hasWarn ? `${it.low_stock_threshold}` : "no warning"}
                                              title="Low-stock warning level — warn when on-hand is at or below this number. Blank = never warn."
                                              value={warnEditVal ?? ""}
                                              onChange={(e) => setStockEdits((s) => ({ ...s, [it.id]: { ...(typeof s[it.id] === "object" ? s[it.id] : {}), warn: e.target.value } }))}
                                              className="h-9 w-28 rounded-xl"
                                              data-testid={`warn-input-${it.id}`}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex flex-wrap items-center gap-2">
                                              <Button
                                                size="sm"
                                                className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                                                disabled={!hasAnyEdit}
                                                onClick={() => saveStock(it.id)}
                                                data-testid={`stock-save-${it.id}`}
                                              >
                                                Save
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="rounded-xl"
                                                onClick={() => openItemHistory(it.id, it.name)}
                                                data-testid={`stock-history-${it.id}`}
                                              >
                                                History
                                              </Button>
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    });
                                  })()}
                                </TableBody>
                              </Table>
                            </CardContent>
                          </Card>
                        </>
                      ) : (
                        <EmptyState icon={Package} title="No menu items yet" description="Add items in Manage Menu to start tracking their stock and cost." />
                      )
                    ) : (
                      <Skeleton className="h-40 w-full rounded-2xl" />
                    )}
                  </TabsContent>

                  <TabsContent value="ingredients" className="space-y-3 pt-3">
                    <IngredientsPanel
                      http={http}
                      authHeaders={authHeaders}
                      sessionForEvents={session}
                      ingredients={ingredients}
                      loaded={ingredientsLoaded}
                      recorder={stockRecorder.trim()}
                      onRefresh={async () => {
                        await Promise.all([loadIngredients(), loadLowStock(), loadStock()]);
                        if (stockHistoryLoaded) loadStockHistory();
                      }}
                      onHistory={openItemHistory}
                      lowOnly={stockLowOnly}
                      onLowOnlyApplied={() => setStockLowOnly(false)}
                    />
                  </TabsContent>

                  <TabsContent value="recipes" className="space-y-3 pt-3">
                    {/* Session 41 — instant-search recipe editor (components/inventory/RecipesPanel.jsx). */}
                    <RecipesPanel
                      http={http}
                      authHeaders={authHeaders}
                      menuItems={menuItems}
                      ingredients={ingredients}
                      onSaved={() => { loadIngredients(); }}
                    />
                  </TabsContent>

                  {/* Session 41 — Prep Sheets: forecast → adjust → send to station →
                      review actuals → confirm production (the only stock effect). */}
                  <TabsContent value="prep" className="space-y-3 pt-3" data-testid="inventory-prep-tab">
                    <PrepSheetsPanel
                      http={http}
                      authHeaders={authHeaders}
                      ingredients={ingredients}
                      sessionForEvents={session}
                      sessionUser={sessionUser}
                      canManage={!sessionUser || ["owner", "manager"].includes(sessionUser.role)}
                    />
                  </TabsContent>
                  {/* Session 63.C — ONE Fallback History screen: approvals (who allowed
                      BOH stock for which table) + the per-ingredient "When service
                      stock runs out" overrides (reset at EOD). */}
                  <TabsContent value="fallback-history" className="space-y-3 pt-3" data-testid="inventory-fallback-history-tab">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="inline-flex rounded-xl border border-border bg-card p-1" role="tablist" aria-label="Fallback history" data-testid="fallback-history-switch">
                        {[
                          { v: "approvals", l: "Approvals" },
                          { v: "overrides", l: "When service stock runs out" },
                        ].map((o) => (
                          <button key={o.v} type="button" role="tab" aria-selected={fallbackHistoryView === o.v} onClick={() => setFallbackHistoryView(o.v)} className={`h-9 rounded-lg px-3 text-sm font-medium transition-colors ${fallbackHistoryView === o.v ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid={`fallback-history-view-${o.v}`}>{o.l}</button>
                        ))}
                      </div>
                      <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={() => { setStockLocationsInitialView("fallback"); setStockLocationsPanelNonce((n) => n + 1); setStockSubtab("locations"); }} data-testid="fallback-history-open-activity">Fallback activity by location →</Button>
                    </div>
                    {stockSubtab === "fallback-history" && fallbackHistoryView === "approvals" ? (
                      <ApprovalsPanel http={http} authHeaders={authHeaders} canManage={!sessionUser || ["owner", "manager", "shift_leader"].includes(sessionUser.role)} />
                    ) : null}
                    {stockSubtab === "fallback-history" && fallbackHistoryView === "overrides" ? (
                      <FallbackOverridesPanel
                        http={http}
                        authHeaders={authHeaders}
                        canManage={!sessionUser || ["owner", "manager"].includes(sessionUser.role)}
                        onChanged={() => { if (ingredientsLoaded) loadIngredients(); }}
                      />
                    ) : null}
                  </TabsContent>

                  {/* ── Session 38 — Inventory → Costing → Profit chain ──
                      Session 63.C — Variance and Waste on ONE screen. */}
                  <TabsContent value="variance-waste" className="space-y-3 pt-3" data-testid="inventory-variance-waste-tab">
                    <div className="inline-flex rounded-xl border border-border bg-card p-1" role="tablist" aria-label="Variance and waste" data-testid="variance-waste-switch">
                      {[
                        ...((!sessionUser || ["owner", "manager"].includes(sessionUser.role)) ? [{ v: "variance", l: "Variance" }] : []),
                        { v: "waste", l: "Waste" },
                      ].map((o) => (
                        <button key={o.v} type="button" role="tab" aria-selected={varianceWasteView === o.v} onClick={() => setVarianceWasteView(o.v)} className={`h-9 rounded-lg px-3 text-sm font-medium transition-colors ${varianceWasteView === o.v ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid={`variance-waste-view-${o.v}`}>{o.l}</button>
                      ))}
                    </div>
                    {stockSubtab === "variance-waste" && varianceWasteView === "variance" && (!sessionUser || ["owner", "manager"].includes(sessionUser.role)) ? <VariancePanel http={http} authHeaders={authHeaders} /> : null}
                    {stockSubtab === "variance-waste" && (varianceWasteView === "waste" || !(!sessionUser || ["owner", "manager"].includes(sessionUser.role))) ? (
                      <WastePanel
                        http={http}
                        authHeaders={authHeaders}
                        ingredients={ingredients}
                        sessionUser={sessionUser}
                        sessionForEvents={session}
                        onStockChanged={async () => {
                          await Promise.all([loadIngredients(), loadLowStock()]);
                          if (stockHistoryLoaded) loadStockHistory();
                        }}
                      />
                    ) : null}
                  </TabsContent>
                  <TabsContent value="receiving" className="space-y-3 pt-3" data-testid="inventory-receiving-tab">
                    {stockSubtab === "receiving" ? (
                      <ReceivingPanel
                        http={http}
                        authHeaders={authHeaders}
                        ingredients={ingredients}
                        sessionUser={sessionUser}
                        sessionForEvents={session}
                        onStockChanged={async () => {
                          await Promise.all([loadIngredients(), loadLowStock(), loadRecipesSummary()]);
                          if (stockHistoryLoaded) loadStockHistory();
                        }}
                      />
                    ) : null}
                  </TabsContent>
                  {/* ── Session 39 — Stock locations + intelligent fallback ── */}
                  <TabsContent value="locations" className="space-y-3 pt-3" data-testid="inventory-locations-tab">
                    {stockSubtab === "locations" ? (
                      <StockLocationsPanel
                        key={`stock-locations-${stockLocationsPanelNonce}`}
                        initialView={stockLocationsInitialView}
                        http={http}
                        authHeaders={authHeaders}
                        ingredients={ingredients}
                        sessionUser={sessionUser}
                        sessionForEvents={session}
                        canManage={!sessionUser?.role || ["owner", "manager"].includes(String(sessionUser?.role || "").toLowerCase())}
                        canTransfer={!sessionUser?.role || ["owner", "manager", "shift_leader"].includes(String(sessionUser?.role || "").toLowerCase())}
                        onStockChanged={async () => {
                          await Promise.all([loadIngredients(), loadLowStock()]);
                          if (stockHistoryLoaded) loadStockHistory();
                        }}
                      />
                    ) : null}
                  </TabsContent>
                  <TabsContent value="coverage" className="space-y-3 pt-3" data-testid="inventory-coverage-tab">
                    {stockSubtab === "coverage" ? (
                      <CostCoveragePanel
                        http={http}
                        authHeaders={authHeaders}
                        onOpenRecipes={() => setStockSubtab("recipes")}
                        onOpenIngredients={() => setStockSubtab("ingredients")}
                      />
                    ) : null}
                  </TabsContent>


                  {/* ── Stock adjustment history (audit ledger) ────────── */}
                  <TabsContent value="history" className="space-y-3 pt-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {[
                        { v: "today", label: "Today" },
                        { v: "yesterday", label: "Yesterday" },
                        { v: "7d", label: "Last 7 days" },
                        { v: "30d", label: "Last 30 days" },
                        { v: "custom", label: "Custom range" },
                      ].map((r) => (
                        <Button
                          key={r.v}
                          type="button"
                          variant={stockHistoryRange === r.v ? "default" : "outline"}
                          size="sm"
                          className="rounded-xl"
                          onClick={() => setStockHistoryRange(r.v)}
                          data-testid={`stock-history-range-${r.v}`}
                        >
                          {r.label}
                        </Button>
                      ))}
                      {stockHistoryRange === "custom" && (
                        <>
                          <Input
                            type="date"
                            className="h-9 w-40 rounded-xl"
                            value={stockHistoryCustom.start}
                            onChange={(e) => setStockHistoryCustom((c) => ({ ...c, start: e.target.value }))}
                          />
                          <Input
                            type="date"
                            className="h-9 w-40 rounded-xl"
                            value={stockHistoryCustom.end}
                            onChange={(e) => setStockHistoryCustom((c) => ({ ...c, end: e.target.value }))}
                          />
                        </>
                      )}
                    </div>
                    {/* Session 66 — stock history is kept per DEPARTMENT. */}
                    {stockDepartments.length > 0 && (
                      <div className="inline-flex flex-wrap rounded-2xl border border-border bg-card p-1" role="tablist" aria-label="Department" data-testid="stock-history-dept-tabs">
                        {[{ id: "all", name: "All departments" }, ...stockDepartments].map((d) => {
                          const selected = stockHistoryDept === d.id;
                          return (
                            <button
                              key={d.id}
                              type="button"
                              role="tab"
                              aria-selected={selected}
                              onClick={() => setStockHistoryDept(d.id)}
                              className={`min-h-10 rounded-xl px-3 text-sm font-medium transition-colors ${selected ? "bg-[hsl(var(--primary))] text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
                              data-testid={`stock-history-dept-${d.id}`}
                            >
                              {d.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {/* Session 50 — Inventory usage + All items sold live INSIDE
                        Stock History as collapsibles (fewer sidebar entries) and
                        follow the date filter above. */}
                    <Collapsible open={stockHistoryUsageOpen} onOpenChange={setStockHistoryUsageOpen} data-testid="stock-history-usage-collapsible">
                      <Card className="rounded-2xl border-border bg-card">
                        <CollapsibleTrigger asChild>
                          <button type="button" className="flex w-full items-center justify-between gap-3 rounded-2xl px-4 py-3 text-left transition-colors hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="stock-history-usage-toggle">
                            <div>
                              <p className="text-sm font-semibold">Inventory usage</p>
                              <p className="text-xs text-muted-foreground">Opening → sold · wasted · received → closing for every ingredient and stock-tracked item in this range. Sold is net of voids.</p>
                            </div>
                            {stockHistoryUsageOpen ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <CardContent className="border-t border-border p-4">
                            {inventoryUsageLoading && !inventoryUsage ? (
                              <Skeleton className="h-32 w-full rounded-2xl" />
                            ) : inventoryUsage && inventoryUsage.rows?.length ? (
                              <>
                                {inventoryUsage.low_stock?.length ? (
                                  <p className="mb-3 text-xs text-amber-700 dark:text-amber-400" data-testid="stock-history-usage-low-note">{inventoryUsage.low_stock.length} item{inventoryUsage.low_stock.length === 1 ? " is" : "s are"} at or below the low-stock level.</p>
                                ) : null}
                                <div className="overflow-x-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Item</TableHead>
                                        <TableHead className="text-right">Opening</TableHead>
                                        <TableHead className="text-right">Sold</TableHead>
                                        <TableHead className="hidden text-right sm:table-cell">Wasted</TableHead>
                                        <TableHead className="hidden text-right md:table-cell">Received</TableHead>
                                        <TableHead className="hidden text-right md:table-cell" title="Manual edits, stock-take corrections, production (net)">Other</TableHead>
                                        <TableHead className="text-right">Closing</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {[...inventoryUsage.rows]
                                        .sort((a, b) => (Number(b.sold ?? b.consumed) || 0) - (Number(a.sold ?? a.consumed) || 0) || String(a.name || "").localeCompare(String(b.name || "")))
                                        .map((r) => {
                                          const unit = r.type === "ingredient" ? (r.unit || "") : "";
                                          const fmt = (v) => { const n = Number(v) || 0; return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, ""); };
                                          const sold = Number(r.sold ?? r.consumed) || 0;
                                          const wasted = Number(r.wasted) || 0;
                                          const received = Number(r.received) || 0;
                                          const other = (Number(r.adjusted) || 0) - (Number(r.produced) || 0);
                                          const moved = sold || wasted || received || other;
                                          return (
                                            <TableRow key={`sh-use-${r.type}-${r.id}`} className={moved ? "" : "text-muted-foreground"} data-testid={`stock-history-usage-row-${r.id}`}>
                                              <TableCell className="font-medium text-foreground">
                                                <span className="block truncate">{r.name}</span>
                                                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{r.type === "ingredient" ? (r.category || "kitchen") : "item"}{Number(r.restored_by_voids) > 0 ? ` · ${fmt(r.restored_by_voids)} ${unit} returned by voids` : ""}</span>
                                              </TableCell>
                                              <TableCell className="text-right text-xs tabular-nums">{fmt(r.opening_stock)} {unit}</TableCell>
                                              <TableCell className={`text-right text-xs tabular-nums ${sold > 0 ? "font-semibold text-destructive" : ""}`}>{sold > 0 ? `−${fmt(sold)}` : "—"}</TableCell>
                                              <TableCell className={`hidden text-right text-xs tabular-nums sm:table-cell ${wasted > 0 ? "text-amber-700 dark:text-amber-400" : ""}`}>{wasted > 0 ? `−${fmt(wasted)}` : "—"}</TableCell>
                                              <TableCell className={`hidden text-right text-xs tabular-nums md:table-cell ${received > 0 ? "text-emerald-700 dark:text-emerald-400" : ""}`}>{received > 0 ? `+${fmt(received)}` : "—"}</TableCell>
                                              <TableCell className="hidden text-right text-xs tabular-nums md:table-cell">{other ? `${other > 0 ? "+" : "−"}${fmt(Math.abs(other))}` : "—"}</TableCell>
                                              <TableCell className="text-right text-xs font-semibold tabular-nums text-foreground">{fmt(r.closing_stock)} {unit}</TableCell>
                                            </TableRow>
                                          );
                                        })}
                                    </TableBody>
                                  </Table>
                                </div>
                              </>
                            ) : (
                              <p className="text-xs text-muted-foreground">No stock movements in this range.</p>
                            )}
                          </CardContent>
                        </CollapsibleContent>
                      </Card>
                    </Collapsible>
                    <Collapsible open={stockHistorySoldOpen} onOpenChange={setStockHistorySoldOpen} data-testid="stock-history-sold-collapsible">
                      <Card className="rounded-2xl border-border bg-card">
                        <CollapsibleTrigger asChild>
                          <button type="button" className="flex w-full items-center justify-between gap-3 rounded-2xl px-4 py-3 text-left transition-colors hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="stock-history-sold-toggle">
                            <div>
                              <p className="text-sm font-semibold">All items sold{stockItemsSold ? ` · ${stockItemsSold.total_items.toLocaleString()} items` : ""}</p>
                              <p className="text-xs text-muted-foreground">Every menu item sold in this range (A–Z) with quantity and revenue — the same figures as Business Summary. Voided orders and lines are excluded.</p>
                            </div>
                            {stockHistorySoldOpen ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <CardContent className="border-t border-border p-4">
                            {stockItemsSoldLoading && !stockItemsSold ? (
                              <Skeleton className="h-32 w-full rounded-2xl" />
                            ) : stockItemsSold && stockItemsSold.items.length ? (
                              <>
                                <Input
                                  type="search"
                                  value={stockItemsSoldQuery}
                                  onChange={(e) => setStockItemsSoldQuery(e.target.value)}
                                  placeholder="Filter items…"
                                  className="mb-3 h-9 max-w-xs rounded-xl"
                                  data-testid="stock-history-sold-search"
                                />
                                <div className="overflow-x-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Item</TableHead>
                                        <TableHead className="text-right">Qty sold</TableHead>
                                        <TableHead className="text-right">Revenue</TableHead>
                                        <TableHead className="hidden text-right sm:table-cell">Share</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {[...stockItemsSold.items]
                                        .filter((it) => !stockItemsSoldQuery.trim() || String(it.name || "").toLowerCase().includes(stockItemsSoldQuery.trim().toLowerCase()))
                                        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
                                        .map((it) => (
                                          <TableRow key={`sh-sold-${it.name}`} data-testid={`stock-history-sold-row-${String(it.name || "").replace(/\s+/g, "-").toLowerCase()}`}>
                                            <TableCell className="font-medium">{it.name}</TableCell>
                                            <TableCell className="text-right tabular-nums">{Number(it.quantity || 0).toLocaleString()}</TableCell>
                                            <TableCell className="text-right tabular-nums">{formatPrice(it.revenue || 0)}</TableCell>
                                            <TableCell className="hidden text-right text-xs tabular-nums text-muted-foreground sm:table-cell">{Number(it.percentage || 0).toFixed(1)}%</TableCell>
                                          </TableRow>
                                        ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              </>
                            ) : (
                              <p className="text-xs text-muted-foreground">Nothing sold in this range.</p>
                            )}
                          </CardContent>
                        </CollapsibleContent>
                      </Card>
                    </Collapsible>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="search"
                        value={stockHistoryQuery}
                        onChange={(e) => setStockHistoryQuery(e.target.value)}
                        placeholder="Search by item name or user…"
                        className="h-10 rounded-xl pl-9"
                        data-testid="stock-history-search"
                      />
                    </div>
                    <Card className="rounded-2xl border-border bg-card">
                      <CardContent className="p-0">
                        {stockHistoryData.length ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="whitespace-nowrap">When</TableHead>
                                <TableHead>Item</TableHead>
                                <TableHead>Change</TableHead>
                                <TableHead className="hidden sm:table-cell">Reason</TableHead>
                                <TableHead className="hidden md:table-cell">User</TableHead>
                                <TableHead></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {stockHistoryData.map((row) => {
                                const diff = Number(row.difference || 0);
                                const tone = diff > 0 ? "text-emerald-600 dark:text-emerald-400" : diff < 0 ? "text-destructive" : "";
                                const unit = row.unit ? ` ${row.unit}` : "";
                                const fmtN = (v) => { const n = Number(v) || 0; return Number.isInteger(n) ? n.toLocaleString() : Number(n.toFixed(3)).toLocaleString(); };
                                const reasonLabel = String(row.reason || "other").replace(/_/g, " ");
                                return (
                                  <TableRow
                                    key={row.id}
                                    className="cursor-pointer transition-colors hover:bg-secondary/50"
                                    onClick={() => setStockEntryDetail({ open: true, row })}
                                    data-testid={`stock-history-row-${row.id}`}
                                  >
                                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                                      {row.created_at ? (<>
                                        <span className="block">{new Date(row.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                                        <span className="block">{new Date(row.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                                      </>) : "—"}
                                    </TableCell>
                                    <TableCell className="text-sm font-medium">
                                      <button
                                        type="button"
                                        className="inline-flex items-center gap-2 text-left hover:underline"
                                        onClick={(e) => { e.stopPropagation(); openItemHistory(row.item_id, row.item_name); }}
                                        data-testid={`stock-history-item-${row.id}`}
                                      >
                                        <span className="block">{String(row.item_name || "").replace(/ \(back of house\)$/i, "")}</span>
                                        {row.department_name ? <Badge className="rounded-full bg-[hsl(var(--primary))]/10 text-[10px] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10" data-testid={`stock-history-dept-badge-${row.id}`}>{row.department_name}</Badge> : null}
                                        <Badge variant="outline" className="rounded-full text-[10px]" data-testid={`stock-history-location-${row.id}`}>{row.reason === "transfer" ? `${row.from_location_name || "?"} → ${row.to_location_name || "?"}` : row.location_name ? row.location_name : row.item_type === "ingredient_boh" ? "Back of house" : (row.item_type === "item" || row.item_type === "menu_item") ? "Item" : "Floor"}</Badge>
                                        {row.consumption_type && row.consumption_type !== "normal" ? (
                                          <Badge variant="outline" className={`rounded-full text-[10px] ${row.consumption_type === "shortfall" ? "border-destructive/50 bg-destructive/10 text-destructive" : row.consumption_type === "fallback" ? "border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-300" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"}`} data-testid={`stock-history-consumption-${row.id}`}>
                                            {row.consumption_type === "shortfall" ? "Shortfall" : row.consumption_type === "fallback" ? "Fallback" : "Restored"}
                                          </Badge>
                                        ) : null}
                                      </button>
                                      <span className="mt-0.5 block text-[11px] capitalize text-muted-foreground sm:hidden">{reasonLabel}{row.user ? ` · ${row.user}` : ""}</span>
                                    </TableCell>
                                    <TableCell className="text-sm tabular-nums">
                                      <span className={`block whitespace-nowrap font-semibold sm:inline ${tone}`}>{diff > 0 ? "+" : diff < 0 ? "−" : ""}{fmtN(Math.abs(diff))}{unit}</span>
                                      <span className="block whitespace-nowrap text-xs text-muted-foreground sm:ml-2 sm:inline">{fmtN(row.previous)} → {fmtN(row.new_value)}{unit}</span>
                                    </TableCell>
                                    <TableCell className="hidden text-sm capitalize sm:table-cell">{reasonLabel}</TableCell>
                                    <TableCell className="hidden text-sm text-muted-foreground md:table-cell">{row.user || "—"}</TableCell>
                                    <TableCell><ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /></TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        ) : (
                          <div className="p-6">
                            <EmptyState icon={Receipt} title="No stock changes yet" description="Stock adjustments are recorded automatically whenever you edit a stock value." />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* ── Stock take history (snapshots) ─────────────────── */}
                  <TabsContent value="takes" className="space-y-3 pt-3">
                    <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-start">
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">Count what you physically have, we record the variance and update the live counts. Start with what you want to count:</p>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5" data-testid="stock-take-quick-start">
                          {[
                            // Session 66 — one quick start per DEPARTMENT (Kitchen, Bar,
                            // Beach Bar …) per location, so a manager counts exactly the
                            // department they are standing in.
                            ...stockDepartments.flatMap((d) => [
                              { location: "floor", category: `dept:${d.id}`, label: `Floor · ${d.name}`, icon: d.route_to_bar && !d.route_to_kitchen ? Wine : ChefHat },
                              { location: "back_of_house", category: `dept:${d.id}`, label: `Back of house · ${d.name}`, icon: Warehouse },
                            ]),
                            ...(stockDepartments.length ? [] : [
                              { location: "floor", category: "kitchen", label: "Floor · Kitchen", icon: ChefHat },
                              { location: "floor", category: "bar", label: "Floor · Bar", icon: Wine },
                              { location: "back_of_house", category: "kitchen", label: "Back of house · Kitchen", icon: Warehouse },
                              { location: "back_of_house", category: "bar", label: "Back of house · Bar", icon: Warehouse },
                            ]),
                            { location: "floor", category: "items", label: "Items (drinks, retail)", icon: Package },
                            // Session 39.5 — merchant-created locations count too.
                            ...stockTakeCustomLocations.map((l) => ({ location: l.id, category: "all", label: `${l.name} · All`, icon: Warehouse })),
                          ].map((q) => {
                            const Icon = q.icon;
                            return (
                              <button
                                key={`${q.location}-${q.category}`}
                                type="button"
                                onClick={() => openStockTakeWizard({ location: q.location, category: q.category })}
                                className="flex min-h-14 items-center gap-2 rounded-2xl border border-border bg-card px-3 text-left text-sm font-medium transition-colors hover:border-[hsl(var(--primary))]/50 hover:bg-secondary"
                                data-testid={`stock-take-quick-${q.location}-${q.category.replace(/^dept:/, "dept-")}`}
                              >
                                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <span className="leading-tight">{q.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button type="button" onClick={() => openStockTakeWizard()} className="h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" data-testid="stock-take-run-btn">
                          <CheckCircle2 className="h-4 w-4" />
                          Run stock take
                        </Button>
                        {/* Session 63.C — "Move stock" lives here now (was under Ingredients). */}
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" className="h-11 flex-1 rounded-xl" onClick={() => { setStockTakeMoveDirection("to_floor"); setStockTakeMoveOpen(true); if (!ingredientsLoaded) loadIngredients(); }} data-testid="stock-take-move-stock-btn">
                            <ArrowRightLeftIcon className="h-4 w-4" />
                            Move stock
                          </Button>
                          <Button type="button" variant="ghost" className="h-11 rounded-xl" onClick={() => { setStockLocationsInitialView("transfer"); setStockLocationsPanelNonce((n) => n + 1); setStockSubtab("locations"); }} title="Record a transfer between any two stock locations (department floors, back of house, storerooms)" data-testid="stock-take-transfer-locations-btn">
                            Between locations →
                          </Button>
                        </div>
                      </div>
                    </div>
                    <MoveStockDialog
                      open={stockTakeMoveOpen}
                      onOpenChange={setStockTakeMoveOpen}
                      direction={stockTakeMoveDirection}
                      ingredients={ingredients}
                      http={http}
                      authHeaders={authHeaders}
                      recorder={stockRecorder.trim()}
                      sessionForEvents={session}
                      onDone={async () => {
                        await Promise.all([loadIngredients(), loadLowStock(), loadStock()]);
                        if (stockHistoryLoaded) loadStockHistory();
                      }}
                    />
                    <p className="pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">History</p>
                    <Card className="rounded-2xl border-border bg-card">
                      <CardContent className="p-0">
                        {stockTakesList.length ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>What was counted</TableHead>
                                <TableHead className="hidden sm:table-cell">User</TableHead>
                                <TableHead className="text-right">Lines</TableHead>
                                <TableHead className="text-right">Variance</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {stockTakesList.map((t) => {
                                const v = Number(t.variance_total || 0);
                                const tone = v > 0 ? "text-emerald-600 dark:text-emerald-400" : v < 0 ? "text-destructive" : "text-muted-foreground";
                                return (
                                  <TableRow
                                    key={t.id}
                                    className="cursor-pointer transition-colors hover:bg-secondary/50"
                                    onClick={async () => {
                                      setStockTakeDetail({ open: true, record: null, loading: true });
                                      try {
                                        const r = await http.get(`/owner/stock-takes/${t.id}`, { headers: authHeaders });
                                        setStockTakeDetail({ open: true, record: r.data.stock_take, loading: false });
                                      } catch (error) {
                                        toast.error(error.response?.data?.detail || "Unable to load stock take.");
                                        setStockTakeDetail({ open: false, record: null, loading: false });
                                      }
                                    }}
                                    data-testid={`stock-take-row-${t.id}`}
                                  >
                                    <TableCell className="text-sm">{t.created_at ? new Date(t.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                                    <TableCell className="text-sm" data-testid={`stock-take-scope-${t.id}`}>{t.scope || "Floor · all"}</TableCell>
                                    <TableCell className="hidden text-sm text-muted-foreground sm:table-cell">{t.user || "—"}</TableCell>
                                    <TableCell className="text-right">{t.items_count}</TableCell>
                                    <TableCell className={`text-right font-semibold ${tone}`}>{v > 0 ? "+" : ""}{v.toLocaleString()}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        ) : (
                          <div className="p-6">
                            <EmptyState icon={CheckCircle2} title="No stock takes yet" description="Run a stock take to capture what you counted and the variance against the expected stock." />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                </Tabs>
              </TabsContent>

              <TabsContent value="alerts" className="space-y-4 pt-4">
                <SectionHeader eyebrow="Inventory" title="Email low-stock report" description="Set an email address that automatically receives a low-stock alert when an ingredient or item drops at or below its threshold. Emails are debounced to one per 24 hours so your inbox stays clean." />
                <Card className="rounded-[24px] border-border bg-card">
                  <CardContent className="space-y-4 p-5">
                    <div className="space-y-2">
                      <Label htmlFor="alerts-email">Notification email</Label>
                      <Input
                        id="alerts-email"
                        type="email"
                        placeholder="manager@yourrestaurant.com"
                        value={businessSettings?.alert_email || ""}
                        onChange={(e) => setBusinessSettings((prev) => ({ ...prev, alert_email: e.target.value }))}
                        className="h-11 rounded-xl"
                        data-testid="alerts-email-input"
                      />
                      <p className="text-xs text-muted-foreground">Leave blank to disable email alerts (the in-app banner still shows).</p>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        onClick={async () => {
                          try {
                            await http.patch(
                              "/owner/business-settings",
                              { alert_email: (businessSettings?.alert_email || "").trim() },
                              { headers: authHeaders },
                            );
                            toast.success((businessSettings?.alert_email || "").trim() ? "Email saved." : "Email cleared.");
                            await refreshDashboard();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Could not save email.");
                          }
                        }}
                        className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                        data-testid="alerts-email-save"
                      >
                        Save email
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                {lowStock.total > 0 ? (
                  <Card className="rounded-[24px] border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30">
                    <CardContent className="space-y-2 p-5">
                      <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">Currently low ({lowStock.total})</p>
                      <ul className="space-y-1 text-sm text-amber-900 dark:text-amber-100">
                        {lowStock.ingredients.map((x) => (
                          <li key={`ing-${x.id}`}>• <strong>{x.name}</strong> <span className="capitalize text-amber-800/80 dark:text-amber-200/80">({x.category || "kitchen"} ingredient)</span> — {x.stock} {x.unit} (alert ≤ {x.threshold}){Number(x.back_of_house_stock) > 0 ? ` · ${x.back_of_house_stock} ${x.unit} back of house` : ""}</li>
                        ))}
                        {lowStock.items.map((x) => (
                          <li key={`it-${x.id}`}>• <strong>{x.name}</strong> <span className="text-amber-800/80 dark:text-amber-200/80">(item)</span> — {x.stock} in stock</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ) : null}
              </TabsContent>

              <TabsContent value="receipts" className="space-y-4 pt-4">
                <ReceiptsPanel
                  authHeaders={authHeaders}
                  onOpenReceipt={(order) => setOrderReceipt({ open: true, order })}
                />
              </TabsContent>

              {/* ── Report Schedules ─────────────────────────────────── */}
              <TabsContent value="schedules" className="space-y-4 pt-4" data-testid="schedules-tab">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <SectionHeader eyebrow="Automated reports" title="Report Schedules" description="Auto-email the management report hourly / daily / weekly / monthly — or send one right now for any period below. Default recipient is receipts.scantopay@gmail.com unless overridden." />
                  <div className="flex gap-2">
                    {/* Refresh button removed per user request — the
                        schedules tab auto-loads when opened and creating
                        / editing schedules triggers a reload anyway. */}
                    <Button size="sm" className="rounded-xl" onClick={() => setScheduleDialog({ open: true, mode: "create", id: "", values: { name: "", frequency: "daily", report_types: ["business_summary", "sales_mix", "category_mix"], recipients: "", enabled: true } })} data-testid="schedules-add">+ Add schedule</Button>
                  </div>
                </div>

                {/* Session 51 — Send a report now (any range, any sections, any recipients) */}
                <Card className="rounded-2xl border-border bg-card" data-testid="test-report-card">
                  <CardContent className="space-y-4 p-5">
                    <div>
                      <p className="text-sm font-semibold">Send a report now</p>
                      <p className="text-xs text-muted-foreground">Pick the period, tick the sections you want, add one or more email addresses and send — the same full management report your schedules email, for exactly the days you choose.</p>
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Period</Label>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {sendNowPresets.map((p) => (
                          <Button key={p.v} type="button" size="sm" variant={sendNow.preset === p.v ? "default" : "outline"} className="rounded-xl" data-testid={`send-now-preset-${p.v}`} onClick={() => setSendNow((s) => ({ ...s, preset: p.v, ...(p.v === "custom" ? {} : { start: p.start, end: p.end }) }))}>{p.label}</Button>
                        ))}
                      </div>
                      <div className="mt-2 flex flex-wrap items-end gap-2">
                        <div>
                          <Label htmlFor="send-now-start" className="text-[11px]">From</Label>
                          <Input id="send-now-start" type="date" value={sendNow.start} onChange={(e) => setSendNow((s) => ({ ...s, preset: "custom", start: e.target.value }))} className="h-10 w-40 rounded-xl" data-testid="send-now-start" />
                        </div>
                        <div>
                          <Label htmlFor="send-now-end" className="text-[11px]">To</Label>
                          <Input id="send-now-end" type="date" value={sendNow.end} onChange={(e) => setSendNow((s) => ({ ...s, preset: "custom", end: e.target.value }))} className="h-10 w-40 rounded-xl" data-testid="send-now-end" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Include</Label>
                        <button type="button" className="text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={() => setSendNow((s) => ({ ...s, sections: s.sections.length === REPORT_SECTION_OPTIONS.length ? [] : REPORT_SECTION_OPTIONS.map((o) => o.v) }))} data-testid="send-now-toggle-all">{sendNow.sections.length === REPORT_SECTION_OPTIONS.length ? "Clear all" : "Select all"}</button>
                      </div>
                      <div className="mt-1.5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {REPORT_SECTION_OPTIONS.map((rt) => {
                          const checked = sendNow.sections.includes(rt.v);
                          return (
                            <label key={rt.v} className={`flex cursor-pointer items-start gap-2 rounded-xl border p-2.5 text-sm transition-colors ${checked ? "border-primary/40 bg-primary/5" : "border-border hover:bg-secondary/50"}`}>
                              <Checkbox checked={checked} onCheckedChange={(c) => setSendNow((s) => { const cur = new Set(s.sections); if (c) cur.add(rt.v); else cur.delete(rt.v); return { ...s, sections: Array.from(cur) }; })} className="mt-0.5" data-testid={`send-now-section-${rt.v}`} />
                              <span className="min-w-0">
                                <span className="block font-medium leading-tight">{rt.l}</span>
                                <span className="block text-[11px] leading-snug text-muted-foreground">{rt.d}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="min-w-[220px] flex-1">
                        <Label htmlFor="test-report-email">Recipient email(s)</Label>
                        <Input id="test-report-email" type="text" placeholder="manager@example.com, owner@example.com" value={testReportEmail} onChange={(e) => setTestReportEmail(e.target.value)} className="h-11 rounded-xl" data-testid="test-report-email" />
                        <p className="mt-1 text-[11px] text-muted-foreground">Separate several addresses with commas.</p>
                      </div>
                      <Button onClick={sendTestReport} disabled={testReportSending} className="h-11 rounded-xl" data-testid="test-report-send">{testReportSending ? "Sending…" : `Send ${(sendNowPresets.find((p) => p.v === sendNow.preset) || {}).label || "report"}`}</Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Schedules list */}
                <Card className="rounded-2xl border-border bg-card">
                  <CardContent className="p-5">
                    <p className="mb-3 text-sm font-semibold">Active schedules</p>
                    {reportSchedules.length ? (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Frequency</TableHead>
                              <TableHead className="hidden sm:table-cell">Recipients</TableHead>
                              <TableHead className="hidden md:table-cell">Reports</TableHead>
                              <TableHead className="text-center">Status</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportSchedules.map((s) => (
                              <TableRow key={s.id} data-testid={`schedule-row-${s.id}`}>
                                <TableCell className="font-medium">{s.name}</TableCell>
                                <TableCell className="text-xs capitalize">{(s.frequency || "").replace(/_/g, " ")}</TableCell>
                                <TableCell className="hidden text-xs text-muted-foreground sm:table-cell">{(s.recipients || []).join(", ") || "—"}</TableCell>
                                <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{(s.report_types || []).map((t) => t.replace(/_/g, " ")).join(", ")}</TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={s.enabled ? "default" : "secondary"} className="rounded-md text-[10px]">{s.enabled ? "Enabled" : "Paused"}</Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs" onClick={() => toggleScheduleEnabled(s)} data-testid={`schedule-toggle-${s.id}`}>{s.enabled ? "Pause" : "Resume"}</Button>
                                  <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs" onClick={() => setScheduleDialog({ open: true, mode: "edit", id: s.id, values: { name: s.name, frequency: s.frequency, report_types: s.report_types || [], recipients: (s.recipients || []).join(", "), enabled: s.enabled } })} data-testid={`schedule-edit-${s.id}`}>Edit</Button>
                                  <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs text-destructive" onClick={() => deleteSchedule(s)} data-testid={`schedule-delete-${s.id}`}>Delete</Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No schedules yet. Click "Add schedule" to create your first one.</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>


              {/* ── Audit Log ─────────────────────────────────────────── */}
              <TabsContent value="audit" className="space-y-4 pt-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <SectionHeader eyebrow="Accountability" title="Audit log" description="Manager-visible record of important actions across the business." />
                  {/* One-shot refresh — pulls fresh audit entries
                      without reloading the whole dashboard. */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={async () => {
                      setAuditRefreshing(true);
                      try { await loadAudit(); toast.success("Audit log refreshed."); }
                      catch (e) { toast.error("Could not refresh audit log."); }
                      finally { setAuditRefreshing(false); }
                    }}
                    disabled={auditRefreshing}
                    data-testid="audit-log-refresh-btn"
                    aria-label="Refresh audit log"
                  >
                    <RefreshCw className={`h-4 w-4 ${auditRefreshing ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { v: "today", label: "Today" },
                    { v: "yesterday", label: "Yesterday" },
                    { v: "7d", label: "Last 7 days" },
                    { v: "30d", label: "Last 30 days" },
                    { v: "all", label: "All time" },
                    { v: "custom", label: "Custom range" },
                  ].map((r) => (
                    <Button
                      key={r.v}
                      type="button"
                      variant={auditRange === r.v ? "default" : "outline"}
                      size="sm"
                      className="rounded-xl"
                      onClick={() => setAuditRange(r.v)}
                      data-testid={`audit-range-${r.v}`}
                    >
                      {r.label}
                    </Button>
                  ))}
                  {auditRange === "custom" && (
                    <>
                      <Input type="date" className="h-9 w-40 rounded-xl" value={auditCustom.start} onChange={(e) => setAuditCustom((c) => ({ ...c, start: e.target.value }))} />
                      <Input type="date" className="h-9 w-40 rounded-xl" value={auditCustom.end} onChange={(e) => setAuditCustom((c) => ({ ...c, end: e.target.value }))} />
                    </>
                  )}
                </div>
                <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-3" data-testid="audit-void-clearers-panel">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-destructive">Voids cleared — who did it</p>
                    <p className="text-[11px] text-muted-foreground">{voidClearers.length ? `${voidClearers.reduce((n, c) => n + (c.clears || 0), 0)} clear${voidClearers.reduce((n, c) => n + (c.clears || 0), 0) === 1 ? "" : "s"} on record` : "No void clears on record yet"}</p>
                  </div>
                  {voidClearers.length ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => jumpToVoidClears({ first_at: voidClearers.reduce((m, c) => (!m || (c.first_at && c.first_at < m) ? c.first_at : m), null), user: "" })} data-testid="audit-void-clearers-all">
                        All void clears
                      </Button>
                      {voidClearers.map((c) => (
                        <Button
                          key={c.user}
                          type="button"
                          size="sm"
                          variant={auditActionFilter === "voided_orders_cleared" && auditUserFilter === c.user ? "default" : "outline"}
                          className="rounded-full"
                          onClick={() => jumpToVoidClears(c)}
                          title={c.last_at ? `Last: ${new Date(c.last_at).toLocaleString()}` : undefined}
                          data-testid={`audit-void-clearer-${c.user}`}
                        >
                          {c.user}{c.user_role ? ` (${String(c.user_role).replace(/_/g, " ")})` : ""} · {c.clears} clear{c.clears === 1 ? "" : "s"} · {c.voids} void{c.voids === 1 ? "" : "s"}{c.value > 0 ? ` · ${formatPrice(c.value)}` : ""}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                  <p className="mt-2 text-[11px] text-muted-foreground">Tap a name to show only that person's void clears, from their first clear to today. Open any entry to see every void that was erased — order, table, waiter, who voided it, reason, items and value.</p>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <Input
                    type="search"
                    value={auditUserFilter}
                    onChange={(e) => setAuditUserFilter(e.target.value)}
                    placeholder="Filter by user…"
                    className="h-10 rounded-xl"
                    data-testid="audit-user-filter"
                  />
                  <Select value={auditActionFilter} onValueChange={setAuditActionFilter}>
                    <SelectTrigger className="h-10 rounded-xl" data-testid="audit-action-filter"><SelectValue placeholder="Action type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All action types</SelectItem>
                      {(auditCatalog.length ? auditCatalog : []).map((a) => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="search"
                      value={auditQuery}
                      onChange={(e) => setAuditQuery(e.target.value)}
                      placeholder="Search target name or action…"
                      className="h-10 rounded-xl pl-9"
                      data-testid="audit-query"
                    />
                  </div>
                </div>
                <Card className="rounded-2xl border-border bg-card">
                  <CardContent className="p-0">
                    {auditEntries.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date / Time</TableHead>
                            <TableHead>Action</TableHead>
                            <TableHead>Target</TableHead>
                            <TableHead>User</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {auditEntries.map((row) => (
                            <TableRow
                              key={row.id}
                              className="cursor-pointer transition-colors hover:bg-secondary/50"
                              onClick={() => setAuditDetail({ open: true, row })}
                              data-testid={`audit-row-${row.id}`}
                            >
                              <TableCell className="text-xs text-muted-foreground">{row.created_at ? new Date(row.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                              <TableCell className="text-sm font-medium">{row.action_label || row.action}</TableCell>
                              <TableCell className="text-sm">{row.target_name || row.target_type || "—"}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {row.user ? (
                                  <div className="flex items-center gap-2">
                                    {row.user_role ? (
                                      <span className={`h-2 w-2 rounded-full ${{
                                        owner: "bg-violet-500",
                                        manager: "bg-blue-500",
                                        supervisor: "bg-amber-500",
                                        waiter: "bg-emerald-500",
                                        staff: "bg-emerald-500",
                                        kitchen: "bg-orange-500",
                                        bar: "bg-red-500",
                                        admin: "bg-slate-500",
                                      }[row.user_role] || "bg-muted-foreground"}`} title={row.user_role} />
                                    ) : null}
                                    <span>{row.user}</span>
                                  </div>
                                ) : "—"}
                              </TableCell>
                              <TableCell><ChevronRight className="h-3.5 w-3.5 text-muted-foreground" /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="p-6">
                        (auditRange !== "all" && (auditActionFilter !== "all" || auditUserFilter.trim() || auditQuery.trim())) ? (
                          <div className="rounded-2xl border border-dashed border-border p-6 text-center" data-testid="audit-empty-filtered">
                            <p className="text-sm font-semibold">Nothing matches in this date window</p>
                            <p className="mt-1 text-xs text-muted-foreground">Your filters are applied across all users. Widen the dates to search every entry ever recorded.</p>
                            <Button type="button" size="sm" className="mt-3 rounded-full" onClick={() => setAuditRange("all")} data-testid="audit-search-all-time">Search all time</Button>
                          </div>
                        ) : (
                          <EmptyState icon={Receipt} title="No audit entries" description="Manager-visible actions (voids, refunds, payments, stock changes, etc.) appear here." />
                        )
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Session 31.5 — Expenses & Profit (monthly / weekly break-even). */}
              <TabsContent value="profit" className="space-y-4 pt-4" data-testid="profit-tab">
                <SectionHeader eyebrow="Reports" title="Expenses & Profit" description="Add what you pay every month — rent, water, lights, tax, salaries — and see exactly how much of it your sales have covered, when you break even, and what's left as profit." />
                {analyticsSubtab === "profit" ? (
                  <ExpensesProfitPanel
                    http={http}
                    authHeaders={authHeaders}
                    onSendToSpreadsheet={(rep) => {
                      if (!rep) return;
                      const rows = [
                        ["Metric", "Amount (R)"],
                        ["Period", rep.period?.label],
                        ["Gross sales", rep.sales?.gross], ["Refunds", -(rep.sales?.refunds || 0)], ["VAT", rep.settings?.profit_basis === "excl_vat" ? -(rep.sales?.vat || 0) : 0], ["Net sales", rep.sales?.net],
                        ["Cost of goods", -(rep.cost_of_goods?.total || 0)], ["Gross margin", rep.gross_margin],
                        ...((rep.expenses?.lines || []).map((l) => [`Expense: ${l.name}`, -(l.amount_in_period || 0)])),
                        ["Expenses total", -(rep.expenses?.total || 0)], ["Profit", rep.profit],
                        [], ["Date", "Orders", "Net sales", "Cost of goods", "Margin", "Running margin"],
                        ...((rep.days || []).map((d) => [d.date, d.orders, d.net_sales, d.cost_of_goods, d.margin, d.cumulative_margin])),
                      ];
                      sendRowsToSpreadsheet(`Profit ${rep.period?.label || ""}`, rows, "profit");
                    }}
                  />
                ) : null}
              </TabsContent>

              {/* Session 74 — Bank Reconciliation (expected in bank / expected cash, day by day). */}
              <TabsContent value="bank-recon" className="space-y-4 pt-4" data-testid="bank-recon-tab">
                <SectionHeader eyebrow="Reports" title="Bank Reconciliation" description="What should have reached your bank account (card + EFT) and what the cash should have added up to, for any period — day by day, so you can tick each line off against the bank statement and your cash-ups." />
                {analyticsSubtab === "bank-recon" ? (
                  <BankReconciliationPanel
                    http={http}
                    authHeaders={authHeaders}
                    onSendToSpreadsheet={(rows, name) => sendRowsToSpreadsheet(name || "Bank reconciliation", rows, "bank_reconciliation")}
                  />
                ) : null}
              </TabsContent>

              {/* Session 38 — Menu Engineering (Sales × Margin, transparent-first). */}
              <TabsContent value="menu-engineering" className="space-y-4 pt-4" data-testid="menu-engineering-tab">
                <SectionHeader eyebrow="Reports" title="Menu Engineering" description="Which dishes make you money and which just keep the kitchen busy. Every label is backed by the sales and margin numbers shown next to it." />
                {analyticsSubtab === "menu-engineering" ? (
                  <MenuEngineeringPanel
                    http={http}
                    authHeaders={authHeaders}
                    onSendToSpreadsheet={(rows, name) => sendRowsToSpreadsheet(name || "Menu engineering", rows, "menu_engineering")}
                  />
                ) : null}
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="branding" className="space-y-4">
            <SectionHeader eyebrow="Visual" title="Branding" description="Upload the banner shown at the top of the customer menu and the logo used as the saved-menu icon." />
            <Card className="rounded-[24px] border-border bg-card">
              <CardContent className="space-y-5 p-5">
                <div className="overflow-hidden rounded-[24px] border border-border">
                  <div className="relative aspect-[3/1] w-full">
                    <img
                      src={coverImageDraft || businessSettings?.cover_image || DEFAULT_COVER}
                      alt="Menu cover preview"
                      className={`absolute inset-0 h-full w-full ${coverImageFit === "contain" ? "object-contain bg-secondary" : coverImageFit === "fill" ? "object-fill" : "object-cover"}`}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cover image</Label>
                  {(coverImageDraft || businessSettings?.cover_image) ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Label className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-secondary" data-testid="owner-cover-replace-label">
                        <ImagePlus className="h-4 w-4" />
                        Replace image
                        <Input type="file" accept="image/*" className="hidden" onChange={handleCoverImageChange} data-testid="owner-cover-upload" />
                      </Label>
                      <Button
                        variant="outline"
                        className="h-11 rounded-xl text-destructive"
                        onClick={() => setCoverImageDraft("")}
                        data-testid="owner-cover-remove"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <Label className="flex h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground hover:bg-secondary/60" data-testid="owner-cover-dropzone">
                      <ImagePlus className="h-6 w-6" />
                      <span><strong className="text-foreground">Click to upload</strong> a cover image</span>
                      <span className="text-xs">JPG or PNG · banner shape works best (e.g. 1500 × 500&nbsp;px)</span>
                      <Input type="file" accept="image/*" className="hidden" onChange={handleCoverImageChange} data-testid="owner-cover-upload" />
                    </Label>
                  )}
                  <p className="text-xs text-muted-foreground">
                    The customer menu displays the cover as a wide banner (3:1 ratio). Design at <strong className="text-foreground">~1500 × 500&nbsp;px</strong> for a perfect fit. Anything bigger gets auto-shrunk to keep menus fast.
                  </p>
                </div>
                <div className="space-y-2" data-testid="owner-logo-section">
                  <Label>Menu icon (logo)</Label>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-[22px] border border-border bg-white shadow-sm" data-testid="owner-logo-preview">
                      {logoImageDraft ? (
                        <img src={logoImageDraft} alt="Menu icon preview" className="h-full w-full object-contain p-1.5" />
                      ) : (
                        <span className="px-2 text-center text-[10px] leading-tight text-muted-foreground">Cover image will be used</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Label className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-secondary" data-testid="owner-logo-upload-label">
                        <ImagePlus className="h-4 w-4" />
                        {logoImageDraft ? "Replace logo" : "Upload logo"}
                        <Input type="file" accept="image/*" className="hidden" onChange={handleLogoImageChange} data-testid="owner-logo-upload" />
                      </Label>
                      {logoImageDraft ? (
                        <Button variant="outline" className="h-11 rounded-xl text-destructive" onClick={() => setLogoImageDraft("")} data-testid="owner-logo-remove">
                          <Trash2 className="h-4 w-4" />
                          Remove
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Shown as the home-screen icon when guests or you save the digital menu as a shortcut. Use a <strong className="text-foreground">square logo</strong> (e.g. 512 × 512&nbsp;px, PNG with a plain background). Without one, the cover image is used.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner-cover-fit">Image fit</Label>
                  <Select value={coverImageFit} onValueChange={setCoverImageFit}>
                    <SelectTrigger id="owner-cover-fit" className="h-11 rounded-xl" data-testid="owner-cover-fit-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cover">Cover — fill the banner, crop overflow (best for photos)</SelectItem>
                      <SelectItem value="contain">Contain — show the whole image, never crop (best for logos / branded artwork)</SelectItem>
                      <SelectItem value="fill">Fill — stretch to exact banner shape</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={saveCoverImage} disabled={saving} data-testid="owner-cover-save-button">
                    <Check className="h-4 w-4" />
                    {saving ? "Saving…" : "Save branding"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Payment Gateways — unified section (Session 30.2) ── */}
          <TabsContent value="payment_gateways" className="space-y-4" data-testid="payment-gateways-tab">
            <SectionHeader eyebrow="Payments" title="Payment Gateways" description="Configure online payments for your digital menu. Pick a gateway below to set it up — every gateway has a Sandbox mode so you can test the full payment flow before going live." />
            {!yocoGatewayLoaded ? (
              <Card className="rounded-[24px] border-border bg-card"><CardContent className="p-6 text-center text-muted-foreground">Loading gateway config…</CardContent></Card>
            ) : (
              <Card className="rounded-[24px] border-border bg-card" data-testid="payment-gateways-card">
                <CardContent className="space-y-5 p-5">
                  {/* Horizontal gateway selector — click a name to open its config. */}
                  <div className="flex flex-wrap gap-2" data-testid="gateway-selector-row">
                    {[
                      { id: "yoco", label: "Yoco", enabled: !!yocoForm.yoco_payments_enabled },
                      { id: "payfast", label: "PayFast", enabled: !!saForm.payfast_enabled },
                      { id: "ozow", label: "Ozow", enabled: !!saForm.ozow_enabled },
                      { id: "snapscan", label: "SnapScan", enabled: !!saForm.snapscan_enabled },
                      { id: "zapper", label: "Zapper", enabled: !!saForm.zapper_enabled },
                    ].map((gw) => (
                      <button
                        type="button"
                        key={gw.id}
                        onClick={() => setSelectedGateway(gw.id)}
                        className={`inline-flex h-10 items-center gap-2 rounded-xl border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] ${
                          selectedGateway === gw.id
                            ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/12 text-foreground"
                            : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                        }`}
                        data-testid={`gateway-tab-${gw.id}`}
                      >
                        <span className={`h-2 w-2 rounded-full ${gw.enabled ? "bg-emerald-500" : "bg-muted-foreground/30"}`} aria-hidden="true" />
                        {gw.label}
                      </button>
                    ))}
                  </div>

                  <Separator />

                  {/* ── Yoco panel ── */}
                  {selectedGateway === "yoco" ? (
                    <div className="space-y-5" data-testid="yoco-gateway-card">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h3 className="font-semibold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Yoco</h3>
                          <p className="text-xs text-muted-foreground">Hosted card checkout in ZAR — customers are redirected to Yoco's secure payment page.</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-muted-foreground">Enabled</Label>
                          <Switch checked={!!yocoForm.yoco_payments_enabled} onCheckedChange={(v) => setYocoForm((f) => ({ ...f, yoco_payments_enabled: v }))} data-testid="yoco-enabled-toggle" />
                        </div>
                      </div>

                      {/* Mode toggle */}
                      <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/30 p-3">
                        <div>
                          <Label className="font-medium">Environment</Label>
                          <p className="text-xs text-muted-foreground">Use Sandbox for testing, Live for real payments.</p>
                        </div>
                        <Select value={yocoForm.yoco_mode} onValueChange={(v) => setYocoForm((f) => ({ ...f, yoco_mode: v }))}>
                          <SelectTrigger className="w-32 h-9 rounded-xl" data-testid="yoco-mode-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="test">Sandbox</SelectItem>
                            <SelectItem value="live">Live</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Secret keys */}
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="yoco-sk-test">Test Secret Key {yocoGateway.yoco_secret_key_test_set && <Badge variant="outline" className="ml-1 text-[10px] rounded-full">configured</Badge>}</Label>
                          <Input id="yoco-sk-test" type="password" className="h-11 rounded-xl font-mono text-sm" placeholder={yocoGateway.yoco_secret_key_test_hint || "sk_test_..."} value={yocoForm.yoco_secret_key_test} onChange={(e) => setYocoForm((f) => ({ ...f, yoco_secret_key_test: e.target.value }))} data-testid="yoco-sk-test" />
                        </div>
                        <div>
                          <Label htmlFor="yoco-sk-live">Live Secret Key {yocoGateway.yoco_secret_key_live_set && <Badge variant="outline" className="ml-1 text-[10px] rounded-full">configured</Badge>}</Label>
                          <Input id="yoco-sk-live" type="password" className="h-11 rounded-xl font-mono text-sm" placeholder={yocoGateway.yoco_secret_key_live_hint || "sk_live_..."} value={yocoForm.yoco_secret_key_live} onChange={(e) => setYocoForm((f) => ({ ...f, yoco_secret_key_live: e.target.value }))} data-testid="yoco-sk-live" />
                        </div>
                      </div>

                      {/* Webhook secrets */}
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="yoco-wh-test">Test Webhook Secret {yocoGateway.yoco_webhook_secret_test_set && <Badge variant="outline" className="ml-1 text-[10px] rounded-full">configured</Badge>}</Label>
                          <Input id="yoco-wh-test" type="password" className="h-11 rounded-xl font-mono text-sm" placeholder={yocoGateway.yoco_webhook_secret_test_hint || "whsec_..."} value={yocoForm.yoco_webhook_secret_test} onChange={(e) => setYocoForm((f) => ({ ...f, yoco_webhook_secret_test: e.target.value }))} data-testid="yoco-wh-test" />
                        </div>
                        <div>
                          <Label htmlFor="yoco-wh-live">Live Webhook Secret {yocoGateway.yoco_webhook_secret_live_set && <Badge variant="outline" className="ml-1 text-[10px] rounded-full">configured</Badge>}</Label>
                          <Input id="yoco-wh-live" type="password" className="h-11 rounded-xl font-mono text-sm" placeholder={yocoGateway.yoco_webhook_secret_live_hint || "whsec_..."} value={yocoForm.yoco_webhook_secret_live} onChange={(e) => setYocoForm((f) => ({ ...f, yoco_webhook_secret_live: e.target.value }))} data-testid="yoco-wh-live" />
                        </div>
                        <p className="text-xs text-muted-foreground">Set your webhook URL in the YOCO portal to: <code className="rounded bg-secondary px-1.5 py-0.5 text-[11px] font-mono">{`https://webnfcs.com/api/webhooks/yoco/${businessSettings?.id || "{business_id}"}`}</code></p>
                      </div>

                      <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/30 p-3">
                        <div>
                          <Label className="cursor-pointer font-medium">Auto-confirm paid orders</Label>
                          <p className="text-xs text-muted-foreground">When a customer pays online, automatically send their order to kitchen/bar without staff confirmation.</p>
                        </div>
                        <Switch checked={!!yocoForm.yoco_auto_confirm_paid} onCheckedChange={(v) => setYocoForm((f) => ({ ...f, yoco_auto_confirm_paid: v }))} data-testid="yoco-auto-confirm-toggle" />
                      </div>

                      <Button className="w-full rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={yocoSaving} onClick={saveYocoGateway} data-testid="yoco-save-btn">{yocoSaving ? "Saving…" : "Save Yoco settings"}</Button>
                    </div>
                  ) : null}

                  {/* ── SA gateway panels (PayFast / Ozow / SnapScan / Zapper) ── */}
                  {[
                    {
                      id: "payfast", name: "PayFast", desc: "Cards, EFT and recurring payments.",
                      fields: (
                        <div className="grid gap-2 sm:grid-cols-3">
                          <Input className="h-10 rounded-xl text-sm" placeholder="Merchant ID" value={saForm.payfast_merchant_id} onChange={(e) => setSaForm((f) => ({ ...f, payfast_merchant_id: e.target.value }))} data-testid="payfast-merchant-id" />
                          <Input className="h-10 rounded-xl font-mono text-sm" type="password" placeholder={yocoGateway.payfast_merchant_key_hint || "Merchant key"} value={saForm.payfast_merchant_key} onChange={(e) => setSaForm((f) => ({ ...f, payfast_merchant_key: e.target.value }))} data-testid="payfast-merchant-key" />
                          <Input className="h-10 rounded-xl font-mono text-sm" type="password" placeholder={yocoGateway.payfast_passphrase_hint || "Passphrase (optional)"} value={saForm.payfast_passphrase} onChange={(e) => setSaForm((f) => ({ ...f, payfast_passphrase: e.target.value }))} data-testid="payfast-passphrase" />
                        </div>
                      ),
                      configured: yocoGateway.payfast_merchant_key_set,
                    },
                    {
                      id: "ozow", name: "Ozow", desc: "Instant EFT payments.",
                      fields: (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <Input className="h-10 rounded-xl text-sm" placeholder="Site code" value={saForm.ozow_site_code} onChange={(e) => setSaForm((f) => ({ ...f, ozow_site_code: e.target.value }))} data-testid="ozow-site-code" />
                          <Input className="h-10 rounded-xl font-mono text-sm" type="password" placeholder={yocoGateway.ozow_private_key_hint || "Private key"} value={saForm.ozow_private_key} onChange={(e) => setSaForm((f) => ({ ...f, ozow_private_key: e.target.value }))} data-testid="ozow-private-key" />
                        </div>
                      ),
                      configured: yocoGateway.ozow_private_key_set,
                    },
                    {
                      id: "snapscan", name: "SnapScan", desc: "QR code payments.",
                      fields: (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <Input className="h-10 rounded-xl text-sm" placeholder="Merchant ID" value={saForm.snapscan_merchant_id} onChange={(e) => setSaForm((f) => ({ ...f, snapscan_merchant_id: e.target.value }))} data-testid="snapscan-merchant-id" />
                          <Input className="h-10 rounded-xl font-mono text-sm" type="password" placeholder={yocoGateway.snapscan_api_key_hint || "API key"} value={saForm.snapscan_api_key} onChange={(e) => setSaForm((f) => ({ ...f, snapscan_api_key: e.target.value }))} data-testid="snapscan-api-key" />
                        </div>
                      ),
                      configured: yocoGateway.snapscan_api_key_set,
                    },
                    {
                      id: "zapper", name: "Zapper", desc: "QR code payments and vouchers.",
                      fields: (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <Input className="h-10 rounded-xl text-sm" placeholder="Merchant ID" value={saForm.zapper_merchant_id} onChange={(e) => setSaForm((f) => ({ ...f, zapper_merchant_id: e.target.value }))} data-testid="zapper-merchant-id" />
                          <Input className="h-10 rounded-xl font-mono text-sm" type="password" placeholder={yocoGateway.zapper_api_key_hint || "API key"} value={saForm.zapper_api_key} onChange={(e) => setSaForm((f) => ({ ...f, zapper_api_key: e.target.value }))} data-testid="zapper-api-key" />
                        </div>
                      ),
                      configured: yocoGateway.zapper_api_key_set,
                    },
                  ].map((gw) => (
                    selectedGateway === gw.id ? (
                      <div key={gw.id} className="space-y-4" data-testid={`${gw.id}-section`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <h3 className="font-semibold text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{gw.name}</h3>
                            <p className="text-xs text-muted-foreground">{gw.desc}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground">Enabled</Label>
                            <Switch checked={!!saForm[`${gw.id}_enabled`]} onCheckedChange={(v) => setSaForm((f) => ({ ...f, [`${gw.id}_enabled`]: v }))} data-testid={`${gw.id}-enabled-toggle`} />
                          </div>
                        </div>

                        {/* Session 30.2 — sandbox/live mode, mirroring Yoco. */}
                        <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/30 p-3">
                          <div>
                            <Label className="font-medium">Environment</Label>
                            <p className="text-xs text-muted-foreground">Sandbox simulates payments end-to-end so you can test the digital menu flow safely.</p>
                          </div>
                          <Select value={saForm[`${gw.id}_mode`] || "sandbox"} onValueChange={(v) => setSaForm((f) => ({ ...f, [`${gw.id}_mode`]: v }))}>
                            <SelectTrigger className="w-32 h-9 rounded-xl" data-testid={`${gw.id}-mode-select`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sandbox">Sandbox</SelectItem>
                              <SelectItem value="live">Live</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {(saForm[`${gw.id}_mode`] || "sandbox") === "live" ? (
                          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300" data-testid={`${gw.id}-live-note`}>
                            Live checkout for {gw.name} is coming soon — while in Live mode it won't appear on the digital menu yet. Switch to Sandbox to test the full flow today.
                          </div>
                        ) : null}

                        {gw.fields}
                        {gw.configured ? <Badge variant="outline" className="rounded-full text-[10px]">key configured</Badge> : null}

                        <Button className="w-full rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={saSaving} onClick={saveSaGateways} data-testid="sa-gateways-save-btn">{saSaving ? "Saving…" : `Save ${gw.name} settings`}</Button>
                      </div>
                    ) : null
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Session 32 — GPT copilot settings (voice + usage). */}
          <TabsContent value="ai-settings" className="space-y-4" data-testid="ai-settings-tab">
            <SectionHeader eyebrow="Settings" title="AI Copilot" description="Talk to your dashboard. Ask for numbers, jump to any screen, dictate stock counts, or change stock and prices — every change is read back and confirmed before it happens." />
            {activeTab === "ai-settings" ? (
              <AiSettingsPanel http={http} authHeaders={authHeaders} isOwner={!sessionUser || sessionUser.role === "owner"} onSettingsChanged={(sx) => setAiContext((c) => ({ ...c, settings: sx }))} />
            ) : null}
          </TabsContent>

          {/* Accounts — running tabs that customers settle later. */}
          <TabsContent value="accounts" className="space-y-4">
            <SectionHeader eyebrow="Overview" title="Tabs" description="Customer tabs — balances from tables moved to a tab plus items added directly to a tab. Record payments here when they settle." />
            <div className="grid gap-3 sm:grid-cols-3">
              <Card className="rounded-[24px] border-border bg-card">
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Open tabs</p>
                  <p className="mt-1 text-3xl font-semibold">{accountsList.summary.open_count}</p>
                </CardContent>
              </Card>
              <Card className="rounded-[24px] border-border bg-card sm:col-span-2">
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Outstanding balance</p>
                  <p className="mt-1 text-3xl font-semibold">R {accountsList.summary.total_outstanding.toFixed(2)}</p>
                </CardContent>
              </Card>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={accountsFilter} onValueChange={(v) => { setAccountsFilter(v); loadAccounts(v); }}>
                <SelectTrigger className="h-9 w-44 rounded-xl" data-testid="accounts-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open only</SelectItem>
                  <SelectItem value="settled">Settled only</SelectItem>
                  <SelectItem value="all">All tabs</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => loadAccounts(accountsFilter)}>Refresh</Button>
            </div>
            {!accountsLoaded ? (
              <Skeleton className="h-40 w-full rounded-2xl" />
            ) : accountsList.accounts.length === 0 ? (
              <EmptyState icon={Receipt} title="No tabs to show" description="When staff open a tab or move a table's balance to a tab, it'll appear here." />
            ) : (
              <Card className="rounded-[24px] border-border bg-card">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead className="text-right">Amount owed</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                        <TableHead className="text-right">Days open</TableHead>
                        <TableHead className="w-24" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accountsList.accounts.map((a) => {
                        const days = a.created_at ? Math.max(0, Math.floor((Date.now() - new Date(a.created_at).getTime()) / 86400000)) : 0;
                        return (
                          <TableRow key={a.id} data-testid={`account-row-${a.id}`}>
                            <TableCell>
                              <div className="font-medium">{a.customer_name}</div>
                              <div className="text-xs text-muted-foreground">{a.original_table_number ? `From Table ${a.original_table_number}` : "Tab"} · {(a.orders || []).length} order{(a.orders || []).length === 1 ? "" : "s"} · {new Date(a.created_at).toLocaleDateString()}</div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{a.phone || "—"}</TableCell>
                            <TableCell className="text-right">R {a.amount_owed.toFixed(2)}</TableCell>
                            <TableCell className="text-right">R {a.total_paid.toFixed(2)}</TableCell>
                            <TableCell className="text-right font-semibold">
                              {a.status === "settled" ? (
                                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">Settled</span>
                              ) : (
                                <span className="text-destructive">R {a.remaining_balance.toFixed(2)}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{a.status === "settled" ? "—" : days}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => openAccountDetail(a.id)} data-testid={`account-view-${a.id}`}>
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── Reservations (owner read + status actions) ─────────────
              Lightweight list view backed by the same /staff/reservations
              endpoint. Owners can filter by status and mark bookings
              arrived / cancelled / completed; the audit log captures each
              action so the booking history is auditable. */}
          <TabsContent value="reservations" className="space-y-4">
            <SectionHeader
              eyebrow="Front of house"
              title="Reservations"
              description="All upcoming bookings created on the Staff dashboard. Mark as arrived when guests sit down, cancel no-shows, or close out completed bookings."
              action={
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={ownerReservationsFilter} onValueChange={(v) => { setOwnerReservationsFilter(v); loadOwnerReservations(v); }}>
                    <SelectTrigger className="h-10 w-[160px] rounded-xl" data-testid="owner-reservations-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                      <SelectItem value="arrived">Arrived</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => loadOwnerReservations(ownerReservationsFilter)} data-testid="owner-reservations-refresh">
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                  </Button>
                  <Button
                    size="sm"
                    className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                    onClick={() => setOwnerReservationDialog((d) => ({ ...d, open: true }))}
                    data-testid="owner-reservation-new-button"
                  >
                    <Plus className="h-4 w-4" />
                    New reservation
                  </Button>
                </div>
              }
            />
            {!ownerReservationsLoaded ? (
              <Card className="rounded-[24px] border-border bg-card">
                <CardContent className="p-6 text-sm text-muted-foreground">Loading reservations…</CardContent>
              </Card>
            ) : ownerReservations.length === 0 ? (
              <EmptyState icon={Calendar} title="No reservations" description={ownerReservationsFilter === "all" ? "No reservations have been created yet." : `No ${ownerReservationsFilter} reservations.`} />
            ) : (
              <div className="grid gap-3" data-testid="owner-reservations-list">
                {ownerReservations.map((r) => {
                  const dt = r.reserved_at ? new Date(r.reserved_at) : null;
                  const when = dt ? dt.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                  return (
                    <Card key={r.id} className="rounded-[20px] border-border bg-card" data-testid={`owner-reservation-row-${r.id}`}>
                      <CardContent className="flex flex-wrap items-start justify-between gap-3 p-4">
                        <div className="space-y-1">
                          <p className="font-semibold">{r.customer_name} <span className="ml-2 text-xs text-muted-foreground">party of {r.party_size}</span></p>
                          <p className="text-xs text-muted-foreground">{when}{r.table_label ? ` · ${r.table_label}` : ""}</p>
                          {r.phone ? <p className="text-xs text-muted-foreground">📞 {r.phone}</p> : null}
                          {r.notes ? <p className="text-xs italic text-muted-foreground">"{r.notes}"</p> : null}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge variant="secondary" className="rounded-full capitalize">{r.status}</Badge>
                          {r.status === "upcoming" ? (
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" className="rounded-xl bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => setOwnerReservationStatus(r.id, "arrived")} data-testid={`owner-reservation-arrived-${r.id}`}>Mark arrived</Button>
                              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setOwnerReservationStatus(r.id, "cancelled")} data-testid={`owner-reservation-cancel-${r.id}`}>Cancel</Button>
                            </div>
                          ) : r.status === "arrived" ? (
                            <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setOwnerReservationStatus(r.id, "completed")} data-testid={`owner-reservation-complete-${r.id}`}>Complete</Button>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Owner-side "New reservation" dialog — mirrors the Staff
                dashboard create form, posts to /staff/reservations. */}
            <Dialog open={ownerReservationDialog.open} onOpenChange={(o) => setOwnerReservationDialog((d) => ({ ...d, open: o }))}>
              <DialogContent className="flex max-h-[92vh] flex-col rounded-[24px] sm:max-w-md" data-testid="owner-reservation-dialog">
                <DialogHeader>
                  <DialogTitle className="font-['Space_Grotesk'] text-2xl">New reservation</DialogTitle>
                  <DialogDescription>Add a booking on behalf of a guest. Visible to staff immediately.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 overflow-y-auto pr-1">
                  <div className="space-y-1.5">
                    <Label htmlFor="owner-res-name">Customer name</Label>
                    <Input
                      id="owner-res-name"
                      className="h-11 rounded-xl"
                      placeholder="e.g. Lisa M."
                      value={ownerReservationDialog.form.customer_name}
                      onChange={(e) => setOwnerReservationDialog((d) => ({ ...d, form: { ...d.form, customer_name: e.target.value } }))}
                      data-testid="owner-reservation-customer-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="owner-res-phone">Phone <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
                      <Input
                        id="owner-res-phone"
                        className="h-11 rounded-xl"
                        placeholder="072…"
                        value={ownerReservationDialog.form.phone}
                        onChange={(e) => setOwnerReservationDialog((d) => ({ ...d, form: { ...d.form, phone: e.target.value } }))}
                        data-testid="owner-reservation-phone"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="owner-res-party">Party size</Label>
                      <Input
                        id="owner-res-party"
                        type="number"
                        inputMode="numeric"
                        min="1"
                        className="h-11 rounded-xl"
                        value={ownerReservationDialog.form.party_size}
                        onChange={(e) => setOwnerReservationDialog((d) => ({ ...d, form: { ...d.form, party_size: e.target.value } }))}
                        data-testid="owner-reservation-party-size"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="owner-res-date">Date</Label>
                      <Input
                        id="owner-res-date"
                        type="date"
                        className="h-11 rounded-xl"
                        value={ownerReservationDialog.form.reserved_date}
                        onChange={(e) => setOwnerReservationDialog((d) => ({ ...d, form: { ...d.form, reserved_date: e.target.value } }))}
                        data-testid="owner-reservation-date"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="owner-res-time">Time</Label>
                      <Input
                        id="owner-res-time"
                        type="time"
                        className="h-11 rounded-xl"
                        value={ownerReservationDialog.form.reserved_time}
                        onChange={(e) => setOwnerReservationDialog((d) => ({ ...d, form: { ...d.form, reserved_time: e.target.value } }))}
                        data-testid="owner-reservation-time"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="owner-res-table">Table label <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
                    <Input
                      id="owner-res-table"
                      className="h-11 rounded-xl"
                      placeholder="e.g. Window 4 or T7"
                      value={ownerReservationDialog.form.table_label}
                      onChange={(e) => setOwnerReservationDialog((d) => ({ ...d, form: { ...d.form, table_label: e.target.value } }))}
                      data-testid="owner-reservation-table-label"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="owner-res-notes">Notes <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
                    <Textarea
                      id="owner-res-notes"
                      rows={2}
                      className="rounded-xl"
                      placeholder="Allergies, occasion, seating prefs…"
                      value={ownerReservationDialog.form.notes}
                      onChange={(e) => setOwnerReservationDialog((d) => ({ ...d, form: { ...d.form, notes: e.target.value } }))}
                      data-testid="owner-reservation-notes"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" className="rounded-xl" onClick={() => setOwnerReservationDialog((d) => ({ ...d, open: false }))} data-testid="owner-reservation-cancel">Cancel</Button>
                  <Button
                    className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                    onClick={submitOwnerReservation}
                    disabled={ownerReservationDialog.saving}
                    data-testid="owner-reservation-save"
                  >
                    {ownerReservationDialog.saving ? "Saving…" : "Save reservation"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Shared "Clear history" confirmation dialog — used by
                both the Finance and Reports tabs. Requires the void
                password and shows a clear blast-radius warning so the
                action can't be triggered casually. */}
            <Dialog open={clearHistoryDialog.open} onOpenChange={(o) => setClearHistoryDialog((d) => ({ ...d, open: o }))}>
              <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="clear-history-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 font-['Space_Grotesk'] text-2xl text-destructive">
                    <Trash2 className="h-5 w-5" />
                    {clearHistoryDialog.scope === "reports" ? "Clear reports history" : "Clear finance history"}
                  </DialogTitle>
                  <DialogDescription>
                    {clearHistoryDialog.scope === "reports"
                      ? "This permanently deletes ALL orders and table sessions for this business. Sales reports, business summary, and tip history will start fresh. Menu, staff, settings, and cash floats are NOT affected."
                      : "This permanently deletes ALL cash floats, payouts, supplier payments, and cash-up records for this business. Sales reports are NOT affected — they're cleared separately under Reports."}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                    <p className="font-semibold uppercase tracking-wide">⚠ Cannot be undone</p>
                    <p className="mt-1">All affected records are removed from the database. The action itself is logged in the audit trail.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="clear-history-pin">Void password</Label>
                    <Input
                      id="clear-history-pin"
                      type="password"
                      autoComplete="off"
                      placeholder="Enter the void password"
                      className="h-11 rounded-xl"
                      value={clearHistoryDialog.void_password}
                      onChange={(e) => setClearHistoryDialog((d) => ({ ...d, void_password: e.target.value }))}
                      data-testid="clear-history-pin-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" className="rounded-xl" onClick={() => setClearHistoryDialog({ open: false, scope: null, void_password: "", saving: false })} data-testid="clear-history-cancel">Cancel</Button>
                  <Button
                    variant="destructive"
                    className="rounded-xl"
                    onClick={submitClearHistory}
                    disabled={clearHistoryDialog.saving || !clearHistoryDialog.void_password}
                    data-testid="clear-history-confirm"
                  >
                    <Trash2 className="h-4 w-4" />
                    {clearHistoryDialog.saving ? "Clearing…" : "Clear permanently"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ── Cash Management ────────────────────────────────────────
              Owner-only. Single page with summary tiles, action buttons,
              live cash ledger, and cash-up reconciliation history. */}
          <TabsContent value="cash" className="space-y-4">
            <SectionHeader
              eyebrow="Finance"
              title="Cash management"
              description="Track floats, payouts, supplier payments and cash-ups. Cash sales are pulled live from receipts."
              action={
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10"
                  onClick={() => openClearHistoryDialog("finance")}
                  data-testid="cash-clear-history"
                >
                  <Trash2 className="h-4 w-4" />
                  Clear history
                </Button>
              }
            />

            {/* Range filter */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { v: "today", label: "Today" },
                { v: "yesterday", label: "Yesterday" },
                { v: "7d", label: "Last 7 days" },
                { v: "custom", label: "Custom range" },
              ].map((r) => (
                <Button
                  key={r.v}
                  type="button"
                  variant={cashRange === r.v ? "default" : "outline"}
                  size="sm"
                  className="rounded-xl"
                  onClick={() => setCashRange(r.v)}
                  data-testid={`cash-range-${r.v}`}
                >
                  {r.label}
                </Button>
              ))}
              {cashRange === "custom" && (
                <>
                  <Input
                    type="date"
                    className="h-9 w-40 rounded-xl"
                    value={cashCustom.start}
                    onChange={(e) => setCashCustom((c) => ({ ...c, start: e.target.value }))}
                    data-testid="cash-custom-start"
                  />
                  <Input
                    type="date"
                    className="h-9 w-40 rounded-xl"
                    value={cashCustom.end}
                    onChange={(e) => setCashCustom((c) => ({ ...c, end: e.target.value }))}
                    data-testid="cash-custom-end"
                  />
                </>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-xl"
                onClick={loadCash}
                data-testid="cash-refresh"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
              {/* Multi-register filter — shown only when there's >1 register */}
              {registers.length > 0 ? (
                <Select value={selectedRegisterId} onValueChange={setSelectedRegisterId}>
                  <SelectTrigger className="h-9 w-48 rounded-xl text-xs" data-testid="cash-register-filter">
                    <SelectValue placeholder="All registers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All registers</SelectItem>
                    {registers.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}{!r.active ? " · disabled" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>

            {/* Registers management — inline card, defaults collapsed-ish */}
            <Card className="rounded-2xl border-border bg-card" data-testid="registers-card">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold">Registers</p>
                    <p className="text-xs text-muted-foreground">Each cash event is tied to a register. Main Register is created automatically.</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => setRegisterDialog({ open: true, mode: "create", id: "", values: { name: "", description: "" } })}
                    data-testid="registers-add"
                  >
                    + Add register
                  </Button>
                </div>
                {registers.length > 0 ? (
                  <div className="mt-3 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead className="hidden sm:table-cell">Description</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {registers.map((r) => (
                          <TableRow key={r.id} data-testid={`register-row-${r.id}`}>
                            <TableCell className="font-medium">
                              {r.name}{r.is_default ? <Badge variant="outline" className="ml-2 rounded-md text-[10px]">Default</Badge> : null}
                            </TableCell>
                            <TableCell className="hidden text-xs text-muted-foreground sm:table-cell">{r.description || "—"}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant={r.active ? "default" : "secondary"} className="rounded-md text-[10px]">{r.active ? "Active" : "Disabled"}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 rounded-lg text-xs"
                                onClick={() => setRegisterDialog({ open: true, mode: "edit", id: r.id, values: { name: r.name, description: r.description || "" } })}
                                data-testid={`register-edit-${r.id}`}
                              >Edit</Button>
                              {!r.is_default ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 rounded-lg text-xs"
                                  onClick={() => toggleRegisterActive(r)}
                                  data-testid={`register-toggle-${r.id}`}
                                >{r.active ? "Disable" : "Enable"}</Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-muted-foreground">No registers yet — click "Add register" to create one.</p>
                )}
              </CardContent>
            </Card>

            {/* Summary tiles */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
              <Card className="rounded-2xl border-emerald-500/30 bg-emerald-500/5">
                <CardContent className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Opening float</p>
                  <p className="mt-1 text-2xl font-semibold" data-testid="cash-summary-opening-float">{formatPrice(cashData.summary.opening_float)}</p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-border bg-card">
                <CardContent className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Cash sales</p>
                  <p className="mt-1 text-2xl font-semibold" data-testid="cash-summary-cash-sales">{formatPrice(cashData.summary.cash_sales)}</p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-sky-500/30 bg-sky-500/5">
                <CardContent className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">Card sales</p>
                  <p className="mt-1 text-2xl font-semibold" data-testid="cash-summary-card-sales">{formatPrice(cashData.summary.card_sales || 0)}</p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-border bg-card">
                <CardContent className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Cash payouts</p>
                  <p className="mt-1 text-2xl font-semibold text-destructive" data-testid="cash-summary-payouts">−{formatPrice(cashData.summary.cash_payouts)}</p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-border bg-card">
                <CardContent className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Supplier (cash)</p>
                  <p className="mt-1 text-2xl font-semibold text-destructive" data-testid="cash-summary-supplier">−{formatPrice(cashData.summary.supplier_payments_cash)}</p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-amber-500/40 bg-amber-500/10">
                <CardContent className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Expected cash</p>
                  <p className="mt-1 text-2xl font-semibold text-amber-700 dark:text-amber-300" data-testid="cash-summary-expected">{formatPrice(cashData.summary.expected_cash)}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">Bill money only — tips shown separately</p>
                </CardContent>
              </Card>
            </div>

            {/* Session 37 — bill money vs tips vs write-offs, kept apart so a
                shortage can never be explained away as "tips". */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="cash-tip-separation">
              <Card className="rounded-2xl border-border bg-card">
                <CardContent className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Cash tips recorded</p>
                  <p className="mt-1 text-xl font-semibold" data-testid="cash-summary-cash-tips">{formatPrice(cashData.summary.cash_tips || 0)}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">Card / EFT tips: {formatPrice((cashData.summary.card_tips || 0) + (cashData.summary.eft_tips || 0))}</p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-border bg-card">
                <CardContent className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Total cash received</p>
                  <p className="mt-1 text-xl font-semibold" data-testid="cash-summary-total-cash-received">{formatPrice(cashData.summary.total_cash_received ?? cashData.summary.cash_sales ?? 0)}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">Bill cash {formatPrice(cashData.summary.cash_sales || 0)} + cash tips {formatPrice(cashData.summary.cash_tips || 0)}</p>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-border bg-card">
                <CardContent className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Expected incl. cash tips</p>
                  <p className="mt-1 text-xl font-semibold" data-testid="cash-summary-expected-incl-tips">{formatPrice(cashData.summary.expected_cash_incl_tips ?? cashData.summary.expected_cash ?? 0)}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">If cash tips are still in the drawer</p>
                </CardContent>
              </Card>
              <Card className={`rounded-2xl ${(cashData.summary.unpaid_closes_count || 0) > 0 ? "border-amber-500/40 bg-amber-500/10" : "border-border bg-card"}`}>
                <CardContent className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Closed unpaid (PIN write-offs)</p>
                  <p className="mt-1 text-xl font-semibold" data-testid="cash-summary-unpaid-closes">{formatPrice(cashData.summary.unpaid_closes_total || 0)}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{cashData.summary.unpaid_closes_count || 0} table{(cashData.summary.unpaid_closes_count || 0) === 1 ? "" : "s"} · review in Tips → Exceptions</p>
                </CardContent>
              </Card>
            </div>

            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Button
                type="button"
                variant="outline"
                className="h-14 rounded-2xl"
                onClick={() => setFloatDialog({ open: true, mode: "create", id: "", values: { amount: "", user: "", notes: "", register_id: "", denominations: {}, use_total_only: false } })}
                data-testid="cash-open-float-btn"
              >
                <Coins className="h-4 w-4" />
                Opening float
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-14 rounded-2xl"
                onClick={() => setPayoutDialog({ open: true, values: { amount: "", reason: "", category: "stock_purchase", user: "", notes: "" } })}
                data-testid="cash-open-payout-btn"
              >
                <Banknote className="h-4 w-4" />
                Cash payout
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-14 rounded-2xl"
                onClick={() => setSupplierDialog({ open: true, values: { supplier_name: "", amount: "", payment_method: "cash", reference: "", user: "", notes: "" } })}
                data-testid="cash-open-supplier-btn"
              >
                <Package className="h-4 w-4" />
                Supplier payment
              </Button>
              <Button
                type="button"
                className="h-14 rounded-2xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                onClick={() => setCashUpDialog({ open: true, values: { actual_cash: "", user: "", notes: "", denominations: {} } })}
                data-testid="cash-open-cashup-btn"
              >
                <CheckCircle2 className="h-4 w-4" />
                Cash up
              </Button>
            </div>

            {/* Ledger */}
            <Card className="rounded-[24px] border-border bg-card">
              <CardHeader>
                <CardTitle className="text-lg">Cash movement history</CardTitle>
                <CardDescription>{cashData.start_date?.slice(0, 10)} → {cashData.end_date?.slice(0, 10)}</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {cashData.ledger.filter((r) => r.kind !== "card_sale").length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Details</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cashData.ledger.filter((r) => r.kind !== "card_sale").map((row, idx) => {
                        const ts = row.created_at ? new Date(row.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                        let typeLabel = row.kind;
                        let tone = "bg-secondary text-muted-foreground border-border";
                        let detail = "";
                        if (row.kind === "float") { typeLabel = "Opening float"; tone = "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"; detail = row.notes || ""; }
                        else if (row.kind === "cash_sale") { typeLabel = "Cash sale"; tone = "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"; detail = `Order ${row.order_number || ""} · Table ${row.table_number || "—"}`; }
                        else if (row.kind === "payout") { typeLabel = "Payout"; tone = "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30"; detail = `${row.reason || ""}${row.category ? ` · ${String(row.category).replace(/_/g, " ")}` : ""}`; }
                        else if (row.kind === "supplier") { typeLabel = "Supplier"; tone = "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30"; detail = `${row.supplier_name || ""}${row.reference ? ` · ${row.reference}` : ""} · ${row.payment_method}`; }
                        else if (row.kind === "cash_up") { typeLabel = "Cash up"; tone = "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30"; detail = `Actual ${formatPrice(row.actual_cash || 0)} vs expected ${formatPrice(row.expected_cash || 0)}`; }
                        const signed = Number(row.signed_amount || 0);
                        return (
                          <TableRow key={`${row.id || idx}-${row.kind}`} data-testid={`cash-ledger-row-${row.id || idx}`}>
                            <TableCell className="text-xs text-muted-foreground">{ts}</TableCell>
                            <TableCell><Badge className={`capitalize ${tone}`}>{typeLabel}</Badge></TableCell>
                            <TableCell className="text-sm">{detail}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {row.user ? (
                                <div className="flex flex-col leading-tight">
                                  <span className="font-medium text-foreground">{row.user}</span>
                                  {row.user_role ? (
                                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                      {String(row.user_role).replace(/_/g, " ")}
                                    </span>
                                  ) : null}
                                </div>
                              ) : "—"}
                            </TableCell>
                            <TableCell className={`text-right font-semibold ${signed > 0 ? "text-emerald-600 dark:text-emerald-400" : signed < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                              {signed === 0 ? "—" : (signed > 0 ? `+${formatPrice(signed)}` : `−${formatPrice(Math.abs(signed))}`)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="p-6">
                    <EmptyState icon={Coins} title="No cash activity yet" description="Add an opening float to start the ledger for this period." />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Card movement history — view-only ledger of card-paid order
                totals in the same window. Mirrors the cash table but
                filtered to card_sale rows only, so owners can audit card
                receipts at a glance. */}
            <Card className="rounded-[24px] border-border bg-card">
              <CardHeader>
                <CardTitle className="text-lg">Card movement history</CardTitle>
                <CardDescription>Read-only — card payments captured against orders in this window.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {cashData.ledger.filter((r) => r.kind === "card_sale").length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Time</TableHead>
                        <TableHead>Details</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cashData.ledger.filter((r) => r.kind === "card_sale").map((row, idx) => {
                        const ts = row.created_at ? new Date(row.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                        const detail = `Order ${row.order_number || ""} · Table ${row.table_number || "—"}`;
                        const signed = Number(row.signed_amount || 0);
                        return (
                          <TableRow key={`card-${row.id || idx}`} data-testid={`card-ledger-row-${row.id || idx}`}>
                            <TableCell className="text-xs text-muted-foreground">{ts}</TableCell>
                            <TableCell className="text-sm">{detail}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {row.user ? (
                                <div className="flex flex-col leading-tight">
                                  <span className="font-medium text-foreground">{row.user}</span>
                                  {row.user_role ? (
                                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                      {String(row.user_role).replace(/_/g, " ")}
                                    </span>
                                  ) : null}
                                </div>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-sky-700 dark:text-sky-300">
                              {signed === 0 ? "—" : `+${formatPrice(signed)}`}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="p-6">
                    <EmptyState icon={CreditCard} title="No card activity yet" description="Card payments captured on orders will appear here." />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cash-up history */}
            <Card className="rounded-[24px] border-border bg-card">
              <CardHeader>
                <CardTitle className="text-lg">Cash-up history</CardTitle>
                <CardDescription>Click any row to see the full snapshot it was reconciled against.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {cashHistory.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead className="text-right">Expected</TableHead>
                        <TableHead className="text-right">Actual</TableHead>
                        <TableHead className="text-right">Variance</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cashHistory.map((rec) => {
                        const tone = rec.status === "balanced"
                          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                          : rec.status === "over"
                            ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30"
                            : "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30";
                        return (
                          <TableRow
                            key={rec.id}
                            className="cursor-pointer transition-colors hover:bg-secondary/50"
                            onClick={() => setCashUpDetail({ open: true, record: rec })}
                            data-testid={`cash-up-row-${rec.id}`}
                          >
                            <TableCell className="text-sm">{rec.created_at ? new Date(rec.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {rec.user ? (
                                <div className="flex flex-col leading-tight">
                                  <span className="font-medium text-foreground">{rec.user}</span>
                                  {rec.user_role ? (
                                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                      {String(rec.user_role).replace(/_/g, " ")}
                                    </span>
                                  ) : null}
                                </div>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right">{formatPrice(rec.expected_cash || 0)}</TableCell>
                            <TableCell className="text-right">{formatPrice(rec.actual_cash || 0)}</TableCell>
                            <TableCell className={`text-right font-semibold ${rec.variance > 0 ? "text-sky-600 dark:text-sky-400" : rec.variance < 0 ? "text-destructive" : ""}`}>
                              {rec.variance > 0 ? "+" : ""}{formatPrice(rec.variance || 0)}
                            </TableCell>
                            <TableCell><Badge className={`capitalize ${tone}`}>{rec.status}</Badge></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="p-6">
                    <EmptyState icon={CheckCircle2} title="No cash-ups recorded" description="When you reconcile a shift, the result is stored here for audit." />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="access" className="space-y-4">
            <SectionHeader eyebrow="Security" title="Access" description="Update the owner, staff, kitchen, bar, or void password for this business." />
            <Card className="rounded-[24px] border-border bg-card">
              <CardContent className="space-y-5 p-5">
                <div className="grid gap-5 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="owner-password-update">Manager password</Label>
                    <Input id="owner-password-update" type="password" value={accessForm.owner_password} onChange={(event) => setAccessForm((current) => ({ ...current, owner_password: event.target.value }))} placeholder="Set a new manager password" className="h-11 rounded-xl" data-testid="owner-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="staff-password-update">Staff password</Label>
                    <Input id="staff-password-update" type="password" value={accessForm.staff_password} onChange={(event) => setAccessForm((current) => ({ ...current, staff_password: event.target.value }))} placeholder="Set a new staff password" className="h-11 rounded-xl" data-testid="staff-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="kitchen-password-update">Kitchen password</Label>
                    <Input id="kitchen-password-update" type="password" value={accessForm.kitchen_password} onChange={(event) => setAccessForm((current) => ({ ...current, kitchen_password: event.target.value }))} placeholder="Set a new kitchen password" className="h-11 rounded-xl" data-testid="kitchen-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bar-password-update">Bar password</Label>
                    <Input id="bar-password-update" type="password" value={accessForm.bar_password} onChange={(event) => setAccessForm((current) => ({ ...current, bar_password: event.target.value }))} placeholder="Set a new bar password" className="h-11 rounded-xl" data-testid="bar-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="runner-password-update">Runner password</Label>
                    <Input id="runner-password-update" type="password" value={accessForm.runner_password} onChange={(event) => setAccessForm((current) => ({ ...current, runner_password: event.target.value }))} placeholder="Set a new runner password" className="h-11 rounded-xl" data-testid="runner-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="retail-password-update">Retail password</Label>
                    <Input id="retail-password-update" type="password" value={accessForm.retail_password} onChange={(event) => setAccessForm((current) => ({ ...current, retail_password: event.target.value }))} placeholder="Set a new retail password" className="h-11 rounded-xl" data-testid="retail-password-input" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="void-password-update">Void (manager) password</Label>
                    <Input id="void-password-update" type="password" value={accessForm.void_password} onChange={(event) => setAccessForm((current) => ({ ...current, void_password: event.target.value }))} placeholder="Shared password for managers to void orders or line items" className="h-11 rounded-xl" data-testid="void-password-input" />
                    <p className="text-xs text-muted-foreground">Share this only with managers. Staff will be prompted for it whenever they try to void an order or a single line.</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Kitchen staff log in at <span className="font-mono">/kitchen</span>, bar staff at <span className="font-mono">/bar</span>, runners at <span className="font-mono">/runner</span>. Each sees only their own dashboard.
                </p>
                <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={saving} onClick={updateAccess} data-testid="staff-password-save-button">
                  Save access changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="staff" className="space-y-4">
            <SectionHeader
              eyebrow="Access"
              title="Staff"
              description="Per-user sign-in with PIN. Every refund, void, cash and stock action is recorded against the signed-in user."
            />

            {/* ── Security · action-gating passwords ───────────────────
                These three passwords protect ACTIONS (not dashboard
                sections), so they survived the role-based refactor.
                The legacy Manager / Staff / Analytics passwords were
                removed — section access is now driven by user role. */}
            <Card className="rounded-[24px] border-border bg-card" data-testid="security-passwords-card">
              <CardContent className="space-y-4 p-5">
                <div>
                  <p className="text-sm font-semibold">Security · action passwords</p>
                  <p className="mt-1 text-xs text-muted-foreground">These passwords protect specific actions (kitchen / bar logins and manager voids). Section visibility is controlled by user roles — no separate dashboard passwords needed.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="kitchen-pw-staff">Kitchen password</Label>
                    <Input id="kitchen-pw-staff" type="password" value={accessForm.kitchen_password} onChange={(e) => setAccessForm((c) => ({ ...c, kitchen_password: e.target.value }))} placeholder="Used at /kitchen sign-in" className="h-11 rounded-xl" data-testid="kitchen-password-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="bar-pw-staff">Bar password</Label>
                    <Input id="bar-pw-staff" type="password" value={accessForm.bar_password} onChange={(e) => setAccessForm((c) => ({ ...c, bar_password: e.target.value }))} placeholder="Used at /bar sign-in" className="h-11 rounded-xl" data-testid="bar-password-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="runner-pw-staff">Runner password</Label>
                    <Input id="runner-pw-staff" type="password" value={accessForm.runner_password} onChange={(e) => setAccessForm((c) => ({ ...c, runner_password: e.target.value }))} placeholder="Used at /runner sign-in" className="h-11 rounded-xl" data-testid="runner-password-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="retail-pw-staff">Retail password</Label>
                    <Input id="retail-pw-staff" type="password" value={accessForm.retail_password} onChange={(e) => setAccessForm((c) => ({ ...c, retail_password: e.target.value }))} placeholder="Used at /retail sign-in" className="h-11 rounded-xl" data-testid="retail-password-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="void-pw-staff">Void (manager) password</Label>
                    <Input id="void-pw-staff" type="password" value={accessForm.void_password} onChange={(e) => setAccessForm((c) => ({ ...c, void_password: e.target.value }))} placeholder="Required to void orders / lines" className="h-11 rounded-xl" data-testid="void-password-input" />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">Leave a field blank to keep the existing password. Kitchen staff sign in at <span className="font-mono">/kitchen</span>, bar staff at <span className="font-mono">/bar</span>, runners at <span className="font-mono">/runner</span>.</p>
                <Button size="sm" className="rounded-xl" disabled={saving} onClick={updateAccess} data-testid="staff-password-save-button">Save security passwords</Button>
              </CardContent>
            </Card>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs text-muted-foreground" data-testid="user-accounts-count">{userAccounts.length} {userAccounts.length === 1 ? "user" : "users"}</p>
                <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                  <SelectTrigger className="h-9 w-44 rounded-xl" data-testid="user-role-filter">
                    <SelectValue placeholder="All roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All roles</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="shift_leader">Shift leader</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="waiter">Waiter</SelectItem>
                    <SelectItem value="runner">Runner</SelectItem>
                    <SelectItem value="retailer">Retailer</SelectItem>
                    <SelectItem value="kitchen">Kitchen</SelectItem>
                    <SelectItem value="bar">Bar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="h-10 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={openCreateUser} data-testid="user-account-add-btn">
                <Plus className="h-4 w-4" />
                Add staff
              </Button>
            </div>
            <Card className="rounded-[24px] border-border bg-card">
              <CardContent className="p-0">
                {(() => {
                  // Order roles by seniority for a stable, meaningful
                  // display (was previously in creation order, which
                  // produced waiter→owner→waiter zig-zags).
                  const rolePriority = { owner: 0, manager: 1, shift_leader: 2, supervisor: 3, waiter: 4, staff: 5, runner: 6, retailer: 7, kitchen: 8, bar: 9, admin: 10 };
                  const visibleUsers = userAccounts
                    .filter((u) => userRoleFilter === "all" ? true : (u.role === userRoleFilter))
                    .slice()
                    .sort((a, b) => {
                      const pa = rolePriority[a.role] ?? 99;
                      const pb = rolePriority[b.role] ?? 99;
                      if (pa !== pb) return pa - pb;
                      const na = ((a.first_name || "") + " " + (a.last_name || "")).trim().toLowerCase();
                      const nb = ((b.first_name || "") + " " + (b.last_name || "")).trim().toLowerCase();
                      return na.localeCompare(nb);
                    });
                  if (!visibleUsers.length) {
                    return (
                      <EmptyState
                        icon={Users}
                        title={userAccounts.length ? "No matches for this role" : "No staff yet"}
                        description={userAccounts.length ? "Try switching the role filter above." : "Add your first team member to grant PIN access."}
                      />
                    );
                  }
                  return (
                    <ul className="divide-y divide-border" data-testid="user-accounts-list">
                    {visibleUsers.map((u) => {
                      const tone = {
                        owner: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
                        manager: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
                        supervisor: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
                        waiter: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
                        staff: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
                        kitchen: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",
                        bar: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30",
                        admin: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30",
                      }[u.role] || "bg-secondary text-muted-foreground border-border";
                      return (
                        <li key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`user-account-row-${u.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="truncate font-medium">{u.full_name || u.first_name}</span>
                              <Badge className={`rounded-full text-[10px] capitalize ${tone}`}>{u.role}</Badge>
                              {!u.active ? <Badge variant="outline" className="rounded-full text-[10px]">Disabled</Badge> : null}
                            </div>
                            <p className="text-xs text-muted-foreground">@{u.username}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-1">
                            <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => openUserActivity(u)} data-testid={`user-account-activity-${u.id}`}>
                              <Receipt className="h-4 w-4" />
                              Activity
                            </Button>
                            <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => setUserPinDialog({ open: true, user: u, pin: "", saving: false })} data-testid={`user-account-pin-${u.id}`}>
                              <Lock className="h-4 w-4" />
                              Reset PIN
                            </Button>
                            <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => openEditUser(u)} data-testid={`user-account-edit-${u.id}`}>
                              Edit
                            </Button>
                            <Button variant="ghost" size="sm" className={`rounded-xl ${u.active ? "text-destructive hover:bg-destructive/10 hover:text-destructive" : ""}`} onClick={() => toggleUserActive(u)} data-testid={`user-account-toggle-${u.id}`}>
                              {u.active ? "Disable" : "Enable"}
                            </Button>
                            <Button variant="ghost" size="sm" className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteUserDialog({ open: true, user: u, pin: "", saving: false })} data-testid={`user-account-delete-${u.id}`}>
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Analytics password prompt — gates the Analytics tab on entry.
          Password is configured by the platform admin only. */}
      <Dialog open={analyticsPromptOpen} onOpenChange={(open) => { if (!open) { setAnalyticsPromptOpen(false); setAnalyticsPasswordInput(""); } }}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Analytics is locked</DialogTitle>
            <DialogDescription>Enter the owner's analytics password to view reports and receipts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="analytics-password-input">Password</Label>
            <Input
              id="analytics-password-input"
              type="password"
              autoFocus
              value={analyticsPasswordInput}
              onChange={(e) => setAnalyticsPasswordInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") verifyAnalyticsPassword(); }}
              className="h-11 rounded-xl"
              data-testid="analytics-password-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => { setAnalyticsPromptOpen(false); setAnalyticsPasswordInput(""); }}>Cancel</Button>
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={verifyAnalyticsPassword}
              disabled={analyticsVerifying || !analyticsPasswordInput.trim()}
              data-testid="analytics-password-unlock"
            >
              {analyticsVerifying ? "Verifying..." : "Unlock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tab detail dialog — orders on the tab + payment history + record payment form. */}
      <Dialog open={!!accountDetail} onOpenChange={(open) => { if (!open) setAccountDetail(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-[28px] border-border bg-background sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{accountDetail?.customer_name || "Tab"}</DialogTitle>
            <DialogDescription>
              {accountDetail?.original_table_number ? `From Table ${accountDetail.original_table_number} · ` : "Tab · "}opened {accountDetail ? new Date(accountDetail.created_at).toLocaleString() : ""} by {accountDetail?.created_by || "staff"}
            </DialogDescription>
          </DialogHeader>
          {accountDetail ? (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl bg-secondary p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Amount owed</p>
                  <p className="text-lg font-semibold">R {accountDetail.amount_owed.toFixed(2)}</p>
                </div>
                <div className="rounded-2xl bg-secondary p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Paid so far</p>
                  <p className="text-lg font-semibold">R {accountDetail.total_paid.toFixed(2)}</p>
                </div>
                <div className="rounded-2xl bg-secondary p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Remaining</p>
                  <p className={`text-lg font-semibold ${accountDetail.status === "settled" ? "text-emerald-700 dark:text-emerald-300" : "text-destructive"}`}>
                    {accountDetail.status === "settled" ? "Settled" : `R ${accountDetail.remaining_balance.toFixed(2)}`}
                  </p>
                </div>
              </div>

              {(accountDetail.phone || accountDetail.notes) ? (
                <div className="rounded-2xl border border-border p-3 text-sm">
                  {accountDetail.phone ? <p><span className="text-muted-foreground">Phone:</span> {accountDetail.phone}</p> : null}
                  {accountDetail.notes ? <p className="mt-1"><span className="text-muted-foreground">Notes:</span> {accountDetail.notes}</p> : null}
                  {accountDetail.original_receipt_number ? <p className="mt-1 text-xs text-muted-foreground">Original receipt #: {accountDetail.original_receipt_number}</p> : null}
                </div>
              ) : null}

              {(accountDetail.orders || []).length ? (
                <div className="rounded-2xl border border-border p-3" data-testid="account-orders">
                  <p className="mb-2 text-sm font-medium">Orders on this tab</p>
                  <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
                    {accountDetail.orders.slice().reverse().map((o, i) => (
                      <li key={`${o.order_id}-${i}`} className="flex items-center justify-between gap-2">
                        <span>{o.order_number || "Order"} <span className="text-xs text-muted-foreground">· {o.at ? new Date(o.at).toLocaleString() : ""}</span></span>
                        <span className="tabular-nums">R {Number(o.total || 0).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {accountDetail.status !== "settled" ? (
                <div className="rounded-2xl border border-border p-3">
                  <p className="mb-2 text-sm font-medium">Record a payment</p>
                  <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={`max R ${accountDetail.remaining_balance.toFixed(2)}`}
                      value={accountPaymentDraft.amount}
                      onChange={(e) => setAccountPaymentDraft((d) => ({ ...d, amount: e.target.value }))}
                      className="h-10 rounded-xl"
                      data-testid="account-payment-amount"
                    />
                    <Select value={accountPaymentDraft.method} onValueChange={(v) => setAccountPaymentDraft((d) => ({ ...d, method: v }))}>
                      <SelectTrigger className="h-10 rounded-xl" data-testid="account-payment-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="eft">EFT</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                      onClick={recordAccountPayment}
                      disabled={accountPaymentSaving}
                      data-testid="account-payment-save"
                    >
                      {accountPaymentSaving ? "Saving…" : "Record payment"}
                    </Button>
                  </div>
                  <Input
                    placeholder="Notes (optional)"
                    value={accountPaymentDraft.notes}
                    onChange={(e) => setAccountPaymentDraft((d) => ({ ...d, notes: e.target.value }))}
                    className="mt-2 h-9 rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setAccountPaymentDraft((d) => ({ ...d, amount: accountDetail.remaining_balance.toFixed(2) }))}
                    className="mt-2 text-xs text-muted-foreground underline-offset-2 hover:underline"
                  >
                    Fill full remaining (R {accountDetail.remaining_balance.toFixed(2)})
                  </button>
                </div>
              ) : null}

              <div>
                <p className="mb-2 text-sm font-medium">Payment history</p>
                {accountDetail.payments.length === 0 ? (
                  <p className="text-sm italic text-muted-foreground">No payments yet.</p>
                ) : (
                  <div className="space-y-1">
                    {accountDetail.payments.map((p, i) => (
                      <div key={i} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-sm">
                        <div>
                          <p className="font-medium">R {p.amount.toFixed(2)} · {p.method}</p>
                          <p className="text-xs text-muted-foreground">{new Date(p.recorded_at).toLocaleString()} · by {p.recorded_by}</p>
                          {p.notes ? <p className="text-xs italic text-muted-foreground">"{p.notes}"</p> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setAccountDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={categoryDialog.open} onOpenChange={(open) => setCategoryDialog((current) => ({ ...current, open }))}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{categoryDialog.mode === "create" ? "Create category" : "Edit category"}</DialogTitle>
            <DialogDescription>Set the category name and visibility.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category-name">Name</Label>
              <Input id="category-name" value={categoryDialog.values.name} onChange={(event) => setCategoryDialog((current) => ({ ...current, values: { ...current.values, name: event.target.value } }))} className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category-description">Description</Label>
              <Textarea id="category-description" value={categoryDialog.values.description} onChange={(event) => setCategoryDialog((current) => ({ ...current, values: { ...current.values, description: event.target.value } }))} className="rounded-xl" />
            </div>
            <ScheduleEditor
              value={categoryDialog.values.schedule}
              onChange={(next) => setCategoryDialog((current) => ({ ...current, values: { ...current.values, schedule: next } }))}
              testidPrefix="category-schedule"
            />
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Always show on customer menu</p>
                <p className="text-xs text-muted-foreground">If off, customers can't see this category at all (time window is ignored).</p>
              </div>
              <Switch
                checked={categoryDialog.values.visible_customer !== false}
                onCheckedChange={(checked) => setCategoryDialog((current) => ({ ...current, values: { ...current.values, visible_customer: checked } }))}
                data-testid="category-visible-customer-toggle"
              />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Always show on staff menu</p>
                <p className="text-xs text-muted-foreground">Waiters can still ring up "off-hours" items (e.g. breakfast at 13:00) when off the customer menu.</p>
              </div>
              <Switch
                checked={categoryDialog.values.visible_staff !== false}
                onCheckedChange={(checked) => setCategoryDialog((current) => ({ ...current, values: { ...current.values, visible_staff: checked } }))}
                data-testid="category-visible-staff-toggle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={resetCategoryDialog}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitCategory} disabled={saving}>Save category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sides & Extras category dialog — 1:1 clone of the Item Categories
          dialog above so the owner gets identical fields/behaviour when
          managing the S&E grouping list. */}
      <Dialog open={sidesCategoryDialog.open} onOpenChange={(open) => setSidesCategoryDialog((current) => ({ ...current, open }))}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{sidesCategoryDialog.mode === "create" ? "Create S&E category" : "Edit S&E category"}</DialogTitle>
            <DialogDescription>Set the category name and visibility.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sides-category-name">Name</Label>
              <Input id="sides-category-name" value={sidesCategoryDialog.values.name} onChange={(event) => setSidesCategoryDialog((current) => ({ ...current, values: { ...current.values, name: event.target.value } }))} className="h-11 rounded-xl" data-testid="sides-category-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sides-category-description">Description</Label>
              <Textarea id="sides-category-description" value={sidesCategoryDialog.values.description} onChange={(event) => setSidesCategoryDialog((current) => ({ ...current, values: { ...current.values, description: event.target.value } }))} className="rounded-xl" />
            </div>
            <ScheduleEditor
              value={sidesCategoryDialog.values.schedule}
              onChange={(next) => setSidesCategoryDialog((current) => ({ ...current, values: { ...current.values, schedule: next } }))}
              testidPrefix="sides-category-schedule"
            />
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Always show on customer menu</p>
                <p className="text-xs text-muted-foreground">If off, customers can't see this category at all (time window is ignored).</p>
              </div>
              <Switch
                checked={sidesCategoryDialog.values.visible_customer !== false}
                onCheckedChange={(checked) => setSidesCategoryDialog((current) => ({ ...current, values: { ...current.values, visible_customer: checked } }))}
                data-testid="sides-category-visible-customer-toggle"
              />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Always show on staff menu</p>
                <p className="text-xs text-muted-foreground">Waiters can still ring up "off-hours" sides (e.g. breakfast extras at 13:00) when off the customer menu.</p>
              </div>
              <Switch
                checked={sidesCategoryDialog.values.visible_staff !== false}
                onCheckedChange={(checked) => setSidesCategoryDialog((current) => ({ ...current, values: { ...current.values, visible_staff: checked } }))}
                data-testid="sides-category-visible-staff-toggle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={resetSidesCategoryDialog}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitSidesCategory} disabled={saving} data-testid="sides-category-save-button">Save category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={itemDialog.open} onOpenChange={(open) => setItemDialog((current) => ({ ...current, open }))}>
        <DialogContent className="flex max-h-[92dvh] flex-col rounded-[28px] border-border bg-background p-0 sm:max-w-xl">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>{itemDialog.mode === "create" ? "Add item" : "Edit item"}</DialogTitle>
            <DialogDescription>Set the price, image, and live menu options.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-2 pt-2 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="item-category">Category</Label>
              <Select value={itemDialog.values.category_id} onValueChange={(value) => setItemDialog((current) => ({ ...current, values: { ...current.values, category_id: value } }))}>
                <SelectTrigger id="item-category" className="h-11 rounded-xl">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="item-name">Name</Label>
                <Input id="item-name" value={itemDialog.values.name} onChange={(event) => setItemDialog((current) => ({ ...current, values: { ...current.values, name: event.target.value } }))} className="h-11 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="item-price">Price (R)</Label>
                <Input id="item-price" type="number" step="0.01" value={itemDialog.values.price} onChange={(event) => setItemDialog((current) => ({ ...current, values: { ...current.values, price: event.target.value } }))} className="h-11 rounded-xl" />
              </div>
            </div>
            {/* Cost (R) lives in Analytics & Stock Take → Items so it stays close to stock-take + profit data. */}
            <div className="space-y-2">
              <Label htmlFor="item-notes-mode">
                Custom notes <span className="text-xs font-normal text-muted-foreground">(controls who can add a free-form note when ordering)</span>
              </Label>
              <Select
                value={itemDialog.values.notes_mode || "off"}
                onValueChange={(value) => setItemDialog((current) => ({ ...current, values: { ...current.values, notes_mode: value } }))}
              >
                <SelectTrigger id="item-notes-mode" className="h-11 rounded-xl" data-testid="menu-item-notes-mode-trigger">
                  <SelectValue placeholder="Off" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off — no custom notes</SelectItem>
                  <SelectItem value="staff">Staff only — waiter can add notes</SelectItem>
                  <SelectItem value="all">Staff &amp; customers — anyone can add notes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="item-description">Description</Label>
              <Textarea id="item-description" value={itemDialog.values.description} onChange={(event) => setItemDialog((current) => ({ ...current, values: { ...current.values, description: event.target.value } }))} className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="item-department">Department <span className="text-xs font-normal text-muted-foreground">(routes the item to the right station)</span></Label>
              <Select value={itemDialog.values.department || ""} onValueChange={(value) => setItemDialog((current) => ({ ...current, values: { ...current.values, department: value } }))}>
                <SelectTrigger id="item-department" className="h-11 rounded-xl" data-testid="menu-item-department-trigger">
                  <SelectValue placeholder={departments.length ? "Select department" : "Create a department first"} />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.name}>
                      {d.name}{d.route_to_kitchen ? "  ·  Kitchen" : ""}{d.route_to_bar ? "  ·  Bar" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {departments.length
                  ? "Items in a department flagged 'routes to kitchen' appear on the Kitchen Dashboard."
                  : "Add departments under Manage Menu → Departments before assigning items."}
              </p>
            </div>
            <div className="space-y-3 rounded-[24px] border border-border bg-secondary/50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Item image</p>
                  <p className="text-xs text-muted-foreground">Optimized automatically for mobile.</p>
                </div>
                <Label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-sm font-medium text-foreground">
                  <ImagePlus className="h-4 w-4" />
                  Upload image
                  <Input type="file" accept="image/*" className="hidden" onChange={handleItemImageChange} />
                </Label>
              </div>
              {itemDialog.values.image ? (
                <div className="flex items-center gap-3 rounded-2xl border border-border bg-background p-3">
                  <img src={itemDialog.values.image} alt="Item preview" className="h-16 w-16 rounded-2xl object-cover" />
                  <div className="flex-1 text-sm text-muted-foreground">Image ready for the live menu.</div>
                  <Button type="button" variant="outline" className="rounded-xl" onClick={() => setItemDialog((current) => ({ ...current, values: { ...current.values, image: "" } }))}>Remove</Button>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-border bg-background p-4 text-sm text-muted-foreground">No image uploaded yet.</div>
              )}
            </div>

            {/* Sides & extras configuration --------------------------------- */}
            {(() => {
              const sc = itemDialog.values.sides_config || { selection_mode: "none", free_count: 0, max_select: 1, allowed_side_ids: [], included_side_ids: [] };
              const setSc = (patch) => setItemDialog((current) => ({
                ...current,
                values: {
                  ...current.values,
                  sides_config: { ...(current.values.sides_config || {}), ...patch },
                },
              }));
              const allowedSet = new Set((sc.allowed_side_ids || []).map(String));
              const includedSet = new Set((sc.included_side_ids || []).map(String));
              const toggleAllowed = (sideId) => {
                const id = String(sideId);
                const next = new Set(allowedSet);
                if (next.has(id)) {
                  next.delete(id);
                  // Drop from included as well — included must be ⊆ allowed.
                  const nextIncluded = new Set(includedSet);
                  nextIncluded.delete(id);
                  setSc({
                    allowed_side_ids: Array.from(next),
                    included_side_ids: Array.from(nextIncluded),
                  });
                } else {
                  next.add(id);
                  setSc({ allowed_side_ids: Array.from(next) });
                }
              };
              const toggleIncluded = (sideId) => {
                const id = String(sideId);
                if (!allowedSet.has(id)) return;
                const next = new Set(includedSet);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                setSc({ included_side_ids: Array.from(next) });
              };
              return (
                <div
                  className="space-y-3 rounded-[24px] border border-border bg-secondary/50 p-4"
                  data-testid="item-sides-config"
                >
                  <div>
                    <p className="text-sm font-medium">Sides & extras</p>
                    <p className="text-xs text-muted-foreground">
                      Let customers add or swap sides for this item. Configure the rules below.
                    </p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="space-y-2 sm:col-span-1">
                      <Label htmlFor="sides-mode">Selection mode</Label>
                      <Select value={sc.selection_mode || "none"} onValueChange={(value) => setSc({ selection_mode: value })}>
                        <SelectTrigger id="sides-mode" className="h-11 rounded-xl" data-testid="sides-mode-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No sides</SelectItem>
                          <SelectItem value="optional">Optional</SelectItem>
                          <SelectItem value="required">Required</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 sm:col-span-1">
                      <Label htmlFor="sides-free">Free sides included</Label>
                      <Input
                        id="sides-free"
                        type="number"
                        min="0"
                        max="20"
                        value={sc.free_count ?? 0}
                        onChange={(event) => setSc({ free_count: Math.max(0, Number(event.target.value) || 0) })}
                        className="h-11 rounded-xl"
                        disabled={sc.selection_mode === "none"}
                        data-testid="sides-free-input"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-1">
                      <Label htmlFor="sides-max">Maximum sides</Label>
                      <Input
                        id="sides-max"
                        type="number"
                        min="1"
                        max="20"
                        value={sc.max_select ?? 1}
                        onChange={(event) => setSc({ max_select: Math.max(1, Number(event.target.value) || 1) })}
                        className="h-11 rounded-xl"
                        disabled={sc.selection_mode === "none"}
                        data-testid="sides-max-input"
                      />
                    </div>
                  </div>
                  {sc.selection_mode !== "none" ? (
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Available sides</Label>
                      {sides.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-border bg-background p-4 text-xs text-muted-foreground">
                          No sides in your library yet — add some under <span className="font-medium text-foreground">Manage Menu → Sides</span> first, then come back here.
                        </div>
                      ) : (
                        <div className="grid gap-2 sm:grid-cols-2">
                          {sides.filter((s) => s.active !== false).map((side) => {
                            const isAllowed = allowedSet.has(side.id);
                            const isIncluded = includedSet.has(side.id);
                            return (
                              <div
                                key={side.id}
                                className={`rounded-2xl border p-3 transition ${isAllowed ? "border-[hsl(var(--primary))]/60 bg-background" : "border-border bg-background/60"}`}
                                data-testid={`item-sides-row-${side.id}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="truncate text-sm font-medium">{side.name}</p>
                                      <span className="text-xs text-muted-foreground">{formatPrice(side.price)}</span>
                                    </div>
                                    {side.description ? (
                                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{side.description}</p>
                                    ) : null}
                                  </div>
                                  <Switch
                                    checked={isAllowed}
                                    onCheckedChange={() => toggleAllowed(side.id)}
                                    aria-label={`Allow ${side.name}`}
                                    data-testid={`item-sides-allow-${side.id}`}
                                  />
                                </div>
                                {isAllowed ? (
                                  <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                                    <Checkbox
                                      checked={isIncluded}
                                      onCheckedChange={() => toggleIncluded(side.id)}
                                      data-testid={`item-sides-included-${side.id}`}
                                    />
                                    <span>Pre-select for the customer (counts toward free quota)</span>
                                  </label>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">
                        First {Math.max(0, Number(sc.free_count) || 0)} side(s) are included for free. Anything beyond that is charged at the side's own price and added to the bill.
                      </p>
                    </div>
                  ) : null}
                </div>
              );
            })()}

            {/* Meal prep prompts configuration ------------------------------ */}
            {(() => {
              const prompts = Array.isArray(itemDialog.values.prep_prompts)
                ? itemDialog.values.prep_prompts
                : [];
              const setPrompts = (nextFn) =>
                setItemDialog((current) => ({
                  ...current,
                  values: {
                    ...current.values,
                    prep_prompts: typeof nextFn === "function" ? nextFn(current.values.prep_prompts || []) : nextFn,
                  },
                }));
              const addPrompt = () => {
                if (prompts.length >= 12) {
                  toast.error("You can add up to 12 prep questions per item.");
                  return;
                }
                setPrompts((cur) => [
                  ...cur,
                  {
                    id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    label: "",
                    required: true,
                    options: [""],
                  },
                ]);
              };
              const updatePrompt = (idx, patch) => {
                setPrompts((cur) => cur.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
              };
              const removePrompt = (idx) => {
                setPrompts((cur) => cur.filter((_, i) => i !== idx));
              };
              const addOption = (idx) => {
                setPrompts((cur) =>
                  cur.map((p, i) => {
                    if (i !== idx) return p;
                    const opts = Array.isArray(p.options) ? p.options : [];
                    if (opts.length >= 20) {
                      toast.error("Up to 20 options per prompt.");
                      return p;
                    }
                    return { ...p, options: [...opts, ""] };
                  })
                );
              };
              const updateOption = (pIdx, oIdx, value) => {
                setPrompts((cur) =>
                  cur.map((p, i) => {
                    if (i !== pIdx) return p;
                    const opts = Array.isArray(p.options) ? [...p.options] : [];
                    opts[oIdx] = value;
                    return { ...p, options: opts };
                  })
                );
              };
              const removeOption = (pIdx, oIdx) => {
                setPrompts((cur) =>
                  cur.map((p, i) => {
                    if (i !== pIdx) return p;
                    const opts = Array.isArray(p.options) ? p.options : [];
                    return { ...p, options: opts.filter((_, j) => j !== oIdx) };
                  })
                );
              };
              return (
                <div
                  className="space-y-3 rounded-[24px] border border-border bg-secondary/50 p-4"
                  data-testid="item-prep-prompts-config"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">Meal prep prompts</p>
                      <p className="text-xs text-muted-foreground">
                        Ask kitchen-prep questions (e.g. "How would you like your steak?"). Customers must answer required questions before adding to cart.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-xl"
                      onClick={addPrompt}
                      data-testid="add-prep-prompt-button"
                    >
                      <Plus className="h-4 w-4" />
                      Add question
                    </Button>
                  </div>
                  {prompts.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border bg-background p-4 text-xs text-muted-foreground">
                      No prep questions yet. Useful for items like steak (doneness), eggs (style), or toast (white / brown).
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {prompts.map((prompt, pIdx) => (
                        <div
                          key={prompt.id || pIdx}
                          className="space-y-3 rounded-2xl border border-border bg-background p-3"
                          data-testid={`prep-prompt-row-${pIdx}`}
                        >
                          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
                            <div className="space-y-1">
                              <Label htmlFor={`prep-label-${pIdx}`} className="text-xs uppercase tracking-wide text-muted-foreground">Question</Label>
                              <Input
                                id={`prep-label-${pIdx}`}
                                value={prompt.label || ""}
                                onChange={(e) => updatePrompt(pIdx, { label: e.target.value })}
                                placeholder="e.g. How would you like your steak?"
                                maxLength={80}
                                className="h-10 rounded-xl"
                                data-testid={`prep-label-input-${pIdx}`}
                              />
                            </div>
                            <label className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-xs">
                              <Switch
                                checked={prompt.required !== false}
                                onCheckedChange={(v) => updatePrompt(pIdx, { required: Boolean(v) })}
                                data-testid={`prep-required-toggle-${pIdx}`}
                              />
                              <span>Required</span>
                            </label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => removePrompt(pIdx)}
                              data-testid={`prep-remove-${pIdx}`}
                            >
                              <Trash2 className="h-4 w-4" />
                              Remove
                            </Button>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Options</Label>
                            <div className="space-y-2">
                              {(prompt.options || []).map((opt, oIdx) => (
                                <div
                                  key={`prompt-${pIdx}-opt-${oIdx}`}
                                  className="flex items-center gap-2"
                                  data-testid={`prep-option-row-${pIdx}-${oIdx}`}
                                >
                                  <Input
                                    value={opt}
                                    onChange={(e) => updateOption(pIdx, oIdx, e.target.value)}
                                    placeholder={`Option ${oIdx + 1} (e.g. Medium)`}
                                    maxLength={60}
                                    className="h-10 rounded-xl"
                                    data-testid={`prep-option-input-${pIdx}-${oIdx}`}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 rounded-full"
                                    onClick={() => removeOption(pIdx, oIdx)}
                                    disabled={(prompt.options || []).length <= 1}
                                    aria-label="Remove option"
                                    data-testid={`prep-option-remove-${pIdx}-${oIdx}`}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="rounded-xl"
                              onClick={() => addOption(pIdx)}
                              data-testid={`prep-add-option-${pIdx}`}
                            >
                              <Plus className="h-4 w-4" />
                              Add option
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Highlight as popular item</p>
                  <p className="text-xs text-muted-foreground">Adds a green "Popular" badge on the customer menu.</p>
                </div>
                <Switch checked={itemDialog.values.featured} onCheckedChange={(checked) => setItemDialog((current) => ({ ...current, values: { ...current.values, featured: checked } }))} />
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Show on customer menu</p>
                  <p className="text-xs text-muted-foreground">Hides this item from the QR/NFC menu when off.</p>
                </div>
                <Switch
                  checked={itemDialog.values.visible_customer !== false}
                  onCheckedChange={(checked) => setItemDialog((current) => ({ ...current, values: { ...current.values, visible_customer: checked } }))}
                  data-testid="item-visible-customer-toggle"
                />
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Show in staff Add-Items</p>
                  <p className="text-xs text-muted-foreground">Hides this item from the waiter Add-Items sheet when off.</p>
                </div>
                <Switch
                  checked={itemDialog.values.visible_staff !== false}
                  onCheckedChange={(checked) => setItemDialog((current) => ({ ...current, values: { ...current.values, visible_staff: checked } }))}
                  data-testid="item-visible-staff-toggle"
                />
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Show on retail dashboard</p>
                  <p className="text-xs text-muted-foreground">Hides this item from the Retail POS when off — e.g. keep a bar-counter retail screen drinks-only.</p>
                </div>
                <Switch
                  checked={itemDialog.values.visible_retail !== false}
                  onCheckedChange={(checked) => setItemDialog((current) => ({ ...current, values: { ...current.values, visible_retail: checked } }))}
                  data-testid="item-visible-retail-toggle"
                />
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Sell even when out of stock</p>
                  <p className="text-xs text-muted-foreground">Off (default): at zero stock the item stays on every menu marked "Out of stock" and cannot be ordered past what is on hand. On: keep selling — stock may go negative.</p>
                </div>
                <Switch
                  checked={Boolean(itemDialog.values.sell_when_out_of_stock ?? (itemDialog.values.hide_when_out_of_stock === false))}
                  onCheckedChange={(checked) => setItemDialog((current) => ({ ...current, values: { ...current.values, sell_when_out_of_stock: checked } }))}
                  data-testid="item-sell-when-oos-toggle"
                />
              </div>

            </div>
          </div>
          <DialogFooter className="border-t border-border bg-background px-6 py-4">
            <Button variant="outline" className="rounded-xl" onClick={resetItemDialog}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitItem} disabled={saving} data-testid="menu-item-save-button">Save item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Side (extra) dialog -------------------------------------- */}
      <Dialog open={sideDialog.open} onOpenChange={(open) => setSideDialog((current) => ({ ...current, open }))}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-[28px] border-border bg-background sm:max-w-xl" data-testid="side-dialog">
          <DialogHeader>
            <DialogTitle>{sideDialog.mode === "create" ? "Add side / extra" : "Edit side / extra"}</DialogTitle>
            <DialogDescription>Set the category, name, price, image, and routing — same options as a regular menu item.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="side-category">Category <span className="text-muted-foreground">(optional)</span></Label>
              <Select
                value={sideDialog.values.sides_category_id || "__none__"}
                onValueChange={(value) => setSideDialog((current) => ({ ...current, values: { ...current.values, sides_category_id: value === "__none__" ? "" : value } }))}
              >
                <SelectTrigger id="side-category" className="h-11 rounded-xl" data-testid="side-category-select">
                  <SelectValue placeholder="Pick an S&E category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No category (shows under "Sides & Extras")</SelectItem>
                  {sidesCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Create or manage these under Menu → S&E Categories.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="side-name">Name</Label>
              <Input
                id="side-name"
                value={sideDialog.values.name}
                onChange={(event) => setSideDialog((current) => ({ ...current, values: { ...current.values, name: event.target.value } }))}
                placeholder="e.g., Chips, Side salad, Garlic sauce"
                className="h-11 rounded-xl"
                data-testid="side-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="side-price">Price (R)</Label>
              <Input
                id="side-price"
                type="number"
                step="0.01"
                min="0"
                value={sideDialog.values.price}
                onChange={(event) => setSideDialog((current) => ({ ...current, values: { ...current.values, price: event.target.value } }))}
                className="h-11 rounded-xl"
                data-testid="side-price-input"
              />
              <p className="text-xs text-muted-foreground">Charged when the customer goes beyond their free quota.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="side-description">Description</Label>
              <Textarea
                id="side-description"
                value={sideDialog.values.description}
                onChange={(event) => setSideDialog((current) => ({ ...current, values: { ...current.values, description: event.target.value } }))}
                placeholder="Optional — short helper text shown next to the side."
                className="rounded-xl"
                data-testid="side-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="side-department">Department</Label>
              <Select
                value={sideDialog.values.department || ""}
                onValueChange={(value) => setSideDialog((current) => ({ ...current, values: { ...current.values, department: value } }))}
              >
                <SelectTrigger id="side-department" className="h-11 rounded-xl" data-testid="side-department-select">
                  <SelectValue placeholder="Pick a department" />
                </SelectTrigger>
                <SelectContent>
                  {(departments || []).map((dept) => (
                    <SelectItem key={dept.id || dept.name} value={dept.name}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Routes the ticket to the right station (kitchen, bar, etc.).</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="side-notes-mode">Custom notes</Label>
              <Select
                value={sideDialog.values.notes_mode || "off"}
                onValueChange={(value) => setSideDialog((current) => ({ ...current, values: { ...current.values, notes_mode: value } }))}
              >
                <SelectTrigger id="side-notes-mode" className="h-11 rounded-xl" data-testid="side-notes-mode-select">
                  <SelectValue placeholder="Off" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="staff">Staff only (waiters can add a note)</SelectItem>
                  <SelectItem value="all">Customers + staff can add a note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="side-image">Image</Label>
              <div className="flex items-center gap-3">
                {sideDialog.values.image ? (
                  <img src={sideDialog.values.image} alt="Side preview" className="h-16 w-16 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-dashed border-border text-xs text-muted-foreground">No image</div>
                )}
                <Input
                  id="side-image"
                  type="file"
                  accept="image/*"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    try {
                      const optimized = await resizeImageFile(file);
                      setSideDialog((current) => ({ ...current, values: { ...current.values, image: optimized } }));
                      toast.success("Image ready.");
                    } catch (err) {
                      toast.error(err.message || "Unable to process image.");
                    } finally {
                      event.target.value = "";
                    }
                  }}
                  className="h-11 rounded-xl"
                  data-testid="side-image-input"
                />
                {sideDialog.values.image ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => setSideDialog((current) => ({ ...current, values: { ...current.values, image: "" } }))}
                    data-testid="side-image-clear"
                  >
                    Clear
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-muted-foreground">Inactive sides won't show on customer menus.</p>
              </div>
              <Switch
                checked={sideDialog.values.active !== false}
                onCheckedChange={(checked) => setSideDialog((current) => ({ ...current, values: { ...current.values, active: checked } }))}
                data-testid="side-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setSideDialog({ open: false, mode: "create", values: { id: "", name: "", description: "", price: "", active: true, sides_category_id: "", department: "", image: "", notes_mode: "off" } })}>Cancel</Button>
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={submitSide}
              disabled={saving}
              data-testid="side-save-button"
            >
              Save side
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Voucher dialog -------------------------------------------- */}
      <Dialog open={voucherDialog.open} onOpenChange={(open) => setVoucherDialog((current) => ({ ...current, open }))}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-[28px] border-border bg-background sm:max-w-lg" data-testid="voucher-dialog">
          <DialogHeader>
            <DialogTitle>Create voucher</DialogTitle>
            <DialogDescription>Set a flat discount, optional expiry, and how many times the code can be redeemed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="voucher-code">Voucher code</Label>
              <Input
                id="voucher-code"
                value={voucherDialog.values.code}
                onChange={(event) => setVoucherDialog((current) => ({ ...current, values: { ...current.values, code: event.target.value.toUpperCase() } }))}
                placeholder="e.g. WELCOME50"
                className="h-11 rounded-xl font-mono tracking-wider"
                data-testid="voucher-code-input"
              />
              <p className="text-xs text-muted-foreground">Letters and numbers only. Staff will type this on the receipt.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="voucher-amount">Discount amount</Label>
              <Input
                id="voucher-amount"
                type="number"
                step="0.01"
                min="0"
                value={voucherDialog.values.discount_amount}
                onChange={(event) => setVoucherDialog((current) => ({ ...current, values: { ...current.values, discount_amount: event.target.value } }))}
                placeholder="e.g. 50.00"
                className="h-11 rounded-xl"
                data-testid="voucher-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Usage</Label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {[
                  { key: "single", label: "Single use" },
                  { key: "multi", label: "Multi-use" },
                  { key: "unlimited", label: "Unlimited" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setVoucherDialog((current) => ({ ...current, values: { ...current.values, usage_type: opt.key, max_uses: opt.key === "single" ? "1" : (current.values.max_uses || "10") } }))}
                    className={`h-11 rounded-xl border-2 px-3 text-sm font-semibold transition-colors ${voucherDialog.values.usage_type === opt.key ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]" : "border-border bg-background text-foreground hover:border-[hsl(var(--accent))]/40"}`}
                    data-testid={`voucher-usage-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {voucherDialog.values.usage_type === "multi" ? (
              <div className="space-y-2">
                <Label htmlFor="voucher-max-uses">Maximum uses</Label>
                <Input
                  id="voucher-max-uses"
                  type="number"
                  step="1"
                  min="1"
                  value={voucherDialog.values.max_uses}
                  onChange={(event) => setVoucherDialog((current) => ({ ...current, values: { ...current.values, max_uses: event.target.value } }))}
                  className="h-11 rounded-xl"
                  data-testid="voucher-max-uses-input"
                />
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="voucher-expires">Expires on (optional)</Label>
              <Input
                id="voucher-expires"
                type="date"
                value={voucherDialog.values.expires_at}
                onChange={(event) => setVoucherDialog((current) => ({ ...current, values: { ...current.values, expires_at: event.target.value } }))}
                className="h-11 rounded-xl"
                data-testid="voucher-expires-input"
              />
              <p className="text-xs text-muted-foreground">Leave blank for no expiry. Expired vouchers are removed automatically.</p>
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/40 p-3">
              <div>
                <p className="text-sm font-semibold">Active</p>
                <p className="text-xs text-muted-foreground">Inactive vouchers can be reactivated later.</p>
              </div>
              <Switch
                checked={Boolean(voucherDialog.values.active)}
                onCheckedChange={(checked) => setVoucherDialog((current) => ({ ...current, values: { ...current.values, active: Boolean(checked) } }))}
                data-testid="voucher-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setVoucherDialog((current) => ({ ...current, open: false }))} data-testid="voucher-cancel-button">
              Cancel
            </Button>
            <Button
              onClick={submitVoucher}
              disabled={voucherDialog.saving}
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              data-testid="voucher-submit-button"
            >
              {voucherDialog.saving ? "Saving…" : "Create voucher"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- Special dialog -------------------------------------------- */}
      <Dialog open={specialDialog.open} onOpenChange={(open) => setSpecialDialog((current) => ({ ...current, open }))}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-[28px] border-border bg-background sm:max-w-2xl" data-testid="special-dialog">
          <DialogHeader>
            <DialogTitle>{specialDialog.mode === "create" ? "Add special" : "Edit special"}</DialogTitle>
            <DialogDescription>
              Discount an existing item, add a standalone promo, or run a menu takeover for an event. Pick a schedule and the special activates automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="special-name">Name</Label>
                <Input id="special-name" value={specialDialog.values.name} onChange={(event) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, name: event.target.value } }))} placeholder="e.g., Wednesday Pizza Special" className="h-11 rounded-xl" data-testid="special-name-input" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="special-description">Description (optional)</Label>
                <Textarea id="special-description" value={specialDialog.values.description} onChange={(event) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, description: event.target.value } }))} placeholder="Shown to customers under the special name." className="rounded-xl" data-testid="special-description-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="special-kind">Kind</Label>
                <Select value={specialDialog.values.kind} onValueChange={(value) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, kind: value } }))}>
                  <SelectTrigger id="special-kind" className="h-11 rounded-xl" data-testid="special-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discount">Discount on a menu item</SelectItem>
                    <SelectItem value="standalone">Standalone promo item</SelectItem>
                    <SelectItem value="menu_takeover">Menu takeover</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="special-mode">Mode</Label>
                <Select value={specialDialog.values.mode} onValueChange={(value) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, mode: value } }))}>
                  <SelectTrigger id="special-mode" className="h-11 rounded-xl" data-testid="special-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="additive">Add on top of full menu</SelectItem>
                    <SelectItem value="exclusive">Replace menu (exclusive)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Kind-specific fields */}
            {specialDialog.values.kind === "discount" ? (
              <div className="grid gap-4 rounded-2xl border border-border bg-secondary/40 p-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="special-linked">Menu item to discount</Label>
                  <Select value={specialDialog.values.linked_item_id} onValueChange={(value) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, linked_item_id: value } }))}>
                    <SelectTrigger id="special-linked" className="h-11 rounded-xl" data-testid="special-linked-select"><SelectValue placeholder="Pick an item" /></SelectTrigger>
                    <SelectContent>
                      {menuItems.map((mi) => (
                        <SelectItem key={mi.id} value={mi.id}>{mi.name} — {formatPrice(mi.price)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="special-special-price">Special price (R)</Label>
                  <Input id="special-special-price" type="number" step="0.01" min="0" value={specialDialog.values.special_price} onChange={(event) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, special_price: event.target.value } }))} className="h-11 rounded-xl" data-testid="special-price-input" />
                </div>
              </div>
            ) : specialDialog.values.kind === "standalone" ? (
              <div className="grid gap-4 rounded-2xl border border-border bg-secondary/40 p-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="special-price">Price (R)</Label>
                  <Input id="special-price" type="number" step="0.01" min="0" value={specialDialog.values.price} onChange={(event) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, price: event.target.value } }))} className="h-11 rounded-xl" data-testid="special-standalone-price-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="special-department">Department</Label>
                  <Select value={specialDialog.values.department} onValueChange={(value) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, department: value } }))}>
                    <SelectTrigger id="special-department" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => (
                        <SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-border bg-secondary/40 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Categories to feature</p>
                <p className="mt-1 text-xs text-muted-foreground">Tick which categories should be visible during the takeover. Combine with "Replace menu" mode to hide everything else.</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {categories.map((cat) => {
                    const checked = (specialDialog.values.takeover_category_ids || []).includes(cat.id);
                    return (
                      <label key={cat.id} className="flex cursor-pointer items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => setSpecialDialog((current) => {
                            const ids = new Set(current.values.takeover_category_ids || []);
                            if (ids.has(cat.id)) ids.delete(cat.id);
                            else ids.add(cat.id);
                            return { ...current, values: { ...current.values, takeover_category_ids: Array.from(ids) } };
                          })}
                          data-testid={`special-takeover-cat-${cat.id}`}
                        />
                        <span>{cat.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Schedule builder ------------------------------------------- */}
            <div className="rounded-2xl border border-border bg-secondary/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium">Schedule</p>
                <Select value={specialDialog.values.schedule.type} onValueChange={(value) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, schedule: { ...current.values.schedule, type: value } } }))}>
                  <SelectTrigger className="h-9 w-[180px] rounded-xl" data-testid="special-schedule-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Recurring weekly</SelectItem>
                    <SelectItem value="date_range">Date range</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {specialDialog.values.schedule.type === "weekly" ? (
                <div className="mt-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Days of the week</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label, idx) => {
                      const checked = (specialDialog.values.schedule.weekdays || []).includes(idx);
                      return (
                        <button
                          key={label}
                          type="button"
                          onClick={() => setSpecialDialog((current) => {
                            const days = new Set(current.values.schedule.weekdays || []);
                            if (days.has(idx)) days.delete(idx);
                            else days.add(idx);
                            return { ...current, values: { ...current.values, schedule: { ...current.values.schedule, weekdays: Array.from(days) } } };
                          })}
                          className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                            checked
                              ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                              : "border border-border bg-card text-foreground hover:bg-secondary"
                          }`}
                          data-testid={`special-weekday-${idx}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="special-start-date">Start date</Label>
                    <Input id="special-start-date" type="date" value={specialDialog.values.schedule.start_date || ""} onChange={(event) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, schedule: { ...current.values.schedule, start_date: event.target.value } } }))} className="h-11 rounded-xl" data-testid="special-start-date-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="special-end-date">End date</Label>
                    <Input id="special-end-date" type="date" value={specialDialog.values.schedule.end_date || ""} onChange={(event) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, schedule: { ...current.values.schedule, end_date: event.target.value } } }))} className="h-11 rounded-xl" data-testid="special-end-date-input" />
                  </div>
                </div>
              )}
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="special-start-time">Start time (optional)</Label>
                  <Input id="special-start-time" type="time" value={specialDialog.values.schedule.start_time || ""} onChange={(event) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, schedule: { ...current.values.schedule, start_time: event.target.value } } }))} className="h-11 rounded-xl" data-testid="special-start-time-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="special-end-time">End time (optional)</Label>
                  <Input id="special-end-time" type="time" value={specialDialog.values.schedule.end_time || ""} onChange={(event) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, schedule: { ...current.values.schedule, end_time: event.target.value } } }))} className="h-11 rounded-xl" data-testid="special-end-time-input" />
                </div>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Leave times blank for an all-day special. Times are interpreted in the restaurant's local timezone.</p>
            </div>

            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-muted-foreground">Pause without deleting if you want to keep the schedule for later.</p>
              </div>
              <Switch
                checked={specialDialog.values.active !== false}
                onCheckedChange={(checked) => setSpecialDialog((current) => ({ ...current, values: { ...current.values, active: checked } }))}
                data-testid="special-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={resetSpecialDialog}>Cancel</Button>
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={submitSpecial}
              disabled={saving}
              data-testid="special-save-button"
            >
              Save special
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={tagDialog.open} onOpenChange={(open) => setTagDialog((current) => ({ ...current, open }))}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{tagDialog.mode === "create" ? "Create tag" : "Edit tag"}</DialogTitle>
            <DialogDescription>Each tag is bound to a specific table. Customers scanning this tag are checked in to the table you name below — no extra step at checkout.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tag-label">Table name or number</Label>
              <Input id="tag-label" value={tagDialog.values.label} onChange={(event) => setTagDialog((current) => ({ ...current, values: { ...current.values, label: event.target.value } }))} placeholder="e.g., 1, 5, Patio 3, Bar 2" className="h-11 rounded-xl" data-testid="tag-label-input" />
              <p className="text-xs text-muted-foreground">This is the table number that staff will see on every order placed from this tag.</p>
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-muted-foreground">Inactive tags will not redirect customers.</p>
              </div>
              <Switch checked={tagDialog.values.active} onCheckedChange={(checked) => setTagDialog((current) => ({ ...current, values: { ...current.values, active: checked } }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={resetTagDialog}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitTag} disabled={saving} data-testid="tag-save-button">Save tag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NfcWriteDialog tag={nfcWriteTag} onClose={() => setNfcWriteTag(null)} />

      {/* Department dialog */}
      <Dialog open={departmentDialog.open} onOpenChange={(open) => setDepartmentDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px]">
          <DialogHeader>
            <DialogTitle>{departmentDialog.mode === "create" ? "Add department" : "Edit department"}</DialogTitle>
            <DialogDescription>Departments group menu items by station and decide what shows on the Kitchen Dashboard.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                className="h-11 rounded-xl"
                value={departmentDialog.values.name || ""}
                onChange={(e) => setDepartmentDialog((d) => ({ ...d, values: { ...d.values, name: e.target.value } }))}
                placeholder="e.g., Kitchen, Bar, Coffee station"
                data-testid="department-name-input"
              />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Routes to kitchen</p>
                <p className="text-xs text-muted-foreground">If on, items in this department appear on the Kitchen Dashboard.</p>
              </div>
              <Switch
                checked={!!departmentDialog.values.route_to_kitchen}
                onCheckedChange={(checked) => setDepartmentDialog((d) => ({ ...d, values: { ...d.values, route_to_kitchen: checked } }))}
                data-testid="department-route-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Routes to bar</p>
                <p className="text-xs text-muted-foreground">If on, items in this department appear on the Bar Dashboard. Drinks usually.</p>
              </div>
              <Switch
                checked={!!departmentDialog.values.route_to_bar}
                onCheckedChange={(checked) => setDepartmentDialog((d) => ({ ...d, values: { ...d.values, route_to_bar: checked } }))}
                data-testid="department-route-bar-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Also notify staff</p>
                <p className="text-xs text-muted-foreground">If on, this department's items also appear on the staff "Kitchen ready" and "Bar ready" banners — handy when you want the floor team to see the full order at delivery.</p>
              </div>
              <Switch
                checked={!!departmentDialog.values.route_to_staff}
                onCheckedChange={(checked) => setDepartmentDialog((d) => ({ ...d, values: { ...d.values, route_to_staff: checked } }))}
                data-testid="department-route-staff-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-muted-foreground">Inactive departments cannot be assigned to new items.</p>
              </div>
              <Switch
                checked={departmentDialog.values.active !== false}
                onCheckedChange={(checked) => setDepartmentDialog((d) => ({ ...d, values: { ...d.values, active: checked } }))}
              />
            </div>

            {/* Bulk-assign categories — Owner */}
            <div className="space-y-2 rounded-2xl border border-dashed border-border bg-secondary/30 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Bulk-assign categories</p>
                  <p className="text-xs text-muted-foreground">
                    Tick a category — every menu item in it will be moved to this department on save.
                  </p>
                </div>
                {categories.length > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={() => setDeptCategoryIds(deptCategoryIds.length === categories.length ? [] : categories.map((c) => c.id))}
                    data-testid="department-toggle-all-categories"
                  >
                    {deptCategoryIds.length === categories.length ? "Clear all" : "Select all"}
                  </Button>
                ) : null}
              </div>
              {categories.length === 0 ? (
                <p className="text-xs text-muted-foreground">No categories yet. Create some first.</p>
              ) : (
                <div className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2">
                  {categories.map((cat) => {
                    const checked = deptCategoryIds.includes(cat.id);
                    const itemCount = (menuItems || []).filter((it) => it.category_id === cat.id).length;
                    return (
                      <label
                        key={cat.id}
                        htmlFor={`dept-cat-${cat.id}`}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${checked ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10" : "border-border bg-background hover:border-[hsl(var(--accent))]/40"}`}
                      >
                        <Checkbox
                          id={`dept-cat-${cat.id}`}
                          checked={checked}
                          onCheckedChange={(value) => {
                            setDeptCategoryIds((current) => {
                              if (value) return current.includes(cat.id) ? current : [...current, cat.id];
                              return current.filter((id) => id !== cat.id);
                            });
                          }}
                          data-testid={`department-category-${cat.id}`}
                        />
                        <span className="flex-1 truncate">{cat.name}</span>
                        <span className="text-xs text-muted-foreground">{itemCount}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {deptCategoryIds.length > 0 ? (
                <p className="rounded-lg bg-emerald-600/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-400">
                  On save, every item in {deptCategoryIds.length} selected categor{deptCategoryIds.length === 1 ? "y" : "ies"} will be reassigned to this department.
                </p>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => { setDepartmentDialog({ open: false, mode: "create", values: { id: "", name: "", route_to_kitchen: false, route_to_bar: false, active: true } }); setDeptCategoryIds([]); }}>Cancel</Button>
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={submitDepartment}
              data-testid="department-save-button"
            >
              <Check className="h-4 w-4" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu Settings Dialog — Session 63.E: one dialog, two modes. "menu"
          = VAT / service fee / service alerts / sell-when-out-of-stock;
          "digital" = everything that only affects the QR / digital menu. */}
      <Dialog open={menuSettingsOpen} onOpenChange={setMenuSettingsOpen}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${menuSettingsMode === "digital" ? "digital-menu" : "menu"}-settings-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {menuSettingsMode === "digital" ? <Smartphone className="h-5 w-5" /> : <Settings className="h-5 w-5" />}
              {menuSettingsMode === "digital" ? "Digital Menu Settings" : "Menu Settings"}
            </DialogTitle>
            <DialogDescription>
              {menuSettingsMode === "digital"
                ? "Everything that shapes the customer-facing QR / digital menu: section order, staff confirmation, online payments, identity fields, availability schedule and price filters."
                : "VAT, automatic service fee, service alerts and the out-of-stock selling default."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {menuSettingsMode === "menu" ? (<>
            {/* VAT Configuration Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">VAT Configuration</h3>
                <p className="text-sm text-muted-foreground">Set the VAT rate applied to all orders</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="settings-vat-rate">VAT Rate (%)</Label>
                <Input
                  id="settings-vat-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  // Use ?? not || so 0% is preserved. `||` treats 0 as
                  // falsy and falls back to 15, which prevented owners
                  // from ever setting a zero VAT rate.
                  value={businessSettings?.vat_rate ?? context?.business?.vat_rate ?? 15}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Empty input → keep null so the placeholder shows;
                    // any typed number (including 0) is preserved.
                    const parsed = raw === "" ? null : parseFloat(raw);
                    setBusinessSettings((prev) => ({
                      ...prev,
                      vat_rate: parsed === null || Number.isNaN(parsed) ? null : parsed,
                    }));
                  }}
                  className="h-11 rounded-xl"
                  placeholder="15.00"
                  data-testid="settings-vat-input"
                />
                <p className="text-sm text-muted-foreground">
                  Current rate: {context?.business?.vat_rate ?? 15}% • Applied to all new orders
                </p>
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Session 34 — Automatic service fee (gratuity) */}
            <div className="space-y-4" data-testid="settings-service-fee">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-semibold"><HandCoins className="h-5 w-5" /> Service fee</h3>
                  <p className="text-sm text-muted-foreground">Automatically added to every table and tab bill as its own line (“Service fee (10%)”) and credited to the waiter like a tip. Guests can still add an extra tip on top.</p>
                </div>
                <Switch
                  checked={Boolean(businessSettings?.service_fee_enabled ?? context?.business?.service_fee_enabled)}
                  onCheckedChange={(v) => editFeeSetting("service_fee_enabled", v)}
                  aria-label="Service fee on or off"
                  data-testid="settings-service-fee-toggle"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="settings-service-fee-pct">Service fee (%)</Label>
                <Input
                  id="settings-service-fee-pct"
                  type="number"
                  step="0.5"
                  min="0"
                  max="50"
                  value={businessSettings?.service_fee_pct ?? context?.business?.service_fee_pct ?? 10}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const parsed = raw === "" ? null : parseFloat(raw);
                    editFeeSetting("service_fee_pct", parsed === null || Number.isNaN(parsed) ? null : parsed);
                  }}
                  disabled={!(businessSettings?.service_fee_enabled ?? context?.business?.service_fee_enabled)}
                  className="h-11 rounded-xl"
                  placeholder="10"
                  data-testid="settings-service-fee-input"
                />
                <p className="text-sm text-muted-foreground" data-testid="settings-service-fee-hint">
                  {(businessSettings?.service_fee_enabled ?? context?.business?.service_fee_enabled)
                    ? `Currently ${context?.business?.service_fee_pct ?? 10}% of the bill (after any voucher) is added at payment. Not removable per bill — switch it off here to stop charging it.`
                    : "Off — bills are not charged a service fee. Tipping stays optional."}
                </p>
              </div>
              {/* Session 46 — keep the service fee out of staff tips */}
              <div className="flex items-start justify-between gap-3 rounded-2xl border border-border bg-secondary/40 p-3" data-testid="settings-service-fee-separate">
                <div>
                  <p className="text-sm font-medium">Report the service fee separately (not as staff tips)</p>
                  <p className="text-xs text-muted-foreground">
                    {(businessSettings?.service_fee_separate ?? context?.business?.service_fee_separate)
                      ? "On — the service fee is NOT credited to waiters. Tips show extra tips only, and a separate “Service fee” figure appears in the Business summary and Operations totals. Grand total never includes tips or the fee."
                      : "Off — the service fee is credited to the waiter like a tip and counted under Tips."}
                  </p>
                </div>
                <Switch
                  checked={Boolean(businessSettings?.service_fee_separate ?? context?.business?.service_fee_separate)}
                  onCheckedChange={(v) => editFeeSetting("service_fee_separate", v)}
                  aria-label="Report service fee separately from tips"
                  data-testid="settings-service-fee-separate-toggle"
                />
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Session 30.20 — Service alerts: long-wait threshold */}
            <div className="space-y-4" data-testid="settings-service-alerts">
              <div>
                <h3 className="text-lg font-semibold">Service alerts</h3>
                <p className="text-sm text-muted-foreground">How long an order may wait before staff, managers and the kitchen/bar are alerted.</p>
              </div>
              {/* Session 31.6 — the business-wide threshold input was removed:
                  each department carries its own limit (below). The legacy
                  `long_wait_minutes` value is kept server-side only as the
                  fallback for a department that has no limit yet. */}
              {/* Session 31.2 — per-department limits (drinks 10 min, food 30 min …). */}
              <div className="space-y-2" data-testid="settings-long-wait-departments">
                <Label>Long-wait limit per department (minutes)</Label>
                <p className="text-sm text-muted-foreground">Give each department its own limit — e.g. drinks should be out in 10 minutes, food in 30. After that many minutes a pending order turns red on the Staff dashboard and Owner overview (with a soft chime for staff) and its ticket is flagged on the Kitchen and Bar boards. 3–120 minutes.</p>
                {(departments || []).filter((d) => d.active !== false).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No departments yet — add them under Menu → Departments.</p>
                ) : (
                  <div className="divide-y divide-border rounded-2xl border border-border">
                    {(departments || []).filter((d) => d.active !== false).map((d) => {
                      const key = String(d.name || "").trim().toLowerCase();
                      const current = businessSettings?.long_wait_by_department ?? context?.business?.long_wait_by_department ?? {};
                      const fallbackMin = Number(businessSettings?.long_wait_minutes ?? context?.business?.long_wait_minutes) || 15;
                      const val = current[key] ?? fallbackMin;
                      const setVal = (v) => setBusinessSettings((prev) => {
                        const next = { ...((prev?.long_wait_by_department ?? context?.business?.long_wait_by_department) || {}) };
                        if (v === null) delete next[key]; else next[key] = v;
                        return { ...prev, long_wait_by_department: next };
                      });
                      return (
                        <div key={d.id || key} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid={`settings-long-wait-dept-${key.replace(/\s+/g, "-")}`}>
                          <span className="text-sm font-medium">{d.name}</span>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {[5, 10, 15, 20, 30, 45].map((m) => (
                              <button key={m} type="button" onClick={() => setVal(m)} className={`h-9 rounded-lg border px-2.5 text-xs font-medium tabular-nums ${Number(val) === m ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`} data-testid={`settings-long-wait-dept-${key.replace(/\s+/g, "-")}-${m}`}>{m}</button>
                            ))}
                            <Input type="number" min="3" max="120" step="1" value={val ?? ""} placeholder="min" onChange={(e) => { const v = parseInt(e.target.value, 10); setVal(Number.isNaN(v) ? fallbackMin : v); }} className="h-9 w-20 rounded-lg tabular-nums" data-testid={`settings-long-wait-dept-${key.replace(/\s+/g, "-")}-input`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="h-px bg-border" />
            {/* Session 27.107 — Global "hide when out of stock" default.
                Individual menu items can override via their own toggle. */}
            <div className="space-y-3" data-testid="settings-hide-oos-section">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">Sell items when out of stock (default)</h3>
                  <p className="text-sm text-muted-foreground">
                    Out-of-stock items are always shown on the customer, staff and retail menus with an "Out of stock" badge.
                    OFF (default): they cannot be ordered past what is on hand — stock can never go negative.
                    ON: keep selling past zero. Each item can override this with its own "Sell even when out of stock" switch.
                  </p>
                </div>
                <Switch
                  checked={businessSettings?.sell_out_of_stock_by_default === true}
                  onCheckedChange={(checked) => setBusinessSettings((prev) => ({ ...prev, sell_out_of_stock_by_default: Boolean(checked) }))}
                  data-testid="settings-sell-oos-switch"
                  aria-label="Sell items when out of stock by default"
                />
              </div>
            </div>
            </>) : null}
            {menuSettingsMode === "digital" ? (<>
            {/* Digital menu — section order toggle. Lets the owner pick
                whether customers see Items first or Sides & Extras
                first on the public menu. Persists via the existing
                businessSettings save endpoint. */}
            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-semibold">Digital menu — section order</h3>
                <p className="text-sm text-muted-foreground">Pick which section customers see first on the public menu page.</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "items_first", label: "Items first" },
                  { id: "sides_first", label: "Sides & Extras first" },
                ].map((opt) => {
                  const selected = (businessSettings?.digital_menu_section_order || "items_first") === opt.id;
                  return (
                    <Button
                      key={opt.id}
                      type="button"
                      variant="outline"
                      className={`h-11 rounded-xl ${selected ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/20 hover:text-[hsl(var(--accent))]" : ""}`}
                      onClick={() => setBusinessSettings((prev) => ({ ...prev, digital_menu_section_order: opt.id }))}
                      data-testid={`settings-digital-order-${opt.id}`}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* Staff confirmation gate for CUSTOMER-placed orders.
                Waiter-placed orders always skip this — the waiter is
                already vouching for the ticket. Persists via the
                existing businessSettings save endpoint. */}
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">Have staff confirm order before departments</h3>
                  <p className="text-sm text-muted-foreground">
                    When ON, orders placed by customers (QR-scan menu) wait for a waiter to review them
                    before they reach the Kitchen or Bar. Orders rung up by waiters on the Staff
                    dashboard are never held — they always go through instantly.
                  </p>
                </div>
                <Switch
                  checked={Boolean(businessSettings?.require_staff_confirmation)}
                  onCheckedChange={(checked) => setBusinessSettings((prev) => ({ ...prev, require_staff_confirmation: Boolean(checked) }))}
                  data-testid="settings-require-confirmation-switch"
                  aria-label="Require staff to confirm customer orders"
                />
              </div>
            </div>

            <div className="h-px bg-border" />

            {/* ── Online Payments + Customer Identity toggles ─────── */}
            <div className="space-y-3" data-testid="settings-menu-payments-section">
              <h3 className="text-lg font-semibold">Digital menu payments</h3>
              <p className="text-sm text-muted-foreground">
                Let customers pay online after placing their order. Requires digital payments to be configured under Settings &rarr; Payment Gateways.
              </p>
              <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/30 p-3" data-testid="settings-menu-ordering-row">
                <div>
                  <Label className="cursor-pointer">Customers can place orders</Label>
                  <p className="text-xs text-muted-foreground">
                    {(businessSettings?.menu_ordering_enabled ?? context?.business?.menu_ordering_enabled) === false
                      ? "Off — view-only menu. Customers can browse and build a cart, but cannot send an order."
                      : "On — customers can send orders from the digital menu."}
                  </p>
                </div>
                <Switch
                  checked={(businessSettings?.menu_ordering_enabled ?? context?.business?.menu_ordering_enabled) !== false}
                  onCheckedChange={(checked) => setBusinessSettings((prev) => ({ ...prev, menu_ordering_enabled: Boolean(checked) }))}
                  data-testid="settings-menu-ordering-switch"
                />
              </div>
              <div className="space-y-2 rounded-xl border border-border bg-secondary/30 p-3">
                <div className="flex items-center justify-between">
                  <Label className="cursor-pointer">Enable online payments on digital menu</Label>
                  <Switch
                    checked={Boolean(businessSettings?.menu_payments_enabled)}
                    onCheckedChange={(checked) => setBusinessSettings((prev) => ({ ...prev, menu_payments_enabled: Boolean(checked) }))}
                    data-testid="settings-menu-payments-switch"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="cursor-pointer">Allow "Pay by cash" option</Label>
                  <Switch
                    checked={businessSettings?.menu_cash_allowed !== false}
                    onCheckedChange={(checked) => setBusinessSettings((prev) => ({ ...prev, menu_cash_allowed: Boolean(checked) }))}
                    data-testid="settings-menu-cash-switch"
                  />
                </div>
              </div>

              <h3 className="text-lg font-semibold mt-4">Customer identity fields</h3>
              <p className="text-sm text-muted-foreground">
                Show optional name and/or phone fields on the digital menu so you know who placed each order.
              </p>
              <div className="space-y-2 rounded-xl border border-border bg-secondary/30 p-3">
                <div className="flex items-center justify-between">
                  <Label className="cursor-pointer">Require customer name</Label>
                  <Switch
                    checked={Boolean(businessSettings?.menu_require_name)}
                    onCheckedChange={(checked) => setBusinessSettings((prev) => ({ ...prev, menu_require_name: Boolean(checked) }))}
                    data-testid="settings-menu-require-name-switch"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="cursor-pointer">Require customer phone number</Label>
                  <Switch
                    checked={Boolean(businessSettings?.menu_require_phone)}
                    onCheckedChange={(checked) => setBusinessSettings((prev) => ({ ...prev, menu_require_phone: Boolean(checked) }))}
                    data-testid="settings-menu-require-phone-switch"
                  />
                </div>
              </div>
            </div>

            <div className="h-px bg-border" />
            {/* Session 27.107 — Menu availability schedule. When OFF the
                customer menu is always reachable. When ON, only reachable
                during the configured local-time window. Staff dashboards
                are never affected. */}
            <div className="space-y-3" data-testid="settings-menu-schedule-section">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">Menu availability schedule</h3>
                  <p className="text-sm text-muted-foreground">
                    Limit when the digital menu is available to customers. Outside this window they'll see a
                    friendly "we're closed" screen. Turn OFF for always-on availability. Staff can still take
                    orders from the Staff dashboard at any time.
                  </p>
                </div>
                <Switch
                  checked={Boolean(businessSettings?.menu_schedule_enabled)}
                  onCheckedChange={(checked) => setBusinessSettings((prev) => ({ ...prev, menu_schedule_enabled: Boolean(checked) }))}
                  data-testid="settings-menu-schedule-switch"
                  aria-label="Enable digital menu schedule"
                />
              </div>
              {Boolean(businessSettings?.menu_schedule_enabled) ? (
                <div className="space-y-3 rounded-2xl border border-border bg-secondary/30 p-4" data-testid="settings-menu-schedule-windows">
                  {(() => {
                    // Session 27.109 — Read/write a list of {start,end}
                    // windows so a business can run multiple sittings
                    // (e.g. lunch 11:00-14:00 + dinner 18:00-22:00).
                    // Falls back to a single window built from the
                    // legacy fields the first time this UI opens.
                    const windows = Array.isArray(businessSettings?.menu_schedule_windows) && businessSettings.menu_schedule_windows.length
                      ? businessSettings.menu_schedule_windows
                      : [{ start: businessSettings?.menu_schedule_start || "09:00", end: businessSettings?.menu_schedule_end || "22:00" }];
                    const setWindows = (nextWindows) => setBusinessSettings((prev) => ({ ...prev, menu_schedule_windows: nextWindows }));
                    return (
                      <>
                        <div className="space-y-2">
                          {windows.map((w, idx) => (
                            <div key={idx} className="grid gap-2 rounded-xl border border-border bg-background p-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end" data-testid={`settings-menu-schedule-row-${idx}`}>
                              <div className="space-y-1">
                                <Label htmlFor={`schedule-start-${idx}`} className="text-xs">Open at</Label>
                                <Input
                                  id={`schedule-start-${idx}`}
                                  type="time"
                                  value={w.start || "09:00"}
                                  onChange={(e) => {
                                    const next = windows.map((row, i) => i === idx ? { ...row, start: e.target.value || "09:00" } : row);
                                    setWindows(next);
                                  }}
                                  className="h-10 rounded-lg"
                                  data-testid={`settings-menu-schedule-start-${idx}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor={`schedule-end-${idx}`} className="text-xs">Close at</Label>
                                <Input
                                  id={`schedule-end-${idx}`}
                                  type="time"
                                  value={w.end || "22:00"}
                                  onChange={(e) => {
                                    const next = windows.map((row, i) => i === idx ? { ...row, end: e.target.value || "22:00" } : row);
                                    setWindows(next);
                                  }}
                                  className="h-10 rounded-lg"
                                  data-testid={`settings-menu-schedule-end-${idx}`}
                                />
                              </div>
                              <div className="flex justify-end sm:pb-0.5">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-10 rounded-lg text-destructive hover:bg-destructive/10"
                                  disabled={windows.length <= 1}
                                  onClick={() => setWindows(windows.filter((_, i) => i !== idx))}
                                  data-testid={`settings-menu-schedule-remove-${idx}`}
                                  aria-label={`Remove window ${idx + 1}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-10 rounded-lg"
                            onClick={() => setWindows([...windows, { start: "18:00", end: "22:00" }])}
                            disabled={windows.length >= 12}
                            data-testid="settings-menu-schedule-add"
                          >
                            <Plus className="h-4 w-4" />
                            Add schedule
                          </Button>
                          <p className="text-xs text-muted-foreground">
                            Tip: to cover late-night service, set the close time BEFORE the open time (e.g. 17:00 → 02:00).
                          </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : null}
            </div>

            <div className="h-px bg-border" />
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">Price Filter Ranges</h3>
                <p className="text-sm text-muted-foreground">Define custom price brackets for customer menu filtering</p>
              </div>
              <div className="space-y-3">
                {priceFilters.map((filter, index) => (
                  <div key={index} className="flex gap-3 items-end">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor={`filter-label-${index}`}>Label</Label>
                      <Input
                        id={`filter-label-${index}`}
                        value={filter.label}
                        onChange={(e) => {
                          const updated = [...priceFilters];
                          updated[index].label = e.target.value;
                          setPriceFilters(updated);
                        }}
                        className="h-11 rounded-xl"
                        placeholder="e.g., Budget"
                      />
                    </div>
                    <div className="w-28 space-y-2">
                      <Label htmlFor={`filter-min-${index}`}>Min</Label>
                      <Input
                        id={`filter-min-${index}`}
                        type="number"
                        value={filter.min}
                        onChange={(e) => {
                          const updated = [...priceFilters];
                          updated[index].min = parseFloat(e.target.value) || 0;
                          setPriceFilters(updated);
                        }}
                        className="h-11 rounded-xl"
                      />
                    </div>
                    <div className="w-28 space-y-2">
                      <Label htmlFor={`filter-max-${index}`}>Max</Label>
                      <Input
                        id={`filter-max-${index}`}
                        type="number"
                        value={filter.max}
                        onChange={(e) => {
                          const updated = [...priceFilters];
                          updated[index].max = parseFloat(e.target.value) || 9999;
                          setPriceFilters(updated);
                        }}
                        className="h-11 rounded-xl"
                      />
                    </div>
                    {priceFilters.length > 1 && (
                      <Button
                        variant="outline"
                        size="icon"
                        className="rounded-xl h-11 w-11"
                        onClick={() => setPriceFilters(priceFilters.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  className="w-full rounded-xl"
                  onClick={() => setPriceFilters([...priceFilters, { label: "New Range", min: 0, max: 100 }])}
                >
                  <Plus className="h-4 w-4" />
                  Add Filter Range
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                💡 Tip: Set max to 9999 for "over" ranges
              </p>
            </div>
            </>) : null}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setMenuSettingsOpen(false)}
              className="rounded-xl"
              data-testid="settings-cancel-button"
            >
              Cancel
            </Button>
            <Button
              className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              disabled={saving}
              data-testid="settings-save-button"
              onClick={async () => {
                setSaving(true);
                try {
                  const resp = await http.patch(
                    "/owner/business-settings",
                    {
                      vat_rate: businessSettings?.vat_rate ?? context?.business?.vat_rate ?? 15,
                      long_wait_minutes: Math.min(120, Math.max(3, parseInt(businessSettings?.long_wait_minutes ?? context?.business?.long_wait_minutes ?? 15, 10) || 15)),
                      // Session 31.2 — per-department overrides (always sent so clearing works).
                      // Session 31.6 — every active department gets an explicit limit
                      // (unset ones take the legacy business-wide value) so all screens agree.
                      long_wait_by_department: (() => {
                        const fallback = Math.min(120, Math.max(3, parseInt(businessSettings?.long_wait_minutes ?? context?.business?.long_wait_minutes ?? 15, 10) || 15));
                        const explicit = Object.fromEntries(Object.entries(businessSettings?.long_wait_by_department ?? context?.business?.long_wait_by_department ?? {}).filter(([, v]) => Number(v) >= 3 && Number(v) <= 120).map(([k, v]) => [k, Number(v)]));
                        for (const d of (departments || [])) {
                          if (d && d.active !== false) {
                            const key = String(d.name || "").trim().toLowerCase();
                            if (key && explicit[key] === undefined) explicit[key] = fallback;
                          }
                        }
                        return explicit;
                      })(),
                      alert_email: businessSettings?.alert_email || "",
                      digital_menu_section_order: businessSettings?.digital_menu_section_order || "items_first",
                      require_staff_confirmation: Boolean(businessSettings?.require_staff_confirmation),
                      // Session 27.107 — Menu availability schedule + hide-OOS default.
                      menu_schedule_enabled: Boolean(businessSettings?.menu_schedule_enabled),
                      menu_schedule_start: businessSettings?.menu_schedule_start || "09:00",
                      menu_schedule_end: businessSettings?.menu_schedule_end || "22:00",
                      // Session 27.109 — Multi-window list; falls back to a
                      // single window built from the legacy start/end when
                      // the user hasn't touched the list yet.
                      menu_schedule_windows: (Array.isArray(businessSettings?.menu_schedule_windows) && businessSettings.menu_schedule_windows.length
                        ? businessSettings.menu_schedule_windows
                        : [{
                            start: businessSettings?.menu_schedule_start || "09:00",
                            end: businessSettings?.menu_schedule_end || "22:00",
                          }]),
                      sell_out_of_stock_by_default: businessSettings?.sell_out_of_stock_by_default === true,
                      // ── Menu payments + customer identity toggles.
                      menu_payments_enabled: Boolean(businessSettings?.menu_payments_enabled),
                      menu_cash_allowed: businessSettings?.menu_cash_allowed !== false,
                      menu_ordering_enabled: (businessSettings?.menu_ordering_enabled ?? context?.business?.menu_ordering_enabled) !== false,
                      menu_require_name: Boolean(businessSettings?.menu_require_name),
                      menu_require_phone: Boolean(businessSettings?.menu_require_phone),
                      // Session 34 / 47 — automatic service fee. Only the
                      // fields the owner touched are sent, so a stale copy of
                      // the settings can never switch the fee off by accident.
                      ...(feeTouchedRef.current.has("service_fee_enabled")
                        ? { service_fee_enabled: Boolean(businessSettings?.service_fee_enabled) }
                        : {}),
                      ...(feeTouchedRef.current.has("service_fee_pct")
                        ? { service_fee_pct: Math.min(50, Math.max(0, parseFloat(businessSettings?.service_fee_pct ?? context?.business?.service_fee_pct ?? 10) || 0)) }
                        : {}),
                      ...(feeTouchedRef.current.has("service_fee_separate")
                        ? { service_fee_separate: Boolean(businessSettings?.service_fee_separate) }
                        : {}),
                    },
                    { headers: authHeaders },
                  );
                  toast.success("Menu settings saved");
                  // Session 30 — Close the dialog IMMEDIATELY and apply
                  // the authoritative settings straight from the PATCH
                  // response (same pattern as saveCoverImage). The old
                  // `await refreshDashboard()` refetched 13 endpoints and
                  // re-rendered the whole dashboard several times, which
                  // held the dialog on screen for many seconds after the
                  // "Saved" toast.
                  feeTouchedRef.current = new Set();
                  if (resp?.data?.business) setBusinessSettings(resp.data.business);
                  setMenuSettingsOpen(false);
                } catch (error) {
                  // Session 30 — A 401 here means the 12-hour session
                  // lapsed (surfaced as a confusing raw "Invalid
                  // session" toast). Route cleanly back to login.
                  if (error?.response?.status === 401) {
                    toast.error("Your session expired — please sign in again.");
                    handleUnauthorized();
                  } else {
                    toast.error(error.response?.data?.detail || "Failed to save settings");
                  }
                } finally {
                  setSaving(false);
                }
              }}
            >
              <Check className="h-4 w-4" />
              {saving ? "Saving..." : "Save settings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tips drill-down dialog — clicked from Tips tab. Shows the
          underlying orders that built up the waiter's pending tips, with
          per-tip method (cash / card / split) and a clickable row that
          opens the full receipt. Max 3 levels: Tips → Orders → Receipt. */}
      <Dialog open={tipDetail.open} onOpenChange={(open) => setTipDetail((d) => ({ ...d, open }))}>
        <DialogContent className="flex max-h-[90vh] flex-col rounded-[24px] sm:max-w-2xl" data-testid="owner-tip-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">
              Tip details · {tipDetail.recipient || "Unassigned"}
            </DialogTitle>
            <DialogDescription>
              {tipDetail.data
                ? `${tipDetail.data.summary.count} ${tipDetail.data.summary.count === 1 ? "order" : "orders"} with tips · click any order to open the full receipt.`
                : "Loading tip orders…"}
            </DialogDescription>
          </DialogHeader>
          <div className="-mx-1 flex-1 space-y-4 overflow-y-auto px-1">
            {tipDetail.loading ? (
              <Skeleton className="h-32 w-full rounded-2xl" />
            ) : tipDetail.data ? (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3" data-testid="tip-detail-cash">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Cash tips</p>
                    <p className="mt-1 text-xl font-semibold text-emerald-700 dark:text-emerald-300">{formatPrice(tipDetail.data.summary.cash_tips)}</p>
                  </div>
                  <div className="rounded-2xl border border-sky-500/30 bg-sky-500/10 p-3" data-testid="tip-detail-card">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">Card tips</p>
                    <p className="mt-1 text-xl font-semibold text-sky-700 dark:text-sky-300">{formatPrice(tipDetail.data.summary.card_tips)}</p>
                  </div>
                  <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3" data-testid="tip-detail-total">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Total tips</p>
                    <p className="mt-1 text-xl font-semibold text-amber-700 dark:text-amber-300">{formatPrice(tipDetail.data.summary.total_tips)}</p>
                  </div>
                </div>
                {tipDetail.data.summary.split_tips > 0 ? (
                  <p className="text-xs text-muted-foreground">Split-method tips: {formatPrice(tipDetail.data.summary.split_tips)} (included in totals).</p>
                ) : null}
                {Number(tipDetail.data.summary.service_fee_total) > 0 ? (
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-secondary/40 px-3 py-2 text-xs" data-testid="owner-tip-gratuity-breakdown">
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground"><HandCoins className="h-3.5 w-3.5" /> Service fee <span className="font-semibold text-foreground">{formatPrice(tipDetail.data.summary.service_fee_total)}</span></span>
                    <span className="text-muted-foreground">+ Extra tips <span className="font-semibold text-foreground">{formatPrice(tipDetail.data.summary.extra_tip_total ?? 0)}</span></span>
                    <span className="text-muted-foreground">= Total <span className="font-semibold text-foreground">{formatPrice(tipDetail.data.summary.total_tips)}</span></span>
                  </div>
                ) : null}
                {groupTipRowsBySplitTable(tipDetail.data.orders).tables.map((t) => (
                  <div key={t.key} className="cursor-pointer" onClick={() => openOrderReceipt(t.rows[0])} data-testid={`owner-tip-split-table-${t.key}`}>
                    <SplitGuestBreakdown
                      breakdown={t.breakdown}
                      title={`Table ${t.breakdown.table_number || "—"} · split bill · tips to ${tipDetail.recipient || "unassigned"} ${formatPrice(t.myTip)}`}
                      myTipBySplit={t.myTipBySplit}
                      testId={`owner-tip-split-${t.key}`}
                    />
                  </div>
                ))}
                {groupTipRowsBySplitTable(tipDetail.data.orders).plain.length ? (
                  <div className="rounded-2xl border border-border bg-card">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Order</TableHead>
                          <TableHead>Table</TableHead>
                          <TableHead>Date / Time</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Tip</TableHead>
                          <TableHead>Method</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groupTipRowsBySplitTable(tipDetail.data.orders).plain.map((o) => {
                          const m = (o.payment_method || "").toLowerCase();
                          const tone = m === "cash"
                            ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                            : m === "card"
                              ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30"
                              : m === "split"
                                ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30"
                                : "bg-secondary text-muted-foreground border-border";
                          return (
                            <TableRow
                              key={o.id}
                              className="cursor-pointer transition-colors hover:bg-secondary/50"
                              onClick={() => openOrderReceipt(o)}
                              data-testid={`tip-order-row-${o.id}`}
                            >
                              <TableCell className="font-medium">{o.order_number}</TableCell>
                              <TableCell>{o.table_number || "—"}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                              <TableCell className="text-right">{formatPrice(o.total ?? (Number(o.subtotal || 0) + Number(o.vat_total || 0)))}</TableCell>
                              <TableCell className="text-right font-semibold">
                                {formatPrice(o.tip_amount || 0)}
                                {Number(o.service_fee_amount) > 0 ? (
                                  <p className="text-[10px] font-normal text-muted-foreground">fee {formatPrice(o.service_fee_amount)}{Number(o.extra_tip_amount) > 0 ? ` + tip ${formatPrice(o.extra_tip_amount)}` : ""}</p>
                                ) : null}
                              </TableCell>
                              <TableCell><Badge className={`capitalize ${tone}`}>{m || "—"}</Badge></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : !groupTipRowsBySplitTable(tipDetail.data.orders).tables.length ? (
                  <EmptyState icon={Receipt} title="No pending tips" description="No tips for this waiter in the current window." />
                ) : null}
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setTipDetail({ open: false, recipient: "", loading: false, data: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reports drill-down — clicked from Analytics → Reports → Daily
          totals row. Shows that day's orders; each row opens the full
          receipt for the maximum 3-level Summary → Orders → Receipt path. */}
      <Dialog open={dailyDetail.open} onOpenChange={(open) => setDailyDetail((d) => ({ ...d, open }))}>
        <DialogContent className="flex max-h-[90vh] flex-col rounded-[24px] sm:max-w-2xl" data-testid="owner-daily-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">
              Orders · {dailyDetail.date}
            </DialogTitle>
            <DialogDescription>
              {dailyDetail.loading
                ? "Loading orders…"
                : `${dailyDetail.orders.length} ${dailyDetail.orders.length === 1 ? "order" : "orders"} · click any row to open the full receipt.`}
            </DialogDescription>
          </DialogHeader>
          <div className="-mx-1 flex-1 overflow-y-auto px-1">
            {dailyDetail.loading ? (
              <Skeleton className="h-32 w-full rounded-2xl" />
            ) : dailyDetail.orders.length ? (
              <div className="rounded-2xl border border-border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Table</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyDetail.orders.map((o) => {
                      const m = (o.payment_method || "").toLowerCase();
                      const tone = m === "cash"
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                        : m === "card"
                          ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30"
                          : m === "split"
                            ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30"
                            : "bg-secondary text-muted-foreground border-border";
                      return (
                        <TableRow
                          key={o.id}
                          className="cursor-pointer transition-colors hover:bg-secondary/50"
                          onClick={() => openOrderReceipt(o)}
                          data-testid={`daily-order-row-${o.id}`}
                        >
                          <TableCell className="font-medium">{o.order_number}</TableCell>
                          <TableCell>{o.table_number || "—"}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                          <TableCell><Badge className={`capitalize ${tone}`}>{m || "—"}</Badge></TableCell>
                          <TableCell className="text-right font-semibold">{formatPrice(o.total ?? (Number(o.subtotal || 0) + Number(o.vat_total || 0)))}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <EmptyState icon={BarChart3} title="No orders" description="Nothing was recorded on this date." />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDailyDetail({ open: false, date: "", loading: false, orders: [] })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Stock audit dialogs ─────────────────────────────────────── */}
      {/* Single-entry "View details" dialog for a stock adjustment row. */}
      <Dialog open={stockEntryDetail.open} onOpenChange={(open) => setStockEntryDetail((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="stock-entry-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Stock change · details</DialogTitle>
            <DialogDescription>
              {stockEntryDetail.row?.item_name || ""}
            </DialogDescription>
          </DialogHeader>
          {stockEntryDetail.row ? (() => {
            const r = stockEntryDetail.row;
            const diff = Number(r.difference || 0);
            const tone = diff > 0 ? "text-emerald-600 dark:text-emerald-400" : diff < 0 ? "text-destructive" : "text-muted-foreground";
            return (
              <div className="space-y-3 text-sm">
                <div className="rounded-2xl border border-border bg-secondary/40 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Item</p>
                  <p className="mt-1 font-semibold">{r.item_name} <Badge variant="outline" className="ml-1 rounded-full text-[10px]" data-testid="stock-entry-location">{r.reason === "transfer" ? `${r.from_location_name || "?"} → ${r.to_location_name || "?"}` : r.location_name ? r.location_name : r.item_type === "ingredient_boh" ? "Back of house" : r.item_type === "ingredient" ? "Floor" : "Item"}</Badge></p>
                  {r.consumption_type && r.consumption_type !== "normal" ? (
                    <p className="mt-2 text-xs text-muted-foreground" data-testid="stock-entry-consumption">
                      {r.consumption_type === "fallback" ? <><b className="text-amber-800 dark:text-amber-300">Fallback consumption</b> — {r.preferred_location_name || "the preferred location"} could not cover this order, so the rest was consumed from {r.location_name || "another location"} automatically. Ordinary usage; nothing was physically moved.</> : null}
                      {r.consumption_type === "shortfall" ? <><b className="text-destructive">Shortfall</b> — no location had stock. The sale went through and {r.location_name || "the preferred location"} went negative by this amount. Not genuine usage until a stock take confirms it.</> : null}
                      {r.consumption_type === "restore" ? <><b className="text-emerald-700 dark:text-emerald-400">Restored</b> — a void returned this quantity to the location it was consumed from.</> : null}
                    </p>
                  ) : null}
                  {r.order_number ? <p className="mt-1 text-xs text-muted-foreground">Order {r.order_number}</p> : null}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-2xl border border-border bg-background p-3">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Previous</p>
                    <p className="mt-1 font-semibold">{Number(r.previous).toLocaleString()}{r.unit || ""}</p>
                  </div>
                  <div className="rounded-2xl border border-border bg-background p-3">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">New</p>
                    <p className="mt-1 font-semibold">{Number(r.new_value).toLocaleString()}{r.unit || ""}</p>
                  </div>
                  <div className="rounded-2xl border border-border bg-background p-3">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Difference</p>
                    <p className={`mt-1 font-semibold ${tone}`}>{diff > 0 ? "+" : ""}{diff.toLocaleString()}{r.unit || ""}</p>
                  </div>
                  <div className="rounded-2xl border border-border bg-background p-3">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Reason</p>
                    <p className="mt-1 font-medium capitalize">{String(r.reason || "other").replace(/_/g, " ")}</p>
                  </div>
                  <div className="rounded-2xl border border-border bg-background p-3">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">User</p>
                    <p className="mt-1 font-medium">{r.user || "—"}</p>
                  </div>
                  <div className="rounded-2xl border border-border bg-background p-3">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Date / Time</p>
                    <p className="mt-1 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</p>
                  </div>
                </div>
                {r.notes ? (
                  <div className="rounded-2xl border border-border bg-background p-3">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Notes</p>
                    <p className="mt-1 text-sm">{r.notes}</p>
                  </div>
                ) : null}
              </div>
            );
          })() : null}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => { setStockEntryDetail({ open: false, row: null }); if (stockEntryDetail.row) openItemHistory(stockEntryDetail.row.item_id, stockEntryDetail.row.item_name); }}>View full item history</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" onClick={() => setStockEntryDetail({ open: false, row: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit log entry detail */}
      <Dialog open={auditDetail.open} onOpenChange={(open) => setAuditDetail((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="audit-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">{auditDetail.row?.action_label || "Audit entry"}</DialogTitle>
            <DialogDescription>{auditDetail.row?.created_at ? new Date(auditDetail.row.created_at).toLocaleString() : ""}</DialogDescription>
          </DialogHeader>
          {auditDetail.row ? (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-2xl border border-border bg-background p-3">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">User</p>
                  <p className="mt-1 font-medium">{auditDetail.row.user || "—"}</p>
                </div>
                <div className="rounded-2xl border border-border bg-background p-3">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Target</p>
                  <p className="mt-1 font-medium">{auditDetail.row.target_name || auditDetail.row.target_type || "—"}</p>
                </div>
              </div>
              {Array.isArray(auditDetail.row.details?.voids) && auditDetail.row.details.voids.length ? (
                <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-3" data-testid="audit-detail-voids">
                  <p className="text-[10px] uppercase tracking-wide text-destructive">Voids erased in this clear ({auditDetail.row.details.voids.length}{auditDetail.row.details.cleared_value ? ` · ${formatPrice(auditDetail.row.details.cleared_value)}` : ""})</p>
                  <div className="mt-2 max-h-72 space-y-2 overflow-y-auto pr-1 text-xs">
                    {auditDetail.row.details.voids.map((v, i) => (
                      <div key={`${v.order_number || "o"}-${i}`} className="rounded-xl border border-border bg-background p-2">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold">{v.order_number || "Order"}{v.table ? ` · Table ${v.table}` : ""}</p>
                          <p className="shrink-0 font-semibold">{formatPrice(Number(v.value) || 0)}</p>
                        </div>
                        <p className="mt-0.5 text-muted-foreground">Voided {v.voided_at || "—"} by <span className="font-medium text-foreground">{v.voided_by || "unknown"}</span>{v.waiter ? ` · waiter ${v.waiter}` : ""}</p>
                        {v.reason ? <p className="mt-0.5 text-muted-foreground">Reason: {v.reason}</p> : null}
                        {v.items ? <p className="mt-0.5 break-words">{v.items}</p> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {auditDetail.row.details && Object.keys(auditDetail.row.details).length > 0 ? (
                <div className="rounded-2xl border border-border bg-background p-3">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Details</p>
                  <div className="mt-2 space-y-1.5 text-xs">
                    {Object.entries(auditDetail.row.details)
                      .filter(([k]) => k !== "voids" && k !== "order_numbers")
                      .filter(([, v]) => v !== null && v !== undefined && v !== "")
                      .map(([k, v]) => {
                        const label = k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                        let display;
                        if (typeof v === "boolean") {
                          display = v ? "Yes" : "No";
                        } else if (typeof v === "number" && /amount|total|price|cost|tip|refund|float|payout|paid|change|value/i.test(k)) {
                          display = formatPrice(v);
                        } else if (Array.isArray(v)) {
                          display = v.map((x) => (typeof x === "object" ? JSON.stringify(x) : String(x))).join(", ");
                        } else if (typeof v === "object") {
                          display = JSON.stringify(v);
                        } else {
                          display = String(v);
                        }
                        const isLong = typeof display === "string" && display.length > 40;
                        return (
                          <div key={k} className={isLong ? "space-y-0.5" : "flex justify-between gap-2"}>
                            <span className="text-muted-foreground">{label}</span>
                            <span className={`font-medium ${isLong ? "block whitespace-pre-wrap break-words" : "text-right"}`}>{display}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setAuditDetail({ open: false, row: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Accounts — add/edit user dialog */}
      <Dialog open={userDialog.open} onOpenChange={(open) => setUserDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="user-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">{userDialog.mode === "edit" ? "Edit user" : "Add user"}</DialogTitle>
            <DialogDescription>
              {userDialog.mode === "edit" ? "Update name, username or role. Use Reset PIN to change the sign-in PIN." : "Create a sign-in account. Roles control which actions they can perform."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="user-first">First name</Label>
                <Input id="user-first" className="h-11 rounded-xl" value={userDialog.values.first_name} onChange={(e) => setUserDialog((d) => ({ ...d, values: { ...d.values, first_name: e.target.value } }))} data-testid="user-first" />
              </div>
              <div>
                <Label htmlFor="user-last">Last name</Label>
                <Input id="user-last" className="h-11 rounded-xl" value={userDialog.values.last_name} onChange={(e) => setUserDialog((d) => ({ ...d, values: { ...d.values, last_name: e.target.value } }))} data-testid="user-last" />
              </div>
            </div>
            <div>
              <Label htmlFor="user-username">Username</Label>
              <Input id="user-username" className="h-11 rounded-xl" value={userDialog.values.username} onChange={(e) => setUserDialog((d) => ({ ...d, values: { ...d.values, username: e.target.value } }))} placeholder="sarah" data-testid="user-username" />
            </div>
            {userDialog.mode === "create" ? (
              <div>
                <Label htmlFor="user-pin">PIN (4–10 chars)</Label>
                <Input id="user-pin" type="password" inputMode="numeric" maxLength={10} className="h-11 rounded-xl tracking-widest" value={userDialog.values.pin} onChange={(e) => setUserDialog((d) => ({ ...d, values: { ...d.values, pin: e.target.value } }))} placeholder="••••" data-testid="user-pin" />
              </div>
            ) : null}
            <div>
              <Label>Role</Label>
              <Select value={userDialog.values.role} onValueChange={(v) => setUserDialog((d) => ({ ...d, values: { ...d.values, role: v } }))}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["owner", "manager", "shift_leader", "supervisor", "waiter", "runner", "retailer"].map((r) => (
                    <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {userDialog.values.role === "owner" ? "Every permission (void, refund, cash-up…). Sections shown on the dashboard can be customised below." :
                 userDialog.values.role === "manager" ? "Full access to all dashboard sections (customisable)." :
                 userDialog.values.role === "shift_leader" ? "Overview, Operations, Staff & Access, Reports and Inventory (customisable)." :
                 userDialog.values.role === "supervisor" ? "Overview and Operations only — POS workflows (customisable)." :
                 userDialog.values.role === "waiter" ? "Waiter dashboard only — no owner access." :
                 userDialog.values.role === "runner" ? "Runner dashboard only — sees ready-to-collect tickets from Kitchen and Bar." :
                 userDialog.values.role === "retailer" ? "Retail POS dashboard only — rings up counter sales; actions appear in the audit log." : ""}
              </p>
            </div>
            {/* Per-user section permissions — defaults to the role's
                preset, but the owner can hand-pick which sections this
                specific user can see. Waiters never see owner sections.
                The Owner role is implicitly all-access (not editable). */}
            {["owner","manager","shift_leader","supervisor"].includes(userDialog.values.role) ? (() => {
              const ROLE_DEFAULT_SECTIONS = {
                owner: ["overview","operations","staff_access","menu","finance","reports","inventory","settings","ai_assistant"],
                manager: ["overview","operations","staff_access","menu","finance","reports","inventory","settings"],
                shift_leader: ["overview","operations","staff_access","reports","inventory"],
                supervisor: ["overview","operations"],
              };
              const ALL_SECTIONS = [
                { v: "overview", l: "Overview" },
                { v: "operations", l: "Operations" },
                { v: "staff_access", l: "Staff & Access" },
                { v: "menu", l: "Menu" },
                { v: "finance", l: "Finance" },
                { v: "reports", l: "Reports" },
                { v: "inventory", l: "Inventory" },
                { v: "settings", l: "Settings" },
                // Session 31 — lets a manager use the voice / text AI assistant.
                { v: "ai_assistant", l: "AI Assistant" },
              ];
              const current = Array.isArray(userDialog.values.allowed_sections) && userDialog.values.allowed_sections.length
                ? userDialog.values.allowed_sections
                : (ROLE_DEFAULT_SECTIONS[userDialog.values.role] || []);
              const isCustom = Array.isArray(userDialog.values.allowed_sections);
              return (
                <div className="rounded-2xl border border-border bg-secondary/40 p-3" data-testid="user-section-permissions">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Sections this user can see</p>
                    {isCustom ? (
                      <Button type="button" size="sm" variant="ghost" className="h-6 rounded-md px-2 text-[10px]" onClick={() => setUserDialog((d) => ({ ...d, values: { ...d.values, allowed_sections: null } }))} data-testid="user-sections-reset">Use role defaults</Button>
                    ) : null}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                    {ALL_SECTIONS.map((s) => {
                      const checked = current.includes(s.v);
                      return (
                        <label key={s.v} className="flex items-center gap-2 rounded-lg border border-border bg-background px-2 py-1.5 text-xs cursor-pointer">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(c) => setUserDialog((d) => {
                              const base = Array.isArray(d.values.allowed_sections) ? d.values.allowed_sections : (ROLE_DEFAULT_SECTIONS[d.values.role] || []);
                              const next = new Set(base);
                              if (c) next.add(s.v); else next.delete(s.v);
                              return { ...d, values: { ...d.values, allowed_sections: Array.from(next) } };
                            })}
                            data-testid={`user-section-${s.v}`}
                          />
                          <span>{s.l}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-[10px] text-muted-foreground">
                    {isCustom ? "Custom selection — overrides the role defaults." : "Showing role defaults. Tick / untick boxes to customise for this user."}
                  </p>
                </div>
              );
            })() : null}
            {userDialog.values.role === "owner" ? (
              <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3" data-testid="user-section-permissions-owner">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Owner role</p>
                <p className="mt-1 text-[11px] text-amber-800 dark:text-amber-200">Owner accounts keep every action permission. Un-ticking sections above only hides those dashboard areas for this person. The business-password login always has full access, and you cannot restrict your own account.</p>
              </div>
            ) : null}
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Active</p>
                <p className="text-xs text-muted-foreground">Disabled users cannot sign in.</p>
              </div>
              <Switch checked={!!userDialog.values.active} onCheckedChange={(v) => setUserDialog((d) => ({ ...d, values: { ...d.values, active: v } }))} data-testid="user-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setUserDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={userDialog.saving} onClick={submitUserDialog} data-testid="user-account-submit">
              {userDialog.saving ? "Saving…" : (userDialog.mode === "edit" ? "Save changes" : "Create user")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Accounts — reset PIN */}
      <Dialog open={userPinDialog.open} onOpenChange={(open) => setUserPinDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-sm" data-testid="user-pin-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Reset PIN</DialogTitle>
            <DialogDescription>{userPinDialog.user ? `Set a new PIN for ${userPinDialog.user.full_name || userPinDialog.user.first_name}.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="user-pin-new">New PIN (4–10 chars)</Label>
            <Input id="user-pin-new" type="password" inputMode="numeric" maxLength={10} className="h-11 rounded-xl tracking-widest" value={userPinDialog.pin} onChange={(e) => setUserPinDialog((d) => ({ ...d, pin: e.target.value }))} placeholder="••••" data-testid="user-pin-new" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setUserPinDialog({ open: false, user: null, pin: "", saving: false })}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" disabled={userPinDialog.saving} onClick={submitResetPin} data-testid="user-pin-submit">
              {userPinDialog.saving ? "Saving…" : "Save PIN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Accounts — hard-delete confirmation. Void Manager Password
          required; supports all roles. Self-delete and last-owner-delete
          are blocked server-side. */}
      <Dialog open={deleteUserDialog.open} onOpenChange={(open) => setDeleteUserDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="user-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl text-destructive">Delete user</DialogTitle>
            <DialogDescription>
              {deleteUserDialog.user ? (
                <>This permanently removes <strong>{deleteUserDialog.user.full_name || deleteUserDialog.user.first_name}</strong> ({String(deleteUserDialog.user.role || "").replace(/_/g, " ")}). Disable them instead if you only want to revoke access temporarily.</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
              This action cannot be undone. Past audit entries and orders for this user are preserved.
            </div>
            <div>
              <Label htmlFor="user-delete-pin">Void Manager Password</Label>
              <Input
                id="user-delete-pin"
                type="password"
                inputMode="text"
                maxLength={72}
                className="h-11 rounded-xl tracking-widest"
                value={deleteUserDialog.pin}
                onChange={(e) => setDeleteUserDialog((d) => ({ ...d, pin: e.target.value }))}
                placeholder="••••"
                data-testid="user-delete-pin"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDeleteUserDialog({ open: false, user: null, pin: "", saving: false })}>Cancel</Button>
            <Button variant="destructive" className="rounded-xl" disabled={deleteUserDialog.saving || !deleteUserDialog.pin} onClick={submitDeleteUser} data-testid="user-delete-confirm">
              <Trash2 className="h-4 w-4" />
              {deleteUserDialog.saving ? "Deleting…" : "Delete user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Accounts — view activity */}
      <Dialog open={userActivityDialog.open} onOpenChange={(open) => setUserActivityDialog((d) => ({ ...d, open }))}>
        <DialogContent className="flex max-h-[85vh] flex-col rounded-[24px] sm:max-w-lg" data-testid="user-activity-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">{userActivityDialog.user ? `${userActivityDialog.user.full_name || userActivityDialog.user.first_name}` : "Activity"}</DialogTitle>
            <DialogDescription>{userActivityDialog.user?.role ? `Recent actions for ${userActivityDialog.user.role}.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="-mx-1 flex-1 overflow-y-auto px-1">
            {userActivityDialog.loading ? (
              <Skeleton className="h-32 w-full rounded-2xl" />
            ) : userActivityDialog.entries.length ? (
              <ul className="space-y-1.5 text-sm">
                {userActivityDialog.entries.map((row) => (
                  <li key={row.id} className="rounded-xl border border-border bg-background px-3 py-2" data-testid={`user-activity-row-${row.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{row.action_label || row.action}</span>
                      <span className="text-xs text-muted-foreground">{row.created_at ? new Date(row.created_at).toLocaleString() : ""}</span>
                    </div>
                    {row.target_name ? <p className="text-xs text-muted-foreground">{row.target_name}</p> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState icon={Receipt} title="No activity yet" description="Refunds, voids, cash-ups and stock actions by this user will appear here." />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setUserActivityDialog({ open: false, user: null, entries: [], loading: false })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Owner — Correct Payment dialog. Mirrors the Staff Dashboard
          version for visual consistency: 3-button method selector with
          brand colors (cash=emerald, card=sky, split=violet), amount
          fields for ALL methods, reason + manager PIN. */}
      <Dialog open={paymentCorrection.open} onOpenChange={(open) => setPaymentCorrection((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="payment-correction-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Correct Payment</DialogTitle>
            <DialogDescription>{paymentCorrection.order ? `Order ${paymentCorrection.order.order_number} · Void Manager Password required. Edit method and / or amount.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payment method</Label>
              <div className="grid grid-cols-3 gap-2 pt-1">
                {[
                  { id: "cash", on: "border-emerald-500 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 hover:text-emerald-700 dark:text-emerald-300 dark:hover:text-emerald-300" },
                  { id: "card", on: "border-sky-500 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 hover:text-sky-700 dark:text-sky-300 dark:hover:text-sky-300" },
                  { id: "split", on: "border-violet-500 bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 hover:text-violet-700 dark:text-violet-300 dark:hover:text-violet-300" },
                ].map(({ id, on }) => {
                  const selected = paymentCorrection.values.new_method === id;
                  return (
                    <Button
                      key={id}
                      type="button"
                      variant="outline"
                      className={`h-11 rounded-xl capitalize ${selected ? on : ""}`}
                      onClick={() => setPaymentCorrection((d) => ({ ...d, values: { ...d.values, new_method: id } }))}
                      data-testid={`payment-correction-method-${id}`}
                    >
                      {id}
                    </Button>
                  );
                })}
              </div>
            </div>
            {/* Amount editing — exposed for ALL methods so managers can
                fix the recorded amount (e.g. discount given off-system,
                tip added on the card terminal, miskey at processing). */}
            {(paymentCorrection.values.new_method === "cash" || paymentCorrection.values.new_method === "split") ? (
              <div>
                <Label htmlFor="pc-cash" className="text-emerald-700 dark:text-emerald-300">Cash amount (R)</Label>
                <Input id="pc-cash" type="number" inputMode="decimal" min="0" step="0.01" value={paymentCorrection.values.cash_paid} onChange={(e) => setPaymentCorrection((d) => ({ ...d, values: { ...d.values, cash_paid: e.target.value } }))} className="h-11 rounded-xl" data-testid="payment-correction-cash" />
              </div>
            ) : null}
            {(paymentCorrection.values.new_method === "card" || paymentCorrection.values.new_method === "split") ? (
              <div>
                <Label htmlFor="pc-card" className="text-sky-700 dark:text-sky-300">Card amount (R)</Label>
                <Input id="pc-card" type="number" inputMode="decimal" min="0" step="0.01" value={paymentCorrection.values.card_paid} onChange={(e) => setPaymentCorrection((d) => ({ ...d, values: { ...d.values, card_paid: e.target.value } }))} className="h-11 rounded-xl" data-testid="payment-correction-card" />
              </div>
            ) : null}
            <div>
              <Label htmlFor="pc-reason">Reason <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
              <Input id="pc-reason" type="text" maxLength={240} placeholder="e.g. Cash entered as card by mistake" value={paymentCorrection.values.reason} onChange={(e) => setPaymentCorrection((d) => ({ ...d, values: { ...d.values, reason: e.target.value } }))} className="h-11 rounded-xl" data-testid="payment-correction-reason" />
            </div>
            <div>
              <Label htmlFor="pc-pin">Void Manager Password</Label>
              <Input id="pc-pin" type="password" inputMode="text" maxLength={72} value={paymentCorrection.values.manager_pin} onChange={(e) => setPaymentCorrection((d) => ({ ...d, values: { ...d.values, manager_pin: e.target.value } }))} className="h-11 rounded-xl tracking-widest" data-testid="payment-correction-pin" />
            </div>
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="h-11 rounded-xl" onClick={() => setPaymentCorrection((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="h-11 rounded-xl bg-sky-600 text-white hover:bg-sky-600/90" disabled={paymentCorrection.saving || !paymentCorrection.values.manager_pin} onClick={submitPaymentCorrection} data-testid="payment-correction-submit">
              {paymentCorrection.saving ? "Saving…" : "Apply correction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Table dialog — manager-PIN-gated, audited. Flips the
          linked table session back to "open" so staff can add corrective
          orders or fix an accidental close. */}
      <Dialog open={reopenTable.open} onOpenChange={(open) => setReopenTable((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="reopen-table-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Reopen Table</DialogTitle>
            <DialogDescription>{reopenTable.order ? `Order ${reopenTable.order.order_number} · Manager PIN required.` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300">
              Reopening this table moves it back to "open" status so corrective orders can be added or an accidental close can be undone. The original receipt stays in history; this action is fully audited.
            </div>
            <div>
              <Label htmlFor="rt-reason">Reason <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="rt-reason" type="text" maxLength={240} placeholder="e.g. Customer added a coffee after closing" value={reopenTable.values.reason} onChange={(e) => setReopenTable((d) => ({ ...d, values: { ...d.values, reason: e.target.value } }))} className="h-11 rounded-xl" data-testid="reopen-table-reason" />
            </div>
            <div>
              <Label htmlFor="rt-pin">Manager PIN</Label>
              <Input id="rt-pin" type="password" inputMode="text" maxLength={72} value={reopenTable.values.manager_pin} onChange={(e) => setReopenTable((d) => ({ ...d, values: { ...d.values, manager_pin: e.target.value } }))} className="h-11 rounded-xl tracking-widest" data-testid="reopen-table-pin" />
            </div>
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="h-11 rounded-xl" onClick={() => setReopenTable((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="h-11 rounded-xl bg-amber-600 text-white hover:bg-amber-600/90" disabled={reopenTable.saving} onClick={submitReopenTable} data-testid="reopen-table-submit">
              <Undo2 className="h-4 w-4" />
              {reopenTable.saving ? "Reopening…" : "Reopen Table"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={refundDialog.open} onOpenChange={(open) => setRefundDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">{refundDialog.editing ? "Edit refund" : "Refund payment"}</DialogTitle>
            <DialogDescription>
              {refundDialog.order ? `Order ${refundDialog.order.order_number || refundDialog.order.id} · Table ${refundDialog.order.table_number || "—"}` : ""}
            </DialogDescription>
          </DialogHeader>
          {refundDialog.order ? (() => {
            const ord = refundDialog.order;
            const orderTotal = Number(ord.total ?? (Number(ord.subtotal || 0) + Number(ord.vat_total || 0)));
            return (
              <div className="space-y-3">
                <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">Original total</p>
                  <p className="text-2xl font-semibold text-amber-700 dark:text-amber-300">{formatPrice(orderTotal)}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => setRefundDialog((d) => ({ ...d, values: { ...d.values, amount: String(orderTotal.toFixed(2)) } }))} data-testid="refund-full-btn">Full refund</Button>
                  <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => setRefundDialog((d) => ({ ...d, values: { ...d.values, amount: "" } }))} data-testid="refund-partial-btn">Partial</Button>
                </div>
                <div>
                  <Label htmlFor="refund-amount">Refund amount</Label>
                  <Input
                    id="refund-amount"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0.01"
                    value={refundDialog.values.amount}
                    onChange={(e) => setRefundDialog((d) => ({ ...d, values: { ...d.values, amount: e.target.value } }))}
                    placeholder="0.00"
                    className="h-12 rounded-xl text-base font-semibold"
                    data-testid="refund-amount"
                  />
                </div>
                <div>
                  <Label>Reason</Label>
                  <Select value={refundDialog.values.reason} onValueChange={(v) => setRefundDialog((d) => ({ ...d, values: { ...d.values, reason: v } }))}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="refund-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(REFUND_REASON_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {refundDialog.values.reason === "other" ? (
                    <Input
                      type="text"
                      maxLength={240}
                      value={refundDialog.values.reason_other}
                      onChange={(e) => setRefundDialog((d) => ({ ...d, values: { ...d.values, reason_other: e.target.value } }))}
                      placeholder="Describe the reason"
                      className="mt-2 h-11 rounded-xl"
                      data-testid="refund-reason-other"
                    />
                  ) : null}
                </div>
                <div>
                  <Label htmlFor="refund-pin">Manager PIN</Label>
                  <Input
                    id="refund-pin"
                    type="password"
                    maxLength={120}
                    value={refundDialog.values.pin}
                    onChange={(e) => setRefundDialog((d) => ({ ...d, values: { ...d.values, pin: e.target.value } }))}
                    placeholder="Enter PIN"
                    className="h-11 rounded-xl"
                    data-testid="refund-pin"
                  />
                </div>
                <div>
                  <Label>Issued by</Label>
                  <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-3 py-2" data-testid="refund-current-user">
                    <span className="text-sm font-medium">{sessionUser?.full_name || "Current user"}</span>
                    {sessionUser?.role ? <Badge variant="outline" className="rounded-full text-[10px] capitalize">{sessionUser.role}</Badge> : null}
                  </div>
                </div>
                <div>
                  <Label htmlFor="refund-notes">Notes <span className="text-muted-foreground">(optional · shown in Audit Log)</span></Label>
                  <Textarea
                    id="refund-notes"
                    rows={2}
                    maxLength={500}
                    placeholder="Extra context for the audit trail (e.g. customer name, manager who approved, etc.)"
                    value={refundDialog.values.notes}
                    onChange={(e) => setRefundDialog((d) => ({ ...d, values: { ...d.values, notes: e.target.value } }))}
                    className="rounded-xl"
                    data-testid="refund-notes"
                  />
                </div>
              </div>
            );
          })() : null}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setRefundDialog({ open: false, order: null, editing: false, values: { amount: "", reason: "wrong_item", reason_other: "", pin: "", user: "", notes: "" }, saving: false })}>Cancel</Button>
            <Button
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={refundDialog.saving}
              onClick={submitRefund}
              data-testid="refund-submit"
            >
              {refundDialog.saving ? (refundDialog.editing ? "Saving…" : "Refunding…") : (refundDialog.editing ? "Save changes" : "Issue refund")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Stock audit dialogs ─────────────────────────────────────── */}
      <Dialog open={itemHistoryDialog.open} onOpenChange={(open) => setItemHistoryDialog((d) => ({ ...d, open }))}>
        <DialogContent className="flex max-h-[85vh] flex-col rounded-[24px] sm:max-w-lg" data-testid="item-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">{itemHistoryDialog.item_name || "Item"} · history</DialogTitle>
            <DialogDescription>Every stock change recorded for this line.</DialogDescription>
          </DialogHeader>
          <div className="-mx-1 flex-1 overflow-y-auto px-1">
            {itemHistoryDialog.loading ? (
              <Skeleton className="h-32 w-full rounded-2xl" />
            ) : itemHistoryDialog.rows.length ? (
              <div className="space-y-2">
                {itemHistoryDialog.rows.map((r) => {
                  const diff = Number(r.difference || 0);
                  const tone = diff > 0 ? "text-emerald-600 dark:text-emerald-400" : diff < 0 ? "text-destructive" : "";
                  return (
                    <div key={r.id} className="rounded-2xl border border-border bg-card p-3 text-sm" data-testid={`item-history-row-${r.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</p>
                        <Badge variant="outline" className="rounded-full capitalize">{String(r.reason || "other").replace(/_/g, " ")}</Badge>
                      </div>
                      <p className={`mt-1 font-semibold ${tone}`}>
                        {Number(r.previous).toLocaleString()}{r.unit || ""} → {Number(r.new_value).toLocaleString()}{r.unit || ""}
                        <span className="ml-2 text-xs">({diff > 0 ? "+" : ""}{diff.toLocaleString()}{r.unit || ""})</span>
                      </p>
                      {r.user ? <p className="text-xs text-muted-foreground">By {r.user}</p> : null}
                      {r.notes ? <p className="mt-1 text-xs italic text-muted-foreground">{r.notes}</p> : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState icon={Receipt} title="No history yet" description="When you change this line's stock, the change shows up here." />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setItemHistoryDialog({ open: false, item_id: "", item_name: "", loading: false, rows: [] })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stockTakeDetail.open} onOpenChange={(open) => setStockTakeDetail((d) => ({ ...d, open }))}>
        <DialogContent className="flex max-h-[90vh] flex-col rounded-[24px] sm:max-w-2xl" data-testid="stock-take-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Stock take</DialogTitle>
            <DialogDescription>
              {stockTakeDetail.record ? `${new Date(stockTakeDetail.record.created_at).toLocaleString()}${stockTakeDetail.record.user ? ` · ${stockTakeDetail.record.user}` : ""}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="-mx-1 flex-1 overflow-y-auto px-1">
            {stockTakeDetail.loading ? (
              <Skeleton className="h-32 w-full rounded-2xl" />
            ) : stockTakeDetail.record ? (() => {
              const rec = stockTakeDetail.record;
              const lines = rec.lines || [];
              const shortages = lines.filter((ln) => Number(ln.variance || 0) < 0).length;
              const overages = lines.filter((ln) => Number(ln.variance || 0) > 0).length;
              return (
                <div className="space-y-3">
                  {/* Summary header — totals so the manager can see at a
                      glance what happened during the count. */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-2xl border border-border bg-secondary/40 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Items counted</p>
                      <p className="mt-1 text-xl font-semibold" data-testid="stock-take-total-items">{lines.length}</p>
                    </div>
                    <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-rose-700 dark:text-rose-300">Shortages</p>
                      <p className="mt-1 text-xl font-semibold text-rose-700 dark:text-rose-300" data-testid="stock-take-shortages">{shortages}</p>
                    </div>
                    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Overages</p>
                      <p className="mt-1 text-xl font-semibold text-emerald-700 dark:text-emerald-300" data-testid="stock-take-overages">{overages}</p>
                    </div>
                  </div>
                  {rec.notes ? (
                    <div className="rounded-2xl border border-border bg-background p-3">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Notes</p>
                      <p className="mt-1 text-sm">{rec.notes}</p>
                    </div>
                  ) : null}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Expected</TableHead>
                        <TableHead className="text-right">Actual</TableHead>
                        <TableHead className="text-right">Variance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lines.map((line, idx) => {
                        const v = Number(line.variance || 0);
                        const tone = v > 0 ? "text-emerald-600 dark:text-emerald-400" : v < 0 ? "text-destructive" : "text-muted-foreground";
                        return (
                          <TableRow key={`${line.item_id}-${idx}`}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {line.item_name}
                                <Badge variant="outline" className="rounded-full text-[10px]">{line.location_name || (line.location === "back_of_house" ? "Back of house" : "Floor")}{line.category ? ` · ${line.category === "bar" ? "Bar" : "Kitchen"}` : ""}</Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{Number(line.expected).toLocaleString()}{line.unit || ""}</TableCell>
                            <TableCell className="text-right">{Number(line.actual).toLocaleString()}{line.unit || ""}</TableCell>
                            <TableCell className={`text-right font-semibold ${tone}`}>{v > 0 ? "+" : ""}{v.toLocaleString()}{line.unit || ""}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              );
            })() : null}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setStockTakeDetail({ open: false, record: null, loading: false })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stockTakeWizard.open} onOpenChange={(open) => setStockTakeWizard((s) => ({ ...s, open }))}>
        <DialogContent className="flex max-h-[92vh] flex-col gap-0 rounded-[24px] p-0 sm:max-w-3xl" data-testid="stock-take-wizard">
          {(() => {
            const w = stockTakeWizard;
            const loc = stockTakeLoc(w);
            const locId = loc?.id || w.location;
            const itemsAllowed = stockTakeItemsAllowed(w);
            const boh = !itemsAllowed; // "no menu items here" — every non-floor location
            const getExp = (ln) => Number(ln.expected?.[locId] ?? 0);
            const getAct = (ln) => (ln.actual?.[locId] ?? "");
            const inScope = w.lines.filter((ln) => (w.scope === "all" || ln.category === w.scope) && !(boh && ln.item_type === "item"));
            const rows = inScope.filter((ln) => !w.search || ln.name.toLowerCase().includes(w.search.toLowerCase()));
            const changed = inScope.filter((ln) => Math.abs(Number(getAct(ln) || 0) - getExp(ln)) > 1e-9).length;
            const setActual = (ln, val) => setStockTakeWizard((s) => ({ ...s, lines: s.lines.map((row) => (row.item_id === ln.item_id && row.item_type === ln.item_type ? { ...row, actual: { ...(row.actual || {}), [locId]: val } } : row)) }));
            const scopeTabs = stockTakeScopes(w);
            const scopeDef = scopeTabs.find((t) => t.id === w.scope);
            const scopeLabel = `${loc?.name || "Floor"} · ${scopeDef ? scopeDef.label : (boh ? "All departments" : "All departments + Items")}`;
            return (
              <>
                <DialogHeader className="px-5 pt-5">
                  <DialogTitle className="flex items-center gap-2 font-['Space_Grotesk'] text-2xl">
                    <CheckCircle2 className="h-5 w-5" />
                    Stock take
                  </DialogTitle>
                  <DialogDescription>Pick where you are counting and which department (or all of them), then type what you physically counted. We record the variance and update the live count when you save.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 px-5 pt-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <div className="inline-flex flex-wrap rounded-2xl border border-border bg-card p-1" role="tablist" aria-label="Location" data-testid="stock-take-location-tabs">
                      {w.locations.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          role="tab"
                          aria-selected={locId === opt.id}
                          onClick={() => setStockTakeWizard((s) => ({ ...s, location: opt.id, scope: !stockTakeItemsAllowed({ ...s, location: opt.id }) && s.scope === "items" ? "all" : s.scope }))}
                          className={`min-h-11 flex-1 rounded-xl px-4 text-sm font-semibold transition-colors sm:flex-none ${locId === opt.id ? "bg-[hsl(var(--primary))] text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
                          data-testid={`stock-take-location-${opt.key || opt.id}`}
                        >
                          {opt.key === "floor" ? "On the floor" : opt.name}
                        </button>
                      ))}
                    </div>
                    <div className="inline-flex flex-wrap rounded-2xl border border-border bg-card p-1" role="tablist" aria-label="Department" data-testid="stock-take-scope-tabs">
                      {(() => {
                        const visibleLines = w.lines.filter((ln) => !(boh && ln.item_type === "item"));
                        return [{ id: "all", label: "All departments", count: visibleLines.length }, ...scopeTabs];
                      })().map((tab) => {
                        const selected = w.scope === tab.id;
                        return (
                          <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={selected}
                            onClick={() => setStockTakeWizard((s) => ({ ...s, scope: tab.id }))}
                            className={`min-h-11 flex-1 rounded-xl px-3 text-sm font-medium transition-colors sm:flex-none ${selected ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                            data-testid={`stock-take-scope-${tab.id.replace(/^dept:/, "dept-")}`}
                          >
                            {tab.label}
                            <span className="ml-1 rounded-full bg-background/60 px-1.5 text-[10px] font-semibold">{tab.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-3 py-2" data-testid="stock-take-current-user">
                      <span className="text-xs text-muted-foreground">Counted by</span>
                      <span className="flex items-center gap-2 text-sm font-medium">{sessionUser?.full_name || "Current user"}{sessionUser?.role ? <Badge variant="outline" className="rounded-full text-[10px] capitalize">{sessionUser.role}</Badge> : null}</span>
                    </div>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={w.search}
                        onChange={(e) => setStockTakeWizard((s) => ({ ...s, search: e.target.value }))}
                        placeholder="Find an ingredient…"
                        className="h-11 rounded-xl pl-9"
                        data-testid="stock-take-search"
                      />
                    </div>
                  </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
                  {w.lines.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Nothing to count yet — add ingredients or give menu items a stock count under Inventory.</p>
                  ) : rows.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">Nothing matches.</p>
                  ) : (
                    <ul className="space-y-2" data-testid="stock-take-rows">
                      {rows.map((ln) => {
                        const expected = getExp(ln);
                        const actualNum = Number(getAct(ln) || 0);
                        const v = actualNum - expected;
                        const tone = v > 0 ? "text-emerald-600 dark:text-emerald-400" : v < 0 ? "text-destructive" : "text-muted-foreground";
                        return (
                          <li key={ln.item_id} className={`grid grid-cols-[1fr_auto] items-center gap-3 rounded-2xl border p-3 sm:grid-cols-[1fr_6rem_8rem_6rem] ${Math.abs(v) > 1e-9 ? "border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/5" : "border-border"}`} data-testid={`stock-take-row-${ln.item_id}`}>
                            <div className="min-w-0">
                              <p className="truncate font-medium">{ln.name}</p>
                              <p className="text-[11px] text-muted-foreground"><span className="capitalize">{ln.item_type === "item" ? "Menu item" : (ln.dept_name || ln.category)}</span> · should be <span className="font-medium tabular-nums text-foreground">{Number(expected).toLocaleString()} {ln.unit}</span></p>
                              {(() => {
                                // Session 63 — what happened to this line since it was last counted.
                                const ctx = w.context?.by_location?.[locId]?.[`${ln.item_type}:${ln.item_id}`];
                                if (!ctx) return null;
                                const n = (x) => Number(Number(x || 0).toFixed(3)).toLocaleString();
                                const ago = (iso) => {
                                  const t = Date.parse(iso || "");
                                  if (!Number.isFinite(t)) return "";
                                  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
                                  if (m < 60) return `${m} min ago`;
                                  const h = Math.round(m / 60);
                                  if (h < 48) return `${h} h ago`;
                                  return `${Math.round(h / 24)} d ago`;
                                };
                                const since = ctx.since || {};
                                const today = ctx.today || {};
                                const chips = [];
                                if (ctx.last_count) chips.push({ k: "count", t: `Last count ${n(ctx.last_count.qty)} · ${ago(ctx.last_count.at)}${ctx.last_count.by ? ` · ${ctx.last_count.by}` : ""}` });
                                else chips.push({ k: "count", t: "Never counted" });
                                if (since.received > 0) chips.push({ k: "received", t: `${boh ? "Stocked up" : "Received"} +${n(since.received)}${ctx.last_received ? ` · last ${ago(ctx.last_received.at)}` : ""}`, tone: "text-emerald-700 dark:text-emerald-400" });
                                else if (ctx.last_received) chips.push({ k: "received", t: `Last ${boh ? "stocked up" : "received"} ${n(ctx.last_received.qty)} · ${ago(ctx.last_received.at)}` });
                                if (since.moved_in > 0) chips.push({ k: "in", t: `← Moved in +${n(since.moved_in)}${ctx.last_moved_in ? ` · ${ago(ctx.last_moved_in.at)}` : ""}` });
                                if (since.moved_out > 0) chips.push({ k: "out", t: `→ ${boh ? "To the floor" : "Moved out"} −${n(since.moved_out)}${ctx.last_moved_out ? ` · ${ago(ctx.last_moved_out.at)}` : ""}`, tone: "text-amber-700 dark:text-amber-400" });
                                if (since.used > 0 || today.used > 0) chips.push({ k: "used", t: `Used −${n(since.used)}${today.used > 0 ? ` (today ${n(today.used)})` : ""}`, tone: "text-amber-700 dark:text-amber-400" });
                                if (since.waste > 0) chips.push({ k: "waste", t: `Waste −${n(since.waste)}`, tone: "text-destructive" });
                                if (Math.abs(since.adjusted || 0) > 0) chips.push({ k: "adj", t: `Adjusted ${since.adjusted > 0 ? "+" : ""}${n(since.adjusted)}` });
                                return (
                                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground" data-testid={`stock-take-context-${ln.item_type}-${ln.item_id}`}>
                                    {chips.map((c) => <span key={c.k} className={`tabular-nums ${c.tone || ""}`}>{c.t}</span>)}
                                  </div>
                                );
                              })()}
                            </div>
                            <p className="hidden text-right text-sm tabular-nums text-muted-foreground sm:block">{Number(expected).toLocaleString()}</p>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                inputMode="decimal"
                                step="0.001"
                                min="0"
                                value={getAct(ln)}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => setActual(ln, e.target.value)}
                                className="h-11 w-24 rounded-xl text-right text-base font-semibold tabular-nums"
                                aria-label={`Counted ${ln.name}`}
                                data-testid={`stock-take-actual-${ln.item_type}-${ln.item_id}`}
                              />
                              <span className="w-8 text-xs text-muted-foreground">{ln.unit}</span>
                            </div>
                            <p className={`col-span-2 text-right text-sm font-semibold tabular-nums sm:col-span-1 ${tone}`} data-testid={`stock-take-variance-${ln.item_id}`}>{v > 0 ? "+" : ""}{Number(v.toFixed(3)).toLocaleString()} {ln.unit}</p>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div className="space-y-3 border-t border-border px-5 py-4">
                  <div>
                    <Label className="text-xs">Notes <span className="text-muted-foreground">(optional)</span></Label>
                    <Textarea
                      rows={1}
                      maxLength={500}
                      value={w.notes}
                      onChange={(e) => setStockTakeWizard((s) => ({ ...s, notes: e.target.value }))}
                      className="mt-1 min-h-11 rounded-xl"
                      data-testid="stock-take-notes"
                    />
                  </div>
                  <DialogFooter className="gap-2 sm:justify-between">
                    <p className="text-xs text-muted-foreground" data-testid="stock-take-summary">Saving <span className="font-medium text-foreground">{scopeLabel}</span> · {inScope.length} line{inScope.length === 1 ? "" : "s"}{changed ? ` · ${changed} changed` : " · no changes yet"} · only this location's balance is updated</p>
                    <div className="flex gap-2">
                      <Button variant="outline" className="h-11 rounded-xl" onClick={() => setStockTakeWizard({ open: false, user: "", notes: "", search: "", scope: "all", location: "floor", locations: [], lines: [], saving: false })} data-testid="stock-take-cancel">Cancel</Button>
                      <Button
                        className="h-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                        disabled={w.saving || !inScope.length}
                        onClick={submitStockTake}
                        data-testid="stock-take-submit"
                      >
                        {w.saving ? "Saving…" : "Save stock take"}
                      </Button>
                    </div>
                  </DialogFooter>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Cash Management dialogs ───────────────────────────────────── */}
      <Dialog open={floatDialog.open} onOpenChange={(open) => setFloatDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="cash-float-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5" />
              {floatDialog.mode === "edit" ? "Edit opening float" : "Add opening float"}
            </DialogTitle>
            <DialogDescription>Cash placed in the drawer at the start of the shift.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Toggle — denomination breakdown vs total-only fast path. */}
            <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/40 px-3 py-2" data-testid="cash-float-mode-toggle">
              <div>
                <p className="text-xs font-semibold">{floatDialog.values.use_total_only ? "Use Total Only" : "Count denominations"}</p>
                <p className="text-[11px] text-muted-foreground">{floatDialog.values.use_total_only ? "Manager enters one float amount." : "Count each note + coin — total auto-calculates."}</p>
              </div>
              <Button type="button" size="sm" variant="outline" className="rounded-xl" data-testid="cash-float-toggle-mode" onClick={() => setFloatDialog((s) => ({ ...s, values: { ...s.values, use_total_only: !s.values.use_total_only, ...(s.values.use_total_only ? {} : { denominations: {}, amount: "" }) } }))}>{floatDialog.values.use_total_only ? "Count denominations" : "Use Total Only"}</Button>
            </div>

            {!floatDialog.values.use_total_only ? (
              <div className="space-y-2 rounded-2xl border border-border bg-secondary/40 p-3" data-testid="cash-float-denominations">
                <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Count cash (auto-totals)</Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {[["R200",200],["R100",100],["R50",50],["R20",20],["R10",10],["R5",5],["R2",2],["R1",1],["50c",0.5],["20c",0.2],["10c",0.1]].map(([k, val]) => {
                    const qty = Number(floatDialog.values.denominations?.[k] || 0);
                    return (
                      <div key={k} className="rounded-xl border border-border bg-background p-2" data-testid={`cash-float-denom-${k}`}>
                        <div className="flex items-center justify-between text-xs"><span className="font-semibold">{k}</span><span className="text-muted-foreground">{formatPrice(qty * val)}</span></div>
                        <Input type="number" inputMode="numeric" min="0" max="9999" placeholder="0"
                          value={floatDialog.values.denominations?.[k] ?? ""}
                          onChange={(e) => { const n = Math.max(0, parseInt(e.target.value || "0", 10) || 0); setFloatDialog((s) => { const d = { ...(s.values.denominations || {}), [k]: n }; const denoms = [["R200",200],["R100",100],["R50",50],["R20",20],["R10",10],["R5",5],["R2",2],["R1",1],["50c",0.5],["20c",0.2],["10c",0.1]]; const tot = denoms.reduce((a, [kk, vv]) => a + ((d[kk] || 0) * vv), 0); return { ...s, values: { ...s.values, denominations: d, amount: tot ? tot.toFixed(2) : "" } }; }); }}
                          className="mt-1 h-9 rounded-lg text-sm" />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div>
              <Label htmlFor="float-amount">{floatDialog.values.use_total_only ? "Float total" : "Float total (auto-calculated)"}</Label>
              <Input
                id="float-amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={floatDialog.values.amount}
                readOnly={!floatDialog.values.use_total_only}
                onChange={(e) => setFloatDialog((d) => ({ ...d, values: { ...d.values, amount: e.target.value } }))}
                placeholder="0.00"
                className={`h-11 rounded-xl ${!floatDialog.values.use_total_only ? "bg-secondary/40 font-semibold" : ""}`}
                data-testid="cash-float-amount"
              />
            </div>
            <div>
              <Label htmlFor="float-user">Recorded by</Label>
              <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-3 py-2" data-testid="cash-float-current-user">
                <span className="text-sm font-medium">{sessionUser?.full_name || "Current user"}</span>
                {sessionUser?.role ? <Badge variant="outline" className="rounded-full text-[10px] capitalize">{sessionUser.role}</Badge> : null}
              </div>
            </div>
            <div>
              <Label htmlFor="float-notes">Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea
                id="float-notes"
                rows={2}
                maxLength={500}
                value={floatDialog.values.notes}
                onChange={(e) => setFloatDialog((d) => ({ ...d, values: { ...d.values, notes: e.target.value } }))}
                className="rounded-xl"
                data-testid="cash-float-notes"
              />
            </div>
            {registers.length > 1 ? (
              <div>
                <Label>Register</Label>
                <Select value={floatDialog.values.register_id || defaultRegisterId} onValueChange={(v) => setFloatDialog((d) => ({ ...d, values: { ...d.values, register_id: v } }))}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="cash-float-register"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {registers.filter((r) => r.active).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setFloatDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitFloat} data-testid="cash-float-submit">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payoutDialog.open} onOpenChange={(open) => setPayoutDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="cash-payout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5" />
              New cash payout
            </DialogTitle>
            <DialogDescription>Reduces expected cash automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="payout-amount">Amount</Label>
              <Input
                id="payout-amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                value={payoutDialog.values.amount}
                onChange={(e) => setPayoutDialog((d) => ({ ...d, values: { ...d.values, amount: e.target.value } }))}
                placeholder="0.00"
                className="h-11 rounded-xl"
                data-testid="cash-payout-amount"
              />
            </div>
            <div>
              <Label htmlFor="payout-reason">Reason</Label>
              <Input
                id="payout-reason"
                type="text"
                maxLength={240}
                value={payoutDialog.values.reason}
                onChange={(e) => setPayoutDialog((d) => ({ ...d, values: { ...d.values, reason: e.target.value } }))}
                placeholder="e.g. Bought milk"
                className="h-11 rounded-xl"
                data-testid="cash-payout-reason"
              />
            </div>
            <div>
              <Label htmlFor="payout-category">Category</Label>
              <Select value={payoutDialog.values.category} onValueChange={(v) => setPayoutDialog((d) => ({ ...d, values: { ...d.values, category: v } }))}>
                <SelectTrigger id="payout-category" className="h-11 rounded-xl" data-testid="cash-payout-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="stock_purchase">Stock purchase</SelectItem>
                  <SelectItem value="emergency_purchase">Emergency purchase</SelectItem>
                  <SelectItem value="petty_cash">Petty cash</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="staff_reimbursement">Staff reimbursement</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="payout-user">Recorded by</Label>
              <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-3 py-2" data-testid="cash-payout-current-user">
                <span className="text-sm font-medium">{sessionUser?.full_name || "Current user"}</span>
                {sessionUser?.role ? <Badge variant="outline" className="rounded-full text-[10px] capitalize">{sessionUser.role}</Badge> : null}
              </div>
            </div>
            <div>
              <Label htmlFor="payout-notes">Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea
                id="payout-notes"
                rows={2}
                maxLength={500}
                value={payoutDialog.values.notes}
                onChange={(e) => setPayoutDialog((d) => ({ ...d, values: { ...d.values, notes: e.target.value } }))}
                className="rounded-xl"
              />
            </div>
            {registers.length > 1 ? (
              <div>
                <Label>Register</Label>
                <Select value={payoutDialog.values.register_id || defaultRegisterId} onValueChange={(v) => setPayoutDialog((d) => ({ ...d, values: { ...d.values, register_id: v } }))}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="cash-payout-register"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {registers.filter((r) => r.active).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setPayoutDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitPayout} data-testid="cash-payout-submit">
              Save payout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={supplierDialog.open} onOpenChange={(open) => setSupplierDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="cash-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              New supplier payment
            </DialogTitle>
            <DialogDescription>Tracked separately from normal cash payouts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="supplier-name">Supplier name</Label>
              <Input
                id="supplier-name"
                type="text"
                maxLength={160}
                value={supplierDialog.values.supplier_name}
                onChange={(e) => setSupplierDialog((d) => ({ ...d, values: { ...d.values, supplier_name: e.target.value } }))}
                placeholder="e.g. Fresh Produce"
                className="h-11 rounded-xl"
                data-testid="cash-supplier-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="supplier-amount">Amount</Label>
                <Input
                  id="supplier-amount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0.01"
                  value={supplierDialog.values.amount}
                  onChange={(e) => setSupplierDialog((d) => ({ ...d, values: { ...d.values, amount: e.target.value } }))}
                  placeholder="0.00"
                  className="h-11 rounded-xl"
                  data-testid="cash-supplier-amount"
                />
              </div>
              <div>
                <Label htmlFor="supplier-method">Method</Label>
                <Select value={supplierDialog.values.payment_method} onValueChange={(v) => setSupplierDialog((d) => ({ ...d, values: { ...d.values, payment_method: v } }))}>
                  <SelectTrigger id="supplier-method" className="h-11 rounded-xl" data-testid="cash-supplier-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="eft">EFT</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="supplier-reference">Reference <span className="text-muted-foreground">(optional)</span></Label>
              <Input
                id="supplier-reference"
                type="text"
                maxLength={160}
                value={supplierDialog.values.reference}
                onChange={(e) => setSupplierDialog((d) => ({ ...d, values: { ...d.values, reference: e.target.value } }))}
                placeholder="e.g. Invoice 1245"
                className="h-11 rounded-xl"
                data-testid="cash-supplier-reference"
              />
            </div>
            <div>
              <Label htmlFor="supplier-user">Recorded by</Label>
              <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-3 py-2" data-testid="cash-supplier-current-user">
                <span className="text-sm font-medium">{sessionUser?.full_name || "Current user"}</span>
                {sessionUser?.role ? <Badge variant="outline" className="rounded-full text-[10px] capitalize">{sessionUser.role}</Badge> : null}
              </div>
            </div>
            <div>
              <Label htmlFor="supplier-notes">Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea
                id="supplier-notes"
                rows={2}
                maxLength={500}
                value={supplierDialog.values.notes}
                onChange={(e) => setSupplierDialog((d) => ({ ...d, values: { ...d.values, notes: e.target.value } }))}
                className="rounded-xl"
              />
            </div>
            {registers.length > 1 ? (
              <div>
                <Label>Register</Label>
                <Select value={supplierDialog.values.register_id || defaultRegisterId} onValueChange={(v) => setSupplierDialog((d) => ({ ...d, values: { ...d.values, register_id: v } }))}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="cash-supplier-register"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {registers.filter((r) => r.active).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setSupplierDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitSupplier} data-testid="cash-supplier-submit">
              Save payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cashUpDialog.open} onOpenChange={(open) => setCashUpDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="cash-up-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Cash up
            </DialogTitle>
            <DialogDescription>Reconcile the drawer against the expected cash for the current range.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">Expected cash</p>
              <p className="text-2xl font-semibold text-amber-700 dark:text-amber-300" data-testid="cash-up-expected">{formatPrice(cashData.summary.expected_cash)}</p>
              <p className="mt-1 text-[11px] text-amber-700/80 dark:text-amber-300/80">Opening {formatPrice(cashData.summary.opening_float)} + Cash sales {formatPrice(cashData.summary.cash_sales)} − Payouts {formatPrice(cashData.summary.cash_payouts)} − Supplier {formatPrice(cashData.summary.supplier_payments_cash)}</p>
            </div>

            {/* Opening Float breakdown — if any float in the range had
                denominations recorded we surface them here so the manager
                can sanity-check before counting the drawer. */}
            {cashData.summary.opening_float_denominations && Object.values(cashData.summary.opening_float_denominations).some((v) => Number(v) > 0) ? (
              <div className="rounded-2xl border border-border bg-secondary/30 p-3" data-testid="cash-up-opening-breakdown">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Opening Float Breakdown</p>
                <div className="mt-2 grid grid-cols-3 gap-1 text-[11px] sm:grid-cols-4">
                  {[["R200",200],["R100",100],["R50",50],["R20",20],["R10",10],["R5",5],["R2",2],["R1",1],["50c",0.5],["20c",0.2],["10c",0.1]].map(([k, val]) => {
                    const qty = Number(cashData.summary.opening_float_denominations[k] || 0);
                    if (!qty) return null;
                    return (
                      <div key={k} className="flex items-center justify-between rounded-lg border border-border bg-background px-2 py-1">
                        <span className="font-semibold">{k}</span>
                        <span className="text-muted-foreground">× {qty}</span>
                        <span>{formatPrice(qty * val)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <div className="space-y-2 rounded-2xl border border-border bg-secondary/40 p-3" data-testid="cash-up-denominations">
              <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Count cash (auto-totals)</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {[["R200",200],["R100",100],["R50",50],["R20",20],["R10",10],["R5",5],["R2",2],["R1",1],["50c",0.5],["20c",0.2],["10c",0.1]].map(([k, val]) => {
                  const qty = Number(cashUpDialog.values.denominations?.[k] || 0);
                  return (
                    <div key={k} className="rounded-xl border border-border bg-background p-2" data-testid={`cash-up-denom-${k}`}>
                      <div className="flex items-center justify-between text-xs"><span className="font-semibold">{k}</span><span className="text-muted-foreground">{formatPrice(qty * val)}</span></div>
                      <Input type="number" inputMode="numeric" min="0" max="9999" placeholder="0"
                        value={cashUpDialog.values.denominations?.[k] ?? ""}
                        onChange={(e) => { const n = Math.max(0, parseInt(e.target.value || "0", 10) || 0); setCashUpDialog((s) => { const d = { ...(s.values.denominations || {}), [k]: n }; const denoms = [["R200",200],["R100",100],["R50",50],["R20",20],["R10",10],["R5",5],["R2",2],["R1",1],["50c",0.5],["20c",0.2],["10c",0.1]]; const tot = denoms.reduce((a, [kk, vv]) => a + ((d[kk] || 0) * vv), 0); return { ...s, values: { ...s.values, denominations: d, actual_cash: tot ? tot.toFixed(2) : "" } }; }); }}
                        className="mt-1 h-9 rounded-lg text-sm" />
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="cashup-actual">Actual cash counted</Label>
              <Input
                id="cashup-actual"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                autoFocus
                value={cashUpDialog.values.actual_cash}
                onChange={(e) => setCashUpDialog((d) => ({ ...d, values: { ...d.values, actual_cash: e.target.value } }))}
                placeholder="0.00"
                className="h-12 rounded-xl text-base font-semibold"
                data-testid="cash-up-actual"
              />
              {cashUpDialog.values.actual_cash !== "" && (() => {
                const diff = Number(cashUpDialog.values.actual_cash || 0) - Number(cashData.summary.expected_cash || 0);
                const status = Math.abs(diff) < 0.005 ? "balanced" : diff > 0 ? "over" : "short";
                const tone = status === "balanced" ? "text-emerald-700 dark:text-emerald-300" : status === "over" ? "text-sky-700 dark:text-sky-300" : "text-destructive";
                return (
                  <p className={`mt-2 text-sm font-semibold ${tone}`} data-testid="cash-up-variance">
                    Variance: {diff > 0 ? "+" : ""}{formatPrice(diff)} · {status.toUpperCase()}
                  </p>
                );
              })()}
            </div>
            <div>
              <Label htmlFor="cashup-user">Recorded by</Label>
              <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-3 py-2" data-testid="cash-up-current-user">
                <span className="text-sm font-medium">{sessionUser?.full_name || "Current user"}</span>
                {sessionUser?.role ? <Badge variant="outline" className="rounded-full text-[10px] capitalize">{sessionUser.role}</Badge> : null}
              </div>
            </div>
            <div>
              <Label htmlFor="cashup-notes">Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea
                id="cashup-notes"
                rows={2}
                maxLength={500}
                value={cashUpDialog.values.notes}
                onChange={(e) => setCashUpDialog((d) => ({ ...d, values: { ...d.values, notes: e.target.value } }))}
                className="rounded-xl"
              />
            </div>
            {registers.length > 1 ? (
              <div>
                <Label>Register</Label>
                <Select value={cashUpDialog.values.register_id || defaultRegisterId} onValueChange={(v) => setCashUpDialog((d) => ({ ...d, values: { ...d.values, register_id: v } }))}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="cash-up-register"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {registers.filter((r) => r.active).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setCashUpDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitCashUp} data-testid="cash-up-submit">
              Confirm cash up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Register CRUD dialog ─────────────────────────────────────── */}
      <Dialog open={registerDialog.open} onOpenChange={(open) => setRegisterDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="register-dialog">
          <DialogHeader>
            <DialogTitle>{registerDialog.mode === "edit" ? "Edit register" : "Add register"}</DialogTitle>
            <DialogDescription>Use registers to separate tills (e.g. Main, Outside Bar, Coffee Station).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="register-name">Name</Label>
              <Input id="register-name" type="text" maxLength={80} value={registerDialog.values.name} onChange={(e) => setRegisterDialog((d) => ({ ...d, values: { ...d.values, name: e.target.value } }))} placeholder="e.g. Outside Bar" className="h-11 rounded-xl" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="register-description">Description <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea id="register-description" rows={2} maxLength={240} value={registerDialog.values.description} onChange={(e) => setRegisterDialog((d) => ({ ...d, values: { ...d.values, description: e.target.value } }))} className="rounded-xl" data-testid="register-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setRegisterDialog({ open: false, mode: "create", id: "", values: { name: "", description: "" } })}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitRegister} data-testid="register-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Report Schedule CRUD dialog ───────────────────────────────── */}
      <Dialog open={scheduleDialog.open} onOpenChange={(open) => setScheduleDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-lg" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>{scheduleDialog.mode === "edit" ? "Edit schedule" : "New report schedule"}</DialogTitle>
            <DialogDescription>Reports auto-email to the chosen recipients at the selected frequency.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="schedule-name">Schedule name</Label>
              <Input id="schedule-name" type="text" maxLength={120} value={scheduleDialog.values.name} onChange={(e) => setScheduleDialog((d) => ({ ...d, values: { ...d.values, name: e.target.value } }))} placeholder="e.g. Daily 6PM summary" className="h-11 rounded-xl" data-testid="schedule-name" />
            </div>
            <div>
              <Label>Frequency</Label>
              <Select value={scheduleDialog.values.frequency} onValueChange={(v) => setScheduleDialog((d) => ({ ...d, values: { ...d.values, frequency: v } }))}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="every_4_hours">Every 4 hours</SelectItem>
                  <SelectItem value="every_8_hours">Every 8 hours</SelectItem>
                  <SelectItem value="every_12_hours">Every 12 hours</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Report types</Label>
              <div className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-secondary/30 p-3" data-testid="schedule-report-types">
                {[
                  { v: "business_summary", l: "Business Summary — revenue, profit, payments (cash/card/EFT/split), tips & service fee, voids, last stock take, stock received, waste" },
                  { v: "sales_mix", l: "Sales Mix — every item sold, qty, revenue, % of sales, profit" },
                  { v: "category_mix", l: "Category Mix" },
                  { v: "cash_up", l: "Cash Drawer — float, cash sales, payouts, expected cash" },
                  { v: "low_stock", l: "Low Stock — items & ingredients under threshold" },
                  { v: "register", l: "Registers" },
                ].map((rt) => {
                  const checked = (scheduleDialog.values.report_types || []).includes(rt.v);
                  return (
                    <label key={rt.v} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(c) => setScheduleDialog((d) => {
                          const cur = new Set(d.values.report_types || []);
                          if (c) cur.add(rt.v); else cur.delete(rt.v);
                          return { ...d, values: { ...d.values, report_types: Array.from(cur) } };
                        })}
                        data-testid={`schedule-type-${rt.v}`}
                      />
                      <span>{rt.l}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <Label htmlFor="schedule-recipients">Recipient emails <span className="text-muted-foreground">(comma-separated; blank uses default receipts.scantopay@gmail.com)</span></Label>
              <Textarea id="schedule-recipients" rows={2} maxLength={500} value={scheduleDialog.values.recipients} onChange={(e) => setScheduleDialog((d) => ({ ...d, values: { ...d.values, recipients: e.target.value } }))} placeholder="manager@example.com, owner@example.com" className="rounded-xl" data-testid="schedule-recipients" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/30 p-3">
              <Label htmlFor="schedule-enabled" className="cursor-pointer">Enabled</Label>
              <Switch id="schedule-enabled" checked={!!scheduleDialog.values.enabled} onCheckedChange={(v) => setScheduleDialog((d) => ({ ...d, values: { ...d.values, enabled: v } }))} data-testid="schedule-enabled" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setScheduleDialog({ open: false, mode: "create", id: "", values: { name: "", frequency: "daily", report_types: ["business_summary", "sales_mix", "category_mix"], recipients: "", enabled: true } })}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitSchedule} data-testid="schedule-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Inventory Alert Schedule Dialog ─────────────────── */}
      <Dialog open={inventoryAlertDialog.open} onOpenChange={(open) => setInventoryAlertDialog((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-lg" data-testid="inventory-alert-dialog">
          <DialogHeader>
            <DialogTitle>{inventoryAlertDialog.mode === "edit" ? "Edit alert schedule" : "New inventory alert schedule"}</DialogTitle>
            <DialogDescription>Get notified when inventory levels need attention. Choose email or WhatsApp delivery.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="inv-alert-name">Schedule name</Label>
              <Input id="inv-alert-name" type="text" maxLength={120} value={inventoryAlertDialog.values.name} onChange={(e) => setInventoryAlertDialog((d) => ({ ...d, values: { ...d.values, name: e.target.value } }))} placeholder="e.g. Daily low-stock check" className="h-11 rounded-xl" data-testid="inv-alert-name" />
            </div>
            <div>
              <Label>Frequency</Label>
              <Select value={inventoryAlertDialog.values.frequency} onValueChange={(v) => setInventoryAlertDialog((d) => ({ ...d, values: { ...d.values, frequency: v } }))}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="inv-alert-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate (every 5 min)</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Alert types</Label>
              <div className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-secondary/30 p-3" data-testid="inv-alert-types">
                {[
                  { v: "low_stock", l: "Low Stock" },
                  { v: "out_of_stock", l: "Out of Stock" },
                  { v: "negative_stock", l: "Negative Stock" },
                  { v: "variance", l: "Variance" },
                  { v: "usage_warning", l: "Usage Warning" },
                ].map((at) => {
                  const checked = (inventoryAlertDialog.values.alert_types || []).includes(at.v);
                  return (
                    <label key={at.v} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(c) => setInventoryAlertDialog((d) => {
                          const cur = new Set(d.values.alert_types || []);
                          if (c) cur.add(at.v); else cur.delete(at.v);
                          return { ...d, values: { ...d.values, alert_types: Array.from(cur) } };
                        })}
                        data-testid={`inv-alert-type-${at.v}`}
                      />
                      <span>{at.l}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Delivery channel</Label>
              <Select value={inventoryAlertDialog.values.channel} onValueChange={(v) => setInventoryAlertDialog((d) => ({ ...d, values: { ...d.values, channel: v } }))}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="inv-alert-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
              {inventoryAlertDialog.values.channel === "whatsapp" && (
                <p className="text-[11px] text-amber-600 mt-1">WhatsApp delivery requires external API configuration (Twilio / Meta). Schedules will be saved but delivery won't work until configured.</p>
              )}
            </div>
            <div>
              <Label htmlFor="inv-alert-recipients">{inventoryAlertDialog.values.channel === "whatsapp" ? "Phone numbers" : "Recipient emails"} <span className="text-muted-foreground">(comma-separated)</span></Label>
              <Textarea id="inv-alert-recipients" rows={2} maxLength={500} value={inventoryAlertDialog.values.recipients} onChange={(e) => setInventoryAlertDialog((d) => ({ ...d, values: { ...d.values, recipients: e.target.value } }))} placeholder={inventoryAlertDialog.values.channel === "whatsapp" ? "+27123456789, +44987654321" : "manager@example.com, owner@example.com"} className="rounded-xl" data-testid="inv-alert-recipients" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/30 p-3">
              <Label htmlFor="inv-alert-enabled" className="cursor-pointer">Enabled</Label>
              <Switch id="inv-alert-enabled" checked={!!inventoryAlertDialog.values.enabled} onCheckedChange={(v) => setInventoryAlertDialog((d) => ({ ...d, values: { ...d.values, enabled: v } }))} data-testid="inv-alert-enabled" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setInventoryAlertDialog({ open: false, mode: "create", id: "", values: { name: "", frequency: "daily", alert_types: ["low_stock", "out_of_stock"], channel: "email", recipients: "", enabled: true } })}>Cancel</Button>
            <Button className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={submitInventoryAlertSchedule} data-testid="inv-alert-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Service Time order detail drill-down ─────────────────── */}
      <Dialog open={serviceTimeDetail.open} onOpenChange={(open) => setServiceTimeDetail((d) => ({ ...d, open }))}>
        <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden rounded-[24px] sm:max-w-lg" data-testid="service-time-detail-dialog">
          <DialogHeader>
            <DialogTitle>Order {serviceTimeDetail.order?.order_number || ""}</DialogTitle>
            <DialogDescription>Service-time breakdown for this ticket.</DialogDescription>
          </DialogHeader>
          {serviceTimeDetail.loading ? (
            <Skeleton className="h-40 w-full rounded-2xl" />
          ) : serviceTimeDetail.order ? (() => {
            const o = serviceTimeDetail.order;
            const f = o.full || {};
            const fmtTime = (v) => v ? new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
            return (
              <div className="-mx-1 min-h-0 flex-1 space-y-3 overflow-y-auto px-1 text-sm">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-xl border border-border bg-secondary/30 p-2"><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Table</p><p className="font-semibold">{o.table || "—"}</p></div>
                  <div className="rounded-xl border border-border bg-secondary/30 p-2">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Waiter</p>
                    <p className="font-semibold">{o.waiter || f.waiter_name || f.user || "—"}</p>
                    {o.waiter_role || f.user_role ? (
                      <p className="text-[10px] capitalize text-muted-foreground">{String(o.waiter_role || f.user_role).replace(/_/g, " ")}</p>
                    ) : null}
                  </div>
                  <div className="rounded-xl border border-border bg-secondary/30 p-2">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Payment method</p>
                    <p className="font-semibold capitalize">{o.payment_method || f.payment_method || "—"}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-secondary/30 p-2">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Payment amount</p>
                    <p className="font-semibold">{formatPrice(o.payment_amount != null ? o.payment_amount : ((Number(f.cash_paid || 0) + Number(f.card_paid || 0)) || o.revenue || f.total || 0))}</p>
                    {(o.tip_amount || f.tip_amount) ? (
                      <p className="text-[10px] text-muted-foreground">Tip {formatPrice(o.tip_amount || f.tip_amount || 0)}</p>
                    ) : null}
                  </div>
                </div>
                <div className="rounded-xl border border-border bg-card p-3 text-xs space-y-1">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Timeline</p>
                  <div className="flex justify-between"><span className="text-muted-foreground">Placed</span><span>{fmtTime(o.placed_at || f.created_at)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Accepted</span><span>{fmtTime(o.accepted_at || f.accepted_at)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Kitchen / bar ready</span><span>{fmtTime(o.ready_at || f.ready_at || f.kitchen_ready_at || f.bar_ready_at)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Delivered</span><span>{fmtTime(o.delivered_at || f.delivered_at || f.completed_at)}</span></div>
                  <Separator className="my-1" />
                  <div className="flex justify-between"><span className="text-muted-foreground">Prep duration</span><span>{o.prep_minutes !== null && o.prep_minutes !== undefined ? `${o.prep_minutes} min` : "—"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Delivery duration</span><span>{o.delivery_minutes !== null && o.delivery_minutes !== undefined ? `${o.delivery_minutes} min` : "—"}</span></div>
                  <div className="flex justify-between font-semibold"><span>Total service time</span><span className={o.total_minutes >= 30 ? "text-destructive" : ""}>{o.total_minutes} min</span></div>
                </div>
                {(f.items || []).length ? (
                  <div className="rounded-xl border border-border bg-card p-3">
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Items</p>
                    <div className="space-y-1 text-xs">
                      {f.items.map((it, idx) => (
                        <div key={idx} className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold">{it.quantity || 1}× {it.name}</p>
                            {it.notes ? <p className="text-[10px] text-muted-foreground">{it.notes}</p> : null}
                            {(it.modifiers || it.sides) ? <p className="text-[10px] text-muted-foreground">{[...(it.modifiers || []), ...(it.sides || [])].map((m) => m.name || m).join(", ")}</p> : null}
                          </div>
                          <span className="shrink-0 text-muted-foreground">{formatPrice(it.line_total || ((it.unit_price || 0) * (it.quantity || 1)))}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {f.notes ? <div className="rounded-xl border border-border bg-card p-3 text-xs"><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Order notes</p><p className="mt-1">{f.notes}</p></div> : null}
              </div>
            );
          })() : null}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setServiceTimeDetail({ open: false, order: null, loading: false })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      <Dialog open={cashUpDetail.open} onOpenChange={(open) => setCashUpDetail((d) => ({ ...d, open }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="cash-up-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Cash-up detail
            </DialogTitle>
            <DialogDescription>
              {cashUpDetail.record ? `Recorded ${new Date(cashUpDetail.record.created_at).toLocaleString()}${cashUpDetail.record.user ? ` by ${cashUpDetail.record.user}` : ""}` : ""}
            </DialogDescription>
          </DialogHeader>
          {cashUpDetail.record ? (() => {
            const snap = cashUpDetail.record.snapshot || {};
            const rec = cashUpDetail.record;
            const tone = rec.status === "balanced" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" : rec.status === "over" ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30" : "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30";
            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/60 p-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                    <Badge className={`mt-1 capitalize ${tone}`}>{rec.status}</Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Variance</p>
                    <p className="text-lg font-semibold">{rec.variance > 0 ? "+" : ""}{formatPrice(rec.variance || 0)}</p>
                  </div>
                </div>
                <div className="rounded-2xl border border-border bg-background p-4 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Opening float</span><span>{formatPrice(snap.opening_float || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Cash sales</span><span>+{formatPrice(snap.cash_sales || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Cash payouts</span><span className="text-destructive">−{formatPrice(snap.cash_payouts || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Supplier (cash)</span><span className="text-destructive">−{formatPrice(snap.supplier_payments_cash || 0)}</span></div>
                  <Separator className="my-2" />
                  <div className="flex justify-between font-semibold"><span>Expected</span><span>{formatPrice(rec.expected_cash || 0)}</span></div>
                  <div className="flex justify-between font-semibold"><span>Actual</span><span>{formatPrice(rec.actual_cash || 0)}</span></div>
                </div>

                {/* Opening + Closing denomination breakdowns side-by-side for full audit. */}
                {(() => {
                  const denomMeta = [["R200",200],["R100",100],["R50",50],["R20",20],["R10",10],["R5",5],["R2",2],["R1",1],["50c",0.5],["20c",0.2],["10c",0.1]];
                  const openD = snap.opening_float_denominations || null;
                  const closeD = rec.denominations || null;
                  if (!openD && !closeD) return null;
                  const renderGrid = (d, testid) => (
                    <div className="grid grid-cols-2 gap-1 text-[11px]" data-testid={testid}>
                      {denomMeta.map(([k, val]) => {
                        const qty = Number((d || {})[k] || 0);
                        if (!qty) return null;
                        return (
                          <div key={k} className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-2 py-1">
                            <span className="font-semibold">{k}</span>
                            <span className="text-muted-foreground">× {qty}</span>
                            <span>{formatPrice(qty * val)}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                  return (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {openD ? (
                        <div className="rounded-2xl border border-border bg-background p-3">
                          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Opening Float Breakdown</p>
                          {renderGrid(openD, "cashup-history-opening-breakdown")}
                        </div>
                      ) : null}
                      {closeD ? (
                        <div className="rounded-2xl border border-border bg-background p-3">
                          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Closing Cash Breakdown</p>
                          {renderGrid(closeD, "cashup-history-closing-breakdown")}
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
                {rec.notes ? (
                  <div className="rounded-2xl border border-border bg-background p-4">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Notes</p>
                    <p className="mt-1 text-sm">{rec.notes}</p>
                  </div>
                ) : null}
              </div>
            );
          })() : null}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setCashUpDetail({ open: false, record: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Receipt dialog — shared drill-down used across Orders, Tips,
          Reports, Service Times, and Receipts search. Single source of
          truth for "show me the full receipt". */}
      <Dialog open={orderReceipt.open} onOpenChange={(open) => setOrderReceipt((r) => ({ ...r, open }))}>
        <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden rounded-[24px] sm:max-w-lg" data-testid="owner-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">
              {orderReceipt.order?.__isSessionMerge ? "Table receipt" : "Receipt"}
              {` · ${orderReceipt.order?.order_number || ""}`}
            </DialogTitle>
            <DialogDescription>
              {orderReceipt.order ? (
                orderReceipt.order.__isSessionMerge
                  ? `Table ${orderReceipt.order.table_number || "—"}${orderReceipt.order.customer_name ? ` · ${orderReceipt.order.customer_name}` : ""} · ${orderReceipt.order.__sourceOrders?.length || 0} rounds on one bill · ${getOrderTimeLabel(orderReceipt.order.created_at)}`
                  : `Table ${orderReceipt.order.table_number || "—"} · ${getOrderTimeLabel(orderReceipt.order.created_at)}`
              ) : ""}
            </DialogDescription>
          </DialogHeader>

          {orderReceipt.order ? (() => {
            const ord = orderReceipt.order;
            const methodRaw = (ord.payment_method || "").toLowerCase();
            const methodLabel = methodRaw === "cash" ? "Cash" : methodRaw === "card" ? "Card" : methodRaw === "split" ? "Split" : methodRaw === "account" ? "Account" : methodRaw === "unpaid" ? "Unpaid" : "—";
            const methodTone = methodRaw === "cash"
              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
              : methodRaw === "card"
                ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30"
                : methodRaw === "split"
                  ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30"
                  : "bg-secondary text-muted-foreground border-border";
            // Session 46 — RECEIPT TRUTH. `cash_paid` / `card_paid` on an
            // order are the whole TABLE's totals (mirrored for filtering) and
            // the tip is stamped on ONE order per table. Showing them as if
            // they belonged to this order produced "R90 of food · Card paid
            // R324 · no tip". When the backend hands us the table's
            // `session_summary` we use THAT for every money line and label
            // it as the table bill.
            const ss = ord.session_summary && typeof ord.session_summary === "object" ? ord.session_summary : null;
            // Session 65 — when the detail fetch has not (yet) attached the
            // table summary, the Operations list already carried the same
            // bill truth as `session_money`.
            const sm = !ss && ord.session_money && typeof ord.session_money === "object" ? ord.session_money : null;
            const multiOrderTable = !!(ss && Number(ss.order_count) > 1) && !ord.__isSessionMerge;
            const tipVal = ss ? Number(ss.gratuity_total || 0) : sm ? Number(sm.tip_amount || 0) + Number(sm.service_fee_amount || 0) : Number(ord.tip_amount || 0);
            // Session 34 — gratuity breakdown mirrored on the tip-target order.
            const feeVal = ss ? Number(ss.service_fee_amount || 0) : sm ? Number(sm.service_fee_amount || 0) : Number(ord.service_fee_amount || 0);
            const feePctVal = ss?.service_fee_pct || sm?.service_fee_pct || ord.service_fee_pct || null;
            const voucherVal = ss ? Number(ss.voucher_discount || 0) : sm ? Number(sm.voucher_discount || 0) : 0;
            const extraTipVal = ss
              ? Number(ss.extra_tip_amount || 0)
              : sm ? Number(sm.tip_amount || 0)
              : (ord.extra_tip_amount != null ? Number(ord.extra_tip_amount || 0) : Math.max(0, tipVal - feeVal));
            const cashVal = ss ? Number(ss.cash_paid || 0) : sm ? Number(sm.cash_paid || 0) : Number(ord.cash_paid || 0);
            const cardVal = ss ? Number(ss.card_paid || 0) : sm ? Number(sm.card_paid || 0) : Number(ord.card_paid || 0);
            const eftVal = ss ? Number(ss.eft_paid || 0) : sm ? Number(sm.eft_paid || 0) : Number(ord.eft_paid || 0);
            const totalVal = Number(ord.total ?? (Number(ord.subtotal || 0) + Number(ord.vat_total || 0)));
            const totalCharged = cashVal + cardVal + eftVal;
            // FINAL BILL TOTAL = items + service fee − voucher (the one number
            // the guest owes; identical to the Operations row).
            const billOwed = ss ? Number(ss.amount_owed || 0) : sm ? Number(sm.amount_owed || 0) : Math.max(0, totalVal + feeVal - voucherVal);
            const billOutstanding = Math.max(0, Math.round((billOwed - totalCharged) * 100) / 100);
            const billFullyPaid = billOwed > 0 && totalCharged >= billOwed - 0.009;
            const overpaidToward = Math.max(0, Math.round((totalCharged - billOwed) * 100) / 100);
            const deptRouting = ord.department_routing
              || (Array.isArray(ord.departments) ? ord.departments : null);
            return (
            <div className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1">
              <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/60 px-4 py-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                  <div className="mt-1"><StatusBadge status={ord.status} /></div>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Items</p>
                  <p className="font-semibold">{ord.total_items || 0}</p>
                </div>
              </div>

              {/* Payment + waiter strip — surfaces the metadata managers
                  most often want when drilling into a single receipt. */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-2xl border border-border bg-background px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Payment</p>
                  <Badge className={`mt-1 ${methodTone}`} data-testid="owner-receipt-method">{methodLabel}</Badge>
                </div>
                <div className="rounded-2xl border border-border bg-background px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Waiter</p>
                  <p className="mt-1 truncate text-sm font-medium" data-testid="owner-receipt-waiter">{orderWaiterLabel(ord) || "—"}</p>
                  {ord.table_waiter && ord.table_waiter !== orderWaiterLabel(ord) ? (
                    <p className="truncate text-[11px] text-muted-foreground" data-testid="owner-receipt-table-waiter">Table assigned to {ord.table_waiter}</p>
                  ) : null}
                </div>
              </div>
              {/* Session 36 — accountability trail: who took the money / closed the table */}
              {(Array.isArray(ord.payment_taken_by) && ord.payment_taken_by.length) || ord.table_closed_at ? (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-2xl border border-border bg-background px-3 py-2 text-xs text-muted-foreground" data-testid="owner-receipt-trail">
                  {Array.isArray(ord.payment_taken_by) && ord.payment_taken_by.length ? (
                    <span>Payment taken by <span className="font-medium text-foreground">{ord.payment_taken_by.join(", ")}</span></span>
                  ) : null}
                  {ord.table_closed_at ? (
                    <span>Table closed {getOrderTimeLabel(ord.table_closed_at)}</span>
                  ) : null}
                </div>
              ) : null}

              <div className="rounded-2xl border border-border bg-background p-4">
                <p className="mb-3 font-semibold">Items</p>
                <div className="space-y-2">
                  {(ord.items || []).map((item, idx) => {
                    const lineTotal = item.line_total ?? item.line_subtotal ?? Number(item.price || 0) * Number(item.quantity || 1);
                    return (
                      <div key={`${item.name}-${idx}`} className="flex items-start justify-between gap-3 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium">{item.quantity} × {item.name}</p>
                          <p className="text-xs text-muted-foreground">{formatPrice(item.price)} each{item.department ? ` · ${item.department}` : ""}</p>
                        </div>
                        <p className="font-semibold whitespace-nowrap">{formatPrice(lineTotal)}</p>
                      </div>
                    );
                  })}
                  {!(ord.items || []).length ? (
                    <p className="text-sm text-muted-foreground">No items recorded.</p>
                  ) : null}
                </div>

                <Separator className="my-4" />

                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>{formatPrice(ord.subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>VAT</span>
                    <span>{formatPrice(ord.vat_total)}</span>
                  </div>
                  <Separator className="my-2" />
                  {/* Session 65 — ONE final bill total:
                        Items + Service fee − Voucher = TOTAL (what the guest owes). */}
                  <div className={`flex items-center justify-between ${multiOrderTable || feeVal > 0 || voucherVal > 0 ? "text-muted-foreground" : "text-base font-semibold"}`}>
                    <span>{multiOrderTable ? "This order" : "Items"}</span>
                    <span data-testid="owner-receipt-total">{formatPrice(totalVal)}</span>
                  </div>
                  {multiOrderTable ? (
                    <p className="text-xs text-muted-foreground" data-testid="owner-receipt-multi-order-note">
                      This order is 1 of {ss.order_count} on Table {ss.table_number || "—"}. Payment was taken for the whole table — see the table bill below.
                    </p>
                  ) : null}
                  {!multiOrderTable && (feeVal > 0 || voucherVal > 0) ? (
                    <>
                      {feeVal > 0 && (
                        <div className="flex items-center justify-between text-muted-foreground" data-testid="owner-receipt-service-fee">
                          <span>Service fee{feePctVal ? ` (${feePctVal}%)` : ""}</span>
                          <span>{formatPrice(feeVal)}</span>
                        </div>
                      )}
                      {voucherVal > 0 && (
                        <div className="flex items-center justify-between text-muted-foreground" data-testid="owner-receipt-voucher">
                          <span>Voucher</span>
                          <span>−{formatPrice(voucherVal)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-base font-semibold" data-testid="owner-receipt-bill-owed">
                        <span>TOTAL</span>
                        <span>{formatPrice(billOwed)}</span>
                      </div>
                    </>
                  ) : null}
                  {!multiOrderTable && (tipVal > 0 || cashVal > 0 || cardVal > 0 || eftVal > 0 || feeVal > 0) ? (
                    <>
                      <Separator className="my-2" />
                      {cashVal > 0 && (
                        <div className="flex items-center justify-between text-muted-foreground">
                          <span>Cash paid</span>
                          <span>{formatPrice(cashVal)}</span>
                        </div>
                      )}
                      {cardVal > 0 && (
                        <div className="flex items-center justify-between text-muted-foreground">
                          <span>Card paid</span>
                          <span>{formatPrice(cardVal)}</span>
                        </div>
                      )}
                      {eftVal > 0 && (
                        <div className="flex items-center justify-between text-muted-foreground">
                          <span>EFT paid</span>
                          <span>{formatPrice(eftVal)}</span>
                        </div>
                      )}
                      {extraTipVal > 0 && (
                        <div className="flex items-center justify-between text-muted-foreground" data-testid="owner-receipt-extra-tip">
                          <span>{feeVal > 0 ? "Extra tip" : "Tip"}</span>
                          <span>{formatPrice(extraTipVal)}</span>
                        </div>
                      )}
                      {tipVal > 0 && feeVal > 0 && totalCharged > 0.009 && (
                        <div className="flex items-center justify-between text-sm font-semibold text-amber-700 dark:text-amber-300">
                          <span>Service fee + tip{(ss?.tip_recipient || ord.tip_recipient) ? ` (${ss?.tip_recipient || ord.tip_recipient})` : ""}</span>
                          <span data-testid="owner-receipt-tip">{formatPrice(tipVal)}</span>
                        </div>
                      )}
                      {tipVal > 0 && feeVal <= 0 && (
                        <div className="flex items-center justify-between text-sm font-semibold text-amber-700 dark:text-amber-300">
                          <span>Tip{(ss?.tip_recipient || ord.tip_recipient) ? ` (${ss?.tip_recipient || ord.tip_recipient})` : ""}</span>
                          <span data-testid="owner-receipt-tip">{formatPrice(tipVal)}</span>
                        </div>
                      )}
                      {totalCharged > 0 && (
                        <div className="flex items-center justify-between text-sm font-semibold">
                          <span>Total charged</span>
                          <span data-testid="owner-receipt-total-charged">{formatPrice(totalCharged + extraTipVal)}</span>
                        </div>
                      )}
                      {billOwed > 0 && !(ord.voided || String(ord.status || "").toLowerCase() === "voided") ? (
                        billFullyPaid ? (
                          <p className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs font-medium text-emerald-800 dark:text-emerald-200" data-testid="owner-receipt-paid-in-full">
                            Paid in full · {formatPrice(billOwed)} received
                          </p>
                        ) : (
                          <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-medium text-rose-800 dark:text-rose-200" data-testid="owner-receipt-outstanding">
                            {totalCharged > 0 ? "Not fully paid" : "Unpaid"} · {formatPrice(billOutstanding)} outstanding of the {formatPrice(billOwed)} bill
                          </p>
                        )
                      ) : null}
                      {overpaidToward > 0.009 && (
                        <p className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200" data-testid="owner-receipt-overpay-warning">
                          {formatPrice(overpaidToward)} more was recorded toward the bill than it came to. That amount was not booked as a tip — check the payment on this table.
                        </p>
                      )}
                    </>
                  ) : null}
                  {Number(ord.refund_amount || 0) > 0 ? (
                    <>
                      <Separator className="my-2" />
                      <div className="flex items-center justify-between text-sm font-semibold text-rose-700 dark:text-rose-300">
                        <span>Refund{ord.refund_user ? ` (${ord.refund_user})` : ""}</span>
                        <span>−{formatPrice(Number(ord.refund_amount))}</span>
                      </div>
                      {ord.refund_reason ? <p className="text-xs italic text-muted-foreground">{ord.refund_reason}</p> : null}
                    </>
                  ) : null}
                </div>
              </div>

              {/* Session 46 — TABLE BILL: the truthful money block for an
                  order that shares its table with other orders. */}
              {multiOrderTable ? (
                <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="owner-receipt-table-bill">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Table {ss.table_number || "—"} bill · {ss.order_count} orders</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 rounded-lg text-xs"
                      onClick={() => openTableReceiptFromSummary(ss)}
                      data-testid="owner-receipt-view-table-bill"
                    >
                      Full table receipt
                    </Button>
                  </div>
                  <div className="space-y-1 text-sm">
                    {(ss.orders || []).map((so) => (
                      <div key={so.id} className={`flex items-center justify-between gap-2 ${so.is_this_order ? "font-semibold" : "text-muted-foreground"}`}>
                        <span className="truncate">{so.order_number || so.id}{so.is_this_order ? " (this order)" : ""}{String(so.status || "") === "voided" ? " · voided" : ""}</span>
                        <span className="tabular-nums">{formatPrice(so.total || 0)}</span>
                      </div>
                    ))}
                    <Separator className="my-2" />
                    <div className="flex items-center justify-between text-muted-foreground"><span>Items total</span><span>{formatPrice(ss.items_total || 0)}</span></div>
                    {Number(ss.voucher_discount || 0) > 0 ? (
                      <div className="flex items-center justify-between text-muted-foreground"><span>Voucher</span><span>−{formatPrice(ss.voucher_discount)}</span></div>
                    ) : null}
                    {feeVal > 0 ? (
                      <div className="flex items-center justify-between text-muted-foreground" data-testid="owner-receipt-service-fee"><span>Service fee{ss.service_fee_pct ? ` (${ss.service_fee_pct}%)` : ""}</span><span>{formatPrice(feeVal)}</span></div>
                    ) : null}
                    <div className="flex items-center justify-between font-semibold" data-testid="owner-receipt-bill-owed"><span>Bill total</span><span>{formatPrice(billOwed)}</span></div>
                    <Separator className="my-2" />
                    {cashVal > 0 ? <div className="flex items-center justify-between text-muted-foreground"><span>Cash paid</span><span>{formatPrice(cashVal)}</span></div> : null}
                    {cardVal > 0 ? <div className="flex items-center justify-between text-muted-foreground"><span>Card paid</span><span>{formatPrice(cardVal)}</span></div> : null}
                    {eftVal > 0 ? <div className="flex items-center justify-between text-muted-foreground"><span>EFT paid</span><span>{formatPrice(eftVal)}</span></div> : null}
                    {extraTipVal > 0 ? (
                      <div className="flex items-center justify-between text-muted-foreground" data-testid="owner-receipt-extra-tip"><span>{feeVal > 0 ? "Extra tip" : "Tip"}</span><span>{formatPrice(extraTipVal)}</span></div>
                    ) : null}
                    {tipVal > 0 ? (
                      <div className="flex items-center justify-between font-semibold text-amber-700 dark:text-amber-300"><span>{feeVal > 0 ? "Service fee + tip" : "Tip"}{ss.tip_recipient ? ` (${ss.tip_recipient})` : ""}</span><span data-testid="owner-receipt-tip">{formatPrice(tipVal)}</span></div>
                    ) : null}
                    {totalCharged > 0 ? (
                      <div className="flex items-center justify-between font-semibold"><span>Total charged</span><span data-testid="owner-receipt-total-charged">{formatPrice(totalCharged + extraTipVal)}</span></div>
                    ) : (
                      <div className="flex items-center justify-between text-muted-foreground"><span>Payment</span><span>Not yet recorded</span></div>
                    )}
                    {Number(ss.outstanding_amount || 0) > 0.009 ? (
                      <div className="flex items-center justify-between font-semibold text-rose-700 dark:text-rose-300"><span>Still outstanding</span><span>{formatPrice(ss.outstanding_amount)}</span></div>
                    ) : null}
                    {overpaidToward > 0.009 ? (
                      <p className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200" data-testid="owner-receipt-overpay-warning">
                        {formatPrice(overpaidToward)} more was recorded toward the bill than it came to. That amount was not booked as a tip — check the payment on this table.
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {/* Session 70 — SPLIT BILL: which guest paid what, and what they ordered. */}
              {ord.split_guests && Array.isArray(ord.split_guests.guests) && ord.split_guests.guests.length ? (
                <SplitGuestBreakdown breakdown={ord.split_guests} testId="owner-receipt-split-guests" />
              ) : null}

              {/* Session 27.9 — Included orders breakdown (only shown
                  for consolidated session receipts). Gives the owner
                  visibility into which individual add-item orders
                  made up this combined bill. */}
              {ord.__isSessionMerge && Array.isArray(ord.__sourceOrders) && ord.__sourceOrders.length > 1 ? (
                <div className="rounded-2xl border border-border bg-background p-4">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rounds on this receipt</p>
                  <p className="mb-2 text-[11px] text-muted-foreground">Click a round to open it on its own (refund, void or edit that round).</p>
                  <div className="space-y-1.5 text-sm">
                    {ord.__sourceOrders.map((so, idx) => (
                      <button
                        type="button"
                        key={so.id}
                        onClick={() => openReceiptById(so.id)}
                        className="flex w-full items-center justify-between gap-2 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid={`receipt-round-open-${so.id}`}
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium">Round {idx + 1}{so.order_number ? <span className="ml-2 text-xs font-normal text-muted-foreground">{so.order_number}</span> : null}</p>
                          <p className="text-xs text-muted-foreground">{getOrderTimeLabel(so.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {so.payment_method ? <Badge variant="outline" className="rounded-full capitalize text-[10px]">{String(so.payment_method).toLowerCase()}</Badge> : null}
                          <p className="font-semibold tabular-nums">{formatPrice(so.total ?? 0)}</p>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Department routing — surfaces which stations a ticket
                  hit (Kitchen, Bar). Helps investigate slow orders. */}
              {(() => {
                const items = ord.items || [];
                const depts = new Set();
                items.forEach((it) => {
                  if (it.department) depts.add(String(it.department));
                });
                if (Array.isArray(deptRouting)) {
                  deptRouting.forEach((d) => depts.add(String(d.name || d)));
                }
                if (!depts.size) return null;
                return (
                  <div className="rounded-2xl border border-border bg-background p-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Department routing</p>
                    <div className="flex flex-wrap gap-1.5">
                      {[...depts].map((d) => (
                        <Badge key={d} variant="outline" className="rounded-full">{d}</Badge>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Timing — only shown when service-times timestamps exist. */}
              {(ord.confirmed_at || ord.completed_at) && (
                <div className="rounded-2xl border border-border bg-background p-4 text-sm">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Timing</p>
                  <div className="space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Placed</span><span>{ord.created_at ? new Date(ord.created_at).toLocaleString() : "—"}</span></div>
                    {ord.confirmed_at && (
                      <div className="flex justify-between"><span className="text-muted-foreground">Ready</span><span>{new Date(ord.confirmed_at).toLocaleString()}</span></div>
                    )}
                    {ord.completed_at && (
                      <div className="flex justify-between"><span className="text-muted-foreground">Delivered</span><span>{new Date(ord.completed_at).toLocaleString()}</span></div>
                    )}
                  </div>
                </div>
              )}
            </div>
            );
          })() : null}

          <DialogFooter className="flex-wrap gap-2 sm:flex-wrap sm:justify-end sm:space-x-0">
            {orderReceipt.order?.__isSessionMerge ? (
              <div className="mr-auto max-w-full text-xs text-muted-foreground">
                Whole-table receipt · {orderReceipt.order.__sourceOrders?.length || 0} round{orderReceipt.order.__sourceOrders?.length === 1 ? "" : "s"}.
                To refund or edit, click a round above to open it on its own.
              </div>
            ) : null}
            {orderReceipt.order && !orderReceipt.order.__isSessionMerge && Number(orderReceipt.order.refund_amount || 0) > 0 ? (
              <div className="mr-auto flex items-center gap-2">
                <Badge className="rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30">
                  Refunded {formatPrice(Number(orderReceipt.order.refund_amount))}
                </Badge>
                {userCan("refund") ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-rose-500/40 text-rose-700 hover:bg-rose-500/10 dark:text-rose-300"
                  onClick={() => {
                    const ord = orderReceipt.order;
                    const currentAmt = Number(ord.refund_amount || 0);
                    const reasonRaw = String(ord.refund_reason || "");
                    const entry = Object.entries(REFUND_REASON_LABELS).find(([, label]) => label.toLowerCase() === reasonRaw.toLowerCase());
                    const reasonKey = entry ? entry[0] : (reasonRaw ? "other" : "wrong_item");
                    const reasonOther = entry ? "" : reasonRaw;
                    setRefundDialog({
                      open: true,
                      order: ord,
                      editing: true,
                      values: { amount: currentAmt.toFixed(2), reason: reasonKey, reason_other: reasonOther, pin: "", user: ord.refund_user || "", notes: "" },
                      saving: false,
                    });
                  }}
                  data-testid="receipt-edit-refund-btn"
                >
                  Edit refund
                </Button>
                ) : null}
              </div>
            ) : orderReceipt.order && !orderReceipt.order.__isSessionMerge && userCan("refund") ? (
              <Button
                type="button"
                variant="outline"
                className="rounded-xl border-rose-500/40 text-rose-700 hover:bg-rose-500/10 dark:text-rose-300"
                onClick={() => {
                  const ord = orderReceipt.order;
                  const total = Number(ord.total ?? (Number(ord.subtotal || 0) + Number(ord.vat_total || 0)));
                  setRefundDialog({ open: true, order: ord, editing: false, values: { amount: total.toFixed(2), reason: "wrong_item", reason_other: "", pin: "", user: "", notes: "" }, saving: false });
                }}
                data-testid="receipt-refund-btn"
              >
                Refund payment
              </Button>
            ) : null}
            {orderReceipt.order && !orderReceipt.order.__isSessionMerge && userCan("refund") ? (
              <Button
                type="button"
                variant="outline"
                className="rounded-xl border-sky-500/40 text-sky-700 hover:bg-sky-500/10 dark:text-sky-300"
                onClick={() => { const o = orderReceipt.order; const totalPaid = Number(o.cash_paid || 0) + Number(o.card_paid || 0) || Number(o.total || 0); const m = o.payment_method === "card" ? "cash" : "card"; setPaymentCorrection({ open: true, order: o, values: { new_method: m, reason: "", manager_pin: "", cash_paid: m === "cash" ? totalPaid.toFixed(2) : (m === "split" ? Number(o.cash_paid || 0).toFixed(2) : "0"), card_paid: m === "card" ? totalPaid.toFixed(2) : (m === "split" ? Number(o.card_paid || 0).toFixed(2) : "0") }, saving: false }); }}
                data-testid="receipt-correct-payment-btn"
              >
                Correct Payment Method
              </Button>
            ) : null}
            {/* Reopen Table button intentionally NOT shown on the Owner
                / Manager Receipts dialog — that workflow is owned by the
                Staff Dashboard paid-receipt screen. */}
            <Button variant="outline" className="rounded-xl" onClick={() => setOrderReceipt({ open: false, order: null })} data-testid="owner-receipt-close-button">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session 32 — floating GPT copilot (Owner + permitted Managers). */}
      {aiContext.enabled && aiContext.allowed ? (
        <AiAssistant
          http={http}
          authHeaders={authHeaders}
          context={aiContext}
          onUsage={(u) => setAiContext((c) => ({ ...c, usage: u }))}
          getScreen={() => (activeTab === "analytics" ? `${activeTab}:${analyticsSubtab}` : activeTab)}
          stockTakeLines={stockTakeWizard.open ? stockTakeWizard.lines.map((ln) => ({ ...ln, expected: Number(ln.expected?.[stockTakeWizard.location] ?? 0), actual: ln.actual?.[stockTakeWizard.location] ?? "" })) : null}
          onNavigate={(action) => {
            const nav = action?.nav || {};
            const GROUP_FOR_TAB = { overview: "overview", accounts: "overview", reservations: "overview", orders: "operations", staff: "operations", "manage-menu": "menu", cash: "finance", links: "settings", tags: "settings", branding: "settings", payment_gateways: "settings", "ai-settings": "settings" };
            const group = nav.tab === "analytics" ? (nav.analyticsSubtab === "stock" ? "inventory" : "finance") : (GROUP_FOR_TAB[nav.tab] || "overview");
            setActiveGroup(group);
            if (aiNavRef.current) aiNavRef.current({ tab: nav.tab, analyticsSubtab: nav.analyticsSubtab, stockSubtab: nav.stockSubtab });
            else setActiveTab(nav.tab);
            if (nav.action === "open_cash_up") setCashUpDialog({ open: true, values: { actual_cash: "", user: "", notes: "", denominations: {} } });
            if (nav.action === "open_stock_take") openStockTakeWizard();
            if (typeof nav.action === "string" && nav.action.startsWith("menu_tab:")) setManageMenuTab(nav.action.split(":")[1]);
            if (action.date_filter) setDateFilter(action.date_filter);
            if (action.highlight?.length) setTimeout(() => aiHighlight(action.highlight), 500);
          }}
          onStockTakeSet={(a) => {
            setStockTakeWizard((s) => ({ ...s, lines: s.lines.map((ln) => (String(ln.item_id) === String(a.item_id) && ln.item_type === a.item_type ? { ...ln, actual: { ...(ln.actual || {}), [s.location]: String(a.value) } } : ln)) }));
          }}
          onStockTakeSave={() => {
            if (!stockTakeWizard.open) { toast.info("Open the stock take first — say \"start a stock take\"."); return; }
            setTimeout(() => aiHighlight(["stock-take-submit"]), 200);
          }}
        />
      ) : null}
    </main>
  );
}
